import { useState, useEffect, useRef } from "react";
import {
  LayoutDashboard, BookOpen, BarChart2, Sparkles, Leaf, Target, Wallet,
  CalendarDays, Map, Wind, Droplets, Moon, Apple, Library, Mail, Settings,
  Lock, ChevronRight, ChevronDown, ChevronUp, Bell, Star, Heart, Flame,
  Check, Plus, Minus, X, Edit2, Trash2, Pause, Play, Clock, Phone, Globe,
  MapPin, FileText, Pill, AlertCircle, Shield, Search, Camera, Mic,
  TrendingUp, Award, Zap, Sun, Coffee, Music, Pencil, Activity, Eye,
  EyeOff, LogOut, User, CreditCard, HelpCircle, CheckCircle, Info,
  ArrowRight, RotateCcw, Save, Download, Share2, Filter, Home, Gift, Brain, ShoppingCart, Car
} from "lucide-react";

// Helper: renders pillar Icon component (Lucide) if available, else falls back to emoji
function PillarIcon({ pillar, size = 16, style = {} }) {
  if (!pillar) return null;
  if (pillar.Icon) return <pillar.Icon size={size} color={pillar.color} style={style} />;
  return <span style={{ fontSize: size * 0.9, ...style }}>{pillar.icon}</span>;
}

// Helper: renders any icon object with Icon (Lucide) or icon (emoji)
function ItemIcon({ item, size = 16, color, style = {} }) {
  if (!item) return null;
  if (item.Icon) return <item.Icon size={size} color={color || item.color || "currentColor"} style={style} />;
  if (item.icon) return <span style={{ fontSize: size * 0.9, ...style }}>{item.icon}</span>;
  return null;
}


// ══════════════════════════════════════════════════════════════════
//  BRAND BUTTERFLY — SVG matching logo style, app gold palette
// ══════════════════════════════════════════════════════════════════
function BrandButterfly({ size = 28, color = "#C9963B", opacity = 1 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100"
      fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ opacity, flexShrink:0 }}>
      {/* Left upper wing */}
      <path d="M50 52 C44 44 28 30 14 26 C6 24 2 30 4 38 C6 46 16 52 28 54 C36 56 44 54 50 52Z"
        fill={color} fillOpacity="0.85" stroke={color} strokeWidth="0.8" />
      {/* Left lower wing */}
      <path d="M50 54 C42 58 28 62 18 70 C10 76 12 84 20 84 C30 84 42 76 50 66Z"
        fill={color} fillOpacity="0.6" stroke={color} strokeWidth="0.8" />
      {/* Right upper wing */}
      <path d="M50 52 C56 44 72 30 86 26 C94 24 98 30 96 38 C94 46 84 52 72 54 C64 56 56 54 50 52Z"
        fill={color} fillOpacity="0.85" stroke={color} strokeWidth="0.8" />
      {/* Right lower wing */}
      <path d="M50 54 C58 58 72 62 82 70 C90 76 88 84 80 84 C70 84 58 76 50 66Z"
        fill={color} fillOpacity="0.6" stroke={color} strokeWidth="0.8" />
      {/* Wing detail lines — left upper */}
      <path d="M50 52 C44 46 34 36 22 30" stroke={color} strokeWidth="0.6" strokeOpacity="0.5" fill="none"/>
      <path d="M50 52 C42 46 30 40 18 38" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" fill="none"/>
      <path d="M50 52 C44 48 36 46 24 48" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" fill="none"/>
      {/* Wing detail lines — right upper */}
      <path d="M50 52 C56 46 66 36 78 30" stroke={color} strokeWidth="0.6" strokeOpacity="0.5" fill="none"/>
      <path d="M50 52 C58 46 70 40 82 38" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" fill="none"/>
      <path d="M50 52 C56 48 64 46 76 48" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" fill="none"/>
      {/* Wing detail lines — lower wings */}
      <path d="M50 56 C46 62 38 68 28 74" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" fill="none"/>
      <path d="M50 56 C54 62 62 68 72 74" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" fill="none"/>
      {/* Body */}
      <ellipse cx="50" cy="56" rx="2.5" ry="18"
        fill={color} fillOpacity="0.9" />
      {/* Head */}
      <circle cx="50" cy="36" r="3"
        fill={color} fillOpacity="0.9" />
      {/* Antennae */}
      <path d="M50 34 C46 28 40 22 36 18" stroke={color} strokeWidth="1" strokeOpacity="0.7"
        fill="none" strokeLinecap="round"/>
      <circle cx="35" cy="17" r="1.5" fill={color} fillOpacity="0.7"/>
      <path d="M50 34 C54 28 60 22 64 18" stroke={color} strokeWidth="1" strokeOpacity="0.7"
        fill="none" strokeLinecap="round"/>
      <circle cx="65" cy="17" r="1.5" fill={color} fillOpacity="0.7"/>
      {/* Wing spots — decorative */}
      <circle cx="32" cy="40" r="3" fill={color} fillOpacity="0.25"/>
      <circle cx="68" cy="40" r="3" fill={color} fillOpacity="0.25"/>
      <circle cx="26" cy="72" r="2.5" fill={color} fillOpacity="0.2"/>
      <circle cx="74" cy="72" r="2.5" fill={color} fillOpacity="0.2"/>
    </svg>
  );
}


import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from "recharts";

// ══════════════════════════════════════════════════════════════════
//  UTILITIES
// ══════════════════════════════════════════════════════════════════

// ── Supabase client (no npm needed — uses REST API directly) ──
const _SURL = "https://uscupujyydgkmtwpedrp.supabase.co";
const _SKEY = "sb_publishable__qEeCtgZaC4Ltvd-wmVLWg_Q2gSrN9j";

// Stripe price IDs
const STRIPE_PRICES = {
  seed:   "price_1T6E4zJnCC8bVo1rUG4WEtu8",
  bloom:  "price_1T6E66JnCC8bVo1rlWoTMH9v",
  thrive: "price_1T6E7ZJnCC8bVo1rIGUaeZEA"
};

let _currentPlan = null; // kept in step with the signed-in member's plan

async function startUpgradeCheckout(targetPlan) {
  // Free members have no subscription yet: start one (the server adds the
  // 14-day free trial if she has never had one).
  if (_currentPlan === "free") {
    try {
      const res = await apiFetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: targetPlan })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert(data.error || "Could not start checkout. Please try again.");
    } catch (e) {
      alert("Connection error. Please try again.");
    }
    return;
  }
  // Paid members change their existing subscription through Stripe's secure
  // billing page, so she is never billed for two plans at once.
  try {
    const res = await apiFetch("/api/billing-portal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ targetPlan })
    });
    const data = await res.json();
    if (data.url) {
      window.location.href = data.url;
    } else {
      alert(data.error || "Could not open billing. Please try again.");
    }
  } catch(e) {
    alert("Connection error. Please try again.");
  }
}
let _uid = null;   // current user's Supabase UUID
let _tok = null;   // current JWT access token

function _h(extra = {}) {
  return { apikey: _SKEY, Authorization: `Bearer ${_tok || _SKEY}`,
    "Content-Type": "application/json", ...extra };
}
// Calls our own /api endpoints with the member's login attached,
// so the server knows who is asking.
function apiFetch(url, opts = {}) {
  const tok = _tok || localStorage.getItem("gf_tok");
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), ...(tok ? { Authorization: `Bearer ${tok}` } : {}) }
  });
}
// The member's plan comes from the server (checked against Stripe),
// never from anything the browser can edit.
async function fetchMyPlan(authUser) {
  try {
    const r = await apiFetch("/api/my-plan", { method: "POST" });
    if (r.ok) {
      const d = await r.json();
      return { plan: d.plan || "none", trialEnded: (d.plan || "none") === "none" && !!d.hadSubscription };
    }
  } catch (e) {}
  // Server unreachable: use the plan the server last recorded on her account.
  const m = authUser?.app_metadata || {};
  const plan = ["seed","bloom","thrive"].includes(m.comp_plan) ? m.comp_plan
             : ["seed","bloom","thrive"].includes(m.plan) ? m.plan : "none";
  return { plan, trialEnded: false };
}
function _saveSession(session) {
  _tok = session?.access_token || null;
  _uid = session?.user?.id || null;
  if (_tok) localStorage.setItem("gf_tok", _tok);
  else localStorage.removeItem("gf_tok");
}

// ── Storage: localStorage is primary, Supabase is background sync ──

// Restore session from localStorage token if globals missing
async function _ensureSession() {
  if (_uid && _tok) return true;
  const token = localStorage.getItem("gf_tok");
  if (!token) return false;
  const authUser = await supaGetUser(token);
  if (authUser) { _saveSession({ access_token: token, user: authUser }); return true; }
  return false;
}

// localStorage key for cache
const _lsKey = (key) => `_gf_v2_${key}`;

// Free (Seedling) members keep their data on this device only: nothing new
// is written to the cloud and nothing overwrites their local copy.
// Paid members get full cloud backup and sync across devices.
let _cloudSync = true;
function setCloudSync(enabled) { _cloudSync = !!enabled; }

// When a free member upgrades, copy everything saved on this device up to
// the cloud so her entries are backed up from then on.
async function pushLocalDataToCloud(email) {
  if (!email) return;
  const prefix = _lsKey("");
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(prefix) && k.includes(email)) keys.push(k);
  }
  for (const k of keys) {
    try {
      const parsed = JSON.parse(localStorage.getItem(k));
      if (parsed && parsed.value !== undefined) await _syncToSupabase(k.slice(prefix.length), parsed.value, true);
    } catch (e) {}
  }
}

// Write to localStorage immediately, then sync to Supabase in background
async function safeSave(key, value) {
  // 1. Save to localStorage immediately — always succeeds, instant
  try {
    localStorage.setItem(_lsKey(key), JSON.stringify({ value, ts: Date.now() }));
  } catch(e) { console.warn("localStorage write failed", e); }

  // 2. Sync to Supabase in background (don't block UI)
  _syncToSupabase(key, value);
}

async function _syncToSupabase(key, value, force = false) {
  if (!_cloudSync && !force) return; // free plan: device only
  try {
    await _ensureSession();
    if (!_uid) return;
    // Use DELETE + INSERT pattern to avoid needing unique constraint
    await fetch(`${_SURL}/rest/v1/user_data?user_id=eq.${_uid}&key=eq.${encodeURIComponent(key)}`, {
      method: "DELETE", headers: _h()
    });
    await fetch(`${_SURL}/rest/v1/user_data`, {
      method: "POST",
      headers: _h({ "Content-Type": "application/json" }),
      body: JSON.stringify({ user_id: _uid, key, value, updated_at: new Date().toISOString() })
    });
  } catch(e) { /* silent — localStorage is the source of truth */ }
}

// Read from localStorage first (fast), then check if Supabase has newer data
async function safeRetrieve(key) {
  // Read localStorage immediately
  let localData = null;
  let localTs = 0;
  try {
    const raw = localStorage.getItem(_lsKey(key));
    if (raw) { const parsed = JSON.parse(raw); localData = parsed.value; localTs = parsed.ts || 0; }
  } catch(e) {}

  // If we have local data, return it right away — don't wait for Supabase
  if (localData !== null && localData !== undefined) {
    // Sync from Supabase in background only if local data is older than 5 minutes
    // (never for free members: their newest data lives only on this device)
    if (_cloudSync && Date.now() - localTs > 5 * 60 * 1000) {
      _syncFromSupabase(key);
    }
    return localData;
  }

  // No local data — try Supabase (first load on new device)
  await _ensureSession();
  if (!_uid) return null;
  try {
    const res = await fetch(
      `${_SURL}/rest/v1/user_data?user_id=eq.${_uid}&key=eq.${encodeURIComponent(key)}&select=value&order=updated_at.desc&limit=1`,
      { headers: _h() }
    );
    const rows = await res.json();
    if (Array.isArray(rows) && rows.length > 0 && rows[0].value) {
      const val = rows[0].value;
      // Cache to localStorage so future loads are instant
      try { localStorage.setItem(_lsKey(key), JSON.stringify({ value: val, ts: Date.now() })); } catch(e) {}
      return val;
    }
  } catch(e) {}
  return null;
}

// Force-refresh a key from Supabase into localStorage (called on login)
async function _forceRefreshFromCloud(key) {
  if (!_cloudSync) return;
  await _ensureSession();
  if (!_uid) return;
  try {
    const res = await fetch(
      `${_SURL}/rest/v1/user_data?user_id=eq.${_uid}&key=eq.${encodeURIComponent(key)}&select=value&order=updated_at.desc&limit=1`,
      { headers: _h() }
    );
    const rows = await res.json();
    if (Array.isArray(rows) && rows.length > 0 && rows[0].value) {
      try { localStorage.setItem(_lsKey(key), JSON.stringify({ value: rows[0].value, ts: Date.now() })); } catch(e) {}
    }
  } catch(e) {}
}

async function _syncFromSupabase(key) {
  if (!_cloudSync) return;
  try {
    await _ensureSession();
    if (!_uid) return;
    const res = await fetch(
      `${_SURL}/rest/v1/user_data?user_id=eq.${_uid}&key=eq.${encodeURIComponent(key)}&select=value&order=updated_at.desc&limit=1`,
      { headers: _h() }
    );
    const rows = await res.json();
    if (Array.isArray(rows) && rows.length > 0 && rows[0].value) {
      try { localStorage.setItem(_lsKey(key), JSON.stringify({ value: rows[0].value, ts: Date.now() })); } catch(e) {}
    }
  } catch(e) {}
}

// Legacy aliases for code that still uses store/retrieve directly
async function store(key, value) { await safeSave(key, value); }
async function retrieve(key) { return await safeRetrieve(key); }

async function removeKey(key) {
  if (!_uid) return;
  try {
    await fetch(
      `${_SURL}/rest/v1/user_data?user_id=eq.${_uid}&key=eq.${encodeURIComponent(key)}`,
      { method: "DELETE", headers: _h() }
    );
  } catch(e) {}
}

// Supabase Auth helpers
// Fetch with 15s timeout to prevent infinite hangs
async function _fetch(url, opts = {}) {
  if (!_SURL || _SURL === "undefined" || !_SKEY || _SKEY === "undefined") {
    throw new Error("App configuration error. Please contact support.");
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(url, { ...opts, signal: controller.signal });
    clearTimeout(timer);
    return res;
  } catch(e) {
    clearTimeout(timer);
    if (e.name === "AbortError") throw new Error("Request timed out. Please check your connection and try again.");
    throw e;
  }
}

async function _safeJson(res) {
  try {
    const text = await res.text();
    if (!text || text.trim() === "") return { error: "Empty response from server. Please try again." };
    return JSON.parse(text);
  } catch(e) {
    return { error: "Could not connect to server. Please check your connection and try again." };
  }
}

async function supaSignUp(email, password, name) {
  const res = await _fetch(`${_SURL}/auth/v1/signup`, {
    method: "POST",
    headers: { apikey: _SKEY, "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, data: { name } })
  });
  return _safeJson(res);
}
async function supaSignIn(email, password) {
  const res = await _fetch(`${_SURL}/auth/v1/token?grant_type=password`, {
    method: "POST",
    headers: { apikey: _SKEY, "Content-Type": "application/json" },
    body: JSON.stringify({ email, password })
  });
  return _safeJson(res);
}
async function supaSignOut() {
  if (!_tok) return;
  try { await fetch(`${_SURL}/auth/v1/logout`, { method: "POST", headers: _h() }); } catch(e) {}
  _saveSession(null);
}
async function supaGetUser(token) {
  try {
    if (!_SURL || !_SKEY) return null;
    const res = await fetch(`${_SURL}/auth/v1/user`,
      { headers: { apikey: _SKEY, Authorization: `Bearer ${token}` } });
    if (!res.ok) return null;
    const data = await _safeJson(res);
    return data?.error ? null : data;
  } catch { return null; }
}
async function supaUpdateMeta(updates) {
  try {
    if (!_uid || !_tok) return null;
    const res = await _fetch(`${_SURL}/auth/v1/user`, {
      method: "PUT", headers: _h(),
      body: JSON.stringify({ data: updates })
    });
    return _safeJson(res);
  } catch { return null; }
}
async function supaUpdatePassword(newPassword) {
  try {
    const res = await fetch(`${_SURL}/auth/v1/user`, {
      method: "PUT", headers: _h(),
      body: JSON.stringify({ password: newPassword })
    });
    return res.json();
  } catch { return null; }
}
async function supaUpdateProfile(updates) {
  if (!_uid) return;
  try {
    await fetch(`${_SURL}/rest/v1/profiles?id=eq.${_uid}`, {
      method: "PATCH", headers: _h({ Prefer: "return=minimal" }),
      body: JSON.stringify(updates)
    });
  } catch(e) {}
}

// ══════════════════════════════════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════════════════════════════════

function useBreakpoint() {
  const get = () => window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const handler = () => setBp(get());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return bp;
}
// Convenience helpers
function useIsMobile() { return useBreakpoint() === "mobile"; }
function useIsTablet() { return useBreakpoint() === "tablet"; }

const PILLARS = [
  {
    id: "emotional", name: "Emotional Healing", short: "Emotional", Icon: Heart,
    color: "#C4614B", light: "#FDE8E3", dark: "#8B3C2B",
    tagline: "You don't have to carry it in silence",
    description: "Breaking the habit of silent suffering. Name what you feel, let it be witnessed, and give your heart the safety it may never have been given.",
    prompts: [
      "When someone asked how I was doing today, did I say \"I'm fine\"? What was the honest answer?",
      "What piece of \"family business\" have I been carrying alone that might feel lighter shared with one trusted person?",
      "What feeling was I taught to swallow growing up? What would it sound like to say it out loud?",
      "What loss have I never been given the time or space to fully grieve?",
      "What do I wish someone had told the little girl I used to be about her feelings?"
    ],
    activities: ["EFT Tapping Sequence", "5-4-3-2-1 Grounding", "Emotional Release Writing", "Nervous System Reset Breath", "Mirror Work Affirmations"],
    education: [
      { title: "The Myth of Silent Suffering", body: "Many of us were raised on \"what happens in this house stays in this house.\" That silence once protected our families from a world that used our pain against us. But feelings that are never spoken don't disappear. They settle into the body as tension, sleeplessness, and exhaustion. Breaking the silence isn't about exposing anyone. It's about letting yourself, and the people coming after you, finally feel safe enough to heal." },
      { title: "Trauma Responses Are Not Weaknesses", body: "Your nervous system learned to protect you through fight, flight, freeze, and fawn. Those responses made sense, for you and for the generations before you who survived impossible conditions. Healing doesn't mean fighting them. It means thanking them for their protection, then gently teaching your body that it is safe now to do something different." },
      { title: "Comfort Food, With Love and Awareness", body: "Our food carries history: survival, celebration, and love passed from one generation's hands to the next. You never have to give up the food of your culture. The invitation is simply to notice when you're reaching for a plate because you're hungry, and when you're reaching for it because a feeling needs tending." },
      { title: "Grief Without a Deadline", body: "Generations of our families were expected to bury their loved ones and be back at work on Monday. Many of us learned to keep moving instead of mourning. Grief that gets rushed doesn't go away. It waits. You are allowed to mourn as long and as fully as your healing requires." }
    ]
  },
  {
    id: "physical", name: "Physical Embodiment", short: "Physical", Icon: Leaf,
    color: "#4A6741", light: "#E3EEE2", dark: "#2D4228",
    tagline: "Care for your body before it has to beg",
    description: "Rest without guilt, nourishment without shame, and care before crisis. Your body has carried you, and generations before you. It deserves to be tended, celebrated, and listened to.",
    prompts: [
      "Is there a symptom I've been managing quietly, telling myself I'll deal with it once things calm down?",
      "When did I last rest without feeling guilty? What would that look like for me today?",
      "How did I nourish myself today? Can I look at it with curiosity instead of judgment?",
      "What does my body need from me right now: rest, movement, water, food, stillness, or a checkup?",
      "What is one thing I love about my body, and one way I want to care for how it feels?"
    ],
    activities: ["Intuitive Eating Check-In", "Gentle Body Scan", "Gratitude Body Practice", "Hunger/Fullness Awareness", "Joyful Movement Discovery"],
    education: [
      { title: "Weathering: When Survival Becomes a Health Condition", body: "Researcher Arline Geronimus found that the constant stress of racism and of carrying too much wears on Black bodies faster, raising blood pressure and aging the body before its time. Your exhaustion is not in your head, and it is not a personal failing. It's your body keeping score. Rest, care, and early checkups are how we answer back." },
      { title: "Rest Is Not a Reward", body: "Many of us were taught that rest is something you earn once the work is done. But the work is never done. Rest is not laziness and it is not a luxury. It's maintenance for a body that has been running on survival. You are allowed to rest before exhaustion forces you to." },
      { title: "Holding Both Truths About Your Body", body: "You can love your curves and still care about your blood pressure. Body pride and honest health goals are not in competition. Let go of the scale as the only measure, and look for wins that have nothing to do with a number: more energy, better sleep, climbing the stairs with ease, keeping that appointment." },
      { title: "Seeking Care Is Self-Love", body: "Our mistrust of medicine was earned, from Tuskegee to the women who were never asked for consent. That history is real, and so is your right to be heard today. Bring your questions written down, bring a support person if you'd like, and keep looking until you find a provider who sees your full humanity." }
    ]
  },
  {
    id: "mental", name: "Mental Wellness", short: "Mental", Icon: Brain,
    color: "#7A6BA0", light: "#EDE9F6", dark: "#4A3D70",
    tagline: "Unlearn what hurts. Reclaim what heals.",
    description: "Putting down the Strong Black Woman script, quieting the inner critic, and remembering that mental health is health. Asking for support is not weakness. It's wisdom.",
    prompts: [
      "Where in my life am I being \"the strong one\" right now? Who is being strong for me?",
      "What belief about myself did I inherit? Is it still protecting me, or just following me?",
      "Am I \"just tired,\" or is it something more that I've been calling by a softer name?",
      "What would I do differently if I believed my worth had nothing to do with how much I produce?",
      "What is one boundary I can set this week to protect my peace?"
    ],
    activities: ["Thought Record Practice", "Cognitive Reframing", "Inner Child Letter", "Personalized Affirmations", "Values Clarification"],
    education: [
      { title: "The Strong Black Woman Script", body: "Psychologists call it the Superwoman schema: the belief that you must always be strong, put everyone else first, never show vulnerability, and succeed without complaint. It has carried many of us through hard seasons. It has also been linked to higher stress, depression, and putting off our own care. You can keep your strength and still put down the script." },
      { title: "The Language We Inherit", body: "\"My nerves are bad.\" \"I'm just tired.\" \"She's got a little sugar.\" Our family words carry warmth and history, and they can also soften something that deserves real attention. Try being bilingual: keep the language that's yours, and learn the words that open doors, like \"I think I might be dealing with anxiety, and I'd like to talk to someone.\"" },
      { title: "Mental Health Is Health", body: "Only about one in three Black adults who need mental health care actually receive it. Cost, access, and stigma all play a part. Reaching out is not a betrayal of your faith, your family, or your strength. Directories like Therapy for Black Girls and Inclusive Therapists can help you find someone who understands where you come from." },
      { title: "Your Brain Is Always Healing", body: "Your brain is not fixed. It's a living, changing network. Every time you choose rest over guilt, speak instead of swallowing, or answer your inner critic with compassion, you're building new pathways. The patterns you inherited can change, and they can change with you." }
    ]
  },
  {
    id: "spiritual", name: "Spiritual Connection", short: "Spiritual", Icon: Sparkles,
    color: "#C9963B", light: "#FDF3E0", dark: "#8A6018",
    tagline: "Rooted in those who came before you",
    description: "Faith, ancestry, purpose, and meaning. Honoring the strength you inherited while choosing which patterns end with you. Whatever your spiritual path, this is a space to come home to yourself.",
    prompts: [
      "What strength did I inherit from the women in my family that I want to keep?",
      "What pattern am I choosing to let end with me, and what will I pass down instead?",
      "Where did I feel grace, peace, or a sense of being held this week?",
      "Is there something I've been praying about that also needs action, support, or a conversation? What's one step?",
      "What do I hope the next generation remembers about how I cared for myself?"
    ],
    activities: ["Gratitude Ritual", "Guided Visualization", "Nature Connection Practice", "Intentional Breathing", "Sacred Space Creation"],
    education: [
      { title: "Healing Honors Those Who Came Before", body: "Our grandmothers did the math of their lives correctly with the numbers they were given. They survived so we could have the chance to thrive. Healing is not a rejection of their way. It honors them, by finally turning some of that hard-won strength toward your own wellbeing." },
      { title: "Faith and Help Can Hold Hands", body: "For many of us, church and prayer have been lifelines. Sometimes, though, we hear \"just pray about it\" when what we also need is a doctor, a therapist, or rest. Your faith and professional support are not in competition. Many women find that getting help is one of the ways their prayers get answered." },
      { title: "Your Body as Sacred Ground", body: "In many traditions, the body is understood as a sacred vessel. For Black women, whose bodies, skin, and hair have been judged against standards never made for us, reclaiming your body as holy, every shade and every texture, is a deeply spiritual act." }
    ]
  },
  {
    id: "financial", name: "Financial Wellness", short: "Financial", Icon: Wallet,
    color: "#9B7D2C", light: "#FBF4DC", dark: "#6B5418",
    tagline: "Love your family without losing yourself",
    description: "Healing your money story, setting loving boundaries around being everyone's provider, and building security for yourself without guilt.",
    prompts: [
      "What did I learn about money growing up, spoken and unspoken? Who taught me?",
      "Where am I being the family bank right now, and what is it costing me?",
      "What honest money conversation have I been avoiding? What would it take to have it?",
      "Where does financial stress show up in my body, my sleep, or my relationships?",
      "What is one small step toward my own security I can take this week, without guilt?"
    ],
    activities: ["Money Story Excavation", "Abundance Affirmation Practice", "Financial Values Mapping", "Scarcity vs Abundance Journal", "Gentle Goal Setting"],
    education: [
      { title: "The Weight of Provision", body: "Many Black women are the first call when someone needs rent, a bill paid, or a loan until Friday. Because the wealth gap is real, our families have long depended on one another to get by. Generosity is beautiful. But provision without limits is not sustainable love. It's exhaustion wearing a different name." },
      { title: "Money Is Always Emotional", body: "Our relationship with money is rarely just about numbers. It's tied up with safety, worthiness, family loyalty, and survival. Healing financial patterns means following the thread back to the feelings that live underneath the bills and the budget." },
      { title: "Scarcity Lives in the Nervous System", body: "Growing up without enough, whether money, food, or security, can leave a scarcity alarm in the body. It can show up as overspending, hoarding, or constant worry, even after things improve. Healing takes practical tools and gentle patience with a body that learned to brace for lack." }
    ]
  }
];

// ══════════════════════════════════════════════════════════════════
//  ACTIVITY GUIDES
// ══════════════════════════════════════════════════════════════════

const ACTIVITY_GUIDES = {
  "EFT Tapping Sequence": {
    duration: "10–15 minutes", Icon: Heart,
    intro: "EFT (Emotional Freedom Technique) combines gentle tapping on acupressure points with affirming words. It helps release emotional tension stored in the body. Find a quiet space and move through each step at your own pace.",
    steps: [
      { title: "Set Up: The Karate Chop Point", body: "Tap the outer edge of your hand continuously while repeating three times: 'Even though I am carrying this feeling right now, I deeply and completely love and accept myself.'" },
      { title: "The Eyebrow Point", body: "Tap gently at the inner edge of your eyebrow, just above the nose. Say: 'This feeling I am holding.'" },
      { title: "Side of Eye", body: "Tap on the bone at the outer corner of your eye. Say: 'I acknowledge this feeling in my body.'" },
      { title: "Under the Eye", body: "Tap on the bone just below the center of your eye. Say: 'I allow myself to feel this without judgment.'" },
      { title: "Under the Nose", body: "Tap the small area between your nose and upper lip. Say: 'I am safe right now in this moment.'" },
      { title: "Chin Point", body: "Tap the crease between your lower lip and chin. Say: 'My body is wise and is trying to protect me.'" },
      { title: "Collarbone Point", body: "Tap just below your collarbone on either side. Say: 'I am releasing what no longer serves me.'" },
      { title: "Under the Arm", body: "Tap about four inches below the armpit. Say: 'I am open to feeling peace.'" },
      { title: "Top of the Head", body: "Tap the crown of your head with all fingertips. Say: 'I am healing. I am worthy. I am enough.' Take three slow, deep breaths and check in with how you feel." }
    ],
    closing: "Notice any shifts in your body, even subtle ones. You may feel lighter, calmer, or simply more present. That is the medicine. You can repeat this sequence as many times as feels right."
  },
  "5-4-3-2-1 Grounding": {
    duration: "5 minutes", Icon: Leaf,
    intro: "This is a powerful sensory grounding technique that brings you back into the present moment when you feel anxious, dissociated, or overwhelmed. It works by engaging all five senses.",
    steps: [
      { title: "5 Things You Can SEE", body: "Look around and name 5 things you can see right now. Be specific: not just 'a chair' but 'a wooden chair with a green cushion.' Take your time with each one." },
      { title: "4 Things You Can TOUCH", body: "Notice 4 things you can physically feel. The texture of your clothing, the temperature of the air, the surface beneath you. Actually touch each one and describe the sensation." },
      { title: "3 Things You Can HEAR", body: "Close your eyes if comfortable and listen for 3 distinct sounds. They can be near or far: traffic, birds, your own breathing. Just notice without judgment." },
      { title: "2 Things You Can SMELL", body: "Notice 2 things you can smell right now. If nothing is obvious, breathe deeply and notice the quality of the air: cool, warm, fresh, familiar." },
      { title: "1 Thing You Can TASTE", body: "Bring awareness to the taste in your mouth right now. Take a sip of water if you have some nearby and really notice the sensation." },
      { title: "Complete Your Grounding", body: "Take three slow breaths. As you inhale, say silently: 'I am here.' As you exhale: 'I am safe.' You have just returned to the present moment." }
    ],
    closing: "Grounding is available to you any time, anywhere. The more you practice, the faster your nervous system learns to settle. You are always able to come back to yourself."
  },
  "Emotional Release Writing": {
    duration: "15–20 minutes", icon: "✍️",
    intro: "This is an unfiltered, private writing practice designed to help release emotions stored in the body. There is no right or wrong: no grammar, no editing, no performing. This is for you and you alone.",
    steps: [
      { title: "Prepare Your Space", body: "Find somewhere you feel safe and will not be interrupted. Have your journal or paper ready. Set a timer for 15 minutes if that helps you commit to the practice." },
      { title: "Start With What Is Present", body: "Begin writing whatever is most alive in you right now. It might be anger, grief, numbness, or anxiety. Do not explain or analyze it. Just write what it feels like from the inside." },
      { title: "Write Without Stopping", body: "Keep your pen moving the entire time. If you get stuck, write 'I do not know what to write' until something else comes. Do not read back. Do not edit. Do not perform." },
      { title: "Go Deeper", body: "When you hit something that feels heavy or true, slow down and stay with it. Ask yourself on paper: What does this feeling need? What has it been trying to say?" },
      { title: "Close With Compassion", body: "In the last two minutes, write a few sentences to yourself as if you were a loving, wise friend. What would you say to someone carrying what you are carrying right now?" }
    ],
    closing: "You do not have to keep what you wrote. Some people find it healing to shred or delete their emotional release writing afterward as a symbolic act of release. Honor whatever feels right for you."
  },
  "Nervous System Reset Breath": {
    duration: "8 minutes", icon: "💨",
    intro: "This breathwork sequence activates your parasympathetic nervous system, your rest and digest state. When we have experienced trauma or chronic stress, our nervous system gets stuck in high alert. This practice gently signals safety to your body.",
    steps: [
      { title: "Find Your Position", body: "Sit comfortably with your spine gently upright, or lie down if that feels safer. Place one hand on your chest and one on your belly. Close your eyes or soften your gaze downward." },
      { title: "Belly Breathing (2 minutes)", body: "Breathe in slowly through your nose for 4 counts, letting your belly rise first. Exhale slowly through your mouth for 6 counts. The longer exhale activates the calming response." },
      { title: "Box Breathing (3 minutes)", body: "Inhale for 4 counts. Hold gently for 4 counts. Exhale for 4 counts. Hold gently for 4 counts. Repeat this box pattern. This regulates the nervous system and brings you into balance." },
      { title: "Sighing Breath (2 minutes)", body: "Take a deep inhale through the nose. At the top, take one more small sip of air. Then release in a long, audible sigh through the mouth. Repeat 4–6 times. This is one of the fastest ways to reduce stress hormones." },
      { title: "Return and Rest", body: "Let your breath return to its natural rhythm. Simply observe it moving in and out without controlling it. Notice any sense of settling or softening in your body." }
    ],
    closing: "Your nervous system just received a powerful signal of safety. Even 2–3 minutes of this practice in moments of overwhelm can create a meaningful shift."
  },
  "Mirror Work Affirmations": {
    duration: "10 minutes", icon: "🪞",
    intro: "Mirror work is one of the most profound healing practices available. Standing before your own reflection and speaking kindly to yourself is deeply counter-cultural for most of us. That is exactly why it works.",
    steps: [
      { title: "Stand Before the Mirror", body: "Stand or sit in front of a mirror. Take three deep breaths. Soften your expression. You are looking at someone who has survived everything that has ever tried to break her." },
      { title: "Make Eye Contact", body: "Look into your own eyes, not at your body, not at your face. Just your eyes. Those eyes have held so much. Let yourself be seen by yourself." },
      { title: "Speak Your Name With Love", body: "Say your own name aloud, followed by: 'I love you. I truly, deeply love you.' Notice whatever arises: resistance, tears, disbelief, or unexpected warmth. All of it is welcome." },
      { title: "Speak Your Affirmations", body: "Say each of these slowly, making eye contact: 'My body is worthy of love exactly as it is right now.' 'I am healing, and healing is not linear.' 'I am enough. I have always been enough.'" },
      { title: "Close With Gratitude", body: "Place your hand over your heart and say: 'Thank you for carrying me. Thank you for being here. I am going to take care of you.' Take three slow breaths and gently close the practice." }
    ],
    closing: "Mirror work gets easier and more powerful with repetition. Even 3 minutes a day creates profound shifts over time. The goal is not to feel immediate love. It is to keep showing up until the love becomes real."
  },
  "Intuitive Eating Check-In": {
    duration: "5–10 minutes", Icon: Leaf,
    intro: "This practice helps you reconnect with your body's innate hunger and fullness signals, the wisdom that diet culture has trained many of us to override and distrust. There is no right or wrong answer here. Only honest noticing.",
    steps: [
      { title: "Pause Before Eating", body: "Before your next meal or snack, stop for one full minute. Put down your phone, step away from distractions. Place your hand on your belly and simply check in." },
      { title: "Assess Your Hunger", body: "On a scale of 1–10, where 1 is uncomfortably hungry and 10 is uncomfortably full, where are you right now? Notice if this is physical hunger (gradual, in the stomach) or emotional hunger (sudden, in the mind)." },
      { title: "Ask What Your Body Wants", body: "Without judgment, ask: What does my body actually want right now? Not what it should want, but what does it genuinely want? Notice if that is different from what you were going to eat." },
      { title: "Eat With Presence", body: "Take three mindful bites before doing anything else. Notice the taste, texture, temperature, and satisfaction of the food. You do not have to eat the whole meal this way. Just begin with presence." },
      { title: "Check In Mid-Meal", body: "Halfway through, pause and check your scale again. Where are you now? What does your body need for the rest of this meal: more, less, something different?" },
      { title: "Reflect Afterward", body: "Briefly ask: How hungry was I before? How satisfied am I now? Did I enjoy this? No judgment, only curiosity and data." }
    ],
    closing: "Intuitive eating is a practice of returning home to yourself. Every meal is an opportunity to rebuild trust with your body. There is no failing here, only learning."
  },
  "Gentle Body Scan": {
    duration: "15 minutes", icon: "✨",
    intro: "The body scan helps you reconnect with your physical self with gentleness and curiosity. For those who have experienced body shame or trauma, this practice is particularly healing. We are learning to inhabit our bodies as safe, sacred homes.",
    steps: [
      { title: "Settle In", body: "Lie down or sit comfortably. Close your eyes or soften your gaze. Take three deep breaths. Set an intention: 'I am here to listen to my body with kindness.'" },
      { title: "Begin at Your Feet", body: "Bring your awareness to the soles of your feet. Notice any sensations: warmth, coolness, tingling, or nothing at all. Simply observe without needing to change anything." },
      { title: "Move Upward Slowly", body: "Gradually move your awareness up through your ankles, calves, knees, and thighs. Spend several breaths in each area. Say silently: 'I acknowledge you. Thank you.'" },
      { title: "The Belly and Chest", body: "Bring your attention to your abdomen and chest. Notice the rise and fall of your breath here. If you notice tightness, breathe into it gently without forcing it to release." },
      { title: "Shoulders, Arms, and Hands", body: "Notice where you are holding tension in your shoulders. Let them soften, just a little. Travel down your arms to your hands, noticing the life pulsing in your fingertips." },
      { title: "Face and Head", body: "Notice your jaw. Is it clenched? Let it soften. Your forehead, your eyes. Feel the crown of your head open toward the sky." },
      { title: "Whole Body Awareness", body: "Expand your awareness to your entire body. Say: 'This body has carried me through everything. I honor it. I am grateful for it. It is mine and it is worthy.'" }
    ],
    closing: "Return slowly, wiggling your fingers and toes. Take your time coming back. The body scan can be done in 5 minutes or 30. Any length is healing."
  },
  "Gratitude Body Practice": {
    duration: "10 minutes", icon: "💛",
    intro: "In a culture that teaches us to see our bodies as problems to fix, gratitude for our bodies is a radical act. This practice honestly witnesses what our bodies actually do for us, regardless of how they look.",
    steps: [
      { title: "Find Stillness", body: "Sit comfortably and close your eyes. Take three grounding breaths. Release any agenda about how your body should look or feel today." },
      { title: "Thank Your Heart", body: "Place your hand over your heart. Feel it beating. Your heart has beaten approximately 100,000 times today without you asking it to. Say: 'Thank you for never stopping. Thank you for keeping me alive.'" },
      { title: "Thank Your Lungs", body: "Take a slow, deep breath. Your lungs have taken thousands of breaths today. Say: 'Thank you for breathing for me even when I forgot to breathe.'" },
      { title: "Thank Your Body's Strength", body: "Think of one physical thing your body did today. Honor that. Your body does this work every single day, and it rises every time." },
      { title: "Thank Your Body's Wisdom", body: "Your body knows when you are unsafe. It knows when you need rest. Say: 'Thank you for trying to protect me, even when I did not understand.'" },
      { title: "Write Your Gratitude", body: "Write 5 things your body did for you today that had nothing to do with how it looked. This is your evidence against the lie that your body is a problem." }
    ],
    closing: "Your body is not your enemy. It never was. It is the only home you will ever truly live in. Today you chose to honor it."
  },
  "Hunger/Fullness Awareness": {
    duration: "Ongoing daily practice", icon: "🌱",
    intro: "Many of us have lost touch with our body's natural hunger signals through years of dieting or emotional eating. This is a gentle, judgment-free practice of rebuilding that connection.",
    steps: [
      { title: "Learn the Scale", body: "1–2: Ravenously hungry, headache. 3–4: Hungry, ready to eat. 5–6: Neutral to slightly satisfied. 7–8: Comfortably full. 9–10: Overfull, uncomfortable. The sweet spot for eating is 3–4. The sweet spot for stopping is 7–8." },
      { title: "Check In Before Every Meal", body: "Before each eating occasion today, pause for 30 seconds. Close your eyes and ask: Where am I on the scale right now? Do not judge the number. Just notice it." },
      { title: "Identify Physical vs Emotional Hunger", body: "Physical hunger builds gradually. Emotional hunger comes on suddenly and craves specific foods. Neither is wrong. They both deserve compassion and a response." },
      { title: "Check In Mid-Meal", body: "Practice pausing halfway through eating and checking the scale again. You do not have to stop eating. Just notice. This pause begins to rebuild your awareness." },
      { title: "Reflect Without Judgment", body: "At the end of the day, reflect: Did I honor my hunger today? What did I learn about my body's signals today?" }
    ],
    closing: "This is a practice, not a test. There is no perfect score. Every moment of awareness is progress. Your body's wisdom is still there, waiting to be trusted again."
  },
  "Joyful Movement Discovery": {
    duration: "20–30 minutes", Icon: Leaf,
    intro: "Joyful movement is about rediscovering physical activity as pleasure and self-care, not punishment or calorie burning. This practice helps you find what actually feels good in your body right now.",
    steps: [
      { title: "Release the Exercise Mindset", body: "Before you begin, take three breaths and set this intention: 'I am moving my body today because it feels good, not to change it.' Release any goals around burning calories or losing weight." },
      { title: "Ask Your Body What It Wants", body: "Close your eyes and check in. Does your body want to stretch? Walk? Dance? Something gentle? Something energizing? There is no right answer, only your body's honest answer today." },
      { title: "Try Something Joyful", body: "Choose one: Put on a song you love and dance freely in your home. Take a slow, unstructured walk. Gentle stretching, moving only as far as feels good. Any movement that has ever felt like play." },
      { title: "Check In While Moving", body: "As you move, notice: Is this enjoyable? Am I pushing past what feels good out of habit? If it stops feeling good, you are allowed to stop. Full stop." },
      { title: "Reflect Afterward", body: "Ask yourself: How did that feel in my body? Was there any moment of genuine enjoyment? What movement have I not tried yet that might feel good?" }
    ],
    closing: "You are allowed to stop any time movement stops feeling good. You are allowed to rest. Your body deserves to experience movement as joy."
  },
  "Thought Record Practice": {
    duration: "15 minutes", icon: "✨",
    intro: "A thought record is a structured way to examine the thoughts that create distress and gently test whether they are actually true. This is a foundational CBT tool, adapted with trauma-informed compassion.",
    steps: [
      { title: "Identify the Triggering Situation", body: "Write down the situation that triggered difficult feelings. Be specific: What happened? When? Who was involved? Just the facts, not the interpretation yet." },
      { title: "Name the Emotions", body: "What did you feel in that situation? Name each emotion specifically: not just 'bad' but hurt, ashamed, angry, scared, alone. Rate the intensity of each from 0–100%." },
      { title: "Capture the Automatic Thought", body: "What thought went through your mind automatically? Write it exactly as it appeared. Often it sounds like: 'I always mess up.' 'Nobody really cares.' 'I am not enough.'" },
      { title: "Examine the Evidence", body: "Ask: What evidence supports this thought? What evidence contradicts it? Our negative thoughts often ignore significant contradicting evidence." },
      { title: "Find an Alternative Thought", body: "What is a more balanced, compassionate way to see this situation? What would you say to a beloved friend who had this thought about herself?" },
      { title: "Re-rate Your Emotions", body: "Now that you have examined the thought, re-rate the emotional intensity from 0–100%. Even a 10–20% reduction is meaningful." }
    ],
    closing: "Your thoughts are not facts. They are interpretations shaped by your history and your nervous system. With practice, you can learn to hold them more lightly."
  },
  "Cognitive Reframing": {
    duration: "10 minutes", Icon: Sparkles,
    intro: "Cognitive reframing is the practice of intentionally shifting perspective on a belief. It does not mean pretending things are fine. It means finding a truer, more complete way of seeing.",
    steps: [
      { title: "Identify the Limiting Frame", body: "Write down a belief about yourself that causes pain. Examples: 'I have no willpower.' 'I am lazy.' 'I always self-sabotage.' 'My body is a problem.'" },
      { title: "Examine the Origin", body: "Where did you first learn this story? Who told you this, directly or indirectly? Is this your truth, or was it projected onto you by someone else's fear or ignorance?" },
      { title: "Find the Compassionate Reframe", body: "For each limiting belief, ask: What would a trauma-informed perspective say? Example: 'I have no willpower' becomes 'I have been fighting exhaustion and pain without adequate support.'" },
      { title: "Find the Hidden Strength", body: "Our perceived failures often contain hidden strengths. 'I cannot stick to diets' becomes 'My body and mind are protecting me from restriction. That is wisdom.' What strength is hidden in your perceived weakness?" },
      { title: "Write Your New Frame", body: "Write the reframed belief in your own words, words that feel true, not just comforting. Read it aloud. Notice how it feels in your body." }
    ],
    closing: "Reframing is not about erasing your experiences. It is about refusing to let the harshest interpretation be the only one. You deserve a story that tells the whole truth, including your strength."
  },
  "Inner Child Letter": {
    duration: "20–30 minutes", Icon: Heart,
    intro: "Inner child work is one of the most powerful forms of healing available. This letter is an act of reparenting: giving yourself what you needed and did not receive.",
    steps: [
      { title: "Create Safety First", body: "Find a quiet, private space. Have tissues nearby. This practice often brings up deep emotion, and that is welcome. Take five slow breaths and set an intention to be gentle with yourself." },
      { title: "Connect With Your Younger Self", body: "Close your eyes and think of yourself at a difficult age, perhaps 6, 8, or 13. What were you going through? What did you most need to hear? What did you believe about yourself that was not true?" },
      { title: "Begin the Letter", body: "Write: 'Dear [your name at that age]...' Write to her as the loving adult you are now. Tell her you see her. Tell her what was happening was not her fault. Tell her she was not too much, not too little, not broken." },
      { title: "Tell Her What She Needed to Hear", body: "What did that little girl most need someone to say? Write it. Maybe: 'You are so loved.' 'Your body is beautiful and worthy.' 'You are going to be okay. I promise.'" },
      { title: "Make Her a Promise", body: "Close the letter with a promise from your adult self. What will you do differently now, for her? How will you protect and honor the little girl who is still living inside you?" }
    ],
    closing: "Keep this letter somewhere safe. Return to it when your inner critic becomes loud. It is often that same wounded child speaking. She needed love then. She still does. And you are now the one who can give it."
  },
  "Personalized Affirmations": {
    duration: "10 minutes", icon: "💛",
    intro: "Generic affirmations often do not land because they feel untrue. This practice helps you create personalized affirmations that bridge the gap between where you are and where you are healing toward.",
    steps: [
      { title: "Identify Your Core Wound", body: "What is the deepest thing you struggle to believe about yourself? Write it as your inner critic would say it. Examples: 'I am unlovable.' 'I am too much.' 'My body is not acceptable.'" },
      { title: "Find the Bridge Statement", body: "A direct opposite affirmation often feels false. Find a bridge instead. Not 'I love my body' if that feels untrue, but: 'I am learning to appreciate what my body does for me.' 'I am open to my body deserving care.'" },
      { title: "Create Your Three Affirmations", body: "Write three personalized bridge affirmations. They should feel like a stretch: hopeful but reachable. Not a lie, but an opening. Write in first person, present tense." },
      { title: "Anchor Them in the Body", body: "Say each affirmation aloud while placing your hand on your heart. Notice the physical sensation. Even if your mind resists, your body can receive the message. Repeat each one three times slowly." },
      { title: "Commit to a Practice", body: "Write your three affirmations on paper or your phone. Read them every morning for 21 days. It takes approximately this long to begin rewiring neural pathways." }
    ],
    closing: "Affirmations are seeds, not instant harvests. Plant them daily with patience. The transformation is happening even when you cannot yet see it."
  },
  "Values Clarification": {
    duration: "20 minutes", icon: "✨",
    intro: "When we know what we truly value, we have a compass that guides us toward a meaningful life. This practice connects you to your deepest values.",
    steps: [
      { title: "Brainstorm Your Values", body: "Write down every value that resonates with you without filtering. Examples: healing, freedom, authenticity, connection, creativity, rest, joy, courage, integrity, beauty, service, peace, growth." },
      { title: "Narrow to Your Top Ten", body: "Circle the ten that feel most essential, the ones where if absent from your life, you would feel deeply unfulfilled. Choose what actually lights something up in you, not what you think you should value." },
      { title: "Narrow to Your Top Five", body: "From your ten, identify your five core values. These are the non-negotiables. The things you cannot live without honoring." },
      { title: "Assess Your Current Life", body: "For each of your top five values, rate how much your current life honors that value from 1–10. The gaps between what you value and how you are living are the places calling for change." },
      { title: "Identify One Aligned Action", body: "Choose the value with the lowest score. Write one specific, small action you could take this week to live more in alignment with it." }
    ],
    closing: "Your values are your north star. When life feels confusing or painful, returning to this list helps you ask: Is this decision aligned with who I truly am?"
  },
  "Gratitude Ritual": {
    duration: "10 minutes", Icon: Sparkles,
    intro: "Gratitude practice is not about bypassing pain. It is about training your awareness to also hold what is present, beautiful, and sustaining, even in difficulty. This ritual creates a sacred container for that practice.",
    steps: [
      { title: "Create a Sacred Space", body: "Light a candle if you have one, or simply create a moment of stillness. This is a ritual, not just a list. Approach it with that intention." },
      { title: "Begin With Your Body", body: "Take three breaths and place your hand on your heart. Say: 'I am grateful to be alive in this body, in this moment.' Even on the hardest days, this is true." },
      { title: "Write Five Specific Gratitudes", body: "Write five things you are genuinely grateful for today. Be specific: not 'my family' but 'the way my [person] laughed today.' Specificity activates the feeling, not just the thought." },
      { title: "Include One Hard Thing", body: "Write one difficult thing from your life and find something in it, even something small, that you can honestly be grateful for. This expands your gratitude beyond only the easy things." },
      { title: "Close With a Blessing", body: "Write a short blessing for yourself. Something like: 'May I move through this day with softness. May I remember my own worth. May I receive what I need.'" }
    ],
    closing: "Regular gratitude practice rewires the brain toward greater well-being, not because it ignores pain, but because it teaches the mind to hold more than pain at once."
  },
  "Guided Visualization": {
    duration: "15–20 minutes", icon: "🌟",
    intro: "Visualization is a powerful spiritual and psychological tool. The brain cannot fully distinguish between a vividly imagined experience and a real one, which means we can use visualization to rehearse healing before it fully arrives.",
    steps: [
      { title: "Settle Into Stillness", body: "Find a comfortable position and close your eyes. Take five slow, deep breaths. Tell your body it is safe to relax right now." },
      { title: "Arrive at Your Sacred Place", body: "Imagine a place, real or imagined, where you feel completely safe and whole. It might be a beach, a forest, a cozy room. Spend several breaths arriving here fully." },
      { title: "Inhabit It With All Your Senses", body: "What do you see around you? What sounds are present? What does the air smell like? The more sensory detail, the more powerful the visualization." },
      { title: "Meet Your Healed Self", body: "Imagine your fully healed, whole self walking toward you. She has done the work. She is at peace with her body, her past, her worth. She is radiant and grounded. What does she look like?" },
      { title: "Receive Her Message", body: "Your healed self has a message for you. Open yourself to receive it without filtering or judging. What does she want you to know right now?" },
      { title: "Return and Integrate", body: "Bring your awareness gently back to the room. Wiggle your fingers and toes. Journal immediately about what arose." }
    ],
    closing: "You just visited the truth of who you are becoming. That version of you is not fantasy. She is the direction your healing is moving."
  },
  "Nature Connection Practice": {
    duration: "20–30 minutes outdoors", Icon: Leaf,
    intro: "Humans evolved in intimate relationship with the natural world. We are not separate from nature. We are nature. Time in genuine connection with the living world has measurable effects on nervous system regulation and spiritual well-being.",
    steps: [
      { title: "Step Outside Intentionally", body: "Go outside: your yard, a park, anywhere with sky and living things. Leave your phone inside or on silent. This is not a walk with earbuds. This is a walk with the world." },
      { title: "Slow Down Radically", body: "Walk at half your normal speed. This is not exercise. It is communion. Notice what is alive around you that you normally move past too quickly to see." },
      { title: "Use Your Senses Fully", body: "What can you hear? What can you smell? What textures surround you? Find something beautiful and stay with it for a full minute without moving on." },
      { title: "Find Something That Mirrors You", body: "Find something in nature that mirrors something in your life right now. A tree that bends but does not break. A flower growing through a crack. What does it have to say to you?" },
      { title: "Offer Something", body: "Before you return inside, offer something to the natural world. Your gratitude. Your breath. A quiet prayer. Something that acknowledges this as a reciprocal relationship." }
    ],
    closing: "You belong to this earth. Not despite your body, but with your body, exactly as it is. The trees do not ask you to be different. Neither does the sky."
  },
  "Intentional Breathing": {
    duration: "10 minutes", Icon: Sparkles,
    intro: "The breath is the only physiological function that is both automatic and consciously controllable. This makes it a powerful bridge between body and spirit. This practice uses breath as a spiritual tool.",
    steps: [
      { title: "Set a Sacred Intention", body: "Before you begin, set an intention for this practice. What do you want to invite in? Peace? Clarity? Release? Courage? Write it down or hold it in your mind." },
      { title: "The Breath of Release (3 minutes)", body: "Inhale deeply through the nose for 4 counts. Exhale slowly through the mouth for 8 counts, as if breathing out everything that no longer serves you. Repeat for 3 minutes." },
      { title: "The Breath of Receiving (3 minutes)", body: "Inhale for 6 counts, breathing in your intention. Pause for 2 counts at the top. Exhale gently for 4 counts. You are receiving with every breath in." },
      { title: "The Breath of Gratitude (2 minutes)", body: "Return to natural breathing and simply witness it. Marvel at the fact that your body does this without you asking, keeping you alive every moment." },
      { title: "Close With Intention", body: "Take one final deep breath, holding your intention at the top. Release it in a long, slow exhale. Say: 'I am connected. I am held. I am here.'" }
    ],
    closing: "You can return to this practice in as little as 60 seconds anywhere. Three conscious breaths in a difficult moment is a spiritual act."
  },
  "Sacred Space Creation": {
    duration: "30–60 minutes", icon: "🌟",
    intro: "A sacred space is a physical area dedicated to your healing and spiritual practice. Creating one declares that your inner life matters, and that your healing deserves a home.",
    steps: [
      { title: "Choose Your Space", body: "Find a corner, a shelf, a small table, or even a windowsill. It does not need to be large. It needs to be yours: a place where you can be intentional." },
      { title: "Clear and Clean It", body: "Clean the space physically. As you do, set the intention that you are also clearing the energy, making room for something sacred that serves your healing." },
      { title: "Choose Your Elements", body: "Add elements that feel meaningful. A candle for light. A plant for life. A crystal or stone from nature. Something that represents each of your five pillars." },
      { title: "Add Words of Power", body: "Include your affirmations, a meaningful quote, or a page from your journal. Words that anchor you to your truth. Your healing intentions made visible." },
      { title: "Activate Your Space", body: "Sit before your space and say aloud: 'This is my healing space. Here I return to myself. Here I am honest. Here I remember who I am and who I am becoming.'" },
      { title: "Use It Consistently", body: "Visit your sacred space daily, even for just two minutes. Light your candle, breathe, check in. The power of a sacred space is built through returning to it consistently." }
    ],
    closing: "You deserve a place dedicated to your growth and wholeness. You created it. Now let it support you."
  },
  "Money Story Excavation": {
    duration: "20–30 minutes", icon: "💛",
    intro: "Your money story is the collection of beliefs and feelings around money that you absorbed in childhood. Most of us have never consciously examined this story. This practice is about excavating it with compassion, not judgment.",
    steps: [
      { title: "Childhood Money Memories", body: "Write freely about your earliest memories of money. Was there enough? Was there scarcity? How did the adults around you talk about money? What emotions were attached to money in your home?" },
      { title: "The Messages You Received", body: "Write every message you received about money growing up. Things like: 'We can not afford that.' 'Money is the root of all evil.' 'Rich people are greedy.' 'Work hard and you will be fine.'" },
      { title: "How Money Feels in Your Body", body: "Close your eyes and think about money right now. Where do you feel this in your body? Chest tightness? Stomach dropping? What emotion lives there?" },
      { title: "Your Current Money Behaviors", body: "Honestly examine: Do you avoid looking at your accounts? Spend impulsively when stressed? Feel guilty spending on yourself? Undersell your services? These behaviors have roots. This is not a character flaw." },
      { title: "The Story You Are Ready to Release", body: "Write: 'The money story I am ready to begin releasing is...' Then write: 'A truer story I want to move toward is...' You do not have to have arrived there yet. Just name the direction." }
    ],
    closing: "Your money story was written by circumstances beyond your control. You are now wise enough and worthy enough to begin writing a new chapter."
  },
  "Abundance Affirmation Practice": {
    duration: "10 minutes", icon: "✨",
    intro: "Scarcity and abundance are not just financial realities. They are mindsets and deeply conditioned ways of seeing the world. This practice begins to shift the internal experience of scarcity toward openness to receiving.",
    steps: [
      { title: "Acknowledge the Scarcity First", body: "Before affirming abundance, acknowledge scarcity honestly. Write: 'The ways I currently experience scarcity are...' Let this be real. Pretending scarcity does not exist bypasses the actual healing." },
      { title: "Find Evidence of Abundance Already Present", body: "Write five ways abundance already exists in your life. Abundance of breath. Abundance of creativity. Abundance of love. The capacity for abundance already lives in you." },
      { title: "Speak Your Abundance Affirmations", body: "Say each of these aloud with your hand on your heart: 'I am open to receiving abundance in all forms.' 'I am worthy of financial security and ease.' 'I release the belief that I must struggle to deserve.'" },
      { title: "Write Your Personal Abundance Statement", body: "Write a personalized statement of abundance in your own words, one that feels true and expansive. What does abundance mean to you specifically?" },
      { title: "Take One Abundant Action", body: "Take one small action today that reflects abundance rather than scarcity. Pay for something you have been denying yourself. Invest in a tool for your business. Say your rate without apologizing." }
    ],
    closing: "Abundance is not the absence of difficulty. It is the presence of enough: enough safety, enough worth, enough trust that more can come."
  },
  "Financial Values Mapping": {
    duration: "25 minutes", icon: "🌟",
    intro: "When our financial decisions are disconnected from our deepest values, money becomes a source of shame and confusion. This practice aligns your financial life with what actually matters to you.",
    steps: [
      { title: "List What You Spend Money On", body: "Without judgment, write down the main categories where your money currently goes. Be honest and specific. This is data, not a report card." },
      { title: "Identify Your True Values", body: "List your top 5 core values. Examples: healing, creativity, freedom, family, security, joy, service, beauty, growth, adventure." },
      { title: "Map Values to Spending", body: "For each spending category, ask: Does this align with one of my core values? Mark the ones that do. Circle the ones that do not align, not to shame yourself, but to notice." },
      { title: "Find the Gaps", body: "Are there things you deeply value that you spend almost nothing on? Rest, creativity, healing, joy? This gap is your roadmap." },
      { title: "Create One Values-Aligned Intention", body: "Write one specific financial intention that aligns your spending more closely with your values. Not a restrictive rule, but an act of self-love." }
    ],
    closing: "When you spend in alignment with your values, you stop feeling guilty about money. Your financial life becomes an expression of who you are and what you love."
  },
  "Scarcity vs Abundance Journal": {
    duration: "15 minutes", icon: "💛",
    intro: "This journaling practice helps you become conscious of the scarcity and abundance thinking that shapes your daily experience, and begin to consciously choose which lens you want to see through.",
    steps: [
      { title: "Track Your Scarcity Thoughts Today", body: "Write down every scarcity thought you can remember having today. 'I can not afford that.' 'There is never enough time.' Just notice them, with no judgment." },
      { title: "Examine One Scarcity Thought Deeply", body: "Choose one scarcity thought and examine it. Where does it come from? How old is this thought? Is it actually true right now, or is it a memory from a time when resources were genuinely scarce?" },
      { title: "Find the Abundant Counter", body: "For each scarcity thought, write an abundant alternative: not a lie, but a fuller truth. 'There is never enough time' becomes 'I am learning to prioritize what matters most.'" },
      { title: "Gratitude for Financial Safety", body: "Write five ways your financial situation provides safety right now, even if imperfect. A roof. Food. A phone. Your own skills. Safety is a form of abundance." },
      { title: "Set an Abundance Intention", body: "Write one abundance intention for tomorrow. How will you consciously choose to see through an abundance lens at least once?" }
    ],
    closing: "Scarcity thinking is often a trauma response. Your nervous system learned to scan for what is lacking to keep you safe. You are now teaching it to also see what is here."
  },
  "Gentle Goal Setting": {
    duration: "20 minutes", icon: "🌱",
    intro: "Traditional goal setting is often rooted in shame and the belief that you are not enough as you are. Gentle goal setting begins from wholeness, not lack. You are not broken. You are growing.",
    steps: [
      { title: "Begin With Appreciation", body: "Before setting any goals, write five things you have already accomplished that you have not fully honored yourself for. You have come further than you give yourself credit for." },
      { title: "Connect Goals to Values", body: "Write goals that emerge from your values rather than from fear or external pressure. Ask: If I fully believed I was worthy right now, what would I want to create or experience?" },
      { title: "Set Gentle, Directional Goals", body: "Write 2–3 financial goals that feel expansive and hopeful, not punishing. Frame them positively: 'I am building toward financial security.' 'I am growing my income through my gifts.'" },
      { title: "Identify the Smallest Possible Step", body: "For each goal, write the absolutely smallest step you could take this week. Not the whole staircase, just the first step. Make it so small it would be hard to fail." },
      { title: "Add Self-Compassion Clauses", body: "Write next to each goal: 'And if I fall short, I will be kind to myself and keep going.' This clause is the difference between sustainable growth and eventual collapse." }
    ],
    closing: "You are allowed to want things. You are allowed to work toward them imperfectly. You are allowed to rest, fall short, and continue. That is not failure. That is being human."
  }
};


// Wellness disclaimer shown across the app
const DISCLAIMER_FULL = "ThriveRoot offers wellness education and support. It is not medical, mental health, or nutrition advice, and it is not a substitute for care from a doctor, therapist, or other licensed professional. Always talk with a qualified provider about health concerns, and never delay or ignore professional advice because of something you read or hear here.";
function WellnessDisclaimer({ variant = "footer", dark = false }) {
  const muted = dark ? "rgba(251,245,236,0.45)" : "#8A7B6E";
  const link = dark ? "rgba(251,245,236,0.7)" : "#8A6018";
  if (variant === "footer") {
    return (
      <p style={{ fontSize:12, color: muted, textAlign:"center", lineHeight:1.7, margin:"32px auto 8px", maxWidth:640, padding:"0 16px" }}>
        ThriveRoot offers wellness support, not medical or mental health advice. In crisis? Call or text <strong>988</strong>, or call 911.{" "}
        <a href="/glowandflow-privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: link }}>Privacy Policy</a>
      </p>
    );
  }
  if (variant === "guide") {
    return (
      <p style={{ fontSize:11.5, color: muted, lineHeight:1.6, margin:"8px 2px 0" }}>
        Your guide offers reflection and encouragement, not medical or mental health advice, and it can make mistakes. If you are in crisis, call or text 988.
      </p>
    );
  }
  // "full"
  return (
    <p style={{ fontSize:13, color: muted, lineHeight:1.75, margin:0 }}>
      {DISCLAIMER_FULL} If you are in crisis or thinking about harming yourself, call or text <strong>988</strong> (Suicide and Crisis Lifeline) or call 911.
    </p>
  );
}

// Free plan (not shown as a card on the plan picker)
const FREE_PLAN = {
  id: "free", name: "Seedling", price: 0, period: "",
  tagline: "Free, saved on this device",
  description: "Daily check-ins, your journal, your last 7 days of progress, and the Wellness Hub. Saved on this device only.",
  features: [
    "Daily check-ins across all five pillars",
    "Private journal with guided prompts",
    "Your last 7 days of progress",
    "Wellness Hub library",
    "Saved on this device only"
  ]
};
function planInfo(id) {
  return PLANS.find(p => p.id === id) || (id === "free" ? FREE_PLAN : undefined);
}

const PLANS = [
  {
    id: "seed", name: "Root", price: 19, period: "/month",
    icon: "🌱",
    tagline: "Everything you need to begin",
    description: "Everything you need to begin. Access the core pillars, weekly intentions, and your personalized assessment.",
    features: [
      "Five Pillars Dashboard, your daily wellness home",
      "Daily check-ins across all five pillars",
      "Guided trauma-informed journal prompts",
      "Personalized healing assessment",
      "Weekly intention setting",
      "Wellness Hub with educational library & podcast",
      "Progress charts & pillar tracking"
    ]
  },
  {
    id: "bloom", name: "Bloom", price: 39, period: "/month",
    Icon: Heart,
    tagline: "Deepen your practice",
    description: "Deepen your practice with guided nourishment support, comfort food exploration, healing recipes, and saved favorites.",
    features: [
      "Everything in Root",
      "Guided Nourishment Support",
      "Comfort food exploration",
      "Healing recipes tailored to your mood",
      "Save and heart your favorite recipes & swaps",
      "Personalized Healing Guidance sessions",
      "My Pillar Goals with reflection notes",
      "Self-Care Budget Tracker",
      "Wellness Appointments & Provider Directory"
    ],
    popular: true
  },
  {
    id: "thrive", name: "Thrive", price: 67, period: "/month",
    Icon: Sparkles,
    tagline: "The full sanctuary",
    description: "The full sanctuary. Add hydration tracking, a rest and relaxation journal, self-care budgeting, appointment management, provider notes, and prescription tracking. All private, all yours.",
    features: [
      "Everything in Bloom",
      "Hydration Tracker with streaks & daily goals",
      "Rest & Relaxation Journal with reflections",
      "Appointment session notes & provider records",
      "Private, secure prescription tracking",
      "Calming Exercises with guided animations",
      "Personalized 30/60/90-Day Wellness Roadmap",
      "Priority Support from our team"
    ]
  }
];

const C = {
  cream: "#FBF5EC", warm: "#2A1A0E", terracotta: "#C4614B",
  gold: "#C9963B", sage: "#4A6741", blush: "#F0DFCF",
  muted: "#9A8B7C", border: "#E8D9C8", white: "#FFFEF9",
  shadow: "0 4px 24px rgba(42,26,14,0.08)"
};

// ══════════════════════════════════════════════════════════════════
//  STYLES
// ══════════════════════════════════════════════════════════════════

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  img, svg, canvas { max-width: 100%; height: auto; }
  .recharts-wrapper { max-width: 100% !important; }
  html, body { background: ${C.cream}; font-family: 'DM Sans', sans-serif; color: ${C.warm}; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #C9963B55; border-radius: 4px; }
  .serif { font-family: 'Cormorant Garamond', serif; }
  @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  @keyframes float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-6px); } }
  @keyframes pulse { 0%,100% { opacity:0.6; } 50% { opacity:1; } }
  @keyframes slideIn { from { transform:translateX(-12px); opacity:0; } to { transform:translateX(0); opacity:1; } }
  @keyframes breatheIn { 0% { transform:scale(1); opacity:0.7; } 100% { transform:scale(1.55); opacity:1; } }
  @keyframes breatheOut { 0% { transform:scale(1.55); opacity:1; } 100% { transform:scale(1); opacity:0.7; } }
  @keyframes breatheHold { 0%,100% { transform:scale(1.55); opacity:1; } }
  @keyframes breatheHoldEmpty { 0%,100% { transform:scale(1); opacity:0.7; } }
  @keyframes ripple { 0% { transform:scale(1); opacity:0.4; } 100% { transform:scale(2.2); opacity:0; } }
  @keyframes twinkle { 0%,100% { opacity:0.3; transform:scale(0.8); } 50% { opacity:1; transform:scale(1.1); } }
  @keyframes shimmer { 0%,100% { opacity:0.6; } 50% { opacity:1; } }
  .fade-up { animation: fadeUp 0.5s ease forwards; }
  .fade-in { animation: fadeIn 0.3s ease forwards; }
  .float { animation: float 4s ease-in-out infinite; }
  .btn-hover { transition: all 0.2s ease; }
  .btn-hover:hover { transform: translateY(-2px); filter: brightness(1.05); }
  .btn-hover:active { transform: translateY(0); }
  .card-hover { transition: all 0.25s ease; cursor: pointer; }
  .card-hover:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(42,26,14,0.14) !important; }
  textarea, input { font-family: 'DM Sans', sans-serif; }
  textarea:focus, input:focus { outline: none; border-color: ${C.gold} !important; }
  @media (max-width: 639px) {
    .mobile-hide { display: none !important; }
    .mobile-full { width: 100% !important; }
    .rg-2col { grid-template-columns: 1fr !important; }
    .rg-3col { grid-template-columns: 1fr !important; }
    .rg-auto { grid-template-columns: 1fr !important; }
    .rf-col { flex-direction: column !important; }
    .rf-wrap { flex-wrap: wrap !important; }
    .rs-full { max-width: 100% !important; width: 100% !important; }
    .rp-sm { padding: 14px !important; }
    .rt-sm { font-size: 26px !important; line-height: 1.2 !important; }
    .rh-sm { font-size: 20px !important; }
    /* Cards get tighter padding on mobile */
    .card-mobile { padding: 16px !important; }
    /* Tabs scroll horizontally */
    .tab-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; white-space: nowrap; }
  }
  @media (min-width: 640px) and (max-width: 1023px) {
    .rg-3col { grid-template-columns: 1fr 1fr !important; }
    .rg-auto { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)) !important; }
    .rs-full { max-width: 100% !important; }
    .rt-sm { font-size: 34px !important; }
  }
  .mobile-nav {
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 200;
    background: ${C.warm}; display: flex; border-top: 1px solid rgba(255,255,255,0.1);
    padding: 8px 0 max(8px, env(safe-area-inset-bottom));
  }
  .mobile-nav-btn {
    flex: 1; background: none; border: none; cursor: pointer;
    display: flex; flex-direction: column; align-items: center; gap: 3px;
    padding: 6px 4px; color: rgba(251,245,236,0.5); font-size: 10px;
    font-family: 'DM Sans', sans-serif; transition: color 0.2s;
  }
  .mobile-nav-btn.active { color: ${C.gold}; }
  .mobile-nav-btn span:first-child { font-size: 20px; }
  .sidebar-link { transition: all 0.2s ease; }
  .sidebar-link:hover { background: rgba(201,150,59,0.12) !important; color: ${C.gold} !important; }
`;

const S = {
  appShell: { display:"flex", minHeight:"100vh", background: C.cream },
  sidebar: {
    width: 220, background: C.warm, display:"flex",
    flexDirection:"column", position:"fixed", left:0, top:0, bottom:0, zIndex:100,
    padding:0, overflow:"hidden"
  },
  sidebarBrand: { padding:"28px 24px 20px", borderBottom:`1px solid rgba(255,255,255,0.08)` },
  main: { marginLeft: 248, flex:1, minHeight:"100vh", padding:"32px 40px", maxWidth:1100 },
  mainTablet: { marginLeft: 220, flex:1, minHeight:"100vh", padding:"24px 24px" },
  mainMobile: { marginLeft: 0, flex:1, minHeight:"100vh", padding:"16px 14px 90px 14px" },
  page: { animation:"fadeUp 0.45s ease forwards", overflowX:"hidden", maxWidth:"100%" },
  card: {
    background: C.white, borderRadius: 16, padding:"20px",
    boxShadow: C.shadow, border:`1px solid ${C.border}`
  },
  input: {
    width:"100%", padding:"12px 16px", borderRadius: 10,
    border:`1.5px solid ${C.border}`, background: C.cream,
    fontSize:15, color: C.warm, transition:"border-color 0.2s"
  },
  textarea: {
    width:"100%", padding:"14px 16px", borderRadius:10,
    border:`1.5px solid ${C.border}`, background: C.cream,
    fontSize:15, color: C.warm, lineHeight:1.7, resize:"vertical",
    minHeight:120, transition:"border-color 0.2s"
  },
  btnPrimary: {
    background: `linear-gradient(135deg, ${C.gold}, #B8821F)`,
    color: "#fff", border:"none", borderRadius:10, padding:"12px 24px",
    fontSize:15, fontWeight:500, cursor:"pointer", letterSpacing:"0.3px"
  },
  btnSecondary: {
    background:"transparent", color: C.gold,
    border:`1.5px solid ${C.gold}`, borderRadius:10, padding:"11px 22px",
    fontSize:15, fontWeight:500, cursor:"pointer"
  },
  btnGhost: {
    background:"transparent", color: C.muted,
    border:`1.5px solid ${C.border}`, borderRadius:10, padding:"10px 20px",
    fontSize:14, cursor:"pointer"
  },
  tag: {
    display:"inline-block", padding:"4px 12px", borderRadius:20,
    fontSize:12, fontWeight:500, letterSpacing:"0.5px"
  }
};

// ══════════════════════════════════════════════════════════════════
//  NOTIFICATION
// ══════════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════════════════
//  ACTIVITY MODAL
// ══════════════════════════════════════════════════════════════════

function ActivityModal({ activity, pillar, onClose }) {
  const guide = ACTIVITY_GUIDES[activity];
  const [activeStep, setActiveStep] = useState(0);
  const [mode, setMode] = useState("overview"); // overview | steps

  if (!guide) return null;

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:1000,
      background:"rgba(42,26,14,0.7)", backdropFilter:"blur(6px)",
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:24, animation:"fadeIn 0.2s ease"
    }} onClick={e => { if(e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: C.white, borderRadius:24, maxWidth:640, width:"100%",
        maxHeight:"90vh", overflowY:"auto",
        boxShadow:"0 24px 80px rgba(42,26,14,0.25)",
        animation:"fadeUp 0.3s ease"
      }}>
        {/* Header */}
        <div style={{
          background:`linear-gradient(135deg, ${pillar.color}, ${pillar.dark})`,
          borderRadius:"24px 24px 0 0", padding:"28px 32px", color:"#fff", position:"relative"
        }}>
          <button onClick={onClose} style={{
            position:"absolute", top:20, right:20, background:"rgba(255,255,255,0.2)",
            border:"none", color:"#fff", width:36, height:36, borderRadius:"50%",
            cursor:"pointer", fontSize:18, display:"flex", alignItems:"center", justifyContent:"center"
          }}>×</button>
          <div style={{ fontSize:36, marginBottom:10 }}>{guide.icon}</div>
          <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:30, fontWeight:400, marginBottom:4 }}>{activity}</h2>
          <div style={{ display:"flex", gap:16, opacity:0.85, fontSize:13 }}>
            <span>⏱ {guide.duration}</span>
            <span><PillarIcon pillar={pillar} size={12} /> {pillar.short} Pillar</span>
          </div>
        </div>

        <div style={{ padding:"28px 32px" }}>
          {/* Mode toggle */}
          <div style={{ display:"flex", gap:8, marginBottom:24, background: C.cream, borderRadius:12, padding:6 }}>
            {["overview","steps"].map(m => (
              <button key={m} onClick={() => setMode(m)}
                style={{
                  flex:1, padding:"10px", borderRadius:8, border:"none", cursor:"pointer",
                  background: mode===m ? pillar.color : "transparent",
                  color: mode===m ? "#fff" : C.muted,
                  fontSize:14, fontWeight:500, textTransform:"capitalize", transition:"all 0.2s"
                }}>
                {m === "overview" ? "Overview" : "Step-by-Step Guide"}
              </button>
            ))}
          </div>

          {mode === "overview" && (
            <div style={{ animation:"fadeIn 0.3s ease" }}>
              <p style={{ fontSize:15, lineHeight:1.85, color: C.warm, marginBottom:24 }}>{guide.intro}</p>
              <div style={{ background: pillar.light, borderRadius:14, padding:"18px 20px", marginBottom:24,
                borderLeft:`4px solid ${pillar.color}` }}>
                <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:18, color: pillar.dark, fontStyle:"italic" }}>
                  This practice has {guide.steps.length} steps and takes approximately {guide.duration}.
                </p>
              </div>
              <h4 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:14 }}>What You Will Do</h4>
              <div style={{ display:"grid", gap:10, marginBottom:28 }}>
                {guide.steps.map((step, i) => (
                  <div key={i} style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
                    <div style={{ width:28, height:28, borderRadius:"50%", background: pillar.light,
                      border:`2px solid ${pillar.color}`, display:"flex", alignItems:"center",
                      justifyContent:"center", fontSize:12, color: pillar.color, fontWeight:600, flexShrink:0 }}>
                      {i+1}
                    </div>
                    <div>
                      <div style={{ fontWeight:500, fontSize:14 }}>{step.title}</div>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => { setMode("steps"); setActiveStep(0); }} className="btn-hover"
                style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${pillar.color}, ${pillar.dark})`, width:"100%", padding:"14px" }}>
                Begin Practice →
              </button>
            </div>
          )}

          {mode === "steps" && (
            <div style={{ animation:"fadeIn 0.3s ease" }}>
              {/* Progress */}
              <div style={{ display:"flex", gap:6, marginBottom:24 }}>
                {guide.steps.map((_, i) => (
                  <div key={i} onClick={() => setActiveStep(i)}
                    style={{ flex:1, height:4, borderRadius:4, cursor:"pointer",
                      background: i <= activeStep ? pillar.color : C.border, transition:"background 0.3s" }} />
                ))}
              </div>

              {/* Step number */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                <span style={{ fontSize:13, color: C.muted, letterSpacing:"0.5px" }}>
                  STEP {activeStep + 1} OF {guide.steps.length}
                </span>
                <span style={{ fontSize:13, color: pillar.color }}>
                  {Math.round(((activeStep + 1) / guide.steps.length) * 100)}% complete
                </span>
              </div>

              {/* Current step */}
              <div style={{ background: pillar.light, borderRadius:16, padding:"24px", marginBottom:24,
                border:`1px solid ${pillar.color}22`, minHeight:180 }}>
                <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16 }}>
                  <div style={{ width:40, height:40, borderRadius:"50%",
                    background:`linear-gradient(135deg, ${pillar.color}, ${pillar.dark})`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color:"#fff", fontWeight:600, fontSize:16, flexShrink:0 }}>
                    {activeStep + 1}
                  </div>
                  <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, color: pillar.dark, fontWeight:500 }}>
                    {guide.steps[activeStep].title}
                  </h3>
                </div>
                <p style={{ fontSize:15, lineHeight:1.9, color: C.warm }}>
                  {guide.steps[activeStep].body}
                </p>
              </div>

              {/* Closing message on last step */}
              {activeStep === guide.steps.length - 1 && (
                <div style={{ background:`${C.gold}12`, border:`1.5px solid ${C.gold}44`,
                  borderRadius:12, padding:"18px 20px", marginBottom:20 }}>
                  <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:17, color: C.warm,
                    fontStyle:"italic", lineHeight:1.8 }}>
                    {guide.closing}
                  </p>
                </div>
              )}

              {/* Navigation */}
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={() => setActiveStep(s => Math.max(0, s-1))}
                  disabled={activeStep === 0}
                  style={{ ...S.btnGhost, flex:1, opacity: activeStep === 0 ? 0.4 : 1 }}>
                  ← Previous
                </button>
                {activeStep < guide.steps.length - 1 ? (
                  <button onClick={() => setActiveStep(s => s+1)} className="btn-hover"
                    style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${pillar.color}, ${pillar.dark})`, flex:2 }}>
                    Next Step →
                  </button>
                ) : (
                  <button onClick={onClose} className="btn-hover"
                    style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${pillar.color}, ${pillar.dark})`, flex:2 }}>
                    Complete Practice
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PWA INSTALL PROMPT
// ══════════════════════════════════════════════════════════════════

function InstallPrompt() {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed
    const isDismissed = localStorage.getItem("gf_pwa_dismissed");
    if (isDismissed) return;

    const handler = e => {
      e.preventDefault();
      setPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setPrompt(null);
  };

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem("gf_pwa_dismissed", "true");
  };

  if (!visible || dismissed) return null;

  return (
    <div style={{
      position:"fixed", bottom:24, left:"50%", transform:"translateX(-50%)",
      zIndex:9998, width:"calc(100% - 48px)", maxWidth:420,
      background: C.warm, borderRadius:16, padding:"18px 20px",
      boxShadow:"0 8px 40px rgba(42,26,14,0.35)",
      display:"flex", alignItems:"center", gap:14,
      animation:"fadeUp 0.4s ease"
    }}>
      <div style={{ flexShrink:0 }}><BrandButterfly size={32} color={C.gold} /></div>
      <div style={{ flex:1 }}>
        <div style={{ color:"#FBF5EC", fontWeight:500, fontSize:14, marginBottom:3 }}>
          Add to your home screen
        </div>
        <div style={{ color:"rgba(251,245,236,0.6)", fontSize:12, lineHeight:1.5 }}>
          Install ThriveRoot for quick access anytime
        </div>
      </div>
      <div style={{ display:"flex", gap:8, flexShrink:0 }}>
        <button onClick={handleDismiss}
          style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"rgba(251,245,236,0.6)",
            borderRadius:8, padding:"8px 12px", fontSize:12, cursor:"pointer" }}>
          Later
        </button>
        <button onClick={handleInstall} className="btn-hover"
          style={{ background:`linear-gradient(135deg, ${C.gold}, #B8821F)`,
            border:"none", color:"#fff", borderRadius:8,
            padding:"8px 14px", fontSize:12, fontWeight:500, cursor:"pointer" }}>
          Install
        </button>
      </div>
    </div>
  );
}

function Notification({ msg, type }) {
  const bg = type === "error" ? "#C4614B" : type === "warning" ? C.gold : "#4A6741";
  return (
    <div style={{
      position:"fixed", bottom:28, right:28, zIndex:9999,
      background:bg, color:"#fff", padding:"14px 22px",
      borderRadius:12, fontSize:14, fontWeight:500,
      boxShadow:"0 8px 32px rgba(0,0,0,0.18)",
      animation:"fadeUp 0.3s ease forwards"
    }}>{msg}</div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  LOADING SCREEN
// ══════════════════════════════════════════════════════════════════

function LoadingScreen() {
  return (
    <div style={{
      minHeight:"100vh", background: C.warm, display:"flex",
      alignItems:"center", justifyContent:"center", flexDirection:"column", gap:20
    }}>
      <div className="float"><BrandButterfly size={52} color={C.gold} /></div>
      <p style={{ color:"rgba(255,255,255,0.6)", fontFamily:"Cormorant Garamond, serif", fontSize:20 }}>
        ThriveRoot by Glow & Flow
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  AUTH: LOGIN
// ══════════════════════════════════════════════════════════════════

function LoginScreen({ setView, setUser, setCheckIns, setJournalEntries, notify }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { notify("Please fill in all fields", "error"); return; }
    setLoading(true);
    try {
    const data = await supaSignIn(email.toLowerCase(), password);
    if (data.error || data.error_description || !data.user) {
      notify("Invalid email or password. Please check your details or create an account.", "error");
      setLoading(false); return;
    }
    _saveSession(data);
    const meta = data.user?.user_metadata || {};
    const { plan, trialEnded } = await fetchMyPlan(data.user);
    // No active subscription (never chose one, or her trial or plan ended):
    // she continues on the free Seedling plan.
    const user = { id: data.user.id, email: data.user.email, name: meta.name || "Member", plan: plan === "none" ? "free" : plan, subscribedAt: meta.subscribedAt, hadTrial: !!trialEnded };
    if (plan === "none") setCloudSync(false);
    setUser(user);
    // Load core data
    const ci = await safeRetrieve(`gf_checkins_${user.email}`) || {};
    const je = await safeRetrieve(`gf_journal_${user.email}`) || [];
    setCheckIns(ci); setJournalEntries(je);
    // Pre-warm ALL tracker caches from Supabase so they're instant when navigated to
    if (user.plan === "thrive" || user.plan === "bloom") {
      const trackerKeys = [
        `gf_hydration_${user.email}`,
        `gf_rest_${user.email}`,
        `gf_goals_${user.email}`,
        `gf_appointments_${user.email}`,
        `gf_weekly_${user.email}`,
        `gf_nourishment_journal_${user.email}`,
        `gf_nourishment_favorites_${user.email}`,
      ];
      // Fire all fetches in parallel, cache results to localStorage
      Promise.all(trackerKeys.map(k => _forceRefreshFromCloud(k))).catch(() => {});
    }
    setView("dashboard");
    notify(trialEnded && plan === "none"
      ? "Your free trial has ended. You're now on Seedling, our free plan. Upgrade anytime."
      : "Welcome back, " + user.name.split(" ")[0]);
    setLoading(false);
    } catch(err) {
      notify(err.message || "Something went wrong. Please try again.", "error");
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight:"100vh", background: C.warm,
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:24
    }}>
      <div style={{ maxWidth:440, width:"100%", animation:"fadeUp 0.5s ease", padding:"0 4px" }}>
        <div style={{ textAlign:"center", marginBottom:40 }}>
          <div style={{ marginBottom:12 }} className="float"><BrandButterfly size={52} color={C.gold} /></div>
          <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:36, color:"#FBF5EC", fontWeight:400, whiteSpace:"nowrap" }}>
            ThriveRoot
          </h1>
          <p style={{ color: C.gold, fontSize:13, marginTop:4, letterSpacing:"2px", textTransform:"uppercase" }}>
            by Glow &amp; Flow
          </p>
          <p style={{ color:"rgba(251,245,236,0.55)", fontSize:15, marginTop:8, letterSpacing:"0.5px" }}>
            Your Healing Sanctuary
          </p>
        </div>

        <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:20, padding:36, backdropFilter:"blur(12px)", border:"1px solid rgba(255,255,255,0.1)" }}>
          <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, color:"#FBF5EC", marginBottom:6 }}>
            Welcome home
          </h2>
          <p style={{ color:"rgba(251,245,236,0.5)", fontSize:14, marginBottom:28 }}>Sign in to continue your journey</p>

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom:16 }}>
              <label style={{ display:"block", color:"rgba(251,245,236,0.7)", fontSize:13, marginBottom:8, letterSpacing:"0.5px" }}>EMAIL</label>
              <input value={email} onChange={e => setEmail(e.target.value)} type="email"
                placeholder="your@email.com"
                style={{ ...S.input, background:"rgba(255,255,255,0.08)", border:"1.5px solid rgba(255,255,255,0.15)", color:"#FBF5EC" }} />
            </div>
            <div style={{ marginBottom:28 }}>
              <label style={{ display:"block", color:"rgba(251,245,236,0.7)", fontSize:13, marginBottom:8, letterSpacing:"0.5px" }}>PASSWORD</label>
              <input value={password} onChange={e => setPassword(e.target.value)} type="password"
                placeholder="••••••••"
                style={{ ...S.input, background:"rgba(255,255,255,0.08)", border:"1.5px solid rgba(255,255,255,0.15)", color:"#FBF5EC" }} />
            </div>
            <button type="submit" disabled={loading} className="btn-hover"
              style={{ ...S.btnPrimary, width:"100%", padding:"14px", fontSize:16, opacity: loading ? 0.7 : 1 }}>
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div style={{ textAlign:"center", marginTop:24 }}>
            <span style={{ color:"rgba(251,245,236,0.5)", fontSize:14 }}>New to ThriveRoot? </span>
            <button onClick={() => setView("register")}
              style={{ background:"none", border:"none", color: C.gold, cursor:"pointer", fontSize:14, fontWeight:500 }}>
              Begin your journey →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  AUTH: REGISTER
// ══════════════════════════════════════════════════════════════════

function RegisterScreen({ setView, notify }) {
  const [form, setForm] = useState({ name:"", email:"", password:"", confirm:"" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({...f, [k]:v}));

  const handleRegister = async (e) => {
    e.preventDefault();
    const { name, email, password, confirm } = form;
    if (!name || !email || !password) { notify("Please fill in all fields", "error"); return; }
    if (password !== confirm) { notify("Passwords don't match", "error"); return; }
    if (password.length < 8) { notify("Password must be at least 8 characters", "error"); return; }
    setLoading(true);
    try {
    const signupData = await supaSignUp(email.toLowerCase(), password, name);
    if (signupData.error || signupData.error_description || signupData.code === 422) {
      notify(signupData.error_description || signupData.msg || "Could not create account. This email may already be registered.", "error");
      setLoading(false); return;
    }
    // Always sign in right after signup to get a guaranteed valid session
    const signinData = await supaSignIn(email.toLowerCase(), password);
    if (signinData.error || signinData.error_description) {
      notify("Account created! Please sign in to continue.", "success");
      setView("login"); setLoading(false); return;
    }
    _saveSession(signinData);
    localStorage.setItem("gf_pending_name", name);
    localStorage.setItem("gf_pending_email", email.toLowerCase());
    notify("Account created! Choose your plan");
    setView("subscribe");
    setLoading(false);
    } catch(err) {
      notify(err.message || "Something went wrong. Please try again.", "error");
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:"100vh", background: C.warm, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ maxWidth:460, width:"100%", animation:"fadeUp 0.5s ease" }}>
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <div style={{ marginBottom:10 }}><BrandButterfly size={52} color={C.gold} /></div>
          <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:34, color:"#FBF5EC", fontWeight:400 }}>Begin Your Journey</h1>
          <p style={{ color:"rgba(251,245,236,0.5)", fontSize:14, marginTop:6 }}>Create your healing sanctuary account</p>
        </div>

        <div style={{ background:"rgba(255,255,255,0.05)", borderRadius:20, padding:36, border:"1px solid rgba(255,255,255,0.1)" }}>
          <form onSubmit={handleRegister}>
            {[
              { key:"name", label:"FULL NAME", type:"text", placeholder:"Your name" },
              { key:"email", label:"EMAIL ADDRESS", type:"email", placeholder:"your@email.com" },
              { key:"password", label:"CREATE PASSWORD", type:"password", placeholder:"Min. 8 characters" },
              { key:"confirm", label:"CONFIRM PASSWORD", type:"password", placeholder:"Repeat password" },
            ].map(({ key, label, type, placeholder }) => (
              <div key={key} style={{ marginBottom:18 }}>
                <label style={{ display:"block", color:"rgba(251,245,236,0.6)", fontSize:12, marginBottom:8, letterSpacing:"0.8px" }}>{label}</label>
                <input value={form[key]} onChange={e => set(key, e.target.value)} type={type} placeholder={placeholder}
                  style={{ ...S.input, background:"rgba(255,255,255,0.08)", border:"1.5px solid rgba(255,255,255,0.15)", color:"#FBF5EC" }} />
              </div>
            ))}
            <button type="submit" disabled={loading} className="btn-hover"
              style={{ ...S.btnPrimary, width:"100%", padding:"14px", fontSize:16, marginTop:10, opacity: loading ? 0.7 : 1 }}>
              {loading ? "Creating account..." : "Create My Account"}
            </button>
          </form>
          <p style={{ color:"rgba(251,245,236,0.45)", fontSize:12, lineHeight:1.7, marginTop:14, textAlign:"center" }}>
            By creating an account, you understand that ThriveRoot offers wellness support, not medical or mental health advice, and you agree to our{" "}
            <a href="/glowandflow-privacy.html" target="_blank" rel="noopener noreferrer" style={{ color:"rgba(251,245,236,0.75)" }}>Privacy Policy</a>.
          </p>

          <div style={{ textAlign:"center", marginTop:22 }}>
            <span style={{ color:"rgba(251,245,236,0.5)", fontSize:14 }}>Already a member? </span>
            <button onClick={() => setView("login")}
              style={{ background:"none", border:"none", color: C.gold, cursor:"pointer", fontSize:14, fontWeight:500 }}>
              Sign in →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  AUTH: SUBSCRIBE
// ══════════════════════════════════════════════════════════════════

function SubscribeScreen({ setView, setUser, notify, pendingEmail, pendingName, trialEnded = false }) {
  const [selected, setSelected] = useState("bloom");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async () => {
    setLoading(true);
    const name = pendingName || localStorage.getItem("gf_pending_name") || "Member";
    const email = pendingEmail || localStorage.getItem("gf_pending_email") || "";
    if (!email) { notify("Session expired. Please register again.", "error"); setView("register"); setLoading(false); return; }

    try {
      const res = await apiFetch("/api/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Pass plan name (not priceId) so API can apply trial correctly.
        // Women whose trial already ended continue straight to a paid plan.
        body: JSON.stringify({ email, name, plan: selected, skipTrial: trialEnded })
      });
      const data = await res.json();
      if (data.url) {
        localStorage.setItem("gf_pending_plan", selected);
        window.location.href = data.url;
      } else {
        notify(data.error || "Could not start checkout. Please try again.", "error");
        setLoading(false);
      }
    } catch(e) {
      notify("Connection error. Please try again.", "error");
      setLoading(false);
    }
  };

  // Start on the free Seedling plan instead of choosing a paid plan
  const continueFree = async () => {
    setLoading(true);
    try {
      const authUser = await supaGetUser(_tok || localStorage.getItem("gf_tok"));
      if (!authUser) { notify("Please sign in again.", "error"); setView("login"); setLoading(false); return; }
      const name = authUser.user_metadata?.name || pendingName || localStorage.getItem("gf_pending_name") || "Member";
      ["gf_pending_name","gf_pending_email","gf_pending_plan","gf_trial_ended"].forEach(k => localStorage.removeItem(k));
      setUser({ id: authUser.id, email: authUser.email, name, plan: "free" });
      setView("dashboard");
      notify("Welcome to ThriveRoot");
      try {
        await apiFetch("/api/send-welcome", { method:"POST", headers:{ "Content-Type":"application/json" }, body: JSON.stringify({ name }) });
      } catch (e) {}
    } catch (e) {
      notify("Something went wrong. Please try again.", "error");
    }
    setLoading(false);
  };

  const selectedPlan = PLANS.find(p => p.id === selected);

  return (
    <div style={{ minHeight:"100vh", background: C.warm, overflowY:"auto", padding:"40px 20px 60px" }}>
      <div style={{ maxWidth:980, margin:"0 auto", animation:"fadeUp 0.5s ease" }}>

        {/* Header */}
        <div style={{ textAlign:"center", marginBottom:48 }}>
          <div style={{ marginBottom:14 }} className="float"><BrandButterfly size={56} color={C.gold} /></div>
          <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:46, color:"#FBF5EC", fontWeight:400, marginBottom:12 }}>
            {trialEnded ? "Your Free Trial Has Ended" : "Choose Your Healing Path"}
          </h1>
          <p style={{ color:"rgba(251,245,236,0.6)", fontSize:16, maxWidth:520, margin:"0 auto", lineHeight:1.8 }}>
            {trialEnded
              ? "Thank you for spending these two weeks with us. Choose a plan to keep your space, your journal, and your progress."
              : "Every journey is sacred. Begin wherever you are. You can always grow into more as you heal."}
          </p>
          {/* Trial banner */}
          {!trialEnded && <div style={{ display:"inline-flex", alignItems:"center", gap:10, marginTop:20,
            background:"linear-gradient(135deg, rgba(201,150,59,0.2), rgba(201,150,59,0.08))",
            border:"1px solid rgba(201,150,59,0.4)", borderRadius:40, padding:"10px 24px" }}>
            <Gift size={18} color={C.gold} />
            <span style={{ color:"#FBF5EC", fontSize:15, fontWeight:500 }}>
              14-day free trial. No card needed to start.
            </span>
          </div>}
        </div>

        {/* Plan cards */}
        <div className="rg-3col" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20, marginBottom:32 }}>
          {PLANS.map(plan => {
            const isSelected = selected === plan.id;
            return (
              <div key={plan.id} onClick={() => setSelected(plan.id)}
                style={{
                  background: isSelected ? "rgba(201,150,59,0.12)" : "rgba(255,255,255,0.04)",
                  border: isSelected ? `2px solid ${C.gold}` : "2px solid rgba(255,255,255,0.08)",
                  borderRadius:20, padding:"28px 24px", cursor:"pointer",
                  transition:"all 0.25s ease", position:"relative",
                  boxShadow: isSelected ? `0 0 32px rgba(201,150,59,0.15)` : "none"
                }}>

                {plan.popular && (
                  <div style={{ position:"absolute", top:-13, left:"50%", transform:"translateX(-50%)",
                    background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, color:"#fff",
                    fontSize:10, fontWeight:600, padding:"5px 16px", borderRadius:20,
                    letterSpacing:"1.5px", whiteSpace:"nowrap" }}>
                    MOST BELOVED
                  </div>
                )}

                {/* Plan icon + name */}
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                  <BrandButterfly size={28} color={plan.id === "thrive" ? C.gold : plan.id === "bloom" ? "#C4614B" : "#4A6741"} />
                  <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:32,
                    color: isSelected ? C.gold : "#FBF5EC", fontWeight:400 }}>
                    {plan.name}
                  </h3>
                  {isSelected && (
                    <div style={{ marginLeft:"auto", width:22, height:22, borderRadius:"50%",
                      background: C.gold, display:"flex", alignItems:"center",
                      justifyContent:"center", fontSize:12, color:"#fff", flexShrink:0 }}>✓</div>
                  )}
                </div>

                {/* Tagline */}
                <p style={{ color:"rgba(251,245,236,0.45)", fontSize:13, fontStyle:"italic", marginBottom:16 }}>
                  {plan.tagline}
                </p>

                {/* Price */}
                <div style={{ marginBottom:16, paddingBottom:16,
                  borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
                  {!trialEnded && <div style={{ fontSize:12, color: C.gold, letterSpacing:"1px", marginBottom:4,
                    fontWeight:600 }}>FREE FOR 14 DAYS</div>}
                  <span style={{ fontFamily:"Cormorant Garamond, serif", fontSize:38,
                    color: trialEnded ? "#FBF5EC" : "rgba(251,245,236,0.5)", fontWeight:400, lineHeight:1,
                    textDecoration: trialEnded ? "none" : "line-through" }}>
                    ${plan.price}
                  </span>
                  <span style={{ color:"rgba(251,245,236,0.35)", fontSize:13 }}>{trialEnded ? "/month" : "/mo after trial"}</span>
                </div>

                {/* Description */}
                <p style={{ color:"rgba(251,245,236,0.55)", fontSize:13, lineHeight:1.7, marginBottom:18 }}>
                  {plan.description}
                </p>

                {/* Features */}
                <ul style={{ listStyle:"none", display:"flex", flexDirection:"column", gap:9 }}>
                  {plan.features.map((f, i) => (
                    <li key={i} style={{ color:"rgba(251,245,236,0.75)", fontSize:13,
                      display:"flex", alignItems:"flex-start", gap:8, lineHeight:1.5 }}>
                      <span style={{ color: C.gold, flexShrink:0, marginTop:2, fontSize:8 }}>◆</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Comparison note */}
        <div style={{ background:"rgba(255,255,255,0.04)", borderRadius:14, padding:"16px 22px",
          marginBottom:32, border:"1px solid rgba(255,255,255,0.06)", textAlign:"center" }}>
          <p style={{ color:"rgba(251,245,236,0.45)", fontSize:13, lineHeight:1.8 }}>
            Not sure? Start with <strong style={{ color:"rgba(251,245,236,0.65)" }}>Root</strong> and upgrade anytime.
            Every plan is built for Black women who carry it all: trauma-informed, judgment-free, and rooted in the book.
            <br/>This is a space of healing, not hustle.
          </p>
        </div>

        {/* CTA */}
        <div style={{ textAlign:"center" }}>
          <button onClick={handleSubscribe} disabled={loading} className="btn-hover"
            style={{ ...S.btnPrimary, padding:"18px 56px", fontSize:18, borderRadius:14,
              opacity: loading ? 0.7 : 1, boxShadow:`0 8px 32px rgba(201,150,59,0.3)` }}>
            {loading
              ? "Redirecting to secure checkout..."
              : trialEnded
                ? `Continue with ${selectedPlan?.name}`
                : `Start My Free 14-Day Trial: ${selectedPlan?.name}`}
          </button>
          <p style={{ color:"rgba(251,245,236,0.35)", fontSize:12, marginTop:10 }}>
            {trialEnded
              ? "Secure checkout through Stripe. Cancel anytime."
              : "No card needed to start. Add a payment method anytime during your trial to keep your access. If you don't, your trial simply ends and you are never charged."}
          </p>
          <div style={{ display:"flex", justifyContent:"center", gap:24, marginTop:10, flexWrap:"wrap" }}>
            {(trialEnded
              ? ["Secure & encrypted", "Cancel anytime"]
              : ["No card required", "Never charged unless you choose", "Cancel anytime"]).map(item => (
              <span key={item} style={{ color:"rgba(251,245,236,0.3)", fontSize:12 }}>{item}</span>
            ))}
          </div>
          {!trialEnded && (
            <div style={{ marginTop:28, paddingTop:22, borderTop:"1px solid rgba(255,255,255,0.08)" }}>
              <p style={{ color:"rgba(251,245,236,0.55)", fontSize:14, marginBottom:10 }}>
                Not ready for a plan? Start with Seedling, our free plan. You can try a paid plan anytime.
              </p>
              <button onClick={continueFree} disabled={loading} className="btn-hover"
                style={{ background:"transparent", border:"1.5px solid rgba(201,150,59,0.5)", color:"#FBF5EC",
                  padding:"12px 28px", borderRadius:12, fontSize:15, cursor:"pointer" }}>
                Continue free with Seedling
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  MOBILE FULL-SCREEN MENU
// ══════════════════════════════════════════════════════════════════
function MobileSidebar({ user, view, navigateTo, logout, onClose }) {
  const plan = planInfo(user?.plan);
  const isBloomOrThrive = user?.plan === "bloom" || user?.plan === "thrive";
  const isThrive = user?.plan === "thrive";

  const sections = [
    {
      label: "NAVIGATE",
      items: [
        { id:"dashboard", label:"Dashboard", Icon:Home },
        { id:"journal", label:"My Journal", Icon:BookOpen },
        { id:"progress", label:"Progress", Icon:BarChart2 },
        { id:"guidance", label:"Healing Guidance", Icon: Sparkles },
        { id:"weekly", label:"Weekly Intention", Icon:Sun, locked: !isBloomOrThrive },
        { id:"goals", label:"My Pillar Goals", Icon:Target, locked: !isBloomOrThrive },
        { id:"budget", label:"Self-Care Budget", Icon:Wallet, locked: !isBloomOrThrive },
        { id:"appointments", label:"Appointments", Icon:CalendarDays, locked: !isBloomOrThrive },
        { id:"roadmap", label:"My Wellness Roadmap", Icon:Map, locked: !isThrive },
        { id:"calming", label:"Calming Exercises", Icon:Wind, locked: !isThrive },
        { id:"hydration", label:"Hydration Tracker", Icon:Droplets, locked: !isThrive },
        { id:"rest", label:"Rest & Relaxation", Icon:Moon, locked: !isThrive },
        { id:"nourishment", label:"Nourishment Guide", Icon:Apple, locked: !isThrive },
        { id:"wellness-hub", label:"Wellness Hub", Icon:Library },
        { id:"support", label:"Priority Support", Icon:Mail, locked: !isThrive },
        { id:"settings", label:"Settings", Icon:Settings },
      ]
    },
    {
      label: "THE FIVE PILLARS",
      items: PILLARS.map(p => ({ id:`pillar_${p.id}`, label: p.short, icon: p.icon, pillar: p }))
    }
  ];

  return (
    <div>
      {/* User info */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:28,
        padding:"0 4px 24px", borderBottom:"1px solid rgba(255,255,255,0.1)" }}>
        <div style={{ width:42, height:42, borderRadius:"50%",
          background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
          display:"flex", alignItems:"center", justifyContent:"center",
          color:"#fff", fontSize:18, fontWeight:600, flexShrink:0 }}>
          {user?.name?.charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ color:"#FBF5EC", fontSize:15, fontWeight:500 }}>{user?.name}</div>
          <div style={{ color: C.gold, fontSize:12 }}>{plan?.name} member</div>
        </div>
      </div>

      {sections.map(section => (
        <div key={section.label} style={{ marginBottom:24 }}>
          <div style={{ fontSize:10, letterSpacing:"1.5px", color:"rgba(251,245,236,0.3)", marginBottom:8, paddingLeft:4 }}>
            {section.label}
          </div>
          <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {section.items.map(item => (
              <button key={item.id}
                onClick={() => {
                  if (item.pillar) { navigateTo("pillar", item.pillar); }
                  else { navigateTo(item.id); }
                }}
                style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px",
                  borderRadius:12, border:"none", cursor: item.locked ? "default" : "pointer",
                  background: view === item.id || (item.pillar && view === "pillar") ? "rgba(201,150,59,0.2)" : "rgba(255,255,255,0.05)",
                  color: item.locked ? "rgba(251,245,236,0.25)" : "rgba(251,245,236,0.75)",
                  fontSize:13, textAlign:"left", transition:"all 0.2s" }}>
                {item.Icon && <item.Icon size={18} style={{ flexShrink:0 }} />}
                <span style={{ flex:1, lineHeight:1.3 }}>{item.label}</span>
                {item.locked && <Lock size={10} style={{ opacity:0.4 }} />}
              </button>
            ))}
          </div>
        </div>
      ))}

      <button onClick={() => { logout(); onClose(); }}
        style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"14px",
          borderRadius:12, border:"1px solid rgba(255,255,255,0.1)", cursor:"pointer",
          background:"transparent", color:"rgba(251,245,236,0.4)", fontSize:14, marginTop:8 }}>
        ↩ Sign Out
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  SIDEBAR
// ══════════════════════════════════════════════════════════════════

function Sidebar({ user, view, setView, setActivePillar, logout, isTablet }) {
  const plan = planInfo(user?.plan) || FREE_PLAN;
  const isThrive = plan?.id === "thrive";
  const isBloomOrThrive = plan?.id === "bloom" || plan?.id === "thrive";

  const navItems = [
    { id:"dashboard",    label:"Dashboard",          Icon: Home },
    { id:"journal",      label:"My Journal",          Icon: BookOpen },
    { id:"progress",     label:"Progress",            Icon: BarChart2 },
    { id:"guidance",     label:"Healing Guidance",    Icon: Sparkles,  locked: !isBloomOrThrive },
    { id:"weekly",       label:"Weekly Intention",    Icon: Leaf,      locked: !isBloomOrThrive },
    { id:"goals",        label:"My Pillar Goals",     Icon: Target,    locked: !isBloomOrThrive },
    { id:"budget",       label:"Self-Care Budget",    Icon: Wallet,    locked: !isBloomOrThrive },
    { id:"appointments", label:"Appointments",        Icon: CalendarDays, locked: !isBloomOrThrive },
    { id:"roadmap",      label:"My Wellness Roadmap", Icon: Map,       locked: !isThrive },
    { id:"calming",      label:"Calming Exercises",   Icon: Wind,      locked: !isThrive },
    { id:"hydration",    label:"Hydration Tracker",   Icon: Droplets,  locked: !isThrive },
    { id:"rest",         label:"Rest & Relaxation",   Icon: Moon,      locked: !isThrive },
    { id:"nourishment",  label:"Nourishment Guide",   Icon: Apple,     locked: !isThrive },
    { id:"wellness-hub", label:"Wellness Hub",        Icon: Library },
    { id:"support",      label:"Priority Support",    Icon: Mail,      locked: !isThrive },
    { id:"settings",     label:"Settings",            Icon: Settings },
  ];

  return (
    <div style={S.sidebar}>
      <div style={{ ...S.sidebarBrand, padding: isTablet ? "20px 16px 16px" : "28px 24px 20px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:isTablet ? 8 : 10 }}>
          <BrandButterfly size={isTablet ? 28 : 36} color={C.gold} />
          {!isTablet && <div>
            <div style={{ fontFamily:"Cormorant Garamond, serif", color:"#FBF5EC", fontSize:19, fontWeight:400, lineHeight:1.2 }}>ThriveRoot</div>
            <div style={{ color:"rgba(251,245,236,0.4)", fontSize:11, letterSpacing:"1px" }}>BY GLOW &amp; FLOW</div>
          </div>}
          {isTablet && <div style={{ fontFamily:"Cormorant Garamond, serif", color:"#FBF5EC", fontSize:15, fontWeight:400 }}>G&F</div>}
        </div>
        <div style={{ marginTop:12, display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width: isTablet ? 26 : 32, height: isTablet ? 26 : 32, borderRadius:"50%", background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
            display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize: isTablet ? 11 : 13, fontWeight:600, flexShrink:0 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
          {!isTablet && <div>
            <div style={{ color:"#FBF5EC", fontSize:13, fontWeight:500 }}>{user?.name?.split(" ")[0]}</div>
            <div style={{ color: C.gold, fontSize:11 }}>{plan?.name} member</div>
          </div>}
        </div>
      </div>

      <div style={{ padding:"20px 12px 8px", flex:1, overflowY:"auto", overflowX:"hidden" }}>
        <div style={{ fontSize:10, letterSpacing:"1.5px", color:"rgba(251,245,236,0.3)", padding:"0 12px 10px" }}>NAVIGATE</div>
        {navItems.map(item => (
          <button key={item.id} onClick={() => setView(item.id)} className="sidebar-link"
            title={isTablet ? item.label : ""}
            style={{
              display:"flex", alignItems:"center", gap: isTablet ? 0 : 12, width:"100%",
              padding: isTablet ? "10px" : "11px 14px", justifyContent: isTablet ? "center" : "flex-start",
              borderRadius:10, border:"none", cursor:"pointer", textAlign:"left", marginBottom:2,
              background: view === item.id ? "rgba(201,150,59,0.18)" : "transparent",
              color: item.locked ? "rgba(251,245,236,0.25)" : view === item.id ? C.gold : "rgba(251,245,236,0.65)",
              fontSize:14, fontWeight: view === item.id ? 500 : 400
            }}>
            {item.Icon && <item.Icon size={isTablet ? 18 : 16} style={{ flexShrink:0 }} />}
            {!isTablet && <span style={{ flex:1 }}>{item.label}</span>}
            {!isTablet && item.locked && <Lock size={10} style={{ opacity:0.4 }} />}
          </button>
        ))}

        <div style={{ fontSize:10, letterSpacing:"1.5px", color:"rgba(251,245,236,0.3)", padding:"20px 12px 10px" }}>THE FIVE PILLARS</div>
        {PILLARS.map(pillar => (
          <button key={pillar.id} onClick={() => { setActivePillar(pillar); setView("pillar"); }} className="sidebar-link"
            title={isTablet ? pillar.short : ""}
            style={{
              display:"flex", alignItems:"center", gap: isTablet ? 0 : 12, width:"100%",
              padding: isTablet ? "8px" : "9px 14px", justifyContent: isTablet ? "center" : "flex-start",
              borderRadius:10, border:"none", cursor:"pointer", textAlign:"left", marginBottom:2,
              background: "transparent", color: "rgba(251,245,236,0.55)", fontSize:13
            }}>
            <PillarIcon pillar={pillar} size={isTablet ? 15 : 14} />
            {!isTablet && pillar.short}
          </button>
        ))}
      </div>

      <div style={{ padding:"0 12px" }}>
        <button onClick={logout} className="sidebar-link"
          style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"10px 14px",
            borderRadius:10, border:"none", cursor:"pointer", background:"transparent",
            color:"rgba(251,245,236,0.35)", fontSize:13 }}>
          <span>↩</span> Sign Out
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  DASHBOARD
// ══════════════════════════════════════════════════════════════════

function Dashboard({ user, checkIns, journalEntries, setView, setActivePillar, saveCheckIn }) {
  const today = new Date().toISOString().split("T")[0];
  const todayScores = PILLARS.map(p => ({ pillar: p, score: checkIns[`${p.id}_${today}`] || null }));
  const completed = todayScores.filter(t => t.score !== null).length;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const firstName = user?.name?.split(" ")[0];
  const isThrive = user?.plan === "thrive";
  const isBloomOrThrive = user?.plan === "bloom" || user?.plan === "thrive";

  // Load all tracker data
  const [hydrationData, setHydrationData] = useState(null);
  const [restData, setRestData] = useState(null);
  const [upcomingAppts, setUpcomingAppts] = useState([]);
  const [weeklyIntention, setWeeklyIntention] = useState(null);
  const [nourJournal, setNourJournal] = useState([]);
  const [loadingWidgets, setLoadingWidgets] = useState(true);

  useEffect(() => {
    (async () => {
      const [hyd, rest, appts, intention, nour] = await Promise.all([
        isThrive ? safeRetrieve(`gf_hydration_${user?.email}`) : null,
        isThrive ? safeRetrieve(`gf_rest_${user?.email}`) : null,
        isBloomOrThrive ? safeRetrieve(`gf_appointments_${user?.email}`) : null,
        isBloomOrThrive ? safeRetrieve(`gf_weekly_${user?.email}`) : null,
        isThrive ? safeRetrieve(`gf_nourishment_journal_${user?.email}`) : null,
      ]);
      if (hyd) setHydrationData(hyd);
      if (rest) setRestData(rest);
      if (nour) setNourJournal(nour || []);
      if (appts) {
        const now = new Date();
        const todayStr = now.toISOString().split("T")[0];
        const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split("T")[0];
        setUpcomingAppts((appts || []).filter(a => a.date === todayStr || a.date === tomorrowStr).sort((a,b) => a.date.localeCompare(b.date)));
      }
      if (intention) {
        const weekKey = (() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().split("T")[0]; })();
        setWeeklyIntention(intention[weekKey] || null);
      }
      setLoadingWidgets(false);
    })();
  }, [user]);

  // Derived stats
  const todayHydration = hydrationData?.[today] || null;
  const hydGoal = todayHydration?.goal || 8;
  const hydGlasses = todayHydration?.glasses || 0;
  const hydPct = Math.min(100, Math.round((hydGlasses / hydGoal) * 100));

  const todayRest = restData?.[today] || null;

  // 7-day pillar trends
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (6 - i));
    const key = d.toISOString().split("T")[0];
    const dayScores = PILLARS.map(p => checkIns[`${p.id}_${key}`] || null);
    const avg = dayScores.filter(Boolean).length > 0
      ? dayScores.filter(Boolean).reduce((a,b) => a+b, 0) / dayScores.filter(Boolean).length
      : null;
    return { label: d.toLocaleDateString("en", { weekday:"short" }), key, avg, isToday: key === today };
  });

  const overallAvg = last7Days.filter(d => d.avg).length > 0
    ? (last7Days.filter(d => d.avg).reduce((a,b) => a + b.avg, 0) / last7Days.filter(d => d.avg).length).toFixed(1)
    : null;

  // Wellness score color
  const scoreColor = (score) => {
    if (!score) return C.muted;
    if (score >= 7) return "#27AE60";
    if (score >= 5) return C.gold;
    return C.terracotta;
  };

  const recentEntries = (journalEntries || []).slice(0, 2);

  return (
    <div style={S.page}>

      {/* ── FREE PLAN BANNER ── */}
      {user?.plan === "free" && (
        <div style={{ background:`linear-gradient(135deg, ${C.gold}18, ${C.gold}08)`, border:`1.5px solid ${C.gold}44`,
          borderRadius:14, padding:"14px 18px", marginBottom:18, display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
          <Sparkles size={20} color={C.gold} style={{ flexShrink:0 }} />
          <div style={{ flex:"1 1 260px" }}>
            <div style={{ fontWeight:500, fontSize:14, color: C.warm, marginBottom:2 }}>You're on Seedling, our free plan</div>
            <div style={{ fontSize:13, color: C.muted, lineHeight:1.6 }}>
              Your entries are saved on this device only. Upgrade to back them up, see your full history, and unlock your healing guide and 90-day roadmap.
            </div>
          </div>
          <button onClick={() => startUpgradeCheckout("bloom")} className="btn-hover"
            style={{ ...S.btnPrimary, fontSize:13, padding:"10px 18px", whiteSpace:"nowrap" }}>
            {user?.hadTrial ? "Upgrade to Bloom" : "Try Bloom free for 14 days"}
          </button>
        </div>
      )}

      {/* ── APPOINTMENT BANNER ── */}
      {upcomingAppts.length > 0 && upcomingAppts.map(appt => {
        const isToday2 = appt.date === today;
        return (
          <div key={appt.id} onClick={() => setView("appointments")}
            style={{ background: isToday2 ? `linear-gradient(135deg,#FDE8E3,#FBF5EC)` : `linear-gradient(135deg,#E3EEE2,#F0F5EF)`,
              border:`1.5px solid ${isToday2 ? "#C4614B55" : "#4A674155"}`,
              borderRadius:14, padding:"12px 18px", marginBottom:14, cursor:"pointer",
              display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            {isToday2 ? <Bell size={22} color="#C4614B" /> : <CalendarDays size={22} color="#4A6741" />}
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:500, fontSize:14, color: isToday2 ? "#8B3C2B" : "#2D4228" }}>
                {isToday2 ? "You have an appointment today" : "Appointment tomorrow"}
              </div>
              <div style={{ fontSize:13, color: isToday2 ? C.terracotta : "#4A6741" }}>
                {appt.type}{appt.providerName ? ` · ${appt.providerName}` : ""}{appt.time ? ` · ${appt.time}` : ""}
              </div>
            </div>
            <span style={{ fontSize:12, color: C.muted }}>View →</span>
          </div>
        );
      })}

      {/* ── GREETING ── */}
      <div style={{ marginBottom:32 }}>
        <p style={{ color: C.muted, fontSize:14, marginBottom:4 }}>
          {new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}
        </p>
        <h1 className="rt-sm" style={{ fontFamily:"Cormorant Garamond, serif", fontSize:44, fontWeight:400, color: C.warm, lineHeight:1.1 }}>
          {greeting},<br /><em style={{ color: C.gold }}>{firstName}</em>
        </h1>
        {weeklyIntention && (
          <div style={{ marginTop:14, padding:"12px 18px", background:`${C.gold}10`,
            border:`1px solid ${C.gold}33`, borderRadius:12, display:"inline-flex", gap:10, alignItems:"flex-start", maxWidth:500 }}>
            <Sun size={16} color={C.gold} style={{ flexShrink:0 }} />
            <div>
              <div style={{ fontSize:11, letterSpacing:"0.8px", color: C.gold, marginBottom:3 }}>THIS WEEK'S INTENTION</div>
              <div style={{ fontSize:14, color: C.warm, fontStyle:"italic", lineHeight:1.6 }}>"{weeklyIntention}"</div>
            </div>
          </div>
        )}
      </div>

      {/* ── TODAY AT A GLANCE ROW ── */}
      <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, fontWeight:400, marginBottom:16, color: C.warm }}>
        Today at a Glance
      </h2>
      <div className="rg-3col" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:32 }}>

        {/* Pillar check-ins */}
        <div style={{ ...S.card, padding:"18px 20px", cursor:"pointer" }} onClick={() => {}}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, fontWeight:500, color: C.warm }}><Activity size={14} color={C.gold} />Pillar Check-Ins</div>
            <div style={{ fontSize:12, color: C.gold, fontWeight:500 }}>{completed}/5</div>
          </div>
          <div style={{ height:6, background: C.border, borderRadius:6, overflow:"hidden", marginBottom:12 }}>
            <div style={{ height:"100%", background:`linear-gradient(90deg,${C.gold},${C.terracotta})`,
              width:`${(completed/5)*100}%`, borderRadius:6, transition:"width 0.5s ease" }} />
          </div>
          <div style={{ display:"flex", gap:6, justifyContent:"space-between" }}>
            {todayScores.map(({ pillar, score }) => (
              <div key={pillar.id} onClick={(e) => { e.stopPropagation(); setActivePillar(pillar); setView("pillar"); }}
                style={{ flex:1, textAlign:"center", cursor:"pointer" }}>
                <div style={{ width:28, height:28, borderRadius:"50%", margin:"0 auto 4px",
                  background: score !== null ? pillar.light : C.border,
                  border:`2px solid ${score !== null ? pillar.color : C.border}`,
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {score !== null && pillar.Icon
                    ? <pillar.Icon size={13} color={pillar.color} />
                    : <span style={{ fontSize:10, color:C.muted }}>·</span>}
                </div>
                {score !== null && <div style={{ fontSize:10, color: pillar.color }}>{score}/10</div>}
              </div>
            ))}
          </div>
          {completed < 5 && (
            <div style={{ marginTop:12, fontSize:12, color: C.muted, textAlign:"center" }}>
              {5 - completed} check-in{completed === 4 ? "" : "s"} remaining →
            </div>
          )}
          {completed === 5 && (
            <div style={{ marginTop:12, fontSize:12, color:"#27AE60", textAlign:"center" }}>
              ✓ All pillars checked in today!
            </div>
          )}
        </div>

        {/* Hydration */}
        {isThrive ? (
          <div style={{ ...S.card, padding:"18px 20px", cursor:"pointer" }} onClick={() => setView("hydration")}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, fontWeight:500, color: C.warm }}><Droplets size={14} color="#5B9BD5" />Hydration</div>
              <div style={{ fontSize:12, color:"#5B9BD5", fontWeight:500 }}>{hydGlasses}/{hydGoal} glasses</div>
            </div>
            <div style={{ height:6, background:"#E8F4FD", borderRadius:6, overflow:"hidden", marginBottom:12 }}>
              <div style={{ height:"100%", background:`linear-gradient(90deg,#7EC8E3,#5B9BD5)`,
                width:`${hydPct}%`, borderRadius:6, transition:"width 0.5s ease" }} />
            </div>
            <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:8 }}>
              {Array.from({ length: hydGoal }).map((_, i) => (
                <div key={i} style={{ width:`calc(${100/hydGoal}% - 4px)`, height:20, borderRadius:4,
                  background: i < hydGlasses ? "#7EC8E3" : "#E8F4FD",
                  border:`1px solid ${i < hydGlasses ? "#5B9BD5" : "#C8E8F5"}`,
                  transition:"background 0.2s" }} />
              ))}
            </div>
            <div style={{ fontSize:12, color: C.muted }}>
              {hydPct >= 100 ? "Goal reached today!" : `${hydGlasses * 8} oz · ${hydPct}% of goal`}
            </div>
          </div>
        ) : (
          <div style={{ ...S.card, padding:"18px 20px", background:`${C.border}44`, cursor:"pointer" }}
            onClick={() => setView("hydration")}>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, fontWeight:500, color: C.muted, marginBottom:8 }}><Droplets size={13} />Hydration</div>
            <div style={{ fontSize:12, color: C.muted, lineHeight:1.7 }}>Track your daily water intake with Thrive</div>
            <div style={{ marginTop:10, fontSize:11, color: C.gold }}>Upgrade to unlock →</div>
          </div>
        )}

        {/* Rest */}
        {isThrive ? (
          <div style={{ ...S.card, padding:"18px 20px", cursor:"pointer" }} onClick={() => setView("rest")}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, fontWeight:500, color: C.warm }}><Moon size={14} color="#7A6BA0" />Rest</div>
              {todayRest?.sleepHours && (
                <div style={{ fontSize:12, color:"#7A6BA0", fontWeight:500 }}>{todayRest.sleepHours}h slept</div>
              )}
            </div>
            {todayRest ? (
              <div style={{ display:"grid", gap:8 }}>
                {todayRest.sleepHours && (
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ flex:1, height:6, background:"#EDE9F6", borderRadius:6, overflow:"hidden" }}>
                      <div style={{ height:"100%", background:`linear-gradient(90deg,#A78BCA,#7A6BA0)`,
                        width:`${Math.min(100,(parseFloat(todayRest.sleepHours)/9)*100)}%`, borderRadius:6 }} />
                    </div>
                    <span style={{ fontSize:10, color:"#7A6BA0" }}>{parseFloat(todayRest.sleepHours) >= 7 ? "✓" : ""}</span>
                  </div>
                )}
                {todayRest.sleepQuality > 0 && (
                  <div style={{ fontSize:13, color: C.muted }}>
                    Quality: {["","Poor","Fair","Okay","Good","Great"][todayRest.sleepQuality]}
                    {["","Poor","Fair","Okay","Good","Great"][todayRest.sleepQuality]}
                  </div>
                )}
                {todayRest.activities?.length > 0 && (
                  <div style={{ fontSize:12, color: C.muted }}>
                    {todayRest.activities.length} relaxation activit{todayRest.activities.length === 1 ? "y" : "ies"} logged
                  </div>
                )}
                {!todayRest.sleepHours && !todayRest.sleepQuality && (
                  <div style={{ fontSize:12, color: C.muted }}>Tap to log today's rest →</div>
                )}
              </div>
            ) : (
              <div style={{ fontSize:12, color: C.muted, lineHeight:1.7 }}>
                No rest data for today yet.<br />
                <span style={{ color:"#7A6BA0" }}>Tap to log your sleep →</span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ ...S.card, padding:"18px 20px", background:`${C.border}44`, cursor:"pointer" }}
            onClick={() => setView("rest")}>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, fontWeight:500, color: C.muted, marginBottom:8 }}><Moon size={13} />Rest & Relaxation</div>
            <div style={{ fontSize:12, color: C.muted, lineHeight:1.7 }}>Track sleep and relaxation with Thrive</div>
            <div style={{ marginTop:10, fontSize:11, color: C.gold }}>Upgrade to unlock →</div>
          </div>
        )}
      </div>

      {/* ── 7-DAY WELLNESS TREND ── */}
      <div style={{ ...S.card, marginBottom:28 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, fontWeight:400 }}>7-Day Wellness Trend</h3>
          {overallAvg && (
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:12, color: C.muted }}>weekly avg</span>
              <span style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, color: scoreColor(parseFloat(overallAvg)) }}>
                {overallAvg}
              </span>
            </div>
          )}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:6, alignItems:"flex-end", height:80 }}>
          {last7Days.map((day) => (
            <div key={day.key} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
              <div style={{ fontSize:10, color: C.muted, marginBottom:2 }}>
                {day.avg ? day.avg.toFixed(1) : "·"}
              </div>
              <div style={{ width:"100%", borderRadius:"4px 4px 0 0",
                height: day.avg ? `${Math.max(8, (day.avg/10)*56)}px` : "4px",
                background: day.avg
                  ? day.isToday
                    ? `linear-gradient(180deg,${C.gold},${C.terracotta})`
                    : `${scoreColor(day.avg)}66`
                  : C.border,
                transition:"height 0.4s ease", border: day.isToday ? `1px solid ${C.gold}` : "none" }} />
              <div style={{ fontSize:10, color: day.isToday ? C.gold : C.muted, fontWeight: day.isToday ? 600 : 400 }}>
                {day.label}
              </div>
            </div>
          ))}
        </div>
        {last7Days.every(d => !d.avg) && (
          <p style={{ textAlign:"center", color: C.muted, fontSize:13, marginTop:12 }}>
            Complete daily pillar check-ins to see your trend →
          </p>
        )}
      </div>

      {/* ── FIVE PILLARS GRID ── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, fontWeight:400 }}>Your Five Pillars</h2>
        <button onClick={() => setView("progress")} style={{ ...S.btnGhost, fontSize:12 }}>Full Progress →</button>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:14, marginBottom:32 }}>
        {PILLARS.map(pillar => {
          const score = checkIns[`${pillar.id}_${today}`];
          // 7-day avg for this pillar
          const pillarWeekAvg = (() => {
            const vals = last7Days.map(d => checkIns[`${pillar.id}_${d.key}`]).filter(Boolean);
            return vals.length ? (vals.reduce((a,b) => a+b,0)/vals.length).toFixed(1) : null;
          })();
          return (
            <div key={pillar.id} onClick={() => { setActivePillar(pillar); setView("pillar"); }}
              className="card-hover"
              style={{ ...S.card, background: pillar.light, border:`1px solid ${pillar.color}22`,
                padding:"20px 18px", cursor:"pointer" }}>
              <div style={{ width:36, height:36, borderRadius:10, background: pillar.light,
                display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 }}>
                {pillar.Icon && <pillar.Icon size={20} color={pillar.color} />}
              </div>
              <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, color: pillar.dark,
                fontWeight:500, marginBottom:3 }}>{pillar.short}</h3>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8 }}>
                <div style={{ flex:1, height:4, background:`${pillar.color}25`, borderRadius:4, overflow:"hidden" }}>
                  <div style={{ height:"100%", background: pillar.color, borderRadius:4,
                    width: score ? `${score*10}%` : "0%", transition:"width 0.5s" }} />
                </div>
                <span style={{ fontSize:11, color: pillar.color, fontWeight:500, minWidth:24 }}>
                  {score ? `${score}/10` : "·"}
                </span>
              </div>
              {pillarWeekAvg && (
                <div style={{ fontSize:11, color: C.muted, marginTop:6 }}>
                  7-day avg: <span style={{ color: scoreColor(parseFloat(pillarWeekAvg)) }}>{pillarWeekAvg}</span>
                </div>
              )}
              {!score && <div style={{ fontSize:11, color: pillar.color, opacity:0.7, marginTop:8 }}>Tap to check in →</div>}
            </div>
          );
        })}
      </div>

      {/* ── BOTTOM ROW: Journal + Nourishment snippets ── */}
      <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:8 }}>

        {/* Recent journal */}
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, fontWeight:400 }}>Recent Journal</h3>
            <button onClick={() => setView("journal")} style={{ ...S.btnGhost, fontSize:11 }}>View All</button>
          </div>
          {recentEntries.length > 0 ? (
            <div style={{ display:"grid", gap:10 }}>
              {recentEntries.map((entry, i) => {
                const pillar = PILLARS.find(p => p.id === entry.pillarId);
                return (
                  <div key={i} style={{ ...S.card, padding:"14px 18px", cursor:"pointer" }}
                    onClick={() => setView("journal")}>
                    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:6 }}>
                      {pillar && <span style={{ ...S.tag, background: pillar.light, color: pillar.color, fontSize:10 }}>
                        <PillarIcon pillar={pillar} size={12} /> {pillar.short}
                      </span>}
                      <span style={{ fontSize:11, color: C.muted, marginLeft:"auto" }}>
                        {new Date(entry.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                      </span>
                    </div>
                    <p style={{ fontSize:13, color: C.warm, lineHeight:1.6 }}>
                      {entry.content.length > 100 ? entry.content.slice(0,100) + "…" : entry.content}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ ...S.card, padding:"24px 18px", textAlign:"center" }}>
              <p style={{ fontSize:13, color: C.muted, lineHeight:1.7 }}>
                Your journal entries will appear here.<br />
                <span style={{ color: C.gold, cursor:"pointer" }} onClick={() => setView("journal")}>Start writing →</span>
              </p>
            </div>
          )}
        </div>

        {/* Nourishment / rest snapshot */}
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, fontWeight:400 }}>
              {isThrive ? "Nourishment" : "Wellness Snapshot"}
            </h3>
            {isThrive && <button onClick={() => setView("nourishment")} style={{ ...S.btnGhost, fontSize:11 }}>Open →</button>}
          </div>
          {isThrive ? (
            <div style={{ display:"grid", gap:10 }}>
              {nourJournal.length > 0 ? (
                nourJournal.slice(0,2).map((entry, i) => (
                  <div key={i} style={{ ...S.card, padding:"14px 18px", cursor:"pointer" }}
                    onClick={() => setView("nourishment")}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <div style={{ fontSize:13, fontWeight:500, color: C.warm }}>{entry.food}</div>
                      <div style={{ fontSize:11, color: C.muted }}>
                        {new Date(entry.date).toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                      </div>
                    </div>
                    {entry.insight && (
                      <p style={{ fontSize:12, color: C.gold, fontStyle:"italic", lineHeight:1.6 }}>{entry.insight.slice(0,80)}{entry.insight.length>80?"…":""}</p>
                    )}
                    {!entry.insight && entry.after && (
                      <p style={{ fontSize:12, color: C.muted, lineHeight:1.6 }}>{entry.after.slice(0,80)}{entry.after.length>80?"…":""}</p>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ ...S.card, padding:"24px 18px", textAlign:"center" }}>
                  <p style={{ fontSize:13, color: C.muted, lineHeight:1.7 }}>
                    Explore your relationship with food.<br />
                    <span style={{ color: C.gold, cursor:"pointer" }} onClick={() => setView("nourishment")}>Open Nourishment Guide →</span>
                  </p>
                </div>
              )}
              {/* Quick rest recap */}
              {todayRest?.note && (
                <div style={{ ...S.card, padding:"14px 18px", background:`linear-gradient(135deg,#EDE9F6,#F5F3FA)`,
                  border:`1px solid #C8C0E0` }}>
                  <div style={{ fontSize:11, color:"#7A6BA0", marginBottom:4 }}>Today's rest reflection</div>
                  <p style={{ fontSize:13, color:"#4A3D70", fontStyle:"italic", lineHeight:1.6 }}>
                    "{todayRest.note.slice(0,100)}{todayRest.note.length>100?"…":""}"
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div style={{ ...S.card, padding:"24px 18px" }}>
              <div style={{ display:"grid", gap:10 }}>
                {[
                  { Icon:Sparkles, label:"Healing Guidance", view:"guidance", locked:!isBloomOrThrive },
                  { Icon:Target, label:"Pillar Goals", view:"goals", locked:!isBloomOrThrive },
                  { Icon:Wallet, label:"Self-Care Budget", view:"budget", locked:!isBloomOrThrive },
                ].map(item => (
                  <div key={item.view} onClick={() => !item.locked && setView(item.view)}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0",
                      borderBottom:`1px solid ${C.border}`, cursor: item.locked ? "default" : "pointer" }}>
                    {item.Icon && <item.Icon size={16} color={item.locked ? C.muted : C.warm} style={{flexShrink:0}} />}
                    <span style={{ fontSize:13, color: item.locked ? C.muted : C.warm }}>{item.label}</span>
                    {item.locked
                      ? <span style={{ marginLeft:"auto", fontSize:10, color: C.gold, display:"flex", alignItems:"center", gap:3 }}><Lock size={9} />Bloom+</span>
                      : <span style={{ marginLeft:"auto", fontSize:11, color: C.muted }}>→</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PILLAR VIEW
// ══════════════════════════════════════════════════════════════════

function PillarView({ pillar, checkIns, saveCheckIn, saveJournalEntry, user, setView }) {
  const [activeTab, setActiveTab] = useState("checkin");
  const [score, setScore] = useState(5);
  const [journalText, setJournalText] = useState("");
  const [activePrompt, setActivePrompt] = useState(0);
  const [learnExpanded, setLearnExpanded] = useState(null);
  const [saved, setSaved] = useState(false);
  const [journalSaved, setJournalSaved] = useState(false);
  const [activeActivity, setActiveActivity] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const todayScore = checkIns[`${pillar.id}_${today}`];
  const tabs = ["checkin", "journal", "activities", "learn"];
  const tabLabels = { checkin:"Check-In", journal:"Journal", activities:"Activities", learn:"Learn" };

  const handleSaveCheckin = async () => {
    await saveCheckIn(pillar.id, score);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleSaveJournal = async () => {
    if (!journalText.trim()) return;
    await saveJournalEntry({
      pillarId: pillar.id,
      promptIndex: activePrompt,
      prompt: pillar.prompts[activePrompt],
      content: journalText,
      createdAt: Date.now()
    });
    setJournalText("");
    setJournalSaved(true);
    setTimeout(() => setJournalSaved(false), 6000);
  };

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ background:`linear-gradient(135deg, ${pillar.color}, ${pillar.dark})`,
        borderRadius:20, padding:"32px 36px", marginBottom:28, color:"#fff",
        boxShadow:`0 8px 32px ${pillar.color}55` }}>
        <div style={{ marginBottom:12 }}><PillarIcon pillar={pillar} size={48} /></div>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:40, fontWeight:400, marginBottom:6 }}>{pillar.name}</h1>
        <p style={{ opacity:0.85, fontSize:16, maxWidth:500, lineHeight:1.6 }}>{pillar.description}</p>
        {todayScore !== undefined && (
          <div style={{ marginTop:18, display:"inline-flex", alignItems:"center", gap:10,
            background:"rgba(255,255,255,0.15)", borderRadius:24, padding:"8px 18px" }}>
            <span style={{ fontSize:13 }}>Today's check-in:</span>
            <span style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22 }}>{todayScore}/10</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:28, background: C.white, borderRadius:14,
        padding:6, border:`1px solid ${C.border}`, width:"fit-content" }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ padding:"10px 20px", borderRadius:10, border:"none", cursor:"pointer", fontSize:14, fontWeight:500,
              background: activeTab === tab ? pillar.color : "transparent",
              color: activeTab === tab ? "#fff" : C.muted, transition:"all 0.2s" }}>
            {tabLabels[tab]}
          </button>
        ))}
      </div>

      {/* Check-In Tab */}
      {activeTab === "checkin" && (
        <div style={{ ...S.card, maxWidth:520 }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:6, fontWeight:400 }}>
            How is your {pillar.short.toLowerCase()} wellness today?
          </h3>
          <p style={{ color: C.muted, fontSize:14, marginBottom:32 }}>Rate yourself honestly and with compassion. There is no wrong answer.</p>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} onClick={() => setScore(n)}
                style={{ width:42, height:42, borderRadius:"50%", border:"none", cursor:"pointer",
                  background: score >= n ? pillar.color : C.border,
                  color: score >= n ? "#fff" : C.muted, fontSize:14, fontWeight:500, transition:"all 0.15s" }}>
                {n}
              </button>
            ))}
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color: C.muted, marginBottom:32 }}>
            <span>Struggling</span><span>Thriving</span>
          </div>
          <button onClick={handleSaveCheckin} className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${pillar.color}, ${pillar.dark})`, width:"100%" }}>
            {saved ? "Saved" : "Save Check-In"}
          </button>
        </div>
      )}

      {/* Journal Tab */}
      {activeTab === "journal" && (
        <div style={{ ...S.card, maxWidth:640 }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, marginBottom:20, fontWeight:400 }}>Journal Prompts</h3>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:24 }}>
            {pillar.prompts.map((p, i) => (
              <button key={i} onClick={() => setActivePrompt(i)}
                style={{ padding:"6px 14px", borderRadius:20, border:`1.5px solid ${activePrompt===i ? pillar.color : C.border}`,
                  background: activePrompt===i ? pillar.light : "transparent",
                  color: activePrompt===i ? pillar.color : C.muted, fontSize:13, cursor:"pointer", fontWeight:500 }}>
                Prompt {i+1}
              </button>
            ))}
          </div>
          <div style={{ background: pillar.light, borderRadius:12, padding:"18px 20px", marginBottom:20,
            borderLeft:`4px solid ${pillar.color}` }}>
            <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:19, color: pillar.dark, lineHeight:1.7, fontStyle:"italic" }}>
              "{pillar.prompts[activePrompt]}"
            </p>
          </div>
          <textarea value={journalText} onChange={e => setJournalText(e.target.value)}
            placeholder="Write freely. This is your sacred space..."
            style={{ ...S.textarea, minHeight:160 }} />
          <button onClick={handleSaveJournal} disabled={!journalText.trim()} className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${pillar.color}, ${pillar.dark})`,
              marginTop:16, opacity: journalText.trim() ? 1 : 0.5 }}>
            Save Entry
          </button>
          {journalSaved && (
            <div style={{ marginTop:14, background: pillar.light, border:`1.5px solid ${pillar.color}`,
              borderRadius:12, padding:"14px 18px", display:"flex", alignItems:"center",
              justifyContent:"space-between", animation:"fadeUp 0.3s ease" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <CheckCircle size={18} color="#27AE60" />
                <span style={{ fontSize:14, color: pillar.dark, fontWeight:500 }}>Entry saved to your journal!</span>
              </div>
              <button onClick={() => setView("journal")}
                style={{ background: pillar.color, border:"none", color:"#fff",
                  borderRadius:8, padding:"7px 14px", fontSize:13, cursor:"pointer", fontWeight:500 }}>
                View My Journal →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Activities Tab */}
      {activeTab === "activities" && (
        <div style={{ display:"grid", gap:14, maxWidth:640 }}>
          <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, color: C.warm, marginBottom:6 }}>
            Healing Activities for {pillar.short} Wellness
          </p>
          {pillar.activities.map((act, i) => (
            <div key={i} style={{ ...S.card, display:"flex", alignItems:"center", gap:18, padding:"18px 22px" }}>
              <div style={{ width:44, height:44, borderRadius:"50%", background: pillar.light,
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>
                <PillarIcon pillar={pillar} size={20} />
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:500, fontSize:15, marginBottom:2 }}>{act}</div>
                <div style={{ color: C.muted, fontSize:13 }}>Tap to begin guided practice</div>
              </div>
              <button onClick={() => setActiveActivity(act)} className="btn-hover"
                style={{ ...S.btnSecondary, borderColor: pillar.color, color: pillar.color,
                padding:"8px 16px", fontSize:13 }}>
                Begin
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Activity Modal */}
      {activeActivity && (
        <ActivityModal
          activity={activeActivity}
          pillar={pillar}
          onClose={() => setActiveActivity(null)}
        />
      )}

      {/* Learn Tab */}
      {activeTab === "learn" && (
        <div style={{ display:"grid", gap:14, maxWidth:680 }}>
          <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, color: C.warm, marginBottom:6 }}>
            Understanding {pillar.name}
          </p>
          {pillar.education.map((item, i) => (
            <div key={i} style={{ ...S.card }}>
              <button onClick={() => setLearnExpanded(learnExpanded===i ? null : i)}
                style={{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center",
                  background:"none", border:"none", cursor:"pointer", textAlign:"left", padding:0 }}>
                <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                  <div style={{ width:36, height:36, borderRadius:10, background: pillar.light,
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>
                    <PillarIcon pillar={pillar} size={22} />
                  </div>
                  <span style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, color: C.warm }}>{item.title}</span>
                </div>
                <span style={{ color: C.muted, fontSize:18, transform: learnExpanded===i?"rotate(180deg)":"rotate(0)", transition:"transform 0.25s" }}>⌄</span>
              </button>
              {learnExpanded === i && (
                <p style={{ marginTop:18, fontSize:15, color: C.warm, lineHeight:1.85,
                  borderTop:`1px solid ${C.border}`, paddingTop:18, animation:"fadeIn 0.3s ease" }}>
                  {item.body}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  JOURNAL VIEW
// ══════════════════════════════════════════════════════════════════

function JournalView({ entries, saveJournalEntry }) {
  const [pillarFilter, setPillarFilter] = useState("all");
  const [newEntry, setNewEntry] = useState("");
  const [newPillar, setNewPillar] = useState("emotional");

  const filtered = (entries || []).filter(e => pillarFilter === "all" || e.pillarId === pillarFilter);

  const handleSave = async () => {
    if (!newEntry.trim()) return;
    await saveJournalEntry({ pillarId: newPillar, content: newEntry, createdAt: Date.now() });
    setNewEntry("");
  };

  return (
    <div style={S.page}>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>My Journal</h1>
      <p style={{ color: C.muted, fontSize:15, marginBottom:32 }}>Your sacred space to reflect, release, and return to yourself.</p>

      <div style={{ ...S.card, marginBottom:28 }}>
        <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:18 }}>New Entry</h3>
        <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
          {PILLARS.map(p => (
            <button key={p.id} onClick={() => setNewPillar(p.id)}
              style={{ padding:"7px 16px", borderRadius:20, border:`1.5px solid ${newPillar===p.id ? p.color : C.border}`,
                background: newPillar===p.id ? p.light : "transparent",
                color: newPillar===p.id ? p.color : C.muted, fontSize:13, cursor:"pointer",
                display:"flex", alignItems:"center", gap:5 }}>
              <PillarIcon pillar={p} size={13} />{p.short}
            </button>
          ))}
        </div>
        <textarea value={newEntry} onChange={e => setNewEntry(e.target.value)}
          placeholder="What's on your heart today? Write freely..." style={{ ...S.textarea, minHeight:130 }} />
        <button onClick={handleSave} disabled={!newEntry.trim()} className="btn-hover"
          style={{ ...S.btnPrimary, marginTop:14, opacity: newEntry.trim() ? 1 : 0.5 }}>
          Save Entry
        </button>
      </div>

      {/* Filter */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
        <button onClick={() => setPillarFilter("all")}
          style={{ padding:"7px 18px", borderRadius:20, border:`1.5px solid ${pillarFilter==="all" ? C.gold : C.border}`,
            background: pillarFilter==="all" ? `${C.gold}15` : "transparent",
            color: pillarFilter==="all" ? C.gold : C.muted, fontSize:13, cursor:"pointer" }}>
          All Pillars
        </button>
        {PILLARS.map(p => (
          <button key={p.id} onClick={() => setPillarFilter(p.id)}
            style={{ padding:"7px 16px", borderRadius:20, border:`1.5px solid ${pillarFilter===p.id ? p.color : C.border}`,
              background: pillarFilter===p.id ? p.light : "transparent",
              color: pillarFilter===p.id ? p.color : C.muted, fontSize:13, cursor:"pointer",
              display:"flex", alignItems:"center", gap:5 }}>
            <PillarIcon pillar={p} size={13} />{p.short}
          </button>
        ))}
      </div>

      {/* Entries */}
      <div style={{ display:"grid", gap:14 }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 20px", color: C.muted }}>
            <div style={{ marginBottom:12 }}><BookOpen size={40} color={C.gold} /></div>
            <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22 }}>Your journal is waiting for you.</p>
          </div>
        ) : filtered.map((entry, i) => {
          const pillar = PILLARS.find(p => p.id === entry.pillarId);
          return (
            <div key={i} style={{ ...S.card }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                <div style={{ flex:1 }}>
                  {pillar && (
                    <span style={{ ...S.tag, background: pillar.light, color: pillar.color, marginBottom:12, display:"inline-block" }}>
                      <PillarIcon pillar={pillar} size={12} /> {pillar.short}
                    </span>
                  )}
                  {entry.prompt && <p style={{ fontSize:13, color: C.muted, fontStyle:"italic", marginBottom:10 }}>"{entry.prompt}"</p>}
                  <p style={{ fontSize:15, lineHeight:1.8, color: C.warm }}>{entry.content}</p>
                </div>
                <span style={{ color: C.muted, fontSize:12, whiteSpace:"nowrap", flexShrink:0 }}>
                  {new Date(entry.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PROGRESS VIEW
// ══════════════════════════════════════════════════════════════════

function ProgressView({ checkIns, journalEntries, isFree = false }) {
  const DAYS = isFree ? 7 : 14;
  const last14 = Array.from({length:DAYS}, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() - (DAYS-1-i));
    return d.toISOString().split("T")[0];
  });

  const chartData = last14.map(date => {
    const row = { date: date.slice(5) };
    PILLARS.forEach(p => { row[p.short] = checkIns[`${p.id}_${date}`] || null; });
    return row;
  }).filter(r => PILLARS.some(p => r[p.short] !== null));

  const today = new Date().toISOString().split("T")[0];
  const radarData = PILLARS.map(p => ({ pillar: p.short, score: checkIns[`${p.id}_${today}`] || 0 }));
  const avgScores = PILLARS.map(p => {
    const scores = last14.map(d => checkIns[`${p.id}_${d}`]).filter(Boolean);
    return { ...p, avg: scores.length ? Math.round((scores.reduce((a,b)=>a+b,0)/scores.length)*10)/10 : null };
  });
  const journalCount = (journalEntries || []).length;
  const totalCheckins = Object.keys(checkIns).length;
  const streakDays = (() => {
    let s=0;
    for(let i=0;i<30;i++){
      const d=new Date(); d.setDate(d.getDate()-i);
      const ds=d.toISOString().split("T")[0];
      if(PILLARS.some(p=>checkIns[`${p.id}_${ds}`])) s++;
      else break;
    }
    return s;
  })();

  return (
    <div style={S.page}>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>My Progress</h1>
      <p style={{ color: C.muted, fontSize:15, marginBottom:32 }}>Witnessing your journey with compassion and curiosity.</p>

      {/* Stats */}
      <div className="rg-3col" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:28 }}>
        {[
          { label:"Day Streak", value: streakDays, icon:"🔥", sub:"Active days" },
          { label:"Total Check-ins", value: totalCheckins, Icon:Sparkles, sub:"Across all pillars" },
          { label:"Journal Entries", value: journalCount, icon:"📖", sub:"Reflections captured" }
        ].map(s => (
          <div key={s.label} style={{ ...S.card, textAlign:"center", padding:"24px 20px" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>{s.icon}</div>
            <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:40, color: C.warm, fontWeight:400 }}>{s.value}</div>
            <div style={{ fontWeight:500, fontSize:14, marginBottom:3 }}>{s.label}</div>
            <div style={{ color: C.muted, fontSize:12 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Radar + Averages */}
      <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:28 }}>
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:20 }}>Today's Wellness Balance</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={radarData}>
              <PolarGrid stroke={C.border} />
              <PolarAngleAxis dataKey="pillar" tick={{ fill: C.muted, fontSize:12 }} />
              <Radar dataKey="score" stroke={C.gold} fill={C.gold} fillOpacity={0.25} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:20 }}>{DAYS}-Day Pillar Averages</h3>
          <div style={{ display:"grid", gap:14 }}>
            {avgScores.map(p => (
              <div key={p.id}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ display:"flex", alignItems:"center", gap:4 }}><PillarIcon pillar={p} size={13} />{p.short}</span>
                  <span style={{ fontSize:13, color: p.avg ? p.color : C.muted }}>{p.avg !== null ? `${p.avg}/10` : "No data"}</span>
                </div>
                <div style={{ height:6, background: p.light, borderRadius:6, overflow:"hidden" }}>
                  <div style={{ height:"100%", background: p.color, borderRadius:6, width:`${(p.avg||0)*10}%`, transition:"width 0.6s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Line Chart */}
      {chartData.length > 0 && (
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:8 }}>Check-In Trends (Last {DAYS} Days)</h3>
          <p style={{ color: C.muted, fontSize:13, marginBottom:24 }}>Your healing is not linear, and that is perfectly okay.</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <XAxis dataKey="date" tick={{ fill: C.muted, fontSize:11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0,10]} tick={{ fill: C.muted, fontSize:11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: C.white, border:`1px solid ${C.border}`, borderRadius:10, fontSize:13 }} />
              {PILLARS.map(p => (
                <Line key={p.id} type="monotone" dataKey={p.short} stroke={p.color} strokeWidth={2}
                  dot={{ fill: p.color, r:3 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginTop:16 }}>
            {PILLARS.map(p => (
              <div key={p.id} style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ width:12, height:3, background: p.color, borderRadius:2 }} />
                <span style={{ fontSize:12, color: C.muted }}>{p.short}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {chartData.length === 0 && (
        <div style={{ ...S.card, textAlign:"center", padding:"48px 20px" }}>
          <div style={{ marginBottom:14 }}><BarChart2 size={44} color={C.gold} /></div>
          <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:6 }}>Your data story is just beginning.</p>
          <p style={{ color: C.muted, fontSize:14 }}>Complete pillar check-ins to see your progress charts come alive.</p>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  AI GUIDANCE VIEW
// ══════════════════════════════════════════════════════════════════

const PILLAR_STARTERS = {
  emotional: [
    "I keep telling everyone I'm fine, but I'm not",
    "I want to talk about something my family never talks about",
    "I've been eating to cope and I want to understand why",
    "I'm carrying a loss I never really got to grieve",
    "Help me set a boundary without feeling guilty"
  ],
  physical: [
    "I'm exhausted and I don't know how to rest without guilt",
    "I've been putting off a doctor's appointment",
    "Help me get ready to speak up for myself at my next appointment",
    "I love my body and I'm also worried about my health",
    "I want to reconnect with my body after years of pushing through"
  ],
  mental: [
    "I'm tired of always being the strong one",
    "My inner critic has been loud lately",
    "I think I might be dealing with anxiety or depression",
    "I want to understand where my beliefs about myself came from",
    "I'm thinking about therapy but I'm not sure where to start"
  ],
  spiritual: [
    "I want to honor my ancestors while healing what they couldn't",
    "Help me figure out which family patterns I want to end with me",
    "I'm searching for more peace and meaning in my life",
    "My faith matters to me and I'm also struggling",
    "I feel disconnected from myself and need grounding"
  ],
  financial: [
    "I'm always the one my family calls for money",
    "I feel guilty saving for myself when others need help",
    "Help me understand the money messages I grew up with",
    "Money stress is affecting my sleep and my peace",
    "I want to build security for myself without feeling selfish"
  ]
};

function GuidanceView({ user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pillar, setPillar] = useState("emotional");
  const [sessionCount, setSessionCount] = useState(0);
  const [viewMode, setViewMode] = useState("chat"); // chat | history
  const [pastSessions, setPastSessions] = useState([]);
  const [viewingSession, setViewingSession] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const plan = planInfo(user?.plan);
  const hasAccess = plan?.id === "bloom" || plan?.id === "thrive";
  const isThrive = plan?.id === "thrive";
  const SESSION_LIMIT = 20;
  const monthKey = new Date().toISOString().slice(0, 7);

  // Load session count and past sessions
  useEffect(() => {
    const load = async () => {
      const count = await retrieve(`gf_aisessions_${user?.email}_${monthKey}`);
      setSessionCount(count || 0);
      const history = await retrieve(`gf_ai_history_${user?.email}`);
      setPastSessions(history || []);
    };
    load();
  }, [user]);

  const atLimit = !isThrive && sessionCount >= SESSION_LIMIT;

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior:"smooth" });
  }, [messages]);

  // Save session whenever messages update and there are at least 2 messages
  useEffect(() => {
    const saveSession = async () => {
      if (messages.length < 2) return;
      const sessionId = currentSessionId || `${user?.email}_${Date.now()}`;
      if (!currentSessionId) setCurrentSessionId(sessionId);

      const title = messages[0]?.content?.slice(0, 60) + (messages[0]?.content?.length > 60 ? "..." : "");
      const session = {
        id: sessionId,
        pillar,
        pillarName: PILLARS.find(p => p.id === pillar)?.name || pillar,
        pillarIcon: PILLARS.find(p => p.id === pillar)?.short || "",
        title,
        messages,
        savedAt: Date.now(),
        messageCount: messages.length
      };

      const history = await retrieve(`gf_ai_history_${user?.email}`) || [];
      const existing = history.findIndex(s => s.id === sessionId);
      let updated;
      if (existing >= 0) {
        updated = [...history];
        updated[existing] = session;
      } else {
        updated = [session, ...history];
      }
      // Keep last 50 sessions
      updated = updated.slice(0, 50);
      await store(`gf_ai_history_${user?.email}`, updated);
      setPastSessions(updated);
    };
    saveSession();
  }, [messages]);

  // Reset chat when pillar changes
  useEffect(() => {
    if (messages.length > 0) setCurrentSessionId(null);
    setMessages([]);
    setInput("");
  }, [pillar]);

  const startNewSession = () => {
    setCurrentSessionId(null);
    setMessages([]);
    setInput("");
    setViewMode("chat");
  };

  const systemPrompt = `You are the ThriveRoot guide, part of Glow & Flow Holistics: a warm, trauma-informed, non-clinical wellness companion for Black women carrying stress, burnout, emotional eating, and the weight of generational patterns. Your purpose is to help each woman move from surviving to thriving, and to stop passing down what hurts while keeping what heals. You draw on the five pillars of Glow & Flow: Emotional, Physical, Mental, Spiritual, and Financial wellness, and on the ideas in Delicia Straughter's book "Breaking Generational Health Myths in the Black Community."

Who you are speaking with:
- A Black woman, often between 30 and 65, who is likely "the strong one" for many people: a mother, daughter, grandmother, caregiver, professional, church member, or family provider.
- She may have been raised on survival wisdom: push through pain, don't complain, keep family business in the house, rest when the work is done, food is love.
- Speak to her as a trusted sister-friend who also knows the research: warm, plain-spoken, never preachy, never clinical, and never stereotyping. Black women are not a monolith; follow her lead about her own life, culture, region, faith, and family.

How you help:
- Honor where the pattern came from before inviting change. Many inherited habits once kept families alive. Ask gently: what was this protecting you from, and is that threat still here in the same way?
- Name patterns the book explores when they fit: the Strong Black Woman or Superwoman script, silent suffering, rest that has to be "earned," burnout disguised as hard work, comfort food and emotional eating, medical mistrust, body pride alongside honest health concerns, being the family provider, rushed grief, colorism, and caregiving without support.
- Validate without toxic positivity. It is okay not to be okay.
- Offer one small, doable next step rather than a long list. Small steps are better than no steps.
- Respect faith. Many women find strength in church and prayer; support that, and gently note that faith and professional help can work together.
- Acknowledge that medical mistrust has real historical roots, and encourage her to seek care anyway, with tools like writing questions down and bringing a support person.
- Encourage connection: a trusted person, a sister circle, a therapist. Healing is not meant to be done alone.

Food and body:
- Never shame, moralize, or push dieting, calorie counting, or weight loss. Honor cultural foods and the love they carry.
- Body pride and health goals can coexist. If she raises health concerns such as blood pressure or blood sugar, support her in talking with her doctor and focus on how she feels, not on a number.
- Avoid words like "guilty," "cheat," "bad food," or "clean eating."

Safety and boundaries:
- You are not a therapist, doctor, or financial advisor, and you do not diagnose. When something sounds serious or ongoing, encourage professional support warmly (for example, Therapy for Black Girls or Inclusive Therapists for therapy, or her doctor for physical symptoms).
- If she mentions thoughts of suicide or self-harm, or seems to be in crisis or danger, respond with care, encourage her to call or text 988 (the Suicide and Crisis Lifeline) or 911 right away, and encourage her to reach out to someone she trusts. If she mentions disordered eating, gently encourage support from a professional.
- Keep responses warm and readable: short paragraphs, no heavy formatting, and never use em dashes.

She is currently focusing on: ${PILLARS.find(p=>p.id===pillar)?.name}. Begin and stay with that thread unless she redirects.`;

  const sendMessage = async () => {
    if (!input.trim() || loading || atLimit) return;
    const userMsg = { role:"user", content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    if (!isThrive) {
      const newCount = sessionCount + 1;
      setSessionCount(newCount);
      await store(`gf_aisessions_${user?.email}_${monthKey}`, newCount);
    }
    try {
      const response = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: systemPrompt,
          messages: [...messages, userMsg].map(m => ({ role: m.role, content: m.content }))
        })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const reply = data.reply || "I am here with you. Could you tell me more?";
      setMessages(prev => [...prev, { role:"assistant", content: reply }]);
    } catch(e) {
      setMessages(prev => [...prev, { role:"assistant", content:"Something went gently wrong on my end. Please try again. I am still here. 🌸" }]);
    }
    setLoading(false);
  };

  const starters = PILLAR_STARTERS[pillar] || PILLAR_STARTERS.emotional;

  // ── PAST SESSION VIEWER ──
  if (viewingSession) {
    const pillarData = PILLARS.find(p => p.id === viewingSession.pillar);
    return (
      <div style={S.page}>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:24, flexWrap:"wrap" }}>
          <button onClick={() => setViewingSession(null)}
            style={{ ...S.btnGhost, fontSize:13, padding:"8px 14px" }}>
            ← Back to History
          </button>
          <div>
            <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, fontWeight:400, margin:0, marginBottom:2 }}>
              {viewingSession.pillarIcon} {viewingSession.pillarName} Session
            </h2>
            <p style={{ color: C.muted, fontSize:13, margin:0 }}>
              {new Date(viewingSession.savedAt).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})} · {viewingSession.messageCount} messages
            </p>
          </div>
        </div>

        <div style={{ ...S.card, maxWidth:720, padding:0, overflow:"hidden" }}>
          <div style={{ padding:"16px 22px", borderBottom:`1px solid ${C.border}`,
            background: pillarData?.light || C.cream, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>{viewingSession.pillarIcon}</span>
            <span style={{ fontWeight:500, color: pillarData?.dark || C.warm }}>
              {viewingSession.pillarName} Healing Session
            </span>
          </div>
          <div style={{ padding:"24px 22px", display:"flex", flexDirection:"column", gap:16, maxHeight:"65vh", overflowY:"auto" }}>
            {viewingSession.messages.map((msg, i) => (
              <div key={i} style={{ display:"flex", justifyContent: msg.role==="user" ? "flex-end" : "flex-start", flexDirection:"column",
                alignItems: msg.role==="user" ? "flex-end" : "flex-start", gap:4 }}>
                <div style={{ fontSize:11, color: C.muted, paddingLeft:4, paddingRight:4 }}>
                  {msg.role === "user" ? "You" : "Healing Guide"}
                </div>
                <div style={{
                  maxWidth:"78%", padding:"13px 18px", borderRadius:18,
                  borderBottomRightRadius: msg.role==="user" ? 4 : 18,
                  borderBottomLeftRadius: msg.role==="assistant" ? 4 : 18,
                  background: msg.role==="user" ? `linear-gradient(135deg, ${C.gold}, #B8821F)` : C.cream,
                  color: msg.role==="user" ? "#fff" : C.warm,
                  fontSize:14, lineHeight:1.8
                }}>
                  {msg.content}
                </div>
              </div>
            ))}
          </div>
          <div style={{ padding:"16px 22px", borderTop:`1px solid ${C.border}`, background: C.cream }}>
            <button onClick={() => { setViewingSession(null); setViewMode("chat"); setPillar(viewingSession.pillar); }}
              style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, fontSize:13 }}>
              Continue This Conversation →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>Healing Guidance</h1>
          <p style={{ color: C.muted, fontSize:15 }}>A compassionate, trauma-informed space for reflection and healing support.</p>
        </div>
        {hasAccess && (
          <div style={{ display:"flex", gap:8, background: C.cream, borderRadius:12, padding:5 }}>
            {["chat","history"].map(m => (
              <button key={m} onClick={() => setViewMode(m)}
                style={{ padding:"9px 18px", borderRadius:9, border:"none", cursor:"pointer", fontSize:13, fontWeight:500,
                  background: viewMode===m ? C.warm : "transparent",
                  color: viewMode===m ? "#FBF5EC" : C.muted, transition:"all 0.2s",
                  textTransform:"capitalize" }}>
                {m === "chat" ? "New Session" : `History (${pastSessions.length})`}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Session counter for Bloom */}
      {hasAccess && !isThrive && (
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20,
          background: atLimit ? "#FDE8E3" : `${C.gold}12`,
          border:`1px solid ${atLimit ? "#C4614B44" : C.gold+"33"}`,
          borderRadius:12, padding:"12px 18px", maxWidth:700 }}>
          <span style={{ fontSize:16 }}>{atLimit ? "!" : "·"}</span>
          <div style={{ flex:1 }}>
            <span style={{ fontSize:14, color: atLimit ? "#8B3C2B" : C.warm, fontWeight:500 }}>
              {atLimit ? "You have used all 20 guided sessions this month."
                : `${sessionCount} of ${SESSION_LIMIT} sessions used this month`}
            </span>
            {atLimit && <div style={{ fontSize:12, color:"#C4614B", marginTop:2 }}>Sessions reset on the 1st. Upgrade to Thrive for unlimited access.</div>}
          </div>
          {!atLimit && <div style={{ fontSize:12, color: C.muted }}>{SESSION_LIMIT - sessionCount} remaining</div>}
        </div>
      )}

      {!hasAccess ? (
        <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"0 auto" }}>
          <div style={{ marginBottom:16 }}><BrandButterfly size={48} color={C.gold} /></div>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Upgrade to Access Healing Guidance</h3>
          <p style={{ color: C.muted, fontSize:14, lineHeight:1.7, marginBottom:24 }}>
            Healing Guidance is available on the Bloom and Thrive plans. Upgrade to access compassionate, trauma-informed personalized support.
          </p>
          <div style={{ ...S.tag, background:`${C.gold}20`, color: C.gold, fontSize:14, padding:"8px 20px" }}>
            Current plan: {plan?.name || "Root"} · <span style={{ color: C.gold, cursor:"pointer", textDecoration:"underline" }}
              onClick={() => startUpgradeCheckout("bloom", user?.email, user?.name)}>Upgrade to Bloom ($39/mo)</span>
          </div>
        </div>

      ) : viewMode === "history" ? (
        /* ── HISTORY VIEW ── */
        <div style={{ maxWidth:720 }}>
          {pastSessions.length === 0 ? (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><Heart size={40} color="#C4614B" /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:8 }}>No sessions yet</p>
              <p style={{ color: C.muted, fontSize:14, marginBottom:20 }}>Your healing conversations will be saved here automatically as you have them.</p>
              <button onClick={() => setViewMode("chat")} className="btn-hover"
                style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)` }}>
                Start Your First Session →
              </button>
            </div>
          ) : (
            <div style={{ display:"grid", gap:12 }}>
              {pastSessions.map((session, i) => {
                const pData = PILLARS.find(p => p.id === session.pillar);
                return (
                  <div key={i} onClick={() => setViewingSession(session)}
                    style={{ ...S.card, cursor:"pointer", transition:"all 0.2s",
                      borderLeft:`4px solid ${pData?.color || C.gold}`,
                      display:"flex", alignItems:"center", gap:16, padding:"18px 20px" }}
                    className="btn-hover">
                    <div style={{ width:44, height:44, borderRadius:"50%", flexShrink:0,
                      background: pData?.light || C.cream,
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>
                      {session.pillarIcon}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:500, fontSize:14, color: C.warm, marginBottom:4,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {session.title}
                      </div>
                      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
                        <span style={{ fontSize:11, background: pData?.light || C.cream,
                          color: pData?.dark || C.muted, padding:"2px 10px", borderRadius:20 }}>
                          {session.pillarName}
                        </span>
                        <span style={{ fontSize:12, color: C.muted }}>
                          {new Date(session.savedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                        </span>
                        <span style={{ fontSize:12, color: C.muted }}>
                          {session.messageCount} messages
                        </span>
                      </div>
                    </div>
                    <span style={{ color: C.muted, fontSize:18, flexShrink:0 }}>›</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      ) : (
        /* ── CHAT VIEW ── */
        <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"200px 1fr", gap:16, minHeight:"calc(100vh - 280px)" }}>
          {/* Pillar selector */}
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <p style={{ fontSize:12, letterSpacing:"1px", color: C.muted, marginBottom:4 }}>FOCUS PILLAR</p>
            {PILLARS.map(p => (
              <button key={p.id} onClick={() => setPillar(p.id)}
                style={{ padding:"11px 14px", borderRadius:12, border:`1.5px solid ${pillar===p.id ? p.color : C.border}`,
                  background: pillar===p.id ? p.light : C.white, color: pillar===p.id ? p.color : C.muted,
                  cursor:"pointer", textAlign:"left", fontSize:13, fontWeight: pillar===p.id ? 500 : 400,
                  transition:"all 0.2s", display:"flex", alignItems:"center", gap:8 }}>
                <PillarIcon pillar={p} size={15} />
                <div>
                  <div>{p.short}</div>
                  {pillar===p.id && <div style={{ fontSize:10, opacity:0.7, marginTop:1 }}>Active</div>}
                </div>
              </button>
            ))}
            {messages.length > 0 && (
              <button onClick={startNewSession}
                style={{ marginTop:8, padding:"10px 14px", borderRadius:12,
                  border:`1.5px dashed ${C.border}`, background:"transparent",
                  color: C.muted, cursor:"pointer", fontSize:12, textAlign:"center" }}>
                + New Session
              </button>
            )}
          </div>

          {/* Chat window */}
          <div style={{ ...S.card, display:"flex", flexDirection:"column", padding:0, overflow:"hidden" }}>
            <div style={{ padding:"16px 22px", borderBottom:`1px solid ${C.border}`,
              display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div>{(() => { const pl = PILLARS.find(p=>p.id===pillar); return pl ? <PillarIcon pillar={pl} size={24} /> : null; })()}</div>
                <div>
                  <div style={{ fontWeight:500 }}>Healing Guide: {PILLARS.find(p=>p.id===pillar)?.short} Focus</div>
                  <div style={{ fontSize:12, color: C.muted }}>Trauma-informed · Body-positive · Always here</div>
                </div>
              </div>
              {messages.length > 0 && (
                <div style={{ fontSize:11, color: C.muted, background: C.cream,
                  padding:"4px 10px", borderRadius:20 }}>
                  Auto-saving 💾
                </div>
              )}
            </div>

            <div style={{ flex:1, overflowY:"auto", padding:"20px 22px", display:"flex", flexDirection:"column", gap:16 }}>
              {messages.length === 0 && (
                <div style={{ textAlign:"center", padding:"32px 20px" }}>
                  <div style={{ marginBottom:12 }}><Heart size={36} color="#C4614B" /></div>
                  <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:8 }}>You are safe here.</p>
                  <p style={{ color: C.muted, fontSize:14, marginBottom:24 }}>Share what's on your heart, or try one of these prompts:</p>
                  <div style={{ display:"grid", gap:8 }}>
                    {starters.map((s, i) => (
                      <button key={i} onClick={() => setInput(s)}
                        style={{ padding:"11px 16px", borderRadius:10, border:`1px solid ${C.border}`,
                          background: C.cream, color: C.warm, fontSize:13, cursor:"pointer",
                          textAlign:"left", lineHeight:1.5 }}>
                        "{s}"
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} style={{ display:"flex", justifyContent: msg.role==="user" ? "flex-end" : "flex-start" }}>
                  <div style={{
                    maxWidth:"75%", padding:"12px 18px", borderRadius:18,
                    borderBottomRightRadius: msg.role==="user" ? 4 : 18,
                    borderBottomLeftRadius: msg.role==="assistant" ? 4 : 18,
                    background: msg.role==="user" ? `linear-gradient(135deg, ${C.gold}, #B8821F)` : C.cream,
                    color: msg.role==="user" ? "#fff" : C.warm,
                    fontSize:14, lineHeight:1.75, animation:"fadeIn 0.3s ease"
                  }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display:"flex", gap:6, padding:"12px 18px", width:"fit-content" }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{ width:8, height:8, borderRadius:"50%", background: C.gold,
                      animation:`pulse 1.2s ease ${i*0.2}s infinite` }} />
                  ))}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div style={{ padding:"16px 22px", borderTop:`1px solid ${C.border}`, display:"flex", gap:10 }}>
              <textarea value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if(e.key==="Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }}}
                placeholder={atLimit ? "Session limit reached for this month" : "Share what's on your heart..."}
                disabled={atLimit}
                style={{ ...S.textarea, minHeight:48, height:48, resize:"none", flex:1, padding:"12px 16px",
                  opacity: atLimit ? 0.5 : 1 }} />
              <button onClick={sendMessage} disabled={!input.trim() || loading || atLimit} className="btn-hover"
                style={{ ...S.btnPrimary, padding:"0 22px", alignSelf:"stretch",
                  opacity: (input.trim() && !loading && !atLimit) ? 1 : 0.5 }}>
                Send
              </button>
            </div>
            <WellnessDisclaimer variant="guide" />
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  SETTINGS VIEW
// ══════════════════════════════════════════════════════════════════

function SettingsView({ user, setUser, logout, notify }) {
  const [name, setName] = useState(user?.name || "");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const plan = planInfo(user?.plan);

  const saveName = async () => {
    await supaUpdateMeta({ name });
    await supaUpdateProfile({ name });
    setUser(u => ({ ...u, name }));
    notify("Name updated");
  };

  const changePassword = async () => {
    if (!newPw || !confirmPw) { notify("Please fill all fields", "error"); return; }
    if (newPw !== confirmPw) { notify("New passwords don't match", "error"); return; }
    if (newPw.length < 8) { notify("Password must be at least 8 characters", "error"); return; }
    const result = await supaUpdatePassword(newPw);
    if (result?.error) { notify(result.error.message || "Could not update password", "error"); return; }
    setCurrentPw(""); setNewPw(""); setConfirmPw("");
    notify("Password updated successfully");
  };

  return (
    <div style={S.page}>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Settings</h1>
      <p style={{ color: C.muted, fontSize:15, marginBottom:32 }}>Manage your account and healing sanctuary preferences.</p>

      <div className="rs-full" style={{ display:"grid", gap:20, maxWidth:540 }}>
        {/* Profile */}
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, marginBottom:20 }}>Your Profile</h3>
          <div style={{ marginBottom:16 }}>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>DISPLAY NAME</label>
            <input value={name} onChange={e => setName(e.target.value)} style={S.input} />
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>EMAIL ADDRESS</label>
            <input value={user?.email} disabled
              style={{ ...S.input, opacity:0.6, cursor:"not-allowed" }} />
          </div>
          <button onClick={saveName} className="btn-hover" style={S.btnPrimary}>Save Changes</button>
        </div>

        {/* Subscription */}
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, marginBottom:16 }}>Your Plan</h3>
          <div style={{ background:`${C.gold}12`, border:`1.5px solid ${C.gold}44`, borderRadius:12, padding:"18px 20px", marginBottom:20 }}>
            <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, color: C.gold, marginBottom:4 }}>{plan?.name}</div>
            <div style={{ color: C.muted, fontSize:14, marginBottom:12 }}>{plan?.tagline}</div>
            <div style={{ fontSize:13, color: C.warm }}>
              {plan?.price ? <><strong style={{ color: C.gold }}>${plan?.price}</strong>/month</> : <strong style={{ color: C.gold }}>Free</strong>}
              {user?.subscribedAt && !isNaN(new Date(user.subscribedAt)) &&
                <span> · Active since {new Date(user.subscribedAt).toLocaleDateString("en-US",{month:"long",year:"numeric"})}</span>
              }
            </div>
          </div>

          {/* Upgrades */}
          {plan?.id !== "thrive" && (
            <div style={{ marginBottom:24 }}>
              <p style={{ color: C.muted, fontSize:13, marginBottom:12, fontWeight:500 }}>Upgrade your plan</p>
              <div style={{ display:"grid", gap:10 }}>
                {PLANS.filter(p => {
                  if (!plan || plan?.id === "free") return true;
                  if (plan?.id === "seed") return p.id === "bloom" || p.id === "thrive";
                  if (plan?.id === "bloom") return p.id === "thrive";
                  return false;
                }).map(p => (
                  <div key={p.id} style={{ background: C.cream, border:`1px solid ${C.border}`,
                    borderRadius:12, padding:"14px 18px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                    <div>
                      <div style={{ fontWeight:500, fontSize:14, marginBottom:2 }}>{p.name} · ${p.price}/mo</div>
                      <div style={{ fontSize:12, color: C.muted }}>{p.tagline}</div>
                    </div>
                    <button onClick={() => startUpgradeCheckout(p.id, user?.email, user?.name)}
                      className="btn-hover"
                      style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
                        fontSize:12, padding:"8px 16px", borderRadius:8,
                        whiteSpace:"nowrap", flexShrink:0, border:"none", cursor:"pointer" }}>
                      Upgrade →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {plan?.id === "thrive" && (
            <p style={{ color: C.muted, fontSize:13, marginBottom:24 }}>You are on our highest tier. Thank you for being a Thrive member. 🌟</p>
          )}

          {/* Downgrades */}
          {plan?.id !== "seed" && plan?.id !== "free" && (
            <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:20 }}>
              <p style={{ color: C.muted, fontSize:13, marginBottom:6, fontWeight:500 }}>Need to adjust your plan?</p>
              <p style={{ color: C.muted, fontSize:12, lineHeight:1.7, marginBottom:14 }}>
                We understand that life and finances shift. There is no shame in moving to a lower tier. Your healing journey continues at every level. Just reach out and we will take care of it with kindness.
              </p>
              <div style={{ display:"grid", gap:10 }}>
                {PLANS.filter(p => {
                  if (plan?.id === "thrive") return p.id === "bloom" || p.id === "seed";
                  if (plan?.id === "bloom") return p.id === "seed";
                  return false;
                }).map(p => (
                  <div key={p.id} style={{ background: C.cream, border:`1px solid ${C.border}`,
                    borderRadius:12, padding:"14px 18px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                    <div>
                      <div style={{ fontWeight:500, fontSize:14, marginBottom:2 }}>{p.name} · ${p.price}/mo</div>
                      <div style={{ fontSize:12, color: C.muted }}>{p.tagline}</div>
                    </div>
                    <a href={`mailto:support@glowandflowholistics.com?subject=Plan Change Request: ${p.name}&body=Hi, I would like to change my plan from ${plan?.name} to ${p.name}. My account email is ${user?.email}.`}
                      style={{ background:"transparent", border:`1.5px solid ${C.border}`,
                        color: C.muted, fontSize:12, padding:"8px 16px", textDecoration:"none",
                        borderRadius:8, display:"inline-block", whiteSpace:"nowrap", flexShrink:0,
                        cursor:"pointer" }}>
                      Switch →
                    </a>
                  </div>
                ))}
              </div>
              <p style={{ color: C.muted, fontSize:12, marginTop:14 }}>
                All plan changes are processed within 24 hours. Your data is always kept safe.
              </p>
            </div>
          )}
        </div>

        {/* Change Password */}
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, marginBottom:20 }}>Change Password</h3>
          {[
            { label:"NEW PASSWORD", value:newPw, set:setNewPw },
            { label:"CONFIRM NEW PASSWORD", value:confirmPw, set:setConfirmPw }
          ].map(({ label, value, set }) => (
            <div key={label} style={{ marginBottom:16 }}>
              <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>{label}</label>
              <input type="password" value={value} onChange={e => set(e.target.value)} style={S.input} />
            </div>
          ))}
          <button onClick={changePassword} className="btn-hover" style={S.btnPrimary}>Update Password</button>
        </div>

        {/* Billing */}
        {user?.plan !== "free" && <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, marginBottom:12 }}>Billing & Subscription</h3>
          <p style={{ color: C.muted, fontSize:14, lineHeight:1.7, marginBottom:18 }}>
            Manage your payment method, view invoices, or cancel your subscription anytime through the secure Stripe billing portal.
          </p>
          <button onClick={async () => {
            try {
              const res = await apiFetch("/api/billing-portal", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: user?.email })
              });
              const data = await res.json();
              if (data.url) window.open(data.url, "_blank");
              else notify(data.error || "Could not open billing portal", "error");
            } catch(e) { notify("Could not connect to billing portal", "error"); }
          }} className="btn-hover" style={S.btnPrimary}>
            Manage Billing →
          </button>
        </div>}

        {/* About & Disclaimer */}
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, marginBottom:12 }}>Important Information</h3>
          <WellnessDisclaimer variant="full" />
          <p style={{ fontSize:13, marginTop:12 }}>
            <a href="/glowandflow-privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: C.gold }}>Read our Privacy Policy</a>
          </p>
        </div>

        {/* Sign Out */}
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, marginBottom:12, color: C.terracotta }}>Sign Out</h3>
          <p style={{ color: C.muted, fontSize:14, marginBottom:18 }}>Your data will remain saved and waiting when you return.</p>
          <button onClick={logout} className="btn-hover"
            style={{ ...S.btnSecondary, borderColor: C.terracotta, color: C.terracotta }}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}



// ══════════════════════════════════════════════════════════════════
//  WEEKLY INTENTION VIEW (BLOOM + THRIVE)
// ══════════════════════════════════════════════════════════════════

function WeeklyIntentionView({ user }) {
  const plan = planInfo(user?.plan);
  const hasAccess = plan?.id === "bloom" || plan?.id === "thrive";
  const weekKey = (() => {
    const d = new Date();
    const startOfYear = new Date(d.getFullYear(), 0, 1);
    const week = Math.ceil(((d - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
    return `${d.getFullYear()}-W${week}`;
  })();

  const [intention, setIntention] = useState("");
  const [savedIntention, setSavedIntention] = useState(null);
  const [pillarFocus, setPillarFocus] = useState("");
  const [gratitude, setGratitude] = useState("");
  const [release, setRelease] = useState("");
  const [saved, setSaved] = useState(false);
  const [pastIntentions, setPastIntentions] = useState([]);

  useEffect(() => {
    const load = async () => {
      const current = await retrieve(`gf_weekly_${user?.email}_${weekKey}`);
      if (current) { setSavedIntention(current); setIntention(current.intention || ""); setPillarFocus(current.pillarFocus || ""); setGratitude(current.gratitude || ""); setRelease(current.release || ""); }
      const past = await retrieve(`gf_weekly_history_${user?.email}`);
      if (past) setPastIntentions(past.slice(0, 5));
    };
    load();
  }, [user]);

  const handleSave = async () => {
    const data = { intention, pillarFocus, gratitude, release, weekKey, savedAt: Date.now() };
    await store(`gf_weekly_${user?.email}_${weekKey}`, data);
    const history = await retrieve(`gf_weekly_history_${user?.email}`) || [];
    const updated = [data, ...history.filter(h => h.weekKey !== weekKey)].slice(0, 12);
    await store(`gf_weekly_history_${user?.email}`, updated);
    setSavedIntention(data);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const getWeekRange = () => {
    const now = new Date();
    const day = now.getDay();
    const monday = new Date(now); monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    return `${monday.toLocaleDateString("en-US",{month:"short",day:"numeric"})} – ${sunday.toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}`;
  };

  if (!hasAccess) {
    return (
      <div style={S.page}>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Weekly Intention</h1>
        <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
          <div style={{ marginBottom:16 }}><Leaf size={48} color="#4A6741" /></div>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Bloom & Thrive Members</h3>
          <p style={{ color: C.muted, fontSize:14, lineHeight:1.7, marginBottom:24 }}>
            Weekly intention setting is available on the Bloom and Thrive plans. Upgrade to begin setting sacred weekly intentions across your healing journey.
          </p>
          <button onClick={() => startUpgradeCheckout("bloom", user?.email, user?.name)} className="btn-hover"
            style={{ ...S.btnPrimary, fontSize:14, padding:"10px 24px", cursor:"pointer" }}>
            Upgrade to Bloom ($39/mo) to unlock →
          </button>
        </div>
      </div>
    );
  }

  const [viewMode, setViewMode] = useState("set"); // set | history
  const pillarData = PILLARS.find(p => p.id === savedIntention?.pillarFocus);

  return (
    <div style={S.page}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>Weekly Intention</h1>
          <p style={{ color: C.muted, fontSize:15 }}>{getWeekRange()}</p>
        </div>
        <div style={{ display:"flex", gap:8, background: C.cream, borderRadius:12, padding:5 }}>
          {["set","history"].map(m => (
            <button key={m} onClick={() => setViewMode(m)}
              style={{ padding:"9px 20px", borderRadius:9, border:"none", cursor:"pointer", fontSize:13, fontWeight:500,
                background: viewMode===m ? "#4A6741" : "transparent",
                color: viewMode===m ? "#fff" : C.muted, transition:"all 0.2s",
                textTransform:"capitalize" }}>
              {m === "set" ? "This Week" : "My History"}
            </button>
          ))}
        </div>
      </div>

      {/* ── THIS WEEK TAB ── */}
      {viewMode === "set" && (
        <div style={{ display:"grid", gap:20, maxWidth:680 }}>

          {/* Show saved intention banner if already set this week */}
          {savedIntention && (
            <div style={{ background:`#E3EEE2`, border:`1.5px solid #4A6741`, borderRadius:16,
              padding:"22px 24px", animation:"fadeIn 0.3s ease" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <CheckCircle size={20} color="#27AE60" />
                  <span style={{ fontWeight:500, color:"#2D4228", fontSize:15 }}>This week&apos;s intention is set</span>
                </div>
                <span style={{ fontSize:12, color:"#4A6741" }}>
                  Saved {new Date(savedIntention.savedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"numeric",minute:"2-digit"})}
                </span>
              </div>

              {/* Intention */}
              <div style={{ background:"rgba(255,255,255,0.6)", borderRadius:12, padding:"16px 18px", marginBottom:12 }}>
                <div style={{ fontSize:11, letterSpacing:"0.8px", color:"#4A6741", marginBottom:8 }}>MY INTENTION</div>
                <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, color:"#2D4228",
                  fontStyle:"italic", lineHeight:1.7, margin:0 }}>
                  &ldquo;{savedIntention.intention}&rdquo;
                </p>
              </div>

              {/* Pillar + Gratitude + Release row */}
              <div className="rg-3col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                {pillarData && (
                  <div style={{ background:"rgba(255,255,255,0.5)", borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontSize:10, letterSpacing:"0.8px", color:"#4A6741", marginBottom:6 }}>PILLAR FOCUS</div>
                    <div style={{ fontSize:13, color:"#2D4228", display:"flex", alignItems:"center", gap:6 }}>
                      <PillarIcon pillar={pillarData} size={15} />{pillarData.short}
                    </div>
                  </div>
                )}
                {savedIntention.gratitude && (
                  <div style={{ background:"rgba(255,255,255,0.5)", borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontSize:10, letterSpacing:"0.8px", color:"#4A6741", marginBottom:6 }}>GRATEFUL FOR</div>
                    <p style={{ fontSize:12, color:"#2D4228", margin:0, lineHeight:1.6 }}>{savedIntention.gratitude}</p>
                  </div>
                )}
                {savedIntention.release && (
                  <div style={{ background:"rgba(255,255,255,0.5)", borderRadius:10, padding:"12px 14px" }}>
                    <div style={{ fontSize:10, letterSpacing:"0.8px", color:"#4A6741", marginBottom:6 }}>RELEASING</div>
                    <p style={{ fontSize:12, color:"#2D4228", margin:0, lineHeight:1.6 }}>{savedIntention.release}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Set / Edit form */}
          <div style={S.card}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, marginBottom:4, fontWeight:400 }}>
              {savedIntention ? "Update This Week's Intention" : "Set Your Intention"}
            </h3>
            <p style={{ color: C.muted, fontSize:13, marginBottom:20 }}>
              {savedIntention ? "You can refine your intention any time this week." : "Set one powerful healing intention. Be specific and compassionate."}
            </p>

            <div style={{ marginBottom:16 }}>
              <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>THIS WEEK I INTEND TO...</label>
              <textarea value={intention} onChange={e => setIntention(e.target.value)} rows={3}
                placeholder="E.g. I intend to practice one grounding exercise each morning and speak to myself with the same kindness I would offer a dear friend..."
                style={{ ...S.input, resize:"vertical", lineHeight:1.8, minHeight:90 }} />
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:10 }}>PILLAR FOCUS</label>
              <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                {PILLARS.map(p => (
                  <button key={p.id} onClick={() => setPillarFocus(p.id)}
                    style={{ padding:"10px 14px", borderRadius:10, border:`1.5px solid ${pillarFocus === p.id ? p.color : C.border}`,
                      background: pillarFocus === p.id ? p.light : C.white,
                      color: pillarFocus === p.id ? p.dark : C.muted,
                      cursor:"pointer", fontSize:13, fontWeight: pillarFocus === p.id ? 500 : 400,
                      display:"flex", alignItems:"center", gap:8, transition:"all 0.2s" }}>
                    <PillarIcon pillar={p} size={15} /> {p.short}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>I AM GRATEFUL FOR...</label>
                <textarea value={gratitude} onChange={e => setGratitude(e.target.value)} rows={2}
                  placeholder="One thing from this week..."
                  style={{ ...S.input, resize:"vertical", fontSize:13, lineHeight:1.7 }} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>I AM RELEASING...</label>
                <textarea value={release} onChange={e => setRelease(e.target.value)} rows={2}
                  placeholder="What no longer serves you..."
                  style={{ ...S.input, resize:"vertical", fontSize:13, lineHeight:1.7 }} />
              </div>
            </div>

            <button onClick={handleSave} disabled={!intention.trim()} className="btn-hover"
              style={{ ...S.btnPrimary, background:`linear-gradient(135deg, #4A6741, #2D4228)`,
                opacity: intention.trim() ? 1 : 0.5, width:"100%", padding:"13px" }}>
              {saved ? "Saved" : savedIntention ? "Update Intention" : "Set My Weekly Intention"}
            </button>
          </div>
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {viewMode === "history" && (
        <div style={{ maxWidth:680 }}>
          {pastIntentions.length === 0 ? (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><Leaf size={40} color="#4A6741" /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, color: C.warm, marginBottom:8 }}>No past intentions yet</p>
              <p style={{ color: C.muted, fontSize:14 }}>Your weekly intentions will appear here as you set them each week.</p>
            </div>
          ) : (
            <div style={{ display:"grid", gap:16 }}>
              {pastIntentions.map((p, i) => {
                const pData = PILLARS.find(pl => pl.id === p.pillarFocus);
                const isCurrentWeek = p.weekKey === weekKey;
                return (
                  <div key={i} style={{ ...S.card, borderLeft:`4px solid ${isCurrentWeek ? "#4A6741" : C.gold}`,
                    background: isCurrentWeek ? "#E3EEE2" : C.white }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        {isCurrentWeek && <span style={{ background:"#4A6741", color:"#fff", fontSize:10, padding:"3px 10px", borderRadius:20, letterSpacing:"0.5px" }}>THIS WEEK</span>}
                        {pData && <span style={{ background: pData.light, color: pData.dark, fontSize:11, padding:"3px 10px", borderRadius:20, display:"inline-flex", alignItems:"center", gap:4 }}><PillarIcon pillar={pData} size={11} />{pData.short}</span>}
                      </div>
                      <span style={{ fontSize:12, color: C.muted }}>
                        {new Date(p.savedAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
                      </span>
                    </div>

                    <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:19, color: C.warm,
                      fontStyle:"italic", lineHeight:1.75, marginBottom: (p.gratitude || p.release) ? 14 : 0 }}>
                      &ldquo;{p.intention}&rdquo;
                    </p>

                    {(p.gratitude || p.release) && (
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginTop:12 }}>
                        {p.gratitude && (
                          <div style={{ background: C.cream, borderRadius:8, padding:"10px 12px" }}>
                            <div style={{ fontSize:10, letterSpacing:"0.8px", color: C.muted, marginBottom:4 }}>GRATEFUL FOR</div>
                            <p style={{ fontSize:12, color: C.warm, margin:0, lineHeight:1.6 }}>{p.gratitude}</p>
                          </div>
                        )}
                        {p.release && (
                          <div style={{ background: C.cream, borderRadius:8, padding:"10px 12px" }}>
                            <div style={{ fontSize:10, letterSpacing:"0.8px", color: C.muted, marginBottom:4 }}>RELEASED</div>
                            <p style={{ fontSize:12, color: C.warm, margin:0, lineHeight:1.6 }}>{p.release}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PRIORITY SUPPORT VIEW (THRIVE)
// ══════════════════════════════════════════════════════════════════

function PrioritySupportView({ user }) {
  const plan = planInfo(user?.plan);
  const hasAccess = plan?.id === "thrive";
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const categories = [
    "Technical issue with the app",
    "Question about my wellness roadmap",
    "Healing Guidance feedback",
    "Billing or subscription question",
    "Feature request",
    "General support"
  ];

  const handleSend = async () => {
    if (!subject.trim() || !message.trim() || !category) return;
    setSending(true);
    try {
      await apiFetch("/api/send-support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: user.name,
          email: user.email,
          subject,
          message,
          category,
          plan: plan?.name
        })
      });
      setSent(true);
    } catch(e) {
      console.log("Support email error:", e);
      setSent(true); // Still show success to user
    }
    setSending(false);
  };

  if (!hasAccess) {
    return (
      <div style={S.page}>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Priority Support</h1>
        <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
          <div style={{ marginBottom:16 }}><Mail size={48} color={C.gold} /></div>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Thrive Members Only</h3>
          <p style={{ color: C.muted, fontSize:14, lineHeight:1.7, marginBottom:24 }}>
            Priority support with direct access to our team is exclusively available on the Thrive plan.
          </p>
          <p style={{ color: C.muted, fontSize:13, marginBottom:24 }}>
            All members can reach us at <strong>support@glowandflowholistics.com</strong>
          </p>
          <button onClick={() => startUpgradeCheckout("thrive", user?.email, user?.name)} className="btn-hover"
            style={{ ...S.btnPrimary, fontSize:14, padding:"10px 24px", cursor:"pointer" }}>
            Upgrade to Thrive ($67/mo) to unlock →
          </button>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div style={S.page}>
        <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
          <div style={{ marginBottom:16 }}><Mail size={56} color={C.gold} /></div>
          <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:32, fontWeight:400, marginBottom:12 }}>Message Received</h2>
          <p style={{ color: C.muted, fontSize:15, lineHeight:1.8, marginBottom:24 }}>
            Thank you for reaching out. As a Thrive member you will receive a personal response within 24 hours.
          </p>
          <button onClick={() => { setSent(false); setSubject(""); setMessage(""); setCategory(""); }}
            style={{ ...S.btnSecondary, borderColor: C.gold, color: C.gold }}>
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Priority Support</h1>
      <p style={{ color: C.muted, fontSize:15, marginBottom:28 }}>As a Thrive member, you receive a personal response within 24 hours.</p>

      <div style={{ ...S.card, maxWidth:580 }}>
        <div style={{ background:`${C.gold}12`, border:`1px solid ${C.gold}33`, borderRadius:12,
          padding:"14px 18px", marginBottom:24, display:"flex", gap:12, alignItems:"center" }}>
          <Star size={20} color={C.gold} />
          <div>
            <div style={{ fontWeight:500, fontSize:14 }}>Thrive Priority Support</div>
            <div style={{ fontSize:12, color: C.muted }}>Personal responses within 24 hours · {user.name}</div>
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>CATEGORY</label>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => setCategory(cat)}
                style={{ padding:"10px 12px", borderRadius:10,
                  border:`1.5px solid ${category === cat ? C.gold : C.border}`,
                  background: category === cat ? `${C.gold}15` : C.white,
                  color: category === cat ? C.warm : C.muted,
                  cursor:"pointer", fontSize:12, textAlign:"left", transition:"all 0.2s" }}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>SUBJECT</label>
          <input value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="Brief description of your question or issue"
            style={S.input} />
        </div>

        <div style={{ marginBottom:24 }}>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>MESSAGE</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={6}
            placeholder="Share as much detail as helpful. We are here to support you."
            style={{ ...S.input, resize:"vertical", lineHeight:1.8 }} />
        </div>

        <button onClick={handleSend}
          disabled={!subject.trim() || !message.trim() || !category || sending}
          className="btn-hover"
          style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
            width:"100%", padding:"14px",
            opacity: subject.trim() && message.trim() && category ? 1 : 0.5 }}>
          {sending ? "Sending..." : "Send Message"}
        </button>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
//  WELLNESS HUB (ALL TIERS)
// ══════════════════════════════════════════════════════════════════

function WellnessHubView() {
  const [activeSection, setActiveSection] = useState("survival");

  const sections = [
    { id:"survival", label:"From Surviving to Thriving", Icon:Leaf },
    { id:"cycle", label:"Comfort Food & Emotional Eating", Icon:RotateCcw },
    { id:"anxiety", label:"Stress, Anxiety & Your Body", Icon:Wind },
    { id:"depression", label:"Depression & the Strong One", Icon:Sun },
    { id:"holistic", label:"The Five Pillars", Icon:Heart },
    { id:"glowflow", label:"How ThriveRoot Helps", Icon:Sparkles },
  ];

  const sectionContent = {
    survival: {
      title: "From Surviving to Thriving",
      subtitle: "Our grandmothers survived so we could thrive",
      color: "#4A6741",
      light: "#E3EEE2",
      Icon: Leaf,
      body: [
        {
          heading: "A Legacy of Survival",
          text: "Push through the pain. Don't complain. Keep family business in the house. Rest when the work is done. For generations, these lessons kept our families alive in a country that made survival hard. They were wisdom for their time. But survival and wellness are not the same thing, and many of us are still living by rules written for a danger that has changed shape."
        },
        {
          heading: "Signs You Might Be Living in Survival Mode",
          steps: [
            { label: "\"I'm fine\" is automatic", desc: "You say it so often you've stopped noticing the gap between the word and how you really feel." },
            { label: "Rest comes with guilt", desc: "An unplanned afternoon makes you anxious instead of relieved, and you can't remember the last time you rested without guilt." },
            { label: "A symptom you keep putting off", desc: "A headache, tight chest, or constant fatigue you've managed quietly for months, telling yourself you'll deal with it when things calm down." },
            { label: "Everyone's needs but your own", desc: "You can list what everyone else needs in detail, but struggle to answer a simple question about yourself." },
            { label: "There for everyone", desc: "People would call you the one who's always there, yet you can't name one person who is reliably there for you." }
          ]
        },
        {
          heading: "Weathering: When Stress Lives in the Body",
          text: "Researcher Arline Geronimus called it weathering: the way constant stress from racism and from carrying too much wears on Black bodies, raising blood pressure and aging the body before its time. This is why your exhaustion is real, and why rest and early care are not indulgences. They're how we protect what generations fought to keep."
        },
        {
          heading: "Making the Shift",
          steps: [
            { label: "Name it", desc: "Write down one survival pattern you see in your own life. You can't change a pattern you haven't named." },
            { label: "Ask where it came from", desc: "What was this protecting me or my family from? Is that threat still here in the same way?" },
            { label: "Choose one thing", desc: "Pick a single pattern to gently experiment with. One small change you keep beats a big overhaul you abandon." },
            { label: "Bring support", desc: "A trusted friend, a sister circle, a therapist, or even a reminder on your phone. Change lasts longer when you're not doing it alone." }
          ]
        }
      ]
    },
    cycle: {
      title: "Comfort Food & Emotional Eating",
      subtitle: "Honoring our food while noticing what we're really reaching for",
      color: "#C4614B",
      light: "#FDE8E3",
      Icon: RotateCcw,
      body: [
        {
          heading: "Our Food Carries Our History",
          text: "From making a way with what was available, to Sunday dinners and repasts, food has been how our families survived, celebrated, grieved, and said \"I love you\" without saying it. You never have to give up the food of your culture. Emotional eating isn't a lack of willpower. It's a very human way of coping when a feeling has nowhere else to go."
        },
        {
          heading: "How the Cycle Works",
          steps: [
            { label: "Trigger", desc: "A hard day, a difficult conversation, loneliness, overwhelm, or simply being everyone's everything." },
            { label: "The Feeling Builds", desc: "Stress rises and your body looks for relief, often before you've even named what you're feeling." },
            { label: "Reaching for Comfort", desc: "Food shows up as the answer, not because you're hungry, but because it has always been there when nothing else was." },
            { label: "Brief Relief", desc: "There's a moment of comfort or numbness, often eaten quickly and without really tasting it." },
            { label: "Shame Follows", desc: "Guilt and self-criticism arrive, and those painful feelings become the next trigger." },
            { label: "The Restriction Trap", desc: "Promising to \"be good\" tomorrow leads to deprivation, which makes the next craving even stronger." }
          ]
        },
        {
          heading: "Why Diets Make It Worse",
          text: "Diet culture often tells us our food is the problem and our bodies need fixing. Restriction leaves the body feeling deprived and the original feeling still unmet, so cravings grow stronger. Healing comes from tending the feeling, not fighting the food."
        },
        {
          heading: "A Different Toolkit",
          steps: [
            { label: "Pause and check in", desc: "Before eating in response to a strong feeling, take one breath and ask: am I hungry, or am I hurting, tired, lonely, or overwhelmed?" },
            { label: "Keep a food and mood journal", desc: "For five days, simply note what you ate and how you felt. No judgment. You're just gathering information." },
            { label: "Reimagine, don't erase", desc: "Keep the family recipes. Try one small twist that keeps the flavor and the meaning, and enjoy it slowly, at the table." },
            { label: "Find other comforts too", desc: "A call with a friend, a walk, music, a bath, or prayer. Food can stay a comfort without being the only one." }
          ]
        }
      ]
    },
    anxiety: {
      title: "Stress, Anxiety & Your Body",
      subtitle: "When \"my nerves are bad\" deserves real attention",
      color: "#7A6BA0",
      light: "#EDE9F6",
      Icon: Wind,
      body: [
        {
          heading: "What Our Families Called It",
          text: "Many of us grew up hearing \"my nerves are bad\" or \"she's just high-strung.\" Those words carried real wisdom, and they also kept anxiety from being named and cared for. Anxiety is a body that has been on high alert for so long it forgot how to stand down. It's not a character flaw, and it is treatable."
        },
        {
          heading: "How Stress Lives in the Body",
          text: "Racing thoughts, a tight chest, clenched jaw, shallow breathing, trouble sleeping, headaches, and stomach trouble are all ways the body says it's carrying too much. Over time, constant stress raises blood pressure and wears the body down. Your symptoms are worth listening to, and worth mentioning to your doctor."
        },
        {
          heading: "The Weight of Being Watched",
          text: "Being the only one in the room. Code-switching all day. Being called \"aggressive\" for speaking plainly. Judged by your skin tone, your hair, or your body. Black women often carry a quiet, constant vigilance that others never have to think about. That vigilance is exhausting, and it belongs in this conversation about anxiety."
        },
        {
          heading: "Ways to Settle Your System",
          steps: [
            { label: "Breathe low and slow", desc: "A few slow breaths, with the exhale longer than the inhale, tell your body it is safe enough to stand down." },
            { label: "Ground in the present", desc: "Name five things you see, four you can touch, three you hear, two you smell, and one you taste." },
            { label: "Name it plainly", desc: "Practice saying, \"I think I'm dealing with anxiety,\" to yourself, a trusted person, or a provider." },
            { label: "Lean on faith and community", desc: "Prayer, worship, and people who understand you can be deeply calming, alongside professional care." },
            { label: "Get support", desc: "If anxiety is affecting your sleep, work, or relationships, a therapist can help. Therapy for Black Girls is a good place to start looking." }
          ]
        }
      ]
    },
    depression: {
      title: "Depression & the Strong One",
      subtitle: "When \"I'm just tired\" is something more",
      color: "#C9963B",
      light: "#FDF3E0",
      Icon: Sun,
      body: [
        {
          heading: "Hiding Behind \"Just Tired\"",
          text: "Depression in Black women often doesn't look like what's on TV. It can look like getting everything done for everyone and feeling empty inside. Irritability, exhaustion that sleep doesn't fix, losing interest in things you used to love, eating more or less than usual, or a heaviness you can't quite name. Many of us call it \"just tired\" for years."
        },
        {
          heading: "Strength Was Never Supposed to Mean Suffering Alone",
          text: "The Strong Black Woman script tells us to keep going no matter what, and that needing help is weakness. Depression is not a lack of faith or a lack of strength. It's a health condition, as real as high blood pressure, and it deserves the same care."
        },
        {
          heading: "Gentle Steps Through Depression",
          steps: [
            { label: "Start very small", desc: "One journal sentence, one minute of sunlight, one glass of water. When everything feels heavy, small counts." },
            { label: "Tend to your body first", desc: "Rest, warmth, gentle movement, and regular meals are real care when words feel like too much." },
            { label: "Say it to one person", desc: "Depression feeds on silence. Telling one trusted person how you're really doing loosens its grip." },
            { label: "Don't isolate", desc: "Connection in small doses helps, even when you'd rather pull away." },
            { label: "Reach for professional support", desc: "If you've felt this way for two weeks or more, talk with your doctor or a therapist. Seeking help is protection, not betrayal." }
          ]
        },
        {
          heading: "If You Need Help Now",
          text: "If you're having thoughts of harming yourself or feel you can't stay safe, call or text 988 to reach the Suicide and Crisis Lifeline, available 24 hours a day, or call 911. You deserve support right now, and you don't have to carry this alone."
        }
      ]
    },
    holistic: {
      title: "The Five Pillars",
      subtitle: "Healing every part of you, not just the part that hurts most",
      color: "#4A6741",
      light: "#E3EEE2",
      Icon: Heart,
      body: [
        {
          heading: "Why Holistic?",
          text: "The patterns we inherit don't stay in one part of our lives. Silence affects our health. Money stress affects our sleep. Burnout affects our relationships and our faith. That's why ThriveRoot looks at your whole life, gently, one pillar at a time."
        },
        {
          heading: "The Five Pillars",
          steps: [
            { label: "Emotional", desc: "Breaking the habit of silent suffering, naming what you feel, grieving fully, and tending to the feelings food has been holding." },
            { label: "Physical", desc: "Rest without guilt, nourishment without shame, care before crisis, and loving your body while caring for how it feels." },
            { label: "Mental", desc: "Putting down the Strong Black Woman script, quieting the inner critic, and treating mental health as health." },
            { label: "Spiritual", desc: "Faith, ancestry, and purpose. Honoring the strength you inherited while choosing which patterns end with you." },
            { label: "Financial", desc: "Healing your money story and loving your family without becoming everyone's bank." }
          ]
        },
        {
          heading: "Holding Both Truths About Your Body",
          text: "You can love your body and still care about your blood pressure. Body pride and honest health goals belong together. Here, health is measured by how you feel and how you're living, not by a number on a scale."
        },
        {
          heading: "Healing Is Not a Straight Line",
          text: "Some days you'll handle it with grace. Other days you'll fall right back into the old patterns. That's not failure. It's what changing a lifetime of inherited habits looks like. You don't have to complete the whole journey to be worthy of having started it."
        }
      ]
    },
    glowflow: {
      title: "How ThriveRoot Helps",
      subtitle: "Built by and for Black women who carry it all",
      color: "#C9963B",
      light: "#FDF3E0",
      Icon: Sparkles,
      body: [
        {
          heading: "Built For You",
          text: "ThriveRoot was created by Delicia Straughter, author of Breaking Generational Health Myths in the Black Community, after more than twenty years in social services watching strong women push through pain because that's what they were taught. It's a private, gentle space to put some of that weight down."
        },
        {
          heading: "What Makes ThriveRoot Different",
          steps: [
            { label: "Made for Black women", desc: "Prompts, lessons, and guidance written for the realities you actually live, from being the strong one to being the family bank." },
            { label: "Rooted in the book", desc: "Built on the ideas in Breaking Generational Health Myths: honoring where our patterns came from while choosing what we pass down." },
            { label: "Non-clinical and judgment-free", desc: "No diets, no calorie counting, no shame. Support that meets you where you are, alongside any professional care you choose." },
            { label: "Your healing guide", desc: "A warm, private guide for reflection and encouragement whenever you need it (Bloom and Thrive)." },
            { label: "A 90-day roadmap", desc: "A gentle, personalized path from survival mode to steady ground, built around your own answers (Thrive)." },
            { label: "Private by design", desc: "Your journal and check-ins belong to you." }
          ]
        },
        {
          heading: "You Belong Here",
          text: "Whatever brought you here, whether exhaustion, curiosity, or a quiet knowing that something needed to change, you are welcome exactly as you are. You don't need to have it figured out. You just need to begin, and you already have."
        }
      ]
    }
  };

  const renderSection = (data) => (
    <div style={{ display:"grid", gap:20 }}>
      {data.body.map((block, i) => (
        <div key={i} style={S.card}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, fontWeight:400,
            color: C.warm, marginBottom:12 }}>{block.heading}</h3>
          {block.text && (
            <p style={{ fontSize:15, lineHeight:1.9, color: C.warm, margin:0 }}>{block.text}</p>
          )}
          {block.steps && (
            <div style={{ display:"grid", gap:12, marginTop: block.text ? 16 : 0 }}>
              {block.steps.map((step, j) => (
                <div key={j} style={{ display:"flex", gap:14, padding:"14px 16px",
                  background: C.cream, borderRadius:12,
                  borderLeft:`3px solid ${sectionContent[activeSection].color}` }}>
                  <div style={{ flexShrink:0, width:28, height:28, borderRadius:"50%",
                    background: sectionContent[activeSection].light,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:13, fontWeight:600, color: sectionContent[activeSection].color }}>
                    {j + 1}
                  </div>
                  <div>
                    <div style={{ fontWeight:500, fontSize:14, marginBottom:4, color: C.warm }}>{step.label}</div>
                    <p style={{ fontSize:13, color: C.muted, lineHeight:1.7, margin:0 }}>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const active = sectionContent[activeSection];

  return (
    <div style={S.page}>
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:6 }}>Wellness Hub</h1>
        <p style={{ color: C.muted, fontSize:15 }}>Education, resources, and support for your whole healing journey.</p>
      </div>

      {/* Glow & Flow Radio */}
      <div style={{ ...S.card, marginBottom:24, background:`linear-gradient(135deg, #2A1A0E, #3D2510)`,
        border:"none", padding:"28px 28px 24px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
          <Mic size={28} color={C.gold} />
          <div>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, color:"#FBF5EC",
              fontWeight:400, margin:0, marginBottom:4 }}>Glow & Flow Radio</h3>
            <p style={{ color:"rgba(251,245,236,0.55)", fontSize:13, margin:0 }}>
              Honest conversations for Black women who carry it all
            </p>
          </div>
        </div>
        {/* Podcast player placeholder: swap in the new season's player when it launches */}
        <div style={{ borderRadius:14, border:"1px dashed rgba(201,150,59,0.45)",
          background:"rgba(251,245,236,0.04)", padding:"28px 24px", textAlign:"center" }}>
          <div style={{ fontSize:11, letterSpacing:"2px", color: C.gold, marginBottom:8 }}>NEW SEASON COMING SOON</div>
          <p style={{ color:"rgba(251,245,236,0.75)", fontSize:15, lineHeight:1.7, margin:0, maxWidth:460, marginInline:"auto" }}>
            We're recording a brand-new season of Glow & Flow Radio, made for Black women who carry it all. Check back soon to listen here.
          </p>
        </div>
      </div>

      {/* Resources link */}
      <div style={{ ...S.card, marginBottom:24, display:"flex", alignItems:"center",
        justifyContent:"space-between", flexWrap:"wrap", gap:14,
        background:`${C.gold}08`, border:`1.5px solid ${C.gold}33` }}>
        <div>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, fontWeight:400, marginBottom:4 }}>
            📖 Free Resources Library
          </h3>
          <p style={{ color: C.muted, fontSize:14, margin:0, lineHeight:1.6 }}>
            Free guides, trusted directories for finding culturally responsive therapists, and support when you need it most.
          </p>
        </div>
        <a href="https://glowandflowholistics.com/resource-listing"
          target="_blank" rel="noopener noreferrer"
          className="btn-hover"
          style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
            textDecoration:"none", whiteSpace:"nowrap", flexShrink:0 }}>
          Visit Resources →
        </a>
      </div>

      {/* Section navigation */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:24 }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            style={{ padding:"9px 16px", borderRadius:20, border:`1.5px solid ${activeSection===s.id ? sectionContent[s.id].color : C.border}`,
              background: activeSection===s.id ? sectionContent[s.id].light : C.white,
              color: activeSection===s.id ? sectionContent[s.id].color : C.muted,
              cursor:"pointer", fontSize:13, fontWeight: activeSection===s.id ? 500 : 400,
              transition:"all 0.2s", display:"flex", alignItems:"center", gap:6 }}>
            <ItemIcon item={s} size={16} color={C.gold} /> {s.label}
          </button>
        ))}
      </div>

      {/* Active section header */}
      <div style={{ marginBottom:20, padding:"20px 24px", borderRadius:16,
        background: active.light, borderLeft:`4px solid ${active.color}` }}>
        <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:32, fontWeight:400,
          color: C.warm, marginBottom:6 }}><ItemIcon item={active} size={20} color={C.warm} /> {active.title}</h2>
        <p style={{ color: C.muted, fontSize:14, margin:0, fontStyle:"italic" }}>{active.subtitle}</p>
      </div>

      {/* Content */}
      <div style={{ maxWidth:740 }}>
        {renderSection(active)}
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
//  PILLAR GOALS VIEW (BLOOM + THRIVE)
// ══════════════════════════════════════════════════════════════════

// ── Standalone so React never recreates it on parent re-render (fixes focus loss)
function ProgressSlider({ goal, pillar, handleUpdateProgress }) {
  const [localProgress, setLocalProgress] = useState(goal.progress || 0);
  const [saved, setSaved] = useState(false);

  // Sync if goal.progress changes externally
  useEffect(() => { setLocalProgress(goal.progress || 0); }, [goal.progress]);

  const handleRelease = async () => {
    await handleUpdateProgress(goal.id, localProgress);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <label style={{ fontSize:12, letterSpacing:"0.8px", color: C.muted }}>
          UPDATE PROGRESS: {localProgress}%
        </label>
        {saved && <span style={{ fontSize:11, color:"#27AE60" }}>Saved</span>}
      </div>
      <input type="range" min={0} max={100} step={5}
        value={localProgress}
        onChange={e => setLocalProgress(parseInt(e.target.value))}
        onMouseUp={handleRelease}
        onTouchEnd={handleRelease}
        style={{ width:"100%", accentColor: pillar?.color || C.gold, cursor:"pointer", height:6 }} />
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color: C.muted, marginTop:4 }}>
        <span>Just started</span><span>Halfway there</span><span>Complete!</span>
      </div>
      {localProgress !== goal.progress && (
        <button onClick={handleRelease} className="btn-hover"
          style={{ ...S.btnPrimary, marginTop:10, fontSize:12, padding:"7px 20px",
            background:`linear-gradient(135deg, ${pillar?.color || C.gold}, ${pillar?.dark || "#8A6018"})` }}>
          Save Progress
        </button>
      )}
    </div>
  );
}

function GoalCard({ goal, expandedGoal, setExpandedGoal, editingGoal, setEditingGoal,
  handleUpdateProgress, handlePauseResume, handleDelete, handleEditSave, handleAddNote }) {

  const [localNote, setLocalNote] = useState("");
  const pillar = PILLARS.find(p => p.id === goal.pillar);
  const isExpanded = expandedGoal === goal.id;
  const isEditing = editingGoal?.id === goal.id;
  const daysLeft = goal.targetDate
    ? Math.ceil((new Date(goal.targetDate) - new Date()) / 86400000)
    : null;

  return (
    <div style={{ ...S.card, padding:0, overflow:"hidden",
      borderLeft:`4px solid ${pillar?.color || C.gold}`,
      opacity: goal.status === "paused" ? 0.7 : 1 }}>

      {/* Header — click to expand */}
      <div style={{ padding:"18px 20px", cursor:"pointer" }}
        onClick={() => setExpandedGoal(isExpanded ? null : goal.id)}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12, gap:10 }}>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6, flexWrap:"wrap" }}>
              <PillarIcon pillar={pillar} size={16} />
              <span style={{ fontSize:11, background: pillar?.light, color: pillar?.dark,
                padding:"2px 10px", borderRadius:20, fontWeight:500 }}>{pillar?.short}</span>
              {goal.status === "paused" && (
                <span style={{ fontSize:11, background:"#FDF3E0", color:"#8A6018", padding:"2px 10px", borderRadius:20 }}>Paused</span>
              )}
              {goal.status === "completed" && (
                <span style={{ fontSize:11, background:"#E3EEE2", color:"#2D4228", padding:"2px 10px", borderRadius:20 }}>✅ Completed</span>
              )}
              {daysLeft !== null && goal.status === "active" && (
                <span style={{ fontSize:11, color: daysLeft < 7 ? "#C4614B" : C.muted }}>
                  {daysLeft > 0 ? `${daysLeft} days left` : daysLeft === 0 ? "Due today" : `${Math.abs(daysLeft)} days past due`}
                </span>
              )}
            </div>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, fontWeight:400, color: C.warm, margin:0 }}>
              {goal.title}
            </h3>
          </div>
          <ChevronUp size={16} color={C.muted} style={{ flexShrink:0, transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)", transition:"transform 0.2s" }} />
        </div>

        {/* Progress bar */}
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ flex:1, background: C.border, borderRadius:8, height:8 }}>
            <div style={{ height:8, borderRadius:8, transition:"width 0.4s ease",
              background: goal.progress >= 100
                ? "linear-gradient(90deg, #4A6741, #2D4228)"
                : `linear-gradient(90deg, ${pillar?.color || C.gold}, ${pillar?.color || C.gold}88)`,
              width:`${goal.progress}%` }} />
          </div>
          <span style={{ fontSize:13, fontWeight:500, color: C.warm, minWidth:36, textAlign:"right" }}>
            {goal.progress}%
          </span>
        </div>
      </div>

      {/* Expanded body */}
      {isExpanded && (
        <div style={{ borderTop:`1px solid ${C.border}`, padding:"20px" }}>

          {isEditing ? (
            /* ── Edit form ── */
            <div style={{ display:"grid", gap:14, marginBottom:20 }}>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:6 }}>GOAL TITLE</label>
                <input value={editingGoal.title}
                  onChange={e => setEditingGoal(g => ({ ...g, title: e.target.value }))}
                  style={S.input} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:6 }}>MY INTENTION FOR THIS GOAL</label>
                <textarea value={editingGoal.intention}
                  onChange={e => setEditingGoal(g => ({ ...g, intention: e.target.value }))}
                  rows={3} style={{ ...S.input, resize:"vertical", lineHeight:1.7 }} />
              </div>
              <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:6 }}>PILLAR</label>
                  <select value={editingGoal.pillar}
                    onChange={e => setEditingGoal(g => ({ ...g, pillar: e.target.value }))}
                    style={{ ...S.input, cursor:"pointer" }}>
                    {PILLARS.map(p => <option key={p.id} value={p.id}>{p.icon} {p.short}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:6 }}>TARGET DATE</label>
                  <input type="date" value={editingGoal.targetDate}
                    onChange={e => setEditingGoal(g => ({ ...g, targetDate: e.target.value }))}
                    style={S.input} />
                </div>
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={handleEditSave} className="btn-hover"
                  style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, fontSize:13 }}>
                  Save Changes
                </button>
                <button onClick={() => setEditingGoal(null)} style={{ ...S.btnGhost, fontSize:13 }}>Cancel</button>
              </div>
            </div>
          ) : (
            /* ── Goal details ── */
            <div>
              {goal.intention && (
                <div style={{ background: pillar?.light || C.cream, borderRadius:10, padding:"14px 16px", marginBottom:16 }}>
                  <div style={{ fontSize:11, letterSpacing:"0.8px", color: pillar?.dark || C.muted, marginBottom:6 }}>MY INTENTION</div>
                  <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:17, fontStyle:"italic",
                    color: C.warm, lineHeight:1.7, margin:0 }}>"{goal.intention}"</p>
                </div>
              )}

              {goal.status !== "completed" && (
                <ProgressSlider goal={goal} pillar={pillar} handleUpdateProgress={handleUpdateProgress} />
              )}

              {goal.targetDate && (
                <div style={{ fontSize:13, color: C.muted, marginBottom:16 }}>
                  Target: {new Date(goal.targetDate).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
                </div>
              )}

              <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
                <button onClick={() => setEditingGoal(goal)} style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px" }}>
                  Edit
                </button>
                {goal.status !== "completed" && (
                  <button onClick={() => handlePauseResume(goal.id)} style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px" }}>
                    {goal.status === "paused" ? "Resume" : "Pause"}
                  </button>
                )}
                <button onClick={() => handleDelete(goal.id)}
                  style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px", color: C.terracotta, borderColor: C.terracotta }}>
                  Remove
                </button>
              </div>
            </div>
          )}

          {/* Notes — always visible when expanded and not editing */}
          {!isEditing && (
            <div>
              <div style={{ fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:12 }}>REFLECTION NOTES</div>
              <div style={{ display:"flex", gap:10, marginBottom:16 }}>
                <textarea value={localNote} onChange={e => setLocalNote(e.target.value)}
                  placeholder="Add a reflection, win, obstacle, or insight about this goal..."
                  rows={2}
                  style={{ ...S.input, flex:1, resize:"none", fontSize:13, lineHeight:1.7 }} />
                <button onClick={() => { handleAddNote(goal.id, localNote); setLocalNote(""); }}
                  disabled={!localNote.trim()} className="btn-hover"
                  style={{ ...S.btnPrimary, padding:"0 16px", alignSelf:"stretch",
                    background:`linear-gradient(135deg, ${pillar?.color || C.gold}, ${pillar?.dark || "#8A6018"})`,
                    opacity: localNote.trim() ? 1 : 0.5, fontSize:13, whiteSpace:"nowrap" }}>
                  Add Note
                </button>
              </div>
              {goal.notes?.length > 0 ? (
                <div style={{ display:"grid", gap:10 }}>
                  {goal.notes.map((note, i) => (
                    <div key={i} style={{ background: C.cream, borderRadius:10, padding:"12px 14px",
                      borderLeft:`3px solid ${pillar?.color || C.gold}` }}>
                      <div style={{ fontSize:11, color: C.muted, marginBottom:6 }}>
                        {new Date(note.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric",hour:"numeric",minute:"2-digit"})}
                      </div>
                      <p style={{ fontSize:13, color: C.warm, lineHeight:1.7, margin:0 }}>{note.text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: C.muted, fontSize:13, fontStyle:"italic" }}>No notes yet. Add your first reflection above.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PillarGoalsView({ user }) {
  const plan = planInfo(user?.plan);
  const hasAccess = plan?.id === "bloom" || plan?.id === "thrive";

  const [goals, setGoals] = useState([]);
  const [viewMode, setViewMode] = useState("active"); // active | completed | new
  const [editingGoal, setEditingGoal] = useState(null);
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [saving, setSaving] = useState(false);

  // New goal form state
  const [form, setForm] = useState({
    title: "", pillar: "emotional", intention: "", targetDate: "", progress: 0
  });

  useEffect(() => {
    const load = async () => {
      const saved = await safeRetrieve(`gf_goals_${user?.email}`);
      setGoals(saved || []);
    };
    load();
  }, [user]);

  const saveGoals = async (updated) => {
    setGoals(updated);
    await safeSave(`gf_goals_${user?.email}`, updated);
  };

  const handleCreateGoal = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const goal = {
      id: `goal_${Date.now()}`,
      ...form,
      progress: 0,
      status: "active",
      notes: [],
      createdAt: Date.now(),
      completedAt: null
    };
    await saveGoals([goal, ...goals]);
    setForm({ title:"", pillar:"emotional", intention:"", targetDate:"", progress:0 });
    setViewMode("active");
    setSaving(false);
  };

  const handleUpdateProgress = async (goalId, progress) => {
    const updated = goals.map(g => {
      if (g.id !== goalId) return g;
      const completed = progress >= 100;
      return {
        ...g,
        progress,
        status: completed ? "completed" : "active",
        completedAt: completed ? Date.now() : null
      };
    });
    await saveGoals(updated);
  };

  const handleAddNote = async (goalId, noteText) => {
    if (!noteText.trim()) return;
    const note = { text: noteText, createdAt: Date.now() };
    const updated = goals.map(g =>
      g.id === goalId ? { ...g, notes: [note, ...(g.notes || [])] } : g
    );
    await saveGoals(updated);
  };

  const handlePauseResume = async (goalId) => {
    const updated = goals.map(g =>
      g.id === goalId ? { ...g, status: g.status === "paused" ? "active" : "paused" } : g
    );
    await saveGoals(updated);
  };

  const handleDelete = async (goalId) => {
    await saveGoals(goals.filter(g => g.id !== goalId));
    setExpandedGoal(null);
  };

  const handleEditSave = async () => {
    if (!editingGoal) return;
    const updated = goals.map(g => g.id === editingGoal.id ? { ...g, ...editingGoal } : g);
    await saveGoals(updated);
    setEditingGoal(null);
  };

  const activeGoals = goals.filter(g => g.status === "active" || g.status === "paused");
  const completedGoals = goals.filter(g => g.status === "completed");

  if (!hasAccess) {
    return (
      <div style={S.page}>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>My Pillar Goals</h1>
        <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
          <div style={{ marginBottom:16 }}><Target size={48} color={C.gold} /></div>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Bloom & Thrive Members</h3>
          <p style={{ color: C.muted, fontSize:14, lineHeight:1.7, marginBottom:24 }}>
            Pillar goal setting and tracking is available on Bloom and Thrive plans. Set meaningful intentions, track your progress, and capture reflections along the way.
          </p>
          <button onClick={() => startUpgradeCheckout("bloom", user?.email, user?.name)} className="btn-hover"
            style={{ ...S.btnPrimary, fontSize:14, padding:"10px 24px", cursor:"pointer" }}>
            Upgrade to Bloom ($39/mo) to unlock →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>My Pillar Goals</h1>
          <p style={{ color: C.muted, fontSize:15 }}>Set meaningful goals, track your progress, and capture your journey.</p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <div style={{ display:"flex", gap:6, background: C.cream, borderRadius:12, padding:5 }}>
            {["active","completed"].map(m => (
              <button key={m} onClick={() => setViewMode(m)}
                style={{ padding:"9px 16px", borderRadius:9, border:"none", cursor:"pointer", fontSize:13, fontWeight:500,
                  background: viewMode===m ? C.warm : "transparent",
                  color: viewMode===m ? "#FBF5EC" : C.muted, transition:"all 0.2s" }}>
                {m === "active"
                  ? `Active (${activeGoals.length})`
                  : "Completed (" + completedGoals.length + ")"}
              </button>
            ))}
          </div>
          <button onClick={() => setViewMode("new")} className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, fontSize:13 }}>
            <span style={{display:"flex",alignItems:"center",gap:6}}><Plus size={14}/>New Goal</span>
          </button>
        </div>
      </div>

      {/* Pillar summary bar */}
      {activeGoals.length > 0 && viewMode === "active" && (
        <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:24 }}>
          {PILLARS.map(p => {
            const pillarGoals = activeGoals.filter(g => g.pillar === p.id);
            if (pillarGoals.length === 0) return null;
            const avgProgress = Math.round(pillarGoals.reduce((a, g) => a + g.progress, 0) / pillarGoals.length);
            return (
              <div key={p.id} style={{ background: p.light, borderRadius:12, padding:"10px 16px",
                border:`1px solid ${p.color}44`, display:"flex", alignItems:"center", gap:10 }}>
                <PillarIcon pillar={p} size={16} />
                <div>
                  <div style={{ fontSize:12, color: p.dark, fontWeight:500 }}>{p.short}</div>
                  <div style={{ fontSize:11, color: p.color }}>{pillarGoals.length} goal{pillarGoals.length > 1 ? "s" : ""} · {avgProgress}% avg</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New goal form */}
      {viewMode === "new" && (
        <div style={{ ...S.card, maxWidth:640, marginBottom:24 }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, fontWeight:400, marginBottom:6 }}>Create a New Goal</h3>
          <p style={{ color: C.muted, fontSize:14, marginBottom:24 }}>Be specific and compassionate. The best goals feel like an act of love toward yourself.</p>

          <div style={{ display:"grid", gap:16 }}>
            <div>
              <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>GOAL TITLE *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="E.g. Practice daily emotional check-ins for 30 days"
                style={S.input} />
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>PILLAR</label>
                <select value={form.pillar} onChange={e => setForm(f => ({ ...f, pillar: e.target.value }))}
                  style={{ ...S.input, cursor:"pointer" }}>
                  {PILLARS.map(p => <option key={p.id} value={p.id}>{p.icon} {p.short}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>TARGET DATE (OPTIONAL)</label>
                <input type="date" value={form.targetDate}
                  onChange={e => setForm(f => ({ ...f, targetDate: e.target.value }))}
                  style={S.input} />
              </div>
            </div>

            <div>
              <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>MY INTENTION FOR THIS GOAL</label>
              <textarea value={form.intention} onChange={e => setForm(f => ({ ...f, intention: e.target.value }))}
                placeholder="Why does this goal matter to your healing? What will it feel like to achieve it?"
                rows={3} style={{ ...S.input, resize:"vertical", lineHeight:1.8 }} />
            </div>

            <div style={{ display:"flex", gap:10 }}>
              <button onClick={handleCreateGoal} disabled={!form.title.trim() || saving}
                className="btn-hover"
                style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
                  opacity: form.title.trim() ? 1 : 0.5 }}>
                {saving ? "Creating..." : "Create Goal"}
              </button>
              <button onClick={() => setViewMode("active")} style={{ ...S.btnGhost }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Active goals */}
      {viewMode === "active" && (
        <div style={{ maxWidth:720 }}>
          {activeGoals.length === 0 ? (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><Target size={44} color={C.gold} /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, marginBottom:8 }}>No active goals yet</p>
              <p style={{ color: C.muted, fontSize:14, marginBottom:24, lineHeight:1.7 }}>
                Setting a goal is an act of self-love. What would you like to work toward in your healing journey?
              </p>
              <button onClick={() => setViewMode("new")} className="btn-hover"
                style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)` }}>
                Create Your First Goal →
              </button>
            </div>
          ) : (
            <div style={{ display:"grid", gap:14 }}>
              {activeGoals.map(goal => (
              <GoalCard key={goal.id} goal={goal}
                expandedGoal={expandedGoal} setExpandedGoal={setExpandedGoal}
                editingGoal={editingGoal} setEditingGoal={setEditingGoal}
                handleUpdateProgress={handleUpdateProgress}
                handlePauseResume={handlePauseResume}
                handleDelete={handleDelete}
                handleEditSave={handleEditSave}
                handleAddNote={handleAddNote} />
            ))}
            </div>
          )}
        </div>
      )}

      {/* Completed goals */}
      {viewMode === "completed" && (
        <div style={{ maxWidth:720 }}>
          {completedGoals.length === 0 ? (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><Star size={44} color={C.gold} /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, marginBottom:8 }}>Your completed goals will live here</p>
              <p style={{ color: C.muted, fontSize:14 }}>Every goal you complete is evidence of your healing and growth.</p>
            </div>
          ) : (
            <div>
              <div style={{ ...S.card, background:`#E3EEE2`, border:`1px solid #4A674144`,
                marginBottom:20, padding:"16px 20px", display:"flex", alignItems:"center", gap:12 }}>
                <Star size={24} color={C.gold} />
                <div>
                  <div style={{ fontWeight:500, color:"#2D4228" }}>
                    You have completed {completedGoals.length} goal{completedGoals.length > 1 ? "s" : ""}
                  </div>
                  <div style={{ fontSize:13, color:"#4A6741" }}>
                    Each one is a testament to your commitment to healing.
                  </div>
                </div>
              </div>
              <div style={{ display:"grid", gap:14 }}>
                {completedGoals.map(goal => (
                  <GoalCard key={goal.id} goal={goal}
                    expandedGoal={expandedGoal} setExpandedGoal={setExpandedGoal}
                    editingGoal={editingGoal} setEditingGoal={setEditingGoal}
                    handleUpdateProgress={handleUpdateProgress}
                    handlePauseResume={handlePauseResume}
                    handleDelete={handleDelete}
                    handleEditSave={handleEditSave}
                    handleAddNote={handleAddNote} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  SELF-CARE BUDGET TRACKER (BLOOM + THRIVE)
// ══════════════════════════════════════════════════════════════════

const DEFAULT_CATEGORIES = [
  // Self-care (protected, highlighted)
  { id:"therapy",    name:"Therapy & Mental Health",   Icon:Brain, color:"#7A6BA0", light:"#EDE9F6", selfCare:true,  budget:0 },
  { id:"bodycare",   name:"Movement & Body Care",      Icon:Leaf, color:"#4A6741", light:"#E3EEE2", selfCare:true,  budget:0 },
  { id:"spiritual",  name:"Spiritual Practices",       Icon:Sparkles, color:"#C9963B", light:"#FDF3E0", selfCare:true,  budget:0 },
  { id:"healing",    name:"Healing Resources",         Icon:Library, color:"#C4614B", light:"#FDE8E3", selfCare:true,  budget:0 },
  // Essentials
  { id:"housing",    name:"Housing & Utilities",       Icon:Home, color:"#6B8CAE", light:"#E8F0F8", selfCare:false, budget:0 },
  { id:"food",       name:"Groceries & Nourishment",   Icon:ShoppingCart, color:"#7A9E7E", light:"#EAF3EB", selfCare:false, budget:0 },
  { id:"transport",  name:"Transportation",            Icon:Car, color:"#9E8E7A", light:"#F3EEE8", selfCare:false, budget:0 },
  // Personal
  { id:"personal",   name:"Personal & Household",      Icon:Home, color:"#8E7A9E", light:"#EEE8F3", selfCare:false, budget:0 },
  { id:"savings",    name:"Savings & Future Self",     Icon:Leaf, color:"#4A8A6B", light:"#E3F3EC", selfCare:false, budget:0 },
  { id:"other",      name:"Other",                     Icon:FileText, color:"#9E9E9E", light:"#F3F3F3", selfCare:false, budget:0 },
];

const SELF_CARE_AFFIRMATIONS = [
  "Caring for myself is not selfish. It's how I keep showing up.",
  "I can love my family generously and still protect my own stability.",
  "Investing in my healing is an investment in my whole family line.",
  "This is not a luxury. It's maintenance for a life I intend to enjoy.",
  "I am allowed to build security for myself without guilt.",
];

// Standalone ExpenseRow — outside BudgetTrackerView to prevent focus loss
function ExpenseRow({ expense, categories, onDelete }) {
  const cat = categories.find(c => c.id === expense.categoryId) || {};
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px",
      background: expense.selfCare ? cat.light || "#FDF3E0" : "#FAFAFA",
      borderRadius:10, borderLeft:`3px solid ${cat.color || C.border}` }}>
      <span style={{ flexShrink:0 }}>{cat.Icon ? <cat.Icon size={18} color={cat.color} /> : <FileText size={18} color={cat.color} />}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontWeight:500, fontSize:14, color: C.warm }}>{expense.description || cat.name}</span>
          {expense.selfCare && (
            <span style={{ fontSize:10, background: cat.color+"22", color: cat.color,
              padding:"2px 8px", borderRadius:20, fontWeight:500 }}>Self-Care</span>
          )}
        </div>
        <div style={{ fontSize:12, color: C.muted, marginTop:2 }}>
          {cat.name} · {new Date(expense.date).toLocaleDateString("en-US",{month:"short",day:"numeric"})}
          {expense.note && <span> · {expense.note}</span>}
        </div>
      </div>
      <span style={{ fontWeight:600, fontSize:15, color: expense.selfCare ? cat.color : C.warm, flexShrink:0 }}>
        ${parseFloat(expense.amount).toFixed(2)}
      </span>
      <button onClick={() => onDelete(expense.id)}
        style={{ background:"none", border:"none", cursor:"pointer", color: C.muted,
          fontSize:16, padding:"2px 6px", flexShrink:0, lineHeight:1 }}>×</button>
    </div>
  );
}

// Standalone AddExpenseForm — outside to prevent focus loss
function AddExpenseForm({ categories, onAdd, onCancel }) {
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [catId, setCatId] = useState(categories[0]?.id || "");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));

  const selectedCat = categories.find(c => c.id === catId);

  const handleSubmit = () => {
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) return;
    onAdd({
      id: `exp_${Date.now()}`,
      description: desc.trim() || selectedCat?.name || "",
      amount: parseFloat(amount),
      categoryId: catId,
      selfCare: selectedCat?.selfCare || false,
      note: note.trim(),
      date: date || new Date().toISOString().slice(0,10),
      createdAt: Date.now()
    });
    setDesc(""); setAmount(""); setNote("");
    setDate(new Date().toISOString().slice(0,10));
  };

  return (
    <div style={{ ...S.card, marginBottom:24, borderTop:`3px solid ${C.gold}` }}>
      <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, fontWeight:400, marginBottom:20 }}>
        Log an Expense
      </h3>
      <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>
        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:6 }}>CATEGORY *</label>
          <select value={catId} onChange={e => setCatId(e.target.value)} style={{ ...S.input, cursor:"pointer" }}>
            <optgroup label="Self-Care">
              {categories.filter(c => c.selfCare).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </optgroup>
            <optgroup label="Essentials & Other">
              {categories.filter(c => !c.selfCare).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:6 }}>AMOUNT ($) *</label>
          <input type="number" min="0" step="0.01" value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="0.00" style={S.input} />
        </div>
        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:6 }}>DESCRIPTION</label>
          <input value={desc} onChange={e => setDesc(e.target.value)}
            placeholder="What was this for?" style={S.input} />
        </div>
        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:6 }}>DATE</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={S.input} />
        </div>
      </div>
      <div style={{ marginBottom:18 }}>
        <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:6 }}>REFLECTION NOTE (OPTIONAL)</label>
        <input value={note} onChange={e => setNote(e.target.value)}
          placeholder="How did this expense support your healing or wellbeing?"
          style={S.input} />
      </div>
      {selectedCat?.selfCare && (
        <div style={{ background: selectedCat.light, border:`1px solid ${selectedCat.color}33`,
          borderRadius:10, padding:"10px 14px", marginBottom:16, display:"flex", gap:10, alignItems:"center" }}>
          
          <span style={{ fontSize:13, color: selectedCat.color, fontStyle:"italic" }}>
            {SELF_CARE_AFFIRMATIONS[Math.floor(Math.random() * SELF_CARE_AFFIRMATIONS.length)]}
          </span>
        </div>
      )}
      <div style={{ display:"flex", gap:10 }}>
        <button onClick={handleSubmit} disabled={!amount || parseFloat(amount) <= 0}
          className="btn-hover"
          style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
            opacity: amount && parseFloat(amount) > 0 ? 1 : 0.5 }}>
          Log Expense 💰
        </button>
        <button onClick={onCancel} style={S.btnGhost}>Cancel</button>
      </div>
    </div>
  );
}

// Category setup form — also standalone
function CategorySetupForm({ categories, income: incomeProp, onSave, onIncomeChange }) {
  const [localCats, setLocalCats] = useState(categories.map(c => ({ ...c })));
  const [income, setIncome] = useState(incomeProp || "");

  const handleIncomeChange = (val) => {
    setIncome(val);
    onIncomeChange(val);
  };

  const updateCat = (id, field, value) => {
    setLocalCats(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const addCustom = () => {
    setLocalCats(prev => [...prev, {
      id: `custom_${Date.now()}`,
      name: "New Category", Icon:Target, color:"#9E9E9E", light:"#F3F3F3",
      selfCare: false, budget: 0, custom: true
    }]);
  };

  const removeCustom = (id) => setLocalCats(prev => prev.filter(c => c.id !== id));

  const total = localCats.reduce((a, c) => a + (parseFloat(c.budget) || 0), 0);
  const remaining = (parseFloat(income) || 0) - total;

  return (
    <div style={{ ...S.card, maxWidth:680 }}>
      <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, fontWeight:400, marginBottom:6 }}>
        Monthly Budget Setup
      </h3>
      <p style={{ color: C.muted, fontSize:14, marginBottom:24 }}>
        Set your monthly income and allocate to each category. Self-care is a protected priority, not an afterthought.
      </p>

      <div style={{ marginBottom:24 }}>
        <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>MONTHLY INCOME (AFTER TAX)</label>
        <input type="number" min="0" value={income} onChange={e => handleIncomeChange(e.target.value)}
          placeholder="Enter your monthly take-home income" style={{ ...S.input, maxWidth:280 }} />
      </div>

      {/* Self-care categories */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:11, letterSpacing:"1.5px", color: C.gold, marginBottom:12, fontWeight:500 }}>
          SELF-CARE BUDGET · PROTECTED
        </div>
        <div style={{ display:"grid", gap:10 }}>
          {localCats.filter(c => c.selfCare).map(c => (
            <div key={c.id} style={{ display:"grid", gridTemplateColumns:"auto 1fr auto", gap:12,
              alignItems:"center", background: c.light, borderRadius:10, padding:"12px 14px" }}>
              <span style={{ fontSize:20 }}>{c.icon}</span>
              <div>
                {c.custom ? (
                  <input value={c.name} onChange={e => updateCat(c.id, "name", e.target.value)}
                    style={{ ...S.input, padding:"4px 8px", fontSize:13 }} />
                ) : (
                  <div style={{ fontWeight:500, fontSize:14, color: C.warm }}>{c.name}</div>
                )}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:13, color: C.muted }}>$</span>
                <input type="number" min="0" value={c.budget}
                  onChange={e => updateCat(c.id, "budget", e.target.value)}
                  style={{ ...S.input, width:90, padding:"6px 10px", fontSize:13 }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Essential categories */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:11, letterSpacing:"1.5px", color: C.muted, marginBottom:12 }}>ESSENTIALS & OTHER</div>
        <div style={{ display:"grid", gap:10 }}>
          {localCats.filter(c => !c.selfCare).map(c => (
            <div key={c.id} style={{ display:"grid", gridTemplateColumns:"auto 1fr auto auto",
              gap:12, alignItems:"center", background: C.cream, borderRadius:10, padding:"12px 14px" }}>
              <span style={{ fontSize:18 }}>{c.icon}</span>
              <div>
                {c.custom ? (
                  <input value={c.name} onChange={e => updateCat(c.id, "name", e.target.value)}
                    style={{ ...S.input, padding:"4px 8px", fontSize:13 }} />
                ) : (
                  <div style={{ fontWeight:500, fontSize:14, color: C.warm }}>{c.name}</div>
                )}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:13, color: C.muted }}>$</span>
                <input type="number" min="0" value={c.budget}
                  onChange={e => updateCat(c.id, "budget", e.target.value)}
                  style={{ ...S.input, width:90, padding:"6px 10px", fontSize:13 }} />
              </div>
              {c.custom && (
                <button onClick={() => removeCustom(c.id)}
                  style={{ background:"none", border:"none", cursor:"pointer", color: C.muted, fontSize:18 }}>×</button>
              )}
            </div>
          ))}
        </div>
        <button onClick={addCustom}
          style={{ marginTop:10, padding:"9px 16px", borderRadius:10,
            border:`1.5px dashed ${C.border}`, background:"transparent",
            color: C.muted, cursor:"pointer", fontSize:13 }}>
          + Add Custom Category
        </button>
      </div>

      {/* Summary */}
      <div style={{ background: remaining < 0 ? "#FDE8E3" : "#E3EEE2", borderRadius:12,
        padding:"14px 18px", marginBottom:20, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontSize:13, color: remaining < 0 ? "#C4614B" : "#4A6741", fontWeight:500 }}>
            {remaining < 0 ? "Over budget by" : remaining === 0 ? "Fully allocated" : "Unallocated"}
          </div>
          <div style={{ fontSize:12, color: C.muted }}>
            ${total.toFixed(2)} allocated of ${parseFloat(income || 0).toFixed(2)}
          </div>
        </div>
        <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28,
          color: remaining < 0 ? "#C4614B" : "#4A6741" }}>
          {remaining < 0 ? "-" : "+"}${Math.abs(remaining).toFixed(2)}
        </div>
      </div>

      <button onClick={() => onSave(localCats, income)} className="btn-hover"
        style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)` }}>
        Save Budget 💰
      </button>
    </div>
  );
}

function BudgetTrackerView({ user }) {
  const plan = planInfo(user?.plan);
  const hasAccess = plan?.id === "bloom" || plan?.id === "thrive";

  const monthKey = new Date().toISOString().slice(0, 7);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [income, setIncome] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [viewMode, setViewMode] = useState("overview"); // overview | log | setup | history
  const [showAddForm, setShowAddForm] = useState(false);
  const [reflection, setReflection] = useState("");
  const [reflectionSaved, setReflectionSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      const budget = await retrieve(`gf_budget_setup_${user?.email}`);
      if (budget) { setCategories(budget.categories || DEFAULT_CATEGORIES); setIncome(budget.income || ""); }
      const exps = await retrieve(`gf_expenses_${user?.email}_${monthKey}`);
      if (exps) setExpenses(exps);
      const ref = await retrieve(`gf_budget_reflection_${user?.email}_${monthKey}`);
      if (ref) setReflection(ref);
    };
    load();
  }, [user]);

  const saveSetup = async (cats, savedIncome) => {
    const incomeToSave = savedIncome !== undefined ? savedIncome : income;
    const setup = { categories: cats, income: incomeToSave };
    setCategories(cats);
    setIncome(incomeToSave);
    await store(`gf_budget_setup_${user?.email}`, setup);
    setViewMode("overview");
  };

  const addExpense = async (expense) => {
    const updated = [expense, ...expenses];
    setExpenses(updated);
    await store(`gf_expenses_${user?.email}_${monthKey}`, updated);
    setShowAddForm(false);
  };

  const deleteExpense = async (id) => {
    const updated = expenses.filter(e => e.id !== id);
    setExpenses(updated);
    await store(`gf_expenses_${user?.email}_${monthKey}`, updated);
  };

  const saveReflection = async () => {
    await store(`gf_budget_reflection_${user?.email}_${monthKey}`, reflection);
    setReflectionSaved(true);
    setTimeout(() => setReflectionSaved(false), 3000);
  };

  // Computed
  const totalIncome = parseFloat(income) || 0;
  const totalSpent = expenses.reduce((a, e) => a + e.amount, 0);
  const remaining = totalIncome - totalSpent;
  const selfCareSpent = expenses.filter(e => e.selfCare).reduce((a, e) => a + e.amount, 0);
  const selfCareBudgeted = categories.filter(c => c.selfCare).reduce((a, c) => a + (parseFloat(c.budget) || 0), 0);

  const getSpentForCat = (catId) => expenses.filter(e => e.categoryId === catId).reduce((a, e) => a + e.amount, 0);

  const getBarColor = (spent, budget) => {
    if (!budget) return C.border;
    const pct = spent / budget;
    if (pct >= 1) return "#C4614B";
    if (pct >= 0.8) return "#C9963B";
    return "#4A6741";
  };

  const monthName = new Date().toLocaleDateString("en-US", { month:"long", year:"numeric" });

  if (!hasAccess) {
    return (
      <div style={S.page}>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Self-Care Budget</h1>
        <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
          <div style={{ marginBottom:16 }}><Wallet size={48} color={C.gold} /></div>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Bloom & Thrive Members</h3>
          <p style={{ color: C.muted, fontSize:14, lineHeight:1.7, marginBottom:24 }}>
            The Self-Care Budget Tracker is available on Bloom and Thrive plans, where self-care is a protected line item, not an afterthought.
          </p>
          <button onClick={() => startUpgradeCheckout("bloom", user?.email, user?.name)} className="btn-hover"
            style={{ ...S.btnPrimary, fontSize:14, padding:"10px 24px", cursor:"pointer" }}>
            Upgrade to Bloom ($39/mo) to unlock →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>Self-Care Budget</h1>
          <p style={{ color: C.muted, fontSize:15 }}>{monthName} · Self-care is never optional.</p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <div style={{ display:"flex", gap:4, background: C.cream, borderRadius:12, padding:5 }}>
            {[
              { id:"overview", label:"Overview" },
              { id:"log",      label:`Expenses (${expenses.length})` },
              { id:"setup",    label:"Budget Setup" },
            ].map(m => (
              <button key={m.id} onClick={() => { setViewMode(m.id); setShowAddForm(false); }}
                style={{ padding:"8px 14px", borderRadius:9, border:"none", cursor:"pointer", fontSize:13, fontWeight:500,
                  background: viewMode===m.id ? C.warm : "transparent",
                  color: viewMode===m.id ? "#FBF5EC" : C.muted, transition:"all 0.2s" }}>
                {m.label}
              </button>
            ))}
          </div>
          <button onClick={() => { setShowAddForm(true); setViewMode("log"); }} className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, fontSize:13 }}>
            + Log Expense
          </button>
        </div>
      </div>

      {/* ── OVERVIEW ── */}
      {viewMode === "overview" && (
        <div style={{ maxWidth:740 }}>
          {/* Top summary cards */}
          <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:24 }}>
            {[
              { label:"Monthly Income", value:`$${totalIncome.toFixed(2)}`, Icon:Wallet,
                sub: totalIncome ? "Your foundation" : "⚠️ Set in Budget Setup", alert: !totalIncome },
              { label:"Total Spent", value:`$${totalSpent.toFixed(2)}`, Icon:BarChart2,
                sub: totalIncome ? `${Math.round((totalSpent/totalIncome)*100)}% of income` : "No income set",
                alert: totalSpent > totalIncome },
              { label:"Remaining This Month", value: `${remaining < 0 ? "-" : ""}$${Math.abs(remaining).toFixed(2)}`,
                Icon: remaining < 0 ? AlertCircle : remaining === 0 ? CheckCircle : CheckCircle,
          iconColor: remaining < 0 ? C.terracotta : remaining === 0 ? C.gold : "#27AE60",
                sub: remaining < 0 ? "You are over budget" : remaining === 0 ? "Fully spent" : "Still available to spend",
                alert: remaining < 0, success: remaining > 0 },
              { label:"Self-Care Invested", value:`$${selfCareSpent.toFixed(2)}`, Icon:Sparkles,
                sub: selfCareBudgeted ? `of $${selfCareBudgeted.toFixed(2)} budgeted` : "Budget not set",
                highlight: true },
            ].map((card, i) => (
              <div key={i} style={{ ...S.card,
                background: card.highlight ? `linear-gradient(135deg, #FDF3E0, #FBF5EC)`
                  : card.success ? `linear-gradient(135deg, #E3EEE2, #F0F5EF)` : undefined,
                border: card.highlight ? `1.5px solid ${C.gold}55`
                  : card.alert ? `1.5px solid #C4614B55`
                  : card.success ? `1.5px solid #4A674155` : undefined }}>
                <div style={{ marginBottom:8 }}><ItemIcon item={card} size={24} color={C.gold} /></div>
                <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28,
                  color: card.alert ? "#C4614B" : card.highlight ? C.gold : card.success ? "#4A6741" : C.warm,
                  marginBottom:4 }}>
                  {card.value}
                </div>
                <div style={{ fontSize:12, color: C.muted, fontWeight:500 }}>{card.label}</div>
                <div style={{ fontSize:11, color: C.muted, marginTop:2, fontStyle:"italic" }}>{card.sub}</div>
              </div>
            ))}
          </div>

          {/* Self-care spotlight */}
          {selfCareSpent > 0 && (
            <div style={{ background:`linear-gradient(135deg, #FDF3E0, #FBF5EC)`,
              border:`1.5px solid ${C.gold}44`, borderRadius:16, padding:"18px 22px", marginBottom:20,
              display:"flex", alignItems:"center", gap:14 }}>
              <Sparkles size={32} color={C.gold} />
              <div>
                <div style={{ fontWeight:500, color: C.warm, marginBottom:4 }}>
                  You have invested ${selfCareSpent.toFixed(2)} in your healing this month
                </div>
                <div style={{ fontSize:13, color: C.muted, fontStyle:"italic" }}>
                  {SELF_CARE_AFFIRMATIONS[new Date().getDate() % SELF_CARE_AFFIRMATIONS.length]}
                </div>
              </div>
            </div>
          )}

          {/* Category progress bars */}
          <div style={S.card}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, fontWeight:400, marginBottom:20 }}>
              Spending by Category
            </h3>
            {/* Self-care first */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, letterSpacing:"1.2px", color: C.gold, marginBottom:12 }}>SELF-CARE</div>
              <div style={{ display:"grid", gap:12 }}>
                {categories.filter(c => c.selfCare).map(cat => {
                  const spent = getSpentForCat(cat.id);
                  const budget = parseFloat(cat.budget) || 0;
                  const pct = budget ? Math.min((spent / budget) * 100, 100) : 0;
                  return (
                    <div key={cat.id}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                        <span style={{ fontSize:13, color: C.warm, display:"flex", alignItems:"center", gap:8 }}>
                          <span>{cat.icon}</span> {cat.name}
                        </span>
                        <span style={{ fontSize:13, color: C.muted }}>
                          ${spent.toFixed(2)}{budget ? ` / $${budget.toFixed(2)}` : ""}
                        </span>
                      </div>
                      <div style={{ background: C.border, borderRadius:8, height:10, overflow:"hidden" }}>
                        <div style={{ height:"100%", borderRadius:8, transition:"width 0.5s ease",
                          background:`linear-gradient(90deg, ${cat.color}, ${cat.color}99)`,
                          width:`${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:20 }}>
              <div style={{ fontSize:11, letterSpacing:"1.2px", color: C.muted, marginBottom:12 }}>ESSENTIALS & OTHER</div>
              <div style={{ display:"grid", gap:12 }}>
                {categories.filter(c => !c.selfCare).map(cat => {
                  const spent = getSpentForCat(cat.id);
                  const budget = parseFloat(cat.budget) || 0;
                  const pct = budget ? Math.min((spent / budget) * 100, 100) : 0;
                  const barColor = getBarColor(spent, budget);
                  return (
                    <div key={cat.id}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                        <span style={{ fontSize:13, color: C.warm, display:"flex", alignItems:"center", gap:8 }}>
                          <span>{cat.icon}</span> {cat.name}
                        </span>
                        <span style={{ fontSize:13, color: spent > budget && budget > 0 ? "#C4614B" : C.muted }}>
                          ${spent.toFixed(2)}{budget ? ` / $${budget.toFixed(2)}` : ""}
                          {spent > budget && budget > 0 && <AlertCircle size={12} color={C.terracotta} />}
                        </span>
                      </div>
                      <div style={{ background: C.border, borderRadius:8, height:8, overflow:"hidden" }}>
                        <div style={{ height:"100%", borderRadius:8, transition:"width 0.5s ease",
                          background:`linear-gradient(90deg, ${barColor}, ${barColor}99)`,
                          width:`${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Monthly reflection */}
          <div style={{ ...S.card, marginTop:20 }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:24, fontWeight:400, marginBottom:6 }}>
              Monthly Money Reflection
            </h3>
            <p style={{ color: C.muted, fontSize:13, marginBottom:16 }}>
              How does your spending this month reflect your values and healing journey? What would you like to do differently?
            </p>
            <textarea value={reflection} onChange={e => setReflection(e.target.value)} rows={4}
              placeholder="This month my spending showed me that..."
              style={{ ...S.input, resize:"vertical", lineHeight:1.8, marginBottom:12 }} />
            <button onClick={saveReflection} disabled={!reflection.trim()} className="btn-hover"
              style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
                opacity: reflection.trim() ? 1 : 0.5, fontSize:13 }}>
              {reflectionSaved ? "Saved" : "Save Reflection"}
            </button>
          </div>
        </div>
      )}

      {/* ── EXPENSE LOG ── */}
      {viewMode === "log" && (
        <div style={{ maxWidth:680 }}>
          {showAddForm && (
            <AddExpenseForm
              categories={categories}
              onAdd={addExpense}
              onCancel={() => setShowAddForm(false)} />
          )}
          {!showAddForm && (
            <button onClick={() => setShowAddForm(true)} className="btn-hover"
              style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
                marginBottom:20, fontSize:13 }}>
              + Log New Expense
            </button>
          )}
          {expenses.length === 0 ? (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><Wallet size={40} color={C.gold} /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:8 }}>No expenses logged yet</p>
              <p style={{ color: C.muted, fontSize:14 }}>Start logging your spending, including what you invest in yourself.</p>
            </div>
          ) : (
            <div>
              {/* Self-care expenses first */}
              {expenses.some(e => e.selfCare) && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:11, letterSpacing:"1.2px", color: C.gold, marginBottom:10 }}>SELF-CARE INVESTMENTS</div>
                  <div style={{ display:"grid", gap:8 }}>
                    {expenses.filter(e => e.selfCare).map(exp => (
                      <ExpenseRow key={exp.id} expense={exp} categories={categories} onDelete={deleteExpense} />
                    ))}
                  </div>
                </div>
              )}
              {expenses.some(e => !e.selfCare) && (
                <div>
                  <div style={{ fontSize:11, letterSpacing:"1.2px", color: C.muted, marginBottom:10 }}>ALL OTHER EXPENSES</div>
                  <div style={{ display:"grid", gap:8 }}>
                    {expenses.filter(e => !e.selfCare).map(exp => (
                      <ExpenseRow key={exp.id} expense={exp} categories={categories} onDelete={deleteExpense} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── BUDGET SETUP ── */}
      {viewMode === "setup" && (
        <CategorySetupForm
          categories={categories}
          income={income}
          onSave={saveSetup}
          onIncomeChange={setIncome} />
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
//  CALMING EXERCISES (THRIVE EXCLUSIVE)
// ══════════════════════════════════════════════════════════════════

const EXERCISES = [
  {
    id: "box",
    name: "Box Breathing",
    Icon: Wind,
    tagline: "Calm your nervous system in 4 counts",
    color: "#7A6BA0",
    light: "#EDE9F6",
    dark: "#4A3D6B",
    duration: 16,
    phases: [
      { label: "Breathe In",  seconds: 4, anim: "breatheIn"        },
      { label: "Hold",        seconds: 4, anim: "breatheHold"      },
      { label: "Breathe Out", seconds: 4, anim: "breatheOut"       },
      { label: "Hold Empty",  seconds: 4, anim: "breatheHoldEmpty" },
    ],
    description: "Box breathing is used by Navy SEALs and therapists alike to quickly regulate the nervous system. Equal counts of inhale, hold, exhale, and hold create a square, grounding you in the present moment.",
    affirmation: "With each breath, you return to yourself.",
  },
  {
    id: "478",
    name: "4-7-8 Breathing",
    Icon: Moon,
    tagline: "Release tension, invite rest",
    color: "#4A6741",
    light: "#E3EEE2",
    dark: "#2D4228",
    duration: 19,
    phases: [
      { label: "Breathe In",  seconds: 4, anim: "breatheIn"        },
      { label: "Hold",        seconds: 7, anim: "breatheHold"      },
      { label: "Breathe Out", seconds: 8, anim: "breatheOut"       },
    ],
    description: "Developed by Dr. Andrew Weil, 4-7-8 breathing activates the parasympathetic nervous system, your body's rest-and-digest response. Especially powerful before sleep or after an emotionally difficult moment.",
    affirmation: "You are allowed to rest. You are safe to let go.",
  },
  {
    id: "soften",
    name: "Soften & Settle",
    Icon: Heart,
    tagline: "Gentle breath for tender moments",
    color: "#C4614B",
    light: "#FDE8E3",
    dark: "#8B3C2B",
    duration: 15,
    phases: [
      { label: "Breathe In Gently",  seconds: 5, anim: "breatheIn"   },
      { label: "Soften & Release",   seconds: 5, anim: "breatheOut"  },
      { label: "Rest Here",          seconds: 5, anim: "breatheHoldEmpty" },
    ],
    description: "A softer, slower breath pattern for moments of emotional overwhelm, grief, or shutdown. There is no holding, just a gentle wave of breath that says to your body: you are safe, you can soften now.",
    affirmation: "You do not have to hold it all together right now.",
  },
  {
    id: "grounding",
    name: "5-4-3-2-1 Grounding",
    Icon: Leaf,
    tagline: "Anchor yourself to this moment",
    color: "#C9963B",
    light: "#FDF3E0",
    dark: "#8A6018",
    type: "grounding",
    description: "A powerful sensory grounding technique that interrupts anxiety, dissociation, and emotional flooding by directing attention to your immediate physical environment.",
    affirmation: "You are here. You are present. You are safe.",
    steps: [
      { count: 5, sense: "See",   Icon: Eye, prompt: "Name 5 things you can SEE right now. Look around slowly and really notice each one: its color, shape, how the light falls on it." },
      { count: 4, sense: "Touch", Icon: Activity, prompt: "Notice 4 things you can FEEL or TOUCH. The weight of your body, the texture of your clothing, the temperature of the air on your skin." },
      { count: 3, sense: "Hear",  Icon: Mic, prompt: "Listen for 3 things you can HEAR. Near sounds, far sounds, the subtle background hum of the world around you." },
      { count: 2, sense: "Smell", Icon: Wind, prompt: "Find 2 things you can SMELL. If nothing is obvious, notice the neutral scent of the air, or take a breath near something familiar and comforting." },
      { count: 1, sense: "Taste", Icon: Sparkles, prompt: "Notice 1 thing you can TASTE. The inside of your mouth, a sip of water, or simply the neutral sensation of being present in your body." },
    ],
  },
  {
    id: "bodyscan",
    name: "Gentle Body Scan",
    Icon: Sparkles,
    tagline: "Return home to your body",
    color: "#6B8CAE",
    light: "#E8F0F8",
    dark: "#3D5C7A",
    type: "scan",
    description: "A trauma-informed body scan that invites awareness without demand. You are never asked to relax, only to notice, with kindness and curiosity, what is present.",
    affirmation: "Your body has been carrying so much. This is a moment to listen.",
    scanSteps: [
      { area: "Crown & Scalp",   prompt: "Bring gentle attention to the top of your head and scalp. Notice any sensation: warmth, tightness, tingling, or nothing at all. No need to change anything." },
      { area: "Face & Jaw",      prompt: "Soften your awareness to your face. Your forehead, eyes, cheeks, and jaw. So many of us hold tension here without realizing it. Simply notice." },
      { area: "Neck & Shoulders",prompt: "Let your attention drift to your neck and shoulders, perhaps the most common home of stress in the body. Notice without judgment what is here." },
      { area: "Chest & Heart",   prompt: "Place a hand on your chest if it feels right. Feel the rise and fall. Notice what emotions or sensations live in this space right now." },
      { area: "Belly & Core",    prompt: "Bring awareness to your belly, the center of your emotional body. Is there tightness? Butterflies? Emptiness? Warmth? Let it be as it is." },
      { area: "Arms & Hands",    prompt: "Notice your arms, elbows, wrists, and hands. The hands that hold, give, create, and protect. What do they need right now?" },
      { area: "Hips & Pelvis",   prompt: "Gently bring awareness to your hips and pelvis, where so much of our story is held. Simply acknowledge this space with compassion." },
      { area: "Legs & Feet",     prompt: "Let your attention travel down through your legs to your feet. Feel the weight, the contact with the floor or seat. You are supported." },
      { area: "Your Whole Body", prompt: "Now expand your awareness to your whole body at once: this beautiful, resilient, feeling body that carries you through every day. Breathe. Rest here." },
    ],
  },
];

// Breathing animation circle
function BreathingCircle({ phase, color, light }) {
  const getAnim = () => {
    if (!phase) return {};
    const dur = `${phase.seconds}s`;
    const animMap = {
      breatheIn:        { animation:`breatheIn ${dur} ease-in forwards` },
      breatheOut:       { animation:`breatheOut ${dur} ease-out forwards` },
      breatheHold:      { animation:`breatheHold ${dur} linear forwards` },
      breatheHoldEmpty: { animation:`breatheHoldEmpty ${dur} linear forwards` },
    };
    return animMap[phase.anim] || {};
  };

  return (
    <div style={{ position:"relative", width:220, height:220, margin:"0 auto", display:"flex",
      alignItems:"center", justifyContent:"center" }}>
      {/* Ripple rings */}
      {[0,1,2].map(i => (
        <div key={i} style={{
          position:"absolute", width:220, height:220, borderRadius:"50%",
          border:`1.5px solid ${color}33`,
          animation:`ripple 3s ease-out ${i*1}s infinite`,
          pointerEvents:"none"
        }} />
      ))}
      {/* Main breathing circle */}
      <div style={{
        width:140, height:140, borderRadius:"50%",
        background:`radial-gradient(circle at 38% 38%, ${light}, ${color}66)`,
        boxShadow:`0 0 40px ${color}44, 0 0 80px ${color}22`,
        display:"flex", alignItems:"center", justifyContent:"center",
        ...getAnim(),
      }}>
        <div><BrandButterfly size={40} color={C.gold} /></div>
      </div>
    </div>
  );
}

function CalmingExercisesView({ user }) {
  const plan = planInfo(user?.plan);
  const hasAccess = plan?.id === "thrive";

  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [phaseTime, setPhaseTime] = useState(0);
  const [cycles, setCycles] = useState(0);
  const [totalCycles, setTotalCycles] = useState(3);
  const [done, setDone] = useState(false);

  // Grounding & scan state
  const [groundingStep, setGroundingStep] = useState(0);
  const [scanStep, setScanStep] = useState(0);
  const [scanAuto, setScanAuto] = useState(false);

  const timerRef = useRef(null);

  // Breathing timer
  useEffect(() => {
    if (!running || !selected || selected.type) return;
    const phases = selected.phases;
    timerRef.current = setInterval(() => {
      setPhaseTime(prev => {
        const currentPhase = phases[phaseIndex];
        if (prev + 1 >= currentPhase.seconds) {
          const nextPhase = (phaseIndex + 1) % phases.length;
          setPhaseIndex(nextPhase);
          if (nextPhase === 0) {
            setCycles(c => {
              if (c + 1 >= totalCycles) {
                clearInterval(timerRef.current);
                setRunning(false);
                setDone(true);
              }
              return c + 1;
            });
          }
          return 0;
        }
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running, phaseIndex, selected, totalCycles]);

  // Auto-advance body scan
  useEffect(() => {
    if (!scanAuto || !selected || selected.id !== "bodyscan") return;
    const t = setTimeout(() => {
      if (scanStep < selected.scanSteps.length - 1) {
        setScanStep(s => s + 1);
      } else {
        setScanAuto(false);
        setDone(true);
      }
    }, 20000);
    return () => clearTimeout(t);
  }, [scanStep, scanAuto, selected]);

  const startExercise = () => {
    setPhaseIndex(0); setPhaseTime(0); setCycles(0); setDone(false);
    setGroundingStep(0); setScanStep(0); setScanAuto(false);
    setRunning(true);
  };

  const stopExercise = () => {
    clearInterval(timerRef.current);
    setRunning(false); setDone(false);
    setPhaseIndex(0); setPhaseTime(0); setCycles(0);
  };

  const selectExercise = (ex) => {
    stopExercise();
    setSelected(ex);
    setDone(false);
  };

  const currentPhase = selected?.phases?.[phaseIndex];
  const progress = currentPhase ? (phaseTime / currentPhase.seconds) * 100 : 0;

  if (!hasAccess) {
    return (
      <div style={S.page}>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Calming Exercises</h1>
        <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
          <div style={{ marginBottom:16, animation:"float 4s ease-in-out infinite" }}><BrandButterfly size={56} color={C.gold} /></div>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Thrive Members Only</h3>
          <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:24 }}>
            Guided breathing, grounding, and body scan exercises with calming animations are exclusively available on the Thrive plan.
          </p>
          <button onClick={() => startUpgradeCheckout("thrive", user?.email, user?.name)} className="btn-hover"
            style={{ ...S.btnPrimary, fontSize:14, padding:"10px 24px", cursor:"pointer" }}>
            Upgrade to Thrive ($67/mo) to unlock →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>Calming Exercises</h1>
      <p style={{ color: C.muted, fontSize:15, marginBottom:28 }}>Gentle practices to regulate your nervous system and return to yourself.</p>

      {/* Exercise selector */}
      {!selected && (
        <div className="rg-auto" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:16, maxWidth:900 }}>
          {EXERCISES.map(ex => (
            <div key={ex.id} onClick={() => selectExercise(ex)}
              className="card-hover"
              style={{ ...S.card, borderTop:`3px solid ${ex.color}`, cursor:"pointer" }}>
              <div style={{ marginBottom:12, animation:"float 4s ease-in-out infinite" }}><ItemIcon item={ex} size={36} color={C.gold} /></div>
              <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, fontWeight:400,
                color: C.warm, marginBottom:6 }}>{ex.name}</h3>
              <p style={{ fontSize:13, color: ex.color, fontWeight:500, marginBottom:10 }}>{ex.tagline}</p>
              <p style={{ fontSize:13, color: C.muted, lineHeight:1.7 }}>
                {ex.description.slice(0, 100)}...
              </p>
              <div style={{ marginTop:14, fontSize:12, color: ex.color,
                display:"flex", alignItems:"center", gap:6 }}>
                <span>{ex.type === "grounding" ? "5 steps" : ex.type === "scan" ? "9 areas" : `${ex.phases?.length} phases · ${ex.duration}s per cycle`}</span>
                <span>→</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Exercise detail view */}
      {selected && (
        <div style={{ maxWidth:620 }}>
          <button onClick={() => { stopExercise(); setSelected(null); }}
            style={{ ...S.btnGhost, fontSize:13, marginBottom:24 }}>
            ← Back to Exercises
          </button>

          {/* ── BREATHING EXERCISES ── */}
          {!selected.type && (
            <div>
              <div style={{ ...S.card, textAlign:"center", marginBottom:20,
                background:`linear-gradient(160deg, ${selected.light}, #FBF5EC)`,
                border:`1.5px solid ${selected.color}33` }}>

                <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:32, fontWeight:400,
                  color: C.warm, marginBottom:4 }}><ItemIcon item={selected} size={22} /> {selected.name}</h2>
                <p style={{ color: selected.color, fontSize:14, fontWeight:500, marginBottom:20 }}>{selected.tagline}</p>

                {/* Breathing circle */}
                <div style={{ marginBottom:28 }}>
                  <BreathingCircle phase={running ? currentPhase : null} color={selected.color} light={selected.light} />
                </div>

                {/* Phase label */}
                <div style={{ minHeight:60, marginBottom:16 }}>
                  {running && currentPhase && (
                    <div style={{ animation:"fadeIn 0.3s ease" }}>
                      <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28,
                        color: selected.dark, marginBottom:8 }}>{currentPhase.label}</div>
                      <div style={{ fontSize:13, color: C.muted }}>
                        {currentPhase.seconds - phaseTime} seconds remaining
                      </div>
                      {/* Phase progress */}
                      <div style={{ width:120, height:4, background:`${selected.color}22`,
                        borderRadius:4, margin:"12px auto 0", overflow:"hidden" }}>
                        <div style={{ height:"100%", borderRadius:4, background: selected.color,
                          width:`${progress}%`, transition:"width 1s linear" }} />
                      </div>
                    </div>
                  )}
                  {!running && !done && (
                    <div>
                      <p style={{ color: C.muted, fontSize:14, lineHeight:1.7, marginBottom:12 }}>
                        {selected.description}
                      </p>
                      <p style={{ fontSize:13, color: C.muted, fontStyle:"italic" }}>
                        Complete {totalCycles} full cycles · {totalCycles * selected.duration}s total
                      </p>
                    </div>
                  )}
                  {done && (
                    <div style={{ animation:"fadeIn 0.5s ease" }}>
                      <div style={{ marginBottom:8 }}><Sparkles size={32} color={C.gold} /></div>
                      <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22,
                        color: selected.dark, marginBottom:6 }}>Beautiful</div>
                      <p style={{ fontSize:13, color: C.muted, fontStyle:"italic", lineHeight:1.7 }}>
                        {selected.affirmation}
                      </p>
                    </div>
                  )}
                </div>

                {/* Cycle count selector */}
                {!running && !done && (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
                    gap:16, marginBottom:20 }}>
                    <span style={{ fontSize:13, color: C.muted }}>Cycles:</span>
                    {[2,3,4,5].map(n => (
                      <button key={n} onClick={() => setTotalCycles(n)}
                        style={{ width:34, height:34, borderRadius:"50%", border:`1.5px solid ${totalCycles===n ? selected.color : C.border}`,
                          background: totalCycles===n ? selected.color : "transparent",
                          color: totalCycles===n ? "#fff" : C.muted,
                          cursor:"pointer", fontSize:13, fontWeight:500 }}>
                        {n}
                      </button>
                    ))}
                  </div>
                )}

                {/* Controls */}
                <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
                  {!running && !done && (
                    <button onClick={startExercise} className="btn-hover"
                      style={{ ...S.btnPrimary,
                        background:`linear-gradient(135deg, ${selected.color}, ${selected.dark})`,
                        padding:"12px 32px" }}>
                      Begin 🦋
                    </button>
                  )}
                  {running && (
                    <button onClick={stopExercise} style={{ ...S.btnGhost, padding:"12px 28px" }}>
                      Pause
                    </button>
                  )}
                  {done && (
                    <button onClick={startExercise} className="btn-hover"
                      style={{ ...S.btnPrimary,
                        background:`linear-gradient(135deg, ${selected.color}, ${selected.dark})`,
                        padding:"12px 28px" }}>
                      Again 🌸
                    </button>
                  )}
                </div>

                {/* Phase guide */}
                {!running && (
                  <div style={{ display:"flex", justifyContent:"center", gap:8,
                    flexWrap:"wrap", marginTop:20 }}>
                    {selected.phases.map((p, i) => (
                      <div key={i} style={{ background:`${selected.color}15`,
                        border:`1px solid ${selected.color}33`, borderRadius:20,
                        padding:"5px 14px", fontSize:12, color: selected.dark }}>
                        {p.label} · {p.seconds}s
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Cycles counter */}
              {(running || done) && (
                <div style={{ display:"flex", justifyContent:"center", gap:10 }}>
                  {Array.from({length: totalCycles}).map((_, i) => (
                    <div key={i} style={{ width:12, height:12, borderRadius:"50%",
                      background: i < cycles ? selected.color : `${selected.color}33`,
                      transition:"background 0.4s ease",
                      animation: i === cycles && running ? `shimmer 1s ease infinite` : "none" }} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── GROUNDING EXERCISE ── */}
          {selected.type === "grounding" && (
            <div>
              <div style={{ ...S.card, marginBottom:20,
                background:`linear-gradient(160deg, ${selected.light}, #FBF5EC)`,
                border:`1.5px solid ${selected.color}33` }}>
                <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:32, fontWeight:400,
                  color: C.warm, marginBottom:4 }}><ItemIcon item={selected} size={22} /> {selected.name}</h2>
                <p style={{ color: selected.color, fontSize:14, fontWeight:500, marginBottom:16 }}>{selected.tagline}</p>
                {!running && !done && (
                  <div>
                    <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:20 }}>{selected.description}</p>
                    <button onClick={() => { setRunning(true); setGroundingStep(0); setDone(false); }}
                      className="btn-hover"
                      style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${selected.color}, ${selected.dark})` }}>
                      Begin Grounding 🌿
                    </button>
                  </div>
                )}
              </div>

              {running && !done && (
                <div style={{ display:"grid", gap:14 }}>
                  {selected.steps.map((step, i) => {
                    const isActive = i === groundingStep;
                    const isDone = i < groundingStep;
                    return (
                      <div key={i} onClick={() => isActive && setGroundingStep(i + 1 < selected.steps.length ? i + 1 : i)}
                        style={{ ...S.card, cursor: isActive ? "pointer" : "default",
                          opacity: isDone ? 0.5 : 1,
                          borderLeft:`4px solid ${isActive ? selected.color : isDone ? "#4A6741" : C.border}`,
                          background: isActive ? selected.light : S.card.background,
                          transition:"all 0.3s ease",
                          transform: isActive ? "scale(1.01)" : "scale(1)" }}>
                        <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
                          <div style={{ width:44, height:44, borderRadius:"50%", flexShrink:0,
                            background: isDone ? "#4A6741" : isActive ? selected.color : C.border,
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize: isDone ? 18 : 20, color:"#fff", transition:"background 0.3s" }}>
                            {isDone ? <Check size={14} /> : (step.Icon ? <step.Icon size={14} /> : null)}
                          </div>
                          <div style={{ flex:1 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                              <span style={{ fontWeight:600, fontSize:18, color: isActive ? selected.dark : C.muted }}>
                                {step.count}
                              </span>
                              <span style={{ fontWeight:500, fontSize:15, color: isActive ? selected.dark : C.muted }}>
                                things you can {step.sense}
                              </span>
                            </div>
                            {isActive && (
                              <p style={{ fontSize:14, color: C.warm, lineHeight:1.8,
                                animation:"fadeIn 0.4s ease" }}>
                                {step.prompt}
                              </p>
                            )}
                            {isActive && (
                              <button onClick={(e) => { e.stopPropagation();
                                if (i < selected.steps.length - 1) { setGroundingStep(i + 1); }
                                else { setRunning(false); setDone(true); }}}
                                className="btn-hover"
                                style={{ ...S.btnPrimary, marginTop:14, fontSize:13, padding:"9px 20px",
                                  background:`linear-gradient(135deg, ${selected.color}, ${selected.dark})` }}>
                                {i < selected.steps.length - 1 ? "Done, next →" : "Complete"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {done && (
                <div style={{ ...S.card, textAlign:"center", padding:"40px 32px",
                  background: selected.light, border:`1.5px solid ${selected.color}33` }}>
                  <div style={{ marginBottom:12, animation:"float 4s ease-in-out infinite" }}><Leaf size={48} color="#4A6741" /></div>
                  <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>You are here</h3>
                  <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:20, fontStyle:"italic" }}>
                    {selected.affirmation}
                  </p>
                  <button onClick={() => { setDone(false); setRunning(false); setGroundingStep(0); }}
                    style={{ ...S.btnGhost, borderColor: selected.color, color: selected.color }}>
                    Begin Again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── BODY SCAN ── */}
          {selected.type === "scan" && (
            <div>
              <div style={{ ...S.card, marginBottom:20,
                background:`linear-gradient(160deg, ${selected.light}, #FBF5EC)`,
                border:`1.5px solid ${selected.color}33` }}>
                <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:32, fontWeight:400,
                  color: C.warm, marginBottom:4 }}><ItemIcon item={selected} size={22} /> {selected.name}</h2>
                <p style={{ color: selected.color, fontSize:14, fontWeight:500, marginBottom:16 }}>{selected.tagline}</p>
                {!running && !done && (
                  <div>
                    <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:20 }}>{selected.description}</p>
                    <div style={{ display:"flex", gap:10 }}>
                      <button onClick={() => { setRunning(true); setScanStep(0); setDone(false); setScanAuto(false); }}
                        className="btn-hover"
                        style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${selected.color}, ${selected.dark})` }}>
                        Begin Scan 💫
                      </button>
                      <button onClick={() => { setRunning(true); setScanStep(0); setDone(false); setScanAuto(true); }}
                        style={{ ...S.btnGhost, borderColor: selected.color, color: selected.color, fontSize:13 }}>
                        Auto-pace (20s each)
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {running && !done && (
                <div>
                  {/* Progress dots */}
                  <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:20, flexWrap:"wrap" }}>
                    {selected.scanSteps.map((_, i) => (
                      <div key={i} onClick={() => setScanStep(i)}
                        style={{ width:10, height:10, borderRadius:"50%", cursor:"pointer",
                          background: i < scanStep ? "#4A6741" : i === scanStep ? selected.color : `${selected.color}33`,
                          transition:"all 0.3s ease",
                          transform: i === scanStep ? "scale(1.4)" : "scale(1)" }} />
                    ))}
                  </div>

                  {/* Current area card */}
                  <div style={{ ...S.card, textAlign:"center", padding:"36px 28px",
                    background: selected.light, border:`1.5px solid ${selected.color}33`,
                    animation:"fadeIn 0.4s ease" }}>
                    <div style={{ fontSize:11, letterSpacing:"1.5px", color: selected.color,
                      marginBottom:12 }}>AREA {scanStep + 1} OF {selected.scanSteps.length}</div>
                    <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28,
                      color: selected.dark, marginBottom:16, fontWeight:400 }}>
                      {selected.scanSteps[scanStep].area}
                    </h3>
                    <p style={{ fontSize:15, color: C.warm, lineHeight:1.9, marginBottom:24,
                      maxWidth:460, margin:"0 auto 24px", fontStyle:"italic" }}>
                      {selected.scanSteps[scanStep].prompt}
                    </p>
                    <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
                      {scanStep > 0 && (
                        <button onClick={() => setScanStep(s => s - 1)} style={{ ...S.btnGhost, fontSize:13 }}>← Back</button>
                      )}
                      <button onClick={() => {
                        if (scanStep < selected.scanSteps.length - 1) { setScanStep(s => s + 1); }
                        else { setRunning(false); setDone(true); setScanAuto(false); }
                      }} className="btn-hover"
                        style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${selected.color}, ${selected.dark})`, fontSize:13 }}>
                        {scanStep < selected.scanSteps.length - 1 ? "Continue →" : "Complete"}
                      </button>
                    </div>
                    {scanAuto && (
                      <div style={{ marginTop:16, fontSize:12, color: C.muted }}>
                        Auto-advancing in 20 seconds
                        <button onClick={() => setScanAuto(false)} style={{ background:"none", border:"none",
                          color: selected.color, cursor:"pointer", fontSize:12, marginLeft:8 }}>
                          Turn off
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {done && (
                <div style={{ ...S.card, textAlign:"center", padding:"40px 32px",
                  background: selected.light, border:`1.5px solid ${selected.color}33` }}>
                  <div style={{ marginBottom:12, animation:"float 4s ease-in-out infinite" }}><Sparkles size={48} color={C.gold} /></div>
                  <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>
                    Beautifully done
                  </h3>
                  <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:20, fontStyle:"italic" }}>
                    {selected.affirmation}
                  </p>
                  <button onClick={() => { setDone(false); setRunning(false); setScanStep(0); setScanAuto(false); }}
                    style={{ ...S.btnGhost, borderColor: selected.color, color: selected.color }}>
                    Begin Again
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
//  WELLNESS APPOINTMENTS & PROVIDER DIRECTORY (BLOOM + THRIVE)
// ══════════════════════════════════════════════════════════════════

const APPT_TYPES = [
  { id:"therapy",   label:"Therapy & Mental Health", Icon:Brain, color:"#7A6BA0", light:"#EDE9F6" },
  { id:"medical",   label:"Medical & Doctor Visit",  Icon:Activity, color:"#6B8CAE", light:"#E8F0F8" },
  { id:"bodywork",  label:"Body Work & Massage",     Icon:Leaf, color:"#4A6741", light:"#E3EEE2" },
  { id:"spiritual", label:"Spiritual Direction",     Icon:Sparkles, color:"#C9963B", light:"#FDF3E0" },
  { id:"other",     label:"Other",                   Icon:FileText, color:"#9E9E9E", light:"#F3F3F3" },
];

// Standalone form to prevent focus loss
// ══════════════════════════════════════════════════════════════════
//  SESSION NOTE FORM
// ══════════════════════════════════════════════════════════════════
function SessionNoteForm({ onSave, onCancel, providers, appointments, initial }) {
  const [form, setForm] = useState({
    appointmentType: initial?.appointmentType || "",
    providerName:    initial?.providerName || "",
    date:            initial?.date || new Date().toISOString().split("T")[0],
    mood:            initial?.mood || "",
    reflection:      initial?.reflection || "",
    insights:        initial?.insights || "",
    homework:        initial?.homework || "",
    nextSession:     initial?.nextSession || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const MOODS = [
    { id:"great",    label:"Great",     color:"#27AE60" },
    { id:"good",     label:"Good",      color:"#4A6741" },
    { id:"okay",     label:"Okay",      color:"#C9963B" },
    { id:"hard",     label:"Hard",      color:"#C4614B" },
    { id:"veryHard", label:"Very Hard", color:"#8B1A1A" },
  ];

  const SESSION_TYPES = ["Therapy", "Psychiatry", "Primary Care", "Specialist", "Chiropractic", "Acupuncture", "Nutrition", "Coaching", "Group Therapy", "Other"];

  const handleSave = async () => {
    if (!form.reflection.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div style={{ ...S.card, marginBottom:24, border:`1.5px solid ${C.gold}44` }}>
      <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, color:C.warm, marginBottom:20 }}>
        {initial ? "Edit Session Note" : "New Session Note"}
      </h3>
      <div style={{ display:"grid", gap:16 }}>
        <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>SESSION TYPE</label>
            <select value={form.appointmentType} onChange={e => set("appointmentType", e.target.value)} style={{ ...S.input }}>
              <option value="">Select type...</option>
              {SESSION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>DATE</label>
            <input type="date" value={form.date} onChange={e => set("date", e.target.value)} style={{ ...S.input }} />
          </div>
        </div>

        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>PROVIDER NAME (optional)</label>
          <input placeholder="e.g. Dr. Smith, Sarah (therapist)..."
            value={form.providerName} onChange={e => set("providerName", e.target.value)} style={{ ...S.input }} />
        </div>

        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:10 }}>HOW DID THIS SESSION FEEL?</label>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {MOODS.map(m => (
              <button key={m.id} type="button" onClick={() => set("mood", m.id)}
                style={{ padding:"8px 16px", borderRadius:20, border:`2px solid ${form.mood === m.id ? m.color : C.border}`,
                  background: form.mood === m.id ? `${m.color}18` : "transparent",
                  color: form.mood === m.id ? m.color : C.muted, cursor:"pointer", fontSize:13, fontWeight:500,
                  transition:"all 0.2s" }}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>
            REFLECTION <span style={{ color:C.terracotta }}>*</span>
          </label>
          <p style={{ fontSize:12, color:C.muted, marginBottom:8 }}>How are you feeling after this session? What came up?</p>
          <textarea rows={4} placeholder="Today in session we explored... I noticed... I felt..."
            value={form.reflection} onChange={e => set("reflection", e.target.value)}
            style={{ ...S.textarea }} />
        </div>

        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>KEY INSIGHTS & BREAKTHROUGHS</label>
          <p style={{ fontSize:12, color:C.muted, marginBottom:8 }}>Any moments of clarity, patterns you noticed, or things that shifted?</p>
          <textarea rows={3} placeholder="I realized that... A pattern I noticed was... Something that shifted..."
            value={form.insights} onChange={e => set("insights", e.target.value)}
            style={{ ...S.textarea }} />
        </div>

        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>HOMEWORK / ACTION STEPS</label>
          <p style={{ fontSize:12, color:C.muted, marginBottom:8 }}>Tasks, practices, or intentions assigned or chosen for between sessions.</p>
          <textarea rows={3} placeholder="Practice... Try... Notice when... Before next session..."
            value={form.homework} onChange={e => set("homework", e.target.value)}
            style={{ ...S.textarea }} />
        </div>

        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>NEXT SESSION DATE (optional)</label>
          <input placeholder="e.g. March 15, 2026 or every Tuesday at 2pm"
            value={form.nextSession} onChange={e => set("nextSession", e.target.value)} style={{ ...S.input }} />
        </div>

        <div style={{ display:"flex", gap:10, paddingTop:4 }}>
          <button onClick={handleSave} disabled={!form.reflection.trim() || saving} className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
              opacity: !form.reflection.trim() || saving ? 0.6 : 1 }}>
            {saving ? "Saving..." : initial ? "Save Changes" : "Save Note"}
          </button>
          <button onClick={onCancel} style={{ ...S.btnGhost }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PRESCRIPTION FORM
// ══════════════════════════════════════════════════════════════════
function PrescriptionForm({ onSave, onCancel, providers, initial }) {
  const [form, setForm] = useState({
    medicationName:      initial?.medicationName || "",
    dosage:              initial?.dosage || "",
    frequency:           initial?.frequency || "",
    prescribingProvider: initial?.prescribingProvider || "",
    condition:           initial?.condition || "",
    startDate:           initial?.startDate || "",
    refillDate:          initial?.refillDate || "",
    pharmacy:            initial?.pharmacy || "",
    rxNumber:            initial?.rxNumber || "",
    status:              initial?.status || "active",
    notes:               initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const FREQUENCIES = ["Once daily", "Twice daily", "Three times daily", "Every morning", "Every evening", "Every night at bedtime", "As needed (PRN)", "Weekly", "Other"];

  const handleSave = async () => {
    if (!form.medicationName.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div style={{ ...S.card, marginBottom:24, border:`1.5px solid #4A674144` }}>
      <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, color:C.warm, marginBottom:6 }}>
        {initial ? "Edit Prescription" : "Add Prescription"}
      </h3>
      <p style={{ fontSize:13, color:C.muted, marginBottom:20 }}>
        For your personal reference only. Always follow your provider's guidance.
      </p>
      <div style={{ display:"grid", gap:16 }}>
        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>
            MEDICATION NAME <span style={{ color:C.terracotta }}>*</span>
          </label>
          <input placeholder="e.g. Sertraline, Metformin, Lisinopril..."
            value={form.medicationName} onChange={e => set("medicationName", e.target.value)} style={{ ...S.input }} />
        </div>

        <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>DOSAGE</label>
            <input placeholder="e.g. 50mg, 10mg/5ml..."
              value={form.dosage} onChange={e => set("dosage", e.target.value)} style={{ ...S.input }} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>FREQUENCY</label>
            <select value={form.frequency} onChange={e => set("frequency", e.target.value)} style={{ ...S.input }}>
              <option value="">Select...</option>
              {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>

        <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>PRESCRIBED BY</label>
            <input placeholder="Provider name..."
              value={form.prescribingProvider} onChange={e => set("prescribingProvider", e.target.value)} style={{ ...S.input }} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>PRESCRIBED FOR (CONDITION)</label>
            <input placeholder="e.g. Depression, Hypertension..."
              value={form.condition} onChange={e => set("condition", e.target.value)} style={{ ...S.input }} />
          </div>
        </div>

        <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>START DATE</label>
            <input type="date" value={form.startDate} onChange={e => set("startDate", e.target.value)} style={{ ...S.input }} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>NEXT REFILL DATE</label>
            <input type="date" value={form.refillDate} onChange={e => set("refillDate", e.target.value)} style={{ ...S.input }} />
          </div>
        </div>

        <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>PHARMACY</label>
            <input placeholder="e.g. Walgreens, CVS, mail order..."
              value={form.pharmacy} onChange={e => set("pharmacy", e.target.value)} style={{ ...S.input }} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>RX NUMBER (optional)</label>
            <input placeholder="From prescription label..."
              value={form.rxNumber} onChange={e => set("rxNumber", e.target.value)} style={{ ...S.input, fontFamily:"monospace" }} />
          </div>
        </div>

        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:6 }}>PERSONAL NOTES</label>
          <textarea rows={3} placeholder="Side effects to watch, instructions, food interactions, how you're responding..."
            value={form.notes} onChange={e => set("notes", e.target.value)}
            style={{ ...S.textarea }} />
        </div>

        <div style={{ display:"flex", gap:10, paddingTop:4 }}>
          <button onClick={handleSave} disabled={!form.medicationName.trim() || saving} className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, #4A6741, #2D4228)`,
              opacity: !form.medicationName.trim() || saving ? 0.6 : 1 }}>
            {saving ? "Saving..." : initial ? "Save Changes" : "Save Prescription"}
          </button>
          <button onClick={onCancel} style={{ ...S.btnGhost }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AppointmentForm({ onSave, onCancel, providers, initial }) {
  const [form, setForm] = useState(initial || {
    type:"therapy", customType:"", providerName:"", providerId:"",
    date:"", time:"", location:"", notes:""
  });
  const up = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const selType = APPT_TYPES.find(t => t.id === form.type) || APPT_TYPES[0];

  return (
    <div style={{ ...S.card, borderTop:`3px solid ${selType.color}`, marginBottom:24 }}>
      <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, fontWeight:400, marginBottom:20 }}>
        {initial ? "Edit Appointment" : "Add Appointment"}
      </h3>
      <div style={{ display:"grid", gap:14 }}>
        {/* Type */}
        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>APPOINTMENT TYPE</label>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {APPT_TYPES.map(t => (
              <button key={t.id} onClick={() => up("type", t.id)}
                style={{ padding:"10px 12px", borderRadius:10, border:`1.5px solid ${form.type===t.id ? t.color : C.border}`,
                  background: form.type===t.id ? t.light : C.white,
                  color: form.type===t.id ? t.color : C.muted,
                  cursor:"pointer", fontSize:13, textAlign:"left",
                  display:"flex", alignItems:"center", gap:8, transition:"all 0.2s" }}>
                <ItemIcon item={t} size={16} /> {t.label}
              </button>
            ))}
          </div>
          {form.type === "other" && (
            <input value={form.customType} onChange={e => up("customType", e.target.value)}
              placeholder="Describe appointment type..."
              style={{ ...S.input, marginTop:10 }} />
          )}
        </div>

        {/* Provider */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>PROVIDER</label>
            <select value={form.providerId} onChange={e => {
              const p = providers.find(p => p.id === e.target.value);
              up("providerId", e.target.value);
              if (p) up("providerName", p.name);
              else up("providerName", "");
            }} style={{ ...S.input, cursor:"pointer" }}>
              <option value="">Select or type below</option>
              {providers.map(p => <option key={p.id} value={p.id}>{p.name} · {p.specialty}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>PROVIDER NAME</label>
            <input value={form.providerName} onChange={e => up("providerName", e.target.value)}
              placeholder="Name if not in directory" style={S.input} />
          </div>
        </div>

        {/* Date & Time */}
        <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>DATE *</label>
            <input type="date" value={form.date} onChange={e => up("date", e.target.value)} style={S.input} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>TIME</label>
            <input type="time" value={form.time} onChange={e => up("time", e.target.value)} style={S.input} />
          </div>
        </div>

        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>LOCATION OR TELEHEALTH LINK</label>
          <input value={form.location} onChange={e => up("location", e.target.value)}
            placeholder="Address or video call link" style={S.input} />
        </div>

        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>NOTES (TOPICS TO DISCUSS, QUESTIONS, ETC.)</label>
          <textarea value={form.notes} onChange={e => up("notes", e.target.value)}
            rows={3} placeholder="What do you want to bring to this appointment?"
            style={{ ...S.input, resize:"vertical", lineHeight:1.7 }} />
        </div>

        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => onSave(form)} disabled={!form.date}
            className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${selType.color}, ${selType.color}cc)`,
              opacity: form.date ? 1 : 0.5 }}>
            {initial ? "Save Changes" : "Add Appointment"}
          </button>
          <button onClick={onCancel} style={S.btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Standalone provider form
function ProviderForm({ onSave, onCancel, initial }) {
  const [form, setForm] = useState(initial || {
    name:"", specialty:"", phone:"", email:"", address:"", website:"", notes:""
  });
  const up = (field, val) => setForm(f => ({ ...f, [field]: val }));

  return (
    <div style={{ ...S.card, borderTop:`3px solid ${C.gold}`, marginBottom:24 }}>
      <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, fontWeight:400, marginBottom:20 }}>
        {initial ? "Edit Provider" : "Add Provider"}
      </h3>
      <div style={{ display:"grid", gap:14 }}>
        <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>PROVIDER NAME *</label>
            <input value={form.name} onChange={e => up("name", e.target.value)} placeholder="Dr. Jane Smith" style={S.input} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>SPECIALTY / ROLE</label>
            <input value={form.specialty} onChange={e => up("specialty", e.target.value)} placeholder="Therapist, Chiropractor..." style={S.input} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>PHONE</label>
            <input type="tel" value={form.phone} onChange={e => up("phone", e.target.value)} placeholder="(555) 000-0000" style={S.input} />
          </div>
          <div>
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>EMAIL</label>
            <input type="email" value={form.email} onChange={e => up("email", e.target.value)} placeholder="provider@email.com" style={S.input} />
          </div>
        </div>
        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>ADDRESS</label>
          <input value={form.address} onChange={e => up("address", e.target.value)} placeholder="123 Healing Way, Suite 4..." style={S.input} />
        </div>
        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>WEBSITE OR PORTAL LINK</label>
          <input value={form.website} onChange={e => up("website", e.target.value)} placeholder="https://..." style={S.input} />
        </div>
        <div>
          <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>NOTES</label>
          <textarea value={form.notes} onChange={e => up("notes", e.target.value)}
            rows={2} placeholder="Insurance info, co-pay, what you like about this provider..."
            style={{ ...S.input, resize:"vertical", lineHeight:1.7 }} />
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => onSave(form)} disabled={!form.name.trim()}
            className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
              opacity: form.name.trim() ? 1 : 0.5 }}>
            {initial ? "Save Changes" : "Add Provider"}
          </button>
          <button onClick={onCancel} style={S.btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// QR scanner component
function QRScanner({ onResult, onCancel }) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const processImage = async (file) => {
    setScanning(true);
    setError("");
    try {
      // Load jsQR dynamically
      if (!window.jsQR) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js";
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }
      const img = new Image();
      img.src = URL.createObjectURL(file);
      await new Promise(r => img.onload = r);
      const canvas = document.createElement("canvas");
      canvas.width = img.width; canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = window.jsQR(imageData.data, imageData.width, imageData.height);
      if (!code) { setError("No QR code found in this image. Try again with a clearer photo."); setScanning(false); return; }
      
      // Parse result — try vCard, JSON, or plain text
      const raw = code.data;
      let parsed = { name:"", specialty:"", phone:"", email:"", address:"", website:"" };

      if (raw.startsWith("BEGIN:VCARD")) {
        // Parse vCard
        const get = (key) => { const m = raw.match(new RegExp(`${key}[^:]*:(.+)`, "i")); return m ? m[1].trim() : ""; };
        const fullName = get("FN") || get("N")?.replace(/;/g, " ").trim() || "";
        parsed.name = fullName;
        parsed.phone = get("TEL");
        parsed.email = get("EMAIL");
        parsed.address = get("ADR")?.replace(/;/g, ", ").trim() || "";
        parsed.website = get("URL");
        parsed.specialty = get("TITLE") || get("ORG") || "";
      } else if (raw.startsWith("{")) {
        try { Object.assign(parsed, JSON.parse(raw)); } catch(e) {}
      } else {
        // Plain text — put everything in name
        parsed.name = raw.slice(0, 60);
      }
      onResult(parsed);
    } catch(e) {
      setError("Something went wrong reading the QR code. Please try again or enter details manually.");
    }
    setScanning(false);
  };

  return (
    <div style={{ ...S.card, marginBottom:24, borderTop:`3px solid ${C.gold}` }}>
      <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, fontWeight:400, marginBottom:8 }}>
        📷 Scan Provider QR Code
      </h3>
      <p style={{ color:C.muted, fontSize:13, lineHeight:1.7, marginBottom:20 }}>
        Take a photo of a provider's QR code or business card QR code to automatically import their contact information.
      </p>

      <div style={{ textAlign:"center", padding:"32px 20px", background:C.cream, borderRadius:14, marginBottom:16 }}>
        <div style={{ marginBottom:12, animation:"float 4s ease-in-out infinite" }}><Camera size={56} color={C.gold} /></div>
        {scanning ? (
          <p style={{ color:C.muted, fontSize:14 }}>Reading QR code...</p>
        ) : (
          <div>
            <button onClick={() => fileRef.current?.click()} className="btn-hover"
              style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, marginBottom:10 }}>
              Take Photo or Choose Image
            </button>
            <p style={{ color:C.muted, fontSize:12, marginTop:8 }}>
              Works with vCard QR codes, business card scanners, and most provider directories
            </p>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment"
          onChange={e => e.target.files[0] && processImage(e.target.files[0])}
          style={{ display:"none" }} />
      </div>

      {error && (
        <div style={{ background:"#FDE8E3", border:`1px solid #C4614B44`, borderRadius:10,
          padding:"12px 16px", marginBottom:16, fontSize:13, color:"#8B3C2B" }}>
          {error}
        </div>
      )}

      <button onClick={onCancel} style={S.btnGhost}>Cancel and Enter Manually</button>
    </div>
  );
}

function WellnessAppointmentsView({ user }) {
  const plan = planInfo(user?.plan);
  const hasAccess = plan?.id === "bloom" || plan?.id === "thrive";

  const [appointments, setAppointments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [sessionNotes, setSessionNotes] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [tab, setTab] = useState("upcoming"); // upcoming | past | providers | notes | rx
  const [showApptForm, setShowApptForm] = useState(false);
  const [showProviderForm, setShowProviderForm] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showRxForm, setShowRxForm] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editingRx, setEditingRx] = useState(null);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [expandedNote, setExpandedNote] = useState(null);
  const [expandedRx, setExpandedRx] = useState(null);
  const [qrPrefill, setQrPrefill] = useState(null);

  useEffect(() => {
    const load = async () => {
      const appts = await safeRetrieve(`gf_appointments_${user?.email}`);
      if (appts) setAppointments(appts);
      const provs = await safeRetrieve(`gf_providers_${user?.email}`);
      if (provs) setProviders(provs);
      const notes = await safeRetrieve(`gf_session_notes_${user?.email}`);
      if (notes) setSessionNotes(notes);
      const rx = await safeRetrieve(`gf_prescriptions_${user?.email}`);
      if (rx) setPrescriptions(rx);
    };
    load();
  }, [user]);

  const saveNotes = async (updated) => { setSessionNotes(updated); await safeSave(`gf_session_notes_${user?.email}`, updated); };
  const saveRx = async (updated) => { setPrescriptions(updated); await safeSave(`gf_prescriptions_${user?.email}`, updated); };

  const saveAppts = async (updated) => { setAppointments(updated); await safeSave(`gf_appointments_${user?.email}`, updated); };
  const saveProviders = async (updated) => { setProviders(updated); await safeSave(`gf_providers_${user?.email}`, updated); };

  const addAppointment = async (form) => {
    const appt = { id:`appt_${Date.now()}`, ...form,
      type: form.type === "other" && form.customType ? form.customType : (APPT_TYPES.find(t => t.id === form.type)?.label || form.type),
      typeId: form.type, createdAt: Date.now() };
    await saveAppts([appt, ...appointments]);
    setShowApptForm(false);
  };

  const updateAppointment = async (form) => {
    const updated = appointments.map(a => a.id === editingAppt.id
      ? { ...a, ...form, type: form.type === "other" && form.customType ? form.customType : (APPT_TYPES.find(t => t.id === form.type)?.label || form.type), typeId: form.type }
      : a);
    await saveAppts(updated);
    setEditingAppt(null);
  };

  const deleteAppt = async (id) => await saveAppts(appointments.filter(a => a.id !== id));

  const addProvider = async (form) => {
    const prov = { id:`prov_${Date.now()}`, ...form, createdAt: Date.now() };
    await saveProviders([prov, ...providers]);
    setShowProviderForm(false);
    setQrPrefill(null);
  };

  const updateProvider = async (form) => {
    await saveProviders(providers.map(p => p.id === editingProvider.id ? { ...p, ...form } : p));
    setEditingProvider(null);
  };

  const deleteProvider = async (id) => { await saveProviders(providers.filter(p => p.id !== id)); setExpandedProvider(null); };

  const handleQRResult = (parsed) => { setShowQR(false); setQrPrefill(parsed); setShowProviderForm(true); };

  const today = new Date().toISOString().split("T")[0];
  const upcomingAppts = appointments.filter(a => a.date >= today).sort((a,b) => a.date.localeCompare(b.date));
  const pastAppts = appointments.filter(a => a.date < today).sort((a,b) => b.date.localeCompare(a.date));

  const formatDate = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"});
  const formatTime = (t) => { if (!t) return ""; const [h, m] = t.split(":"); const hr = parseInt(h); return `${hr > 12 ? hr-12 : hr || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`; };

  const getDaysUntil = (date) => {
    const diff = Math.ceil((new Date(date + "T12:00:00") - new Date()) / 86400000);
    if (diff === 0) return { label:"Today", color:"#C4614B" };
    if (diff === 1) return { label:"Tomorrow", color:"#C9963B" };
    if (diff <= 7) return { label:`In ${diff} days`, color:"#4A6741" };
    return { label:`In ${diff} days`, color:C.muted };
  };

  if (!hasAccess) {
    return (
      <div style={S.page}>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Wellness Appointments</h1>
        <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
          <div style={{ marginBottom:16 }}><CalendarDays size={48} color={C.gold} /></div>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Bloom & Thrive Members</h3>
          <p style={{ color:C.muted, fontSize:14, lineHeight:1.7, marginBottom:24 }}>
            Track wellness appointments, store provider information, and receive in-app reminders. Available on Bloom and Thrive.
          </p>
          <div style={{ ...S.tag, background:`${C.gold}20`, color:C.gold, fontSize:14, padding:"8px 20px" }}>
            Upgrade to Bloom ($39/mo) to unlock
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>Wellness Appointments</h1>
          <p style={{ color:C.muted, fontSize:15 }}>Your healing calendar and provider directory in one place.</p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          <div className="tab-scroll" style={{ display:"flex", gap:4, background:C.cream, borderRadius:12, padding:5, flexWrap:"wrap" }}>
            {[
              { id:"upcoming", label:`Upcoming (${upcomingAppts.length})`, Icon: CalendarDays },
              { id:"past",     label:`Past (${pastAppts.length})`, Icon: FileText },
              { id:"providers",label:`Providers (${providers.length})`, Icon: User },
              { id:"notes",    label:`Session Notes (${sessionNotes.length})`, Icon: Pencil },
              { id:"rx",       label:`Prescriptions (${prescriptions.length})`, Icon: Pill },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ padding:"8px 14px", borderRadius:9, border:"none", cursor:"pointer", fontSize:13, fontWeight:500,
                  background: tab===t.id ? C.warm : "transparent",
                  color: tab===t.id ? "#FBF5EC" : C.muted, transition:"all 0.2s" }}>
                {t.label}
              </button>
            ))}
          </div>
          {tab === "upcoming" || tab === "past" ? (
            <button onClick={() => { setShowApptForm(true); setEditingAppt(null); }} className="btn-hover"
              style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, fontSize:13 }}>
              <span style={{display:"flex",alignItems:"center",gap:6}}><Plus size={14}/>Add Appointment</span>
            </button>
          ) : tab === "providers" ? (
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={() => setShowQR(true)}
                style={{ ...S.btnGhost, fontSize:13, display:"flex", alignItems:"center", gap:6 }}>
                Scan QR
              </button>
              <button onClick={() => { setShowProviderForm(true); setEditingProvider(null); setQrPrefill(null); }} className="btn-hover"
                style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, fontSize:13 }}>
                <span style={{display:"flex",alignItems:"center",gap:6}}><Plus size={14}/>Add Provider</span>
              </button>
            </div>
          ) : tab === "notes" ? (
            <button onClick={() => { setShowNoteForm(true); setEditingNote(null); }} className="btn-hover"
              style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, fontSize:13 }}>
              <span style={{display:"flex",alignItems:"center",gap:6}}><Plus size={14}/>Add Note</span>
            </button>
          ) : tab === "rx" ? (
            <button onClick={() => { setShowRxForm(true); setEditingRx(null); }} className="btn-hover"
              style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)`, fontSize:13 }}>
              <span style={{display:"flex",alignItems:"center",gap:6}}><Plus size={14}/>Add Prescription</span>
            </button>
          ) : null}
        </div>
      </div>

      {/* ── UPCOMING TAB ── */}
      {tab === "upcoming" && (
        <div style={{ maxWidth:680 }}>
          {(showApptForm || editingAppt) && (
            <AppointmentForm
              providers={providers}
              initial={editingAppt}
              onSave={editingAppt ? updateAppointment : addAppointment}
              onCancel={() => { setShowApptForm(false); setEditingAppt(null); }} />
          )}
          {!showApptForm && !editingAppt && upcomingAppts.length === 0 && (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><CalendarDays size={44} color={C.gold} /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:8 }}>No upcoming appointments</p>
              <p style={{ color:C.muted, fontSize:14, marginBottom:20, lineHeight:1.7 }}>
                Add your next therapy session, doctor visit, or healing appointment to keep your wellness calendar organized.
              </p>
              <button onClick={() => setShowApptForm(true)} className="btn-hover"
                style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)` }}>
                Add Your First Appointment →
              </button>
            </div>
          )}
          <div style={{ display:"grid", gap:14 }}>
            {upcomingAppts.map(appt => {
              const typeData = APPT_TYPES.find(t => t.id === appt.typeId) || APPT_TYPES[4];
              const daysUntil = getDaysUntil(appt.date);
              return (
                <div key={appt.id} style={{ ...S.card, borderLeft:`4px solid ${typeData.color}`, padding:0, overflow:"hidden" }}>
                  <div style={{ padding:"18px 20px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, flexWrap:"wrap" }}>
                          <ItemIcon item={typeData} size={20} />
                          <span style={{ fontSize:11, background:typeData.light, color:typeData.color,
                            padding:"2px 10px", borderRadius:20, fontWeight:500 }}>{appt.type}</span>
                          <span style={{ fontSize:12, fontWeight:500, color:daysUntil.color }}>
                            {daysUntil.label}
                          </span>
                        </div>
                        <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, color:C.warm, marginBottom:4 }}>
                          {appt.providerName || appt.type}
                        </div>
                        <div style={{ fontSize:13, color:C.muted, display:"flex", gap:14, flexWrap:"wrap" }}>
                          <span>{formatDate(appt.date)}</span>
                          {appt.time && <span>{formatTime(appt.time)}</span>}
                          {appt.location && <span>{appt.location}</span>}
                        </div>
                        {appt.notes && (
                          <div style={{ marginTop:10, background:typeData.light, borderRadius:8,
                            padding:"10px 12px", fontSize:13, color:C.warm, lineHeight:1.7, fontStyle:"italic" }}>
                            "{appt.notes}"
                          </div>
                        )}
                      </div>
                      <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                        <button onClick={() => { setEditingAppt(appt); setShowApptForm(false); }}
                          style={{ ...S.btnGhost, fontSize:12, padding:"6px 12px" }}>✏️</button>
                        <button onClick={() => deleteAppt(appt.id)}
                          style={{ ...S.btnGhost, fontSize:12, padding:"6px 12px",
                            color:C.terracotta, borderColor:C.terracotta }}>🗑</button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── PAST TAB ── */}
      {tab === "past" && (
        <div style={{ maxWidth:680 }}>
          {pastAppts.length === 0 ? (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><FileText size={40} color={C.muted} /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:8 }}>No past appointments yet</p>
              <p style={{ color:C.muted, fontSize:14 }}>Your appointment history will appear here.</p>
            </div>
          ) : (
            <div style={{ display:"grid", gap:12 }}>
              {pastAppts.map(appt => {
                const typeData = APPT_TYPES.find(t => t.id === appt.typeId) || APPT_TYPES[4];
                return (
                  <div key={appt.id} style={{ ...S.card, borderLeft:`4px solid ${C.border}`, opacity:0.8 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10 }}>
                      <div>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                          <ItemIcon item={typeData} size={16} />
                          <span style={{ fontWeight:500, fontSize:14, color:C.warm }}>{appt.providerName || appt.type}</span>
                          <span style={{ fontSize:11, background:typeData.light, color:typeData.color,
                            padding:"2px 8px", borderRadius:20 }}>{appt.type}</span>
                        </div>
                        <div style={{ fontSize:12, color:C.muted }}>
                          {formatDate(appt.date)}{appt.time ? ` · ${formatTime(appt.time)}` : ""}
                        </div>
                        {appt.notes && <div style={{ fontSize:12, color:C.muted, marginTop:4, fontStyle:"italic" }}>"{appt.notes}"</div>}
                      </div>
                      <button onClick={() => deleteAppt(appt.id)}
                        style={{ ...S.btnGhost, fontSize:12, padding:"6px 10px", color:C.muted, flexShrink:0 }}>🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── PROVIDERS TAB ── */}
      {tab === "providers" && (
        <div style={{ maxWidth:680 }}>
          {showQR && <QRScanner onResult={handleQRResult} onCancel={() => setShowQR(false)} />}

          {(showProviderForm || editingProvider) && (
            <ProviderForm
              initial={editingProvider || qrPrefill}
              onSave={editingProvider ? updateProvider : addProvider}
              onCancel={() => { setShowProviderForm(false); setEditingProvider(null); setQrPrefill(null); }} />
          )}

          {!showProviderForm && !editingProvider && !showQR && providers.length === 0 && (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><Heart size={44} color="#C4614B" /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:8 }}>No providers yet</p>
              <p style={{ color:C.muted, fontSize:14, marginBottom:20, lineHeight:1.7 }}>
                Store your therapist, doctor, and wellness provider information here for easy access when scheduling.
              </p>
              <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
                <button onClick={() => setShowQR(true)} style={{ ...S.btnGhost }}>
                  Scan QR Code
                </button>
                <button onClick={() => setShowProviderForm(true)} className="btn-hover"
                  style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)` }}>
                  Add First Provider →
                </button>
              </div>
            </div>
          )}

          <div style={{ display:"grid", gap:14 }}>
            {providers.map(prov => {
              const isExpanded = expandedProvider === prov.id;
              return (
                <div key={prov.id} style={{ ...S.card, padding:0, overflow:"hidden" }}>
                  <div style={{ padding:"18px 20px", cursor:"pointer", display:"flex",
                    justifyContent:"space-between", alignItems:"center", gap:10 }}
                    onClick={() => setExpandedProvider(isExpanded ? null : prov.id)}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20,
                        color:C.warm, marginBottom:4 }}>{prov.name}</div>
                      <div style={{ fontSize:13, color:C.muted }}>
                        {prov.specialty}{prov.phone ? ` · ${prov.phone}` : ""}
                      </div>
                    </div>
                    <ChevronUp size={16} color={C.muted} style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)", transition:"transform 0.2s" }} />
                  </div>

                  {isExpanded && (
                    <div style={{ borderTop:`1px solid ${C.border}`, padding:"18px 20px" }}>
                      <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
                        {prov.phone && (
                          <a href={`tel:${prov.phone}`} style={{ textDecoration:"none" }}>
                            <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                              <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>PHONE</div>
                              <div style={{ fontSize:14, color:C.gold, fontWeight:500 }}>{prov.phone}</div>
                            </div>
                          </a>
                        )}
                        {prov.email && (
                          <a href={`mailto:${prov.email}`} style={{ textDecoration:"none" }}>
                            <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                              <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>EMAIL</div>
                              <div style={{ fontSize:14, color:C.gold, fontWeight:500 }}>{prov.email}</div>
                            </div>
                          </a>
                        )}
                        {prov.address && (
                          <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px", gridColumn:"1/-1" }}>
                            <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>ADDRESS</div>
                            <div style={{ fontSize:13, color:C.warm }}>{prov.address}</div>
                          </div>
                        )}
                        {prov.website && (
                          <a href={prov.website} target="_blank" rel="noopener noreferrer"
                            style={{ textDecoration:"none", gridColumn:"1/-1" }}>
                            <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                              <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>WEBSITE / PORTAL</div>
                              <div style={{ fontSize:13, color:C.gold }}>{prov.website}</div>
                            </div>
                          </a>
                        )}
                      </div>
                      {prov.notes && (
                        <div style={{ background:`${C.gold}10`, border:`1px solid ${C.gold}33`,
                          borderRadius:10, padding:"12px 14px", marginBottom:16, fontSize:13,
                          color:C.warm, lineHeight:1.7, fontStyle:"italic" }}>
                          "{prov.notes}"
                        </div>
                      )}
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={() => { setEditingProvider(prov); setShowProviderForm(false); }}
                          style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px" }}>Edit</button>
                        <button onClick={() => deleteProvider(prov.id)}
                          style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px",
                            color:C.terracotta, borderColor:C.terracotta }}>Remove</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SESSION NOTES TAB ── */}
      {tab === "notes" && (
        <div style={{ maxWidth:720 }}>

          {/* Note Form */}
          {(showNoteForm || editingNote) && (
            <SessionNoteForm
              providers={providers}
              appointments={[...pastAppts, ...upcomingAppts]}
              initial={editingNote}
              onSave={async (form) => {
                if (editingNote) {
                  await saveNotes(sessionNotes.map(n => n.id === editingNote.id ? { ...n, ...form } : n));
                  setEditingNote(null);
                } else {
                  await saveNotes([{ id:`note_${Date.now()}`, ...form, createdAt: Date.now() }, ...sessionNotes]);
                  setShowNoteForm(false);
                }
              }}
              onCancel={() => { setShowNoteForm(false); setEditingNote(null); }} />
          )}

          {!showNoteForm && !editingNote && sessionNotes.length === 0 && (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><Pencil size={44} color={C.gold} /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:8 }}>No session notes yet</p>
              <p style={{ color:C.muted, fontSize:14, lineHeight:1.7, marginBottom:20 }}>
                Record reflections, breakthroughs, homework, and insights from therapy, doctor visits, and healing appointments.
              </p>
              <button onClick={() => setShowNoteForm(true)} className="btn-hover"
                style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)` }}>
                Add Your First Note →
              </button>
            </div>
          )}

          <div style={{ display:"grid", gap:14 }}>
            {sessionNotes.map(note => {
              const isExpanded = expandedNote === note.id;
              const moodColors = { great:"#27AE60", good:"#4A6741", okay:"#C9963B", hard:"#C4614B", veryHard:"#8B1A1A" };
              const moodLabels = { great:"Great session", good:"Good session", okay:"Okay session", hard:"Hard session", veryHard:"Very hard session" };
              return (
                <div key={note.id} style={{ ...S.card, padding:0, overflow:"hidden",
                  borderLeft:`4px solid ${moodColors[note.mood] || C.gold}` }}>
                  <div style={{ padding:"18px 20px", cursor:"pointer" }}
                    onClick={() => setExpandedNote(isExpanded ? null : note.id)}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:6 }}>
                          <span style={{ fontWeight:500, fontSize:15, color:C.warm }}>
                            {note.appointmentType || "Session"}
                          </span>
                          {note.providerName && (
                            <span style={{ fontSize:12, color:C.muted }}>· {note.providerName}</span>
                          )}
                          {note.mood && (
                            <span style={{ fontSize:11, padding:"2px 10px", borderRadius:20,
                              background:`${moodColors[note.mood]}15`, color:moodColors[note.mood] }}>
                              {moodLabels[note.mood]}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize:12, color:C.muted }}>
                          {note.date ? new Date(note.date + "T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"}) : new Date(note.createdAt).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
                        </div>
                        {!isExpanded && note.reflection && (
                          <p style={{ fontSize:13, color:C.muted, marginTop:6, fontStyle:"italic",
                            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:480 }}>
                            "{note.reflection}"
                          </p>
                        )}
                      </div>
                      <ChevronUp size={16} color={C.muted} style={{ flexShrink:0, transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)", transition:"transform 0.2s" }} />
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ borderTop:`1px solid ${C.border}`, padding:"20px" }}>
                      {note.reflection && (
                        <div style={{ marginBottom:16 }}>
                          <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>REFLECTION</div>
                          <p style={{ fontSize:14, color:C.warm, lineHeight:1.85, fontStyle:"italic",
                            background:`${C.gold}08`, borderRadius:10, padding:"14px 16px",
                            borderLeft:`3px solid ${C.gold}` }}>
                            "{note.reflection}"
                          </p>
                        </div>
                      )}
                      {note.insights && (
                        <div style={{ marginBottom:16 }}>
                          <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>KEY INSIGHTS / BREAKTHROUGHS</div>
                          <p style={{ fontSize:14, color:C.warm, lineHeight:1.85, background:C.cream,
                            borderRadius:10, padding:"14px 16px" }}>{note.insights}</p>
                        </div>
                      )}
                      {note.homework && (
                        <div style={{ marginBottom:16 }}>
                          <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:8 }}>HOMEWORK / ACTION STEPS</div>
                          <div style={{ fontSize:14, color:C.warm, lineHeight:1.85, background:`#E3EEE2`,
                            borderRadius:10, padding:"14px 16px", borderLeft:"3px solid #4A6741" }}>
                            {note.homework}
                          </div>
                        </div>
                      )}
                      {note.nextSession && (
                        <div style={{ fontSize:13, color:C.muted, marginBottom:16 }}>
                          Next session: <span style={{ color:C.warm }}>{note.nextSession}</span>
                        </div>
                      )}
                      <div style={{ display:"flex", gap:8 }}>
                        <button onClick={() => { setEditingNote(note); setShowNoteForm(false); }}
                          style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px" }}>Edit</button>
                        <button onClick={() => saveNotes(sessionNotes.filter(n => n.id !== note.id))}
                          style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px",
                            color:C.terracotta, borderColor:C.terracotta }}>Remove</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── PRESCRIPTIONS TAB ── */}
      {tab === "rx" && (
        <div style={{ maxWidth:720 }}>

          {/* Privacy notice */}
          <div style={{ background:`${C.gold}10`, border:`1px solid ${C.gold}33`,
            borderRadius:12, padding:"12px 16px", marginBottom:20,
            display:"flex", gap:10, alignItems:"flex-start" }}>
            <Shield size={16} style={{ flexShrink:0, color: C.gold }} />
            <p style={{ fontSize:13, color:C.warm, lineHeight:1.7, margin:0 }}>
              Your prescription information is stored securely and privately. It is never shared with third parties.
              This is for your personal reference only. Always consult your prescribing provider for medical decisions.
            </p>
          </div>

          {/* Rx Form */}
          {(showRxForm || editingRx) && (
            <PrescriptionForm
              providers={providers}
              initial={editingRx}
              onSave={async (form) => {
                if (editingRx) {
                  await saveRx(prescriptions.map(r => r.id === editingRx.id ? { ...r, ...form } : r));
                  setEditingRx(null);
                } else {
                  await saveRx([{ id:`rx_${Date.now()}`, ...form, createdAt: Date.now() }, ...prescriptions]);
                  setShowRxForm(false);
                }
              }}
              onCancel={() => { setShowRxForm(false); setEditingRx(null); }} />
          )}

          {!showRxForm && !editingRx && prescriptions.length === 0 && (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px" }}>
              <div style={{ marginBottom:12 }}><Pill size={44} color="#4A6741" /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:8 }}>No prescriptions tracked yet</p>
              <p style={{ color:C.muted, fontSize:14, lineHeight:1.7, marginBottom:20 }}>
                Keep your prescription information handy to share with providers at appointments.
              </p>
              <button onClick={() => setShowRxForm(true)} className="btn-hover"
                style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #8A6018)` }}>
                Add First Prescription →
              </button>
            </div>
          )}

          <div style={{ display:"grid", gap:14 }}>
            {prescriptions.map(rx => {
              const isExpanded = expandedRx === rx.id;
              const isActive = rx.status !== "discontinued";
              const refillSoon = rx.refillDate && Math.ceil((new Date(rx.refillDate + "T12:00:00") - new Date()) / 86400000) <= 7;
              return (
                <div key={rx.id} style={{ ...S.card, padding:0, overflow:"hidden",
                  borderLeft:`4px solid ${isActive ? "#4A6741" : C.border}`,
                  opacity: isActive ? 1 : 0.7 }}>
                  <div style={{ padding:"18px 20px", cursor:"pointer" }}
                    onClick={() => setExpandedRx(isExpanded ? null : rx.id)}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:4 }}>
                          <span style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, color:C.warm }}>
                            {rx.medicationName}
                          </span>
                          {rx.dosage && (
                            <span style={{ fontSize:12, background:"#E3EEE2", color:"#2D4228",
                              padding:"2px 10px", borderRadius:20, fontWeight:500 }}>{rx.dosage}</span>
                          )}
                          {rx.status === "discontinued" && (
                            <span style={{ fontSize:11, background:C.cream, color:C.muted,
                              padding:"2px 10px", borderRadius:20 }}>Discontinued</span>
                          )}
                          {refillSoon && (
                            <span style={{ fontSize:11, background:"#FDE8E3", color:C.terracotta,
                              padding:"2px 10px", borderRadius:20 }}>Refill soon</span>
                          )}
                        </div>
                        <div style={{ fontSize:12, color:C.muted }}>
                          {rx.frequency && <span>{rx.frequency}</span>}
                          {rx.prescribingProvider && <span> · Dr. {rx.prescribingProvider}</span>}
                        </div>
                      </div>
                      <ChevronUp size={16} color={C.muted} style={{ flexShrink:0, transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)", transition:"transform 0.2s" }} />
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ borderTop:`1px solid ${C.border}`, padding:"20px" }}>
                      <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
                        {rx.dosage && (
                          <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                            <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>DOSAGE</div>
                            <div style={{ fontSize:14, color:C.warm, fontWeight:500 }}>{rx.dosage}</div>
                          </div>
                        )}
                        {rx.frequency && (
                          <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                            <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>FREQUENCY</div>
                            <div style={{ fontSize:14, color:C.warm, fontWeight:500 }}>{rx.frequency}</div>
                          </div>
                        )}
                        {rx.prescribingProvider && (
                          <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                            <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>PRESCRIBED BY</div>
                            <div style={{ fontSize:14, color:C.warm }}>{rx.prescribingProvider}</div>
                          </div>
                        )}
                        {rx.pharmacy && (
                          <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                            <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>PHARMACY</div>
                            <div style={{ fontSize:14, color:C.warm }}>{rx.pharmacy}</div>
                          </div>
                        )}
                        {rx.startDate && (
                          <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                            <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>START DATE</div>
                            <div style={{ fontSize:14, color:C.warm }}>{new Date(rx.startDate + "T12:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</div>
                          </div>
                        )}
                        {rx.refillDate && (
                          <div style={{ background: refillSoon ? "#FDE8E3" : C.cream, borderRadius:10, padding:"12px 14px",
                            border: refillSoon ? `1px solid ${C.terracotta}44` : "none" }}>
                            <div style={{ fontSize:11, letterSpacing:"0.8px", color: refillSoon ? C.terracotta : C.muted, marginBottom:4 }}>REFILL DATE</div>
                            <div style={{ fontSize:14, color: refillSoon ? C.terracotta : C.warm, fontWeight: refillSoon ? 500 : 400 }}>
                              {refillSoon ? "🔔 " : "🔄 "}{new Date(rx.refillDate + "T12:00:00").toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}
                            </div>
                          </div>
                        )}
                        {rx.rxNumber && (
                          <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                            <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>RX NUMBER</div>
                            <div style={{ fontSize:13, color:C.warm, fontFamily:"monospace" }}>{rx.rxNumber}</div>
                          </div>
                        )}
                        {rx.condition && (
                          <div style={{ background:C.cream, borderRadius:10, padding:"12px 14px" }}>
                            <div style={{ fontSize:11, letterSpacing:"0.8px", color:C.muted, marginBottom:4 }}>PRESCRIBED FOR</div>
                            <div style={{ fontSize:14, color:C.warm }}>🩺 {rx.condition}</div>
                          </div>
                        )}
                      </div>
                      {rx.notes && (
                        <div style={{ background:`${C.gold}08`, border:`1px solid ${C.gold}33`,
                          borderRadius:10, padding:"12px 14px", marginBottom:16,
                          fontSize:13, color:C.warm, lineHeight:1.7, fontStyle:"italic" }}>
                          "{rx.notes}"
                        </div>
                      )}
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                        <button onClick={() => { setEditingRx(rx); setShowRxForm(false); }}
                          style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px" }}>Edit</button>
                        <button onClick={() => saveRx(prescriptions.map(r => r.id === rx.id ? { ...r, status: r.status === "discontinued" ? "active" : "discontinued" } : r))}
                          style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px" }}>
                          {rx.status === "discontinued" ? "Mark Active" : "Mark Discontinued"}
                        </button>
                        <button onClick={() => { if(window.confirm("Remove this prescription?")) saveRx(prescriptions.filter(r => r.id !== rx.id)); }}
                          style={{ ...S.btnGhost, fontSize:12, padding:"7px 14px",
                            color:C.terracotta, borderColor:C.terracotta }}>Remove</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
//  WELLNESS ROADMAP VIEW (THRIVE EXCLUSIVE)
// ══════════════════════════════════════════════════════════════════

const ROADMAP_QUESTIONS = [
  // Opening
  { id: "q1", pillar: "intro", section: "About You",
    question: "What is your first name, and what brought you to ThriveRoot right now?",
    placeholder: "Share as much or as little as feels right...",
    type: "textarea" },
  { id: "q2", pillar: "intro", section: "About You",
    question: "Who depends on you day to day, and who do you lean on?",
    placeholder: "E.g. my kids, my mother, my job, my church family... and honestly, not many people I lean on...",
    type: "textarea" },

  // Emotional
  { id: "q3", pillar: "emotional", section: "Emotional Healing",
    question: "On a scale of 1 to 10, how would you rate your emotional wellness right now? What makes it that number?",
    placeholder: "E.g. a 4, because I've been holding a lot and haven't told anyone...",
    type: "textarea" },
  { id: "q4", pillar: "emotional", section: "Emotional Healing",
    question: "When something is weighing on you, what do you usually do with it? Who, if anyone, do you tell?",
    placeholder: "E.g. I keep it to myself and keep going, or I call my sister, or I eat and watch TV...",
    type: "textarea" },
  { id: "q5", pillar: "emotional", section: "Emotional Healing",
    question: "Is there a loss, hurt, or hard season you never had the space to fully work through? Share only what feels safe.",
    placeholder: "Share only what feels comfortable...",
    type: "textarea" },

  // Physical
  { id: "q6", pillar: "physical", section: "Physical Embodiment",
    question: "When did you last rest without feeling guilty? What does rest look like in your life right now?",
    placeholder: "E.g. I can't remember, or I only rest when I get sick...",
    type: "textarea" },
  { id: "q7", pillar: "physical", section: "Physical Embodiment",
    question: "What is your relationship with food like right now? Do you find yourself reaching for food when you're stressed, tired, or sad?",
    placeholder: "E.g. food is how I unwind at night, or I skip meals when I'm busy and eat whatever's fast later...",
    type: "textarea" },
  { id: "q8", pillar: "physical", section: "Physical Embodiment",
    question: "How do you feel about doctors and checkups? Is there an appointment, screening, or symptom you've been putting off?",
    placeholder: "Be honest, there's no judgment here...",
    type: "textarea" },

  // Mental
  { id: "q9", pillar: "mental", section: "Mental Wellness",
    question: "Where in your life are you \"the strong one\"? What do you think would happen if you weren't strong for a day?",
    placeholder: "Share what comes to mind first...",
    type: "textarea" },
  { id: "q10", pillar: "mental", section: "Mental Wellness",
    question: "What beliefs about yourself or your worth did you grow up with that you know aren't serving you?",
    placeholder: "E.g. I have to earn rest, asking for help means I'm weak, my value is in how much I do...",
    type: "textarea" },
  { id: "q11", pillar: "mental", section: "Mental Wellness",
    question: "Do you deal with stress, anxiety, low mood, or feeling numb? How does it show up in your daily life?",
    placeholder: "Many of us call it \"just tired\" or \"my nerves.\" Share what feels true...",
    type: "textarea" },

  // Spiritual
  { id: "q12", pillar: "spiritual", section: "Spiritual Connection",
    question: "What role do faith, spirituality, or your ancestors play in your life right now? Is it a source of strength, a struggle, or both?",
    placeholder: "This can be church, prayer, nature, meditation, family roots, or anything that grounds you...",
    type: "textarea" },
  { id: "q13", pillar: "spiritual", section: "Spiritual Connection",
    question: "What is one pattern from your family that you want to end with you, and one thing you want to pass down instead?",
    placeholder: "E.g. I want to stop the silence around mental health and pass down the habit of talking things through...",
    type: "textarea" },

  // Financial
  { id: "q14", pillar: "financial", section: "Financial Wellness",
    question: "How do you feel when you think about money? Where do you feel it in your body?",
    placeholder: "E.g. anxious, tight in my chest, I avoid checking my account...",
    type: "textarea" },
  { id: "q15", pillar: "financial", section: "Financial Wellness",
    question: "Are you a financial support for family or friends? How does that feel, and is it affecting your own stability?",
    placeholder: "Share whatever feels true...",
    type: "textarea" },

  // Goals
  { id: "q16", pillar: "goals", section: "Your Vision",
    question: "Picture yourself 90 days from now, carrying a little less. What feels different? What are you doing differently?",
    placeholder: "Be as specific as you can. This becomes the foundation of your roadmap...",
    type: "textarea" },
  { id: "q17", pillar: "goals", section: "Your Vision",
    question: "What are the 2 or 3 shifts that matter most to you in the next 90 days?",
    placeholder: "E.g. resting without guilt, finally scheduling my physical, and setting a money boundary with family...",
    type: "textarea" },
  { id: "q18", pillar: "goals", section: "Your Vision",
    question: "What has gotten in the way of caring for yourself before, and what support would help this time?",
    placeholder: "Be honest about your patterns and what you truly need...",
    type: "textarea" }
];

function RoadmapView({ user }) {
  const [phase, setPhase] = useState("intro"); // intro | assessment | generating | roadmap
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [roadmap, setRoadmap] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [savedRoadmap, setSavedRoadmap] = useState(null);
  const [error, setError] = useState(null);

  const plan = planInfo(user?.plan);
  const hasAccess = plan?.id === "thrive";

  useEffect(() => {
    // Load saved roadmap
    const load = async () => {
      const saved = await retrieve(`gf_roadmap_${user?.email}`);
      if (saved) setSavedRoadmap(saved);
    };
    load();
  }, [user]);

  const totalQ = ROADMAP_QUESTIONS.length;
  const progress = Math.round((currentQ / totalQ) * 100);
  const currentQuestion = ROADMAP_QUESTIONS[currentQ];

  const handleNext = () => {
    if (!currentAnswer.trim()) return;
    const updated = { ...answers, [currentQuestion.id]: currentAnswer };
    setAnswers(updated);
    setCurrentAnswer("");
    if (currentQ < totalQ - 1) {
      setCurrentQ(q => q + 1);
    } else {
      generateRoadmap(updated);
    }
  };

  const handleBack = () => {
    if (currentQ > 0) {
      setCurrentQ(q => q - 1);
      setCurrentAnswer(answers[ROADMAP_QUESTIONS[currentQ - 1].id] || "");
    }
  };

  const generateRoadmap = async (allAnswers) => {
    setPhase("generating");
    setGenerating(true);
    setError(null);

    const answerSummary = ROADMAP_QUESTIONS.map(q =>
      `${q.section}: ${q.question}\nAnswer: ${allAnswers[q.id] || "Not answered"}`
    ).join("\n\n");

    const systemPrompt = `You are the ThriveRoot roadmap guide for Glow & Flow Holistics, a non-clinical wellness space for Black women carrying stress, burnout, emotional eating, and the weight of generational patterns. You create personalized 90-day healing roadmaps based on the ninety-day roadmap in Delicia Straughter's book "Breaking Generational Health Myths in the Black Community," which moves through three phases:
- Days 1 to 30, Notice: taking honest inventory without judgment, naming what has been kept silent, and noticing her relationship with food and comfort.
- Days 31 to 60, Release: building in rest without guilt and setting one real boundary, engaging her own healthcare (scheduling what she's avoided and preparing her questions), and making peace with her body, holding body pride and health goals together.
- Days 61 to 90, Thrive: prioritizing her mental health and support, and choosing what she passes down to the next generation.

Your roadmaps are:
- Written to her as a trusted sister-friend: warm, plain-spoken, specific to her answers, and never generic, preachy, or clinical. Never stereotype; follow her lead about her own life, faith, and family.
- Honoring of where her patterns came from, while gently inviting change. Small, realistic steps over big overhauls.
- Respectful of faith, and clear that faith and professional help can work together.
- Anti-diet: never suggest weight loss, restriction, or calorie counting. Honor cultural food. Measure progress by how she feels and lives, not a number.
- Non-clinical: you do not diagnose. Encourage a doctor or therapist where it fits (for example, Therapy for Black Girls or Inclusive Therapists). If her answers suggest crisis, self-harm, or danger, include a caring note to call or text 988 or call 911.
- Formatted in clean markdown with headers and short bullet points. No emojis and never use em dashes.`;

    const userPrompt = `Based on these intake answers, create a personalized 90-day healing roadmap for this member. Reference her specific answers, struggles, and hopes throughout so it feels written just for her.

INTAKE ANSWERS:
${answerSummary}

Use this exact structure:

# Your 90-Day Healing Roadmap

## A Message For You
[A warm, personal 2-paragraph opening that reflects back what she shared: what she carries, who depends on her, and what she hopes for. Help her feel truly seen.]

## Your Focus Areas
[Her 3 most important focus areas based on her answers, with one or two sentences on why each matters for her]

---

## Days 1 to 30: Notice
*Theme: [a short theme drawn from her answers]*

### What This Month Is About
[2 to 3 sentences: observing without judgment, naming what's been silent, and noticing food and comfort, tailored to her]

### Your Practices
- **Emotional:** [2 specific practices]
- **Physical:** [2 specific practices]
- **Mental:** [2 specific practices]
- **Spiritual:** [1 to 2 practices that fit her path]
- **Financial:** [1 to 2 practices]

### One Small Step This Week
[One concrete, doable action for her first week]

### Your 30-Day Intention
[One sentence]

---

## Days 31 to 60: Release
*Theme: [a short theme]*

### What This Month Is About
[2 to 3 sentences: rest and one real boundary, the appointment she's been avoiding, and peace with her body, tailored to her]

### Your Practices
- **Emotional:** [2 practices]
- **Physical:** [2 practices, including preparing for an appointment if relevant]
- **Mental:** [2 practices]
- **Spiritual:** [1 to 2 practices]
- **Financial:** [1 to 2 practices]

### Your 60-Day Intention
[One sentence]

---

## Days 61 to 90: Thrive
*Theme: [a short theme]*

### What This Month Is About
[2 to 3 sentences: her mental health and support system, and what she chooses to pass down]

### Your Practices
- **Emotional:** [2 practices]
- **Physical:** [2 practices]
- **Mental:** [2 practices, including support options if relevant]
- **Spiritual:** [1 to 2 practices]
- **Financial:** [1 to 2 practices]

### Your 90-Day Intention
[One sentence]

---

## Words to Carry With You
[5 personalized affirmations based on what she shared, written in first person]

## A Final Word
[A warm closing paragraph honoring her courage and reminding her that she doesn't have to complete the whole journey to be worthy of having started it]`;

    try {
      const response = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: systemPrompt,
          messages: [{ role: "user", content: userPrompt }],
          purpose: "roadmap"
        })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      const roadmapText = data.reply;
      setRoadmap(roadmapText);

      // Save roadmap
      const roadmapData = {
        content: roadmapText,
        createdAt: Date.now(),
        answers: allAnswers
      };
      await store(`gf_roadmap_${user.email}`, roadmapData);
      setSavedRoadmap(roadmapData);
      setPhase("roadmap");
    } catch(e) {
      setError("Something went gently wrong generating your roadmap. Please try again.");
      setPhase("assessment");
      setCurrentQ(totalQ - 1);
    }
    setGenerating(false);
  };

  // Simple markdown renderer
  const renderMarkdown = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => {
      if (line.startsWith("# ")) return <h1 key={i} style={{ fontFamily:"Cormorant Garamond, serif", fontSize:38, fontWeight:400, color: C.warm, marginBottom:8, marginTop:24 }}>{line.replace("# ", "")}</h1>;
      if (line.startsWith("## ")) return <h2 key={i} style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, fontWeight:400, color: C.warm, marginBottom:6, marginTop:28, borderBottom:`1px solid ${C.border}`, paddingBottom:8 }}>{line.replace("## ", "")}</h2>;
      if (line.startsWith("### ")) return <h3 key={i} style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, fontWeight:500, color: C.warm, marginBottom:6, marginTop:18 }}>{line.replace("### ", "")}</h3>;
      if (line.startsWith("- ")) return <div key={i} style={{ display:"flex", gap:10, marginBottom:8, alignItems:"flex-start" }}><span style={{ color: C.gold, marginTop:3, flexShrink:0 }}>✦</span><p style={{ margin:0, fontSize:15, lineHeight:1.8, color: C.warm }}>{line.replace("- ", "")}</p></div>;
      if (line.startsWith("*") && line.endsWith("*")) return <p key={i} style={{ fontStyle:"italic", color: C.muted, fontSize:14, margin:"4px 0" }}>{line.replace(/\*/g, "")}</p>;
      if (line.startsWith("---")) return <hr key={i} style={{ border:"none", borderTop:`2px solid ${C.border}`, margin:"28px 0" }} />;
      if (line.trim() === "") return <div key={i} style={{ height:8 }} />;
      return <p key={i} style={{ fontSize:15, lineHeight:1.85, color: C.warm, margin:"4px 0" }}>{line}</p>;
    });
  };

  if (!hasAccess) {
    return (
      <div style={S.page}>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Your Wellness Roadmap</h1>
        <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
          <div style={{ marginBottom:16 }}><Star size={48} color={C.gold} /></div>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Thrive Members Only</h3>
          <p style={{ color: C.muted, fontSize:14, lineHeight:1.7, marginBottom:24 }}>
            Your personalized 30/60/90 day healing roadmap is exclusively available on the Thrive plan. Upgrade to receive a roadmap built entirely around your unique healing journey.
          </p>
          <button onClick={() => startUpgradeCheckout("thrive", user?.email, user?.name)} className="btn-hover"
            style={{ ...S.btnPrimary, fontSize:14, padding:"10px 24px", cursor:"pointer" }}>
            Upgrade to Thrive ($67/mo) to unlock →
          </button>
        </div>
      </div>
    );
  }

  // Intro phase
  if (phase === "intro") {
    return (
      <div style={S.page}>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Your Wellness Roadmap</h1>
        <p style={{ color: C.muted, fontSize:15, marginBottom:32 }}>A personalized 30/60/90 day healing plan created just for you.</p>

        {savedRoadmap && (
          <div style={{ ...S.card, background:`${C.gold}10`, border:`1.5px solid ${C.gold}44`, marginBottom:24, padding:"20px 24px",
            display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
            <div>
              <div style={{ fontWeight:500, marginBottom:4 }}>✨ You have an existing roadmap</div>
              <div style={{ fontSize:13, color: C.muted }}>Created {new Date(savedRoadmap.createdAt).toLocaleDateString("en-US", { month:"long", day:"numeric", year:"numeric" })}</div>
            </div>
            <button onClick={() => { setRoadmap(savedRoadmap.content); setPhase("roadmap"); }} className="btn-hover"
              style={{ ...S.btnPrimary, background:`linear-gradient(135deg, ${C.gold}, #B8821F)` }}>
              View My Roadmap →
            </button>
          </div>
        )}

        <div style={{ ...S.card, maxWidth:680 }}>
          <div style={{ marginBottom:16 }}><Star size={48} color={C.gold} /></div>
          <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:32, fontWeight:400, marginBottom:12 }}>
            Your Personalized Healing Roadmap
          </h2>
          <p style={{ fontSize:15, lineHeight:1.85, color: C.warm, marginBottom:16 }}>
            This assessment takes about 15–20 minutes and covers all five pillars of your healing journey. Your answers will be used to create a deeply personalized 30/60/90 day roadmap built entirely around you: your patterns, your wounds, your strengths, and your goals.
          </p>
          <p style={{ fontSize:15, lineHeight:1.85, color: C.warm, marginBottom:28 }}>
            There are 18 questions. Be as honest and specific as you can. The more you share, the more your roadmap will truly reflect your unique journey. All of your answers are private and used only to generate your roadmap.
          </p>
          <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:32 }}>
            {[
              { label:"Emotional Healing",  Icon: Heart,    color:"#C4614B" },
              { label:"Physical Embodiment",Icon: Leaf,     color:"#4A6741" },
              { label:"Mental Wellness",    Icon: Brain,    color:"#7A6BA0" },
              { label:"Spiritual Connection",Icon: Sparkles,color:"#C9963B" },
              { label:"Financial Wellness", Icon: Wallet,   color:"#9B7D2C" },
              { label:"Your 90-Day Vision", Icon: Map,      color:"#C9963B" },
            ].map((item, i) => (
              <div key={i} style={{ background: C.cream, borderRadius:10, padding:"12px 16px",
                fontSize:14, color: C.warm, display:"flex", alignItems:"center", gap:8 }}>
                <item.Icon size={15} color={item.color} style={{ flexShrink:0 }} />
                {item.label}
              </div>
            ))}
          </div>
          <button onClick={() => setPhase("assessment")} className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, #C9963B, #8A6018)`, width:"100%", padding:"16px", fontSize:16 }}>
            Begin My Assessment
          </button>
          <p style={{ fontSize:12, color: C.muted, lineHeight:1.7, marginTop:14 }}>
            Your roadmap is created with AI for reflection and gentle planning. It is not medical, mental health, or nutrition advice, and it can make mistakes. Please check with your own providers before making health decisions.
          </p>
        </div>
      </div>
    );
  }

  // Generating phase
  if (phase === "generating") {
    return (
      <div style={{ ...S.page, display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh" }}>
        <div style={{ textAlign:"center", maxWidth:480 }}>
          <div style={{ marginBottom:24, animation:"pulse 2s infinite" }}><BrandButterfly size={64} color={C.gold} /></div>
          <h2 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:34, fontWeight:400, marginBottom:12 }}>
            Creating Your Roadmap
          </h2>
          <p style={{ color: C.muted, fontSize:15, lineHeight:1.8, marginBottom:32 }}>
            Your healing guide is reading your answers carefully and crafting a roadmap that is uniquely yours. This takes about 30 to 60 seconds. Please stay on this page.
          </p>
          <div style={{ display:"flex", justifyContent:"center", gap:8 }}>
            {[0,1,2].map(i => (
              <div key={i} style={{ width:10, height:10, borderRadius:"50%", background: C.gold,
                animation:`pulse ${1 + i * 0.3}s infinite` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Roadmap display phase
  if (phase === "roadmap" && roadmap) {
    return (
      <div style={S.page}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:28, flexWrap:"wrap", gap:12 }}>
          <div>
            <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>Your Healing Roadmap</h1>
            <p style={{ color: C.muted, fontSize:14 }}>Your personalized 30/60/90 day journey</p>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={() => window.print()} style={{ ...S.btnGhost, fontSize:13 }}>🖨 Print</button>
            <button onClick={() => { setPhase("intro"); setCurrentQ(0); setAnswers({}); setCurrentAnswer(""); }}
              style={{ ...S.btnSecondary, fontSize:13, borderColor: C.gold, color: C.gold }}>
              Regenerate Roadmap
            </button>
          </div>
        </div>

        <div style={{ ...S.card, maxWidth:760, padding:"40px 48px" }}>
          {renderMarkdown(roadmap)}
        </div>

        {error && (
          <div style={{ marginTop:16, padding:"14px 18px", background:"#FDE8E3", borderRadius:12, color:"#8B3C2B", fontSize:14 }}>
            {error}
          </div>
        )}
      </div>
    );
  }

  // Assessment phase
  const sectionQ = ROADMAP_QUESTIONS[currentQ];
  const prevSection = currentQ > 0 ? ROADMAP_QUESTIONS[currentQ - 1].section : null;
  const showSectionHeader = currentQ === 0 || sectionQ.section !== prevSection;

  return (
    <div style={S.page}>
      <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Your Healing Assessment</h1>
      <p style={{ color: C.muted, fontSize:15, marginBottom:28 }}>Question {currentQ + 1} of {totalQ}</p>

      {/* Progress bar */}
      <div style={{ background: C.border, borderRadius:8, height:6, marginBottom:32, maxWidth:680 }}>
        <div style={{ height:6, borderRadius:8, background:`linear-gradient(90deg, ${C.gold}, #C4614B)`,
          width:`${progress}%`, transition:"width 0.4s ease" }} />
      </div>

      <div style={{ ...S.card, maxWidth:680 }}>
        {showSectionHeader && (
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20,
            padding:"10px 14px", background: C.cream, borderRadius:10 }}>
            <span style={{ fontSize:18 }}>
              {sectionQ.section === "About You" ? <User size={18} color={C.gold} /> :
               sectionQ.section === "Emotional Healing" ? <Heart size={18} color="#C4614B" /> :
               sectionQ.section === "Physical Embodiment" ? <Leaf size={18} color="#4A6741" /> :
               sectionQ.section === "Mental Wellness" ? <Brain size={18} color="#7A6BA0" /> :
               sectionQ.section === "Spiritual Connection" ? <Sparkles size={18} color={C.gold} /> :
               sectionQ.section === "Financial Wellness" ? <Wallet size={18} color={C.gold} /> : <Sparkles size={18} color={C.gold} />}
            </span>
            <span style={{ fontSize:13, letterSpacing:"0.8px", color: C.muted, fontWeight:500 }}>
              {sectionQ.section.toUpperCase()}
            </span>
          </div>
        )}

        <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, fontWeight:400, color: C.warm, marginBottom:8, lineHeight:1.5 }}>
          {currentQuestion.question}
        </h3>
        <p style={{ color: C.muted, fontSize:13, marginBottom:20, fontStyle:"italic" }}>
          Be as honest and specific as you feel comfortable. The more you share, the more personalized your roadmap will be.
        </p>

        <textarea
          value={currentAnswer}
          onChange={e => setCurrentAnswer(e.target.value)}
          placeholder={currentQuestion.placeholder}
          rows={6}
          style={{ width:"100%", padding:"16px", borderRadius:12, border:`1.5px solid ${C.border}`,
            fontSize:15, fontFamily:"DM Sans, sans-serif", lineHeight:1.8, color: C.warm,
            background: C.cream, resize:"vertical", outline:"none", boxSizing:"border-box" }}
        />

        {error && (
          <div style={{ marginTop:12, padding:"12px 16px", background:"#FDE8E3", borderRadius:10, color:"#8B3C2B", fontSize:13 }}>
            {error}
          </div>
        )}

        <div style={{ display:"flex", gap:10, marginTop:20 }}>
          {currentQ > 0 && (
            <button onClick={handleBack} style={{ ...S.btnGhost, flex:1 }}>← Back</button>
          )}
          <button onClick={handleNext} disabled={!currentAnswer.trim()} className="btn-hover"
            style={{ ...S.btnPrimary, background:`linear-gradient(135deg, #C9963B, #8A6018)`,
              flex:2, opacity: currentAnswer.trim() ? 1 : 0.5 }}>
            {currentQ < totalQ - 1 ? "Next Question →" : "Generate My Roadmap"}
          </button>
        </div>
      </div>

      {/* Section progress dots */}
      <div style={{ display:"flex", gap:6, marginTop:20, flexWrap:"wrap", maxWidth:680 }}>
        {ROADMAP_QUESTIONS.map((q, i) => (
          <div key={i} style={{ width:8, height:8, borderRadius:"50%", transition:"background 0.3s",
            background: i < currentQ ? C.gold : i === currentQ ? "#C4614B" : C.border }} />
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  STRIPE SUCCESS SCREEN
// ══════════════════════════════════════════════════════════════════
function SuccessScreen({ plan, onContinue }) {
  const planData = PLANS.find(p => p.id === plan);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(timer); onContinue(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={{ minHeight:"100vh", background: C.warm, display:"flex", alignItems:"center",
      justifyContent:"center", padding:24, textAlign:"center" }}>
      <div style={{ maxWidth:480, width:"100%", animation:"fadeUp 0.6s ease" }}>
        <div style={{ marginBottom:24, animation:"float 3s ease-in-out infinite" }}><BrandButterfly size={80} color={C.gold} /></div>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, color:"#FBF5EC",
          fontWeight:400, marginBottom:12 }}>
          Welcome Home
        </h1>
        <p style={{ color:"rgba(251,245,236,0.7)", fontSize:16, lineHeight:1.8, marginBottom:24 }}>
          Your trial is active. Your {planData?.name} space is ready for you. Explore everything, take your time, and know there is nothing to pay during your 14 days.
        </p>

        {/* Trial callout */}
        <div style={{ background:"linear-gradient(135deg,rgba(201,150,59,0.2),rgba(201,150,59,0.08))",
          border:`1.5px solid ${C.gold}55`, borderRadius:20, padding:"22px 28px", marginBottom:20 }}>
          <div style={{ marginBottom:8 }}><Gift size={28} color={C.gold} /></div>
          <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, color: C.gold, marginBottom:6 }}>
            Your 14-Day Free Trial Has Begun
          </div>
          <div style={{ color:"rgba(251,245,236,0.7)", fontSize:14, lineHeight:1.8 }}>
            No card was needed. If you love it, add a payment method anytime in Settings under
            Manage Billing to keep your access. If you don't, your trial simply ends and you are never charged.
          </div>
          <div style={{ marginTop:14, padding:"10px 16px",
            background:"rgba(255,255,255,0.06)", borderRadius:10,
            color:"rgba(251,245,236,0.5)", fontSize:12 }}>
            {planData?.name} plan · ${planData?.price}/month if you continue · Cancel anytime
          </div>
        </div>

        <div style={{ background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:16, padding:"20px 24px", marginBottom:28 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {planData?.features?.slice(0,3).map((f, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10,
                color:"rgba(251,245,236,0.75)", fontSize:13 }}>
                <CheckCircle size={14} color={C.gold} style={{ flexShrink:0, marginTop:1 }} />{f}
              </div>
            ))}
          </div>
        </div>

        <button onClick={onContinue} className="btn-hover"
          style={{ ...S.btnPrimary, padding:"16px 48px", fontSize:16, marginBottom:16 }}>
          Enter My Sanctuary ✨
        </button>
        <p style={{ color:"rgba(251,245,236,0.35)", fontSize:13 }}>
          Entering automatically in {countdown} second{countdown !== 1 ? "s" : ""}...
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  PAYMENT SUCCESS SCREEN
// ══════════════════════════════════════════════════════════════════
function PaymentSuccessScreen({ user, setView }) {
  const plan = planInfo(user?.plan);
  return (
    <div style={{ minHeight:"100vh", background: C.warm, display:"flex", alignItems:"center",
      justifyContent:"center", padding:24 }}>
      <div style={{ maxWidth:520, width:"100%", textAlign:"center", animation:"fadeUp 0.6s ease" }}>
        <div style={{ marginBottom:20, animation:"float 3s ease-in-out infinite" }}><BrandButterfly size={72} color={C.gold} /></div>
        <h1 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, color:"#FBF5EC",
          fontWeight:400, marginBottom:12, lineHeight:1.2 }}>
          Welcome home, {user?.name?.split(" ")[0]}
        </h1>
        <p style={{ color: C.gold, fontSize:16, letterSpacing:"1px", marginBottom:32 }}>
          YOUR HEALING JOURNEY BEGINS NOW
        </p>

        <div style={{ background:"rgba(255,255,255,0.06)", borderRadius:20, padding:"32px 28px",
          border:"1px solid rgba(255,255,255,0.12)", marginBottom:32, backdropFilter:"blur(12px)" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:12, marginBottom:20 }}>
            <div style={{ width:48, height:48, borderRadius:"50%",
              background:`linear-gradient(135deg, ${C.gold}, #8A6018)`,
              display:"flex", alignItems:"center", justifyContent:"center",
              color:"#fff", fontSize:20, fontWeight:600 }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div style={{ textAlign:"left" }}>
              <div style={{ color:"#FBF5EC", fontWeight:500, fontSize:15 }}>{user?.name}</div>
              <div style={{ color: C.gold, fontSize:13 }}>{plan?.name} Member</div>
            </div>
          </div>

          <div style={{ borderTop:"1px solid rgba(255,255,255,0.1)", paddingTop:20, marginBottom:20 }}>
            <div style={{ color:"rgba(251,245,236,0.7)", fontSize:14, marginBottom:16 }}>
              Your {plan?.name} plan includes:
            </div>
            <div style={{ display:"grid", gap:10 }}>
              {plan?.features?.slice(0,4).map((f, i) => (
                <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10,
                  color:"rgba(251,245,236,0.8)", fontSize:13, textAlign:"left" }}>
                  <CheckCircle size={14} color={C.gold} style={{ flexShrink:0, marginTop:2 }} />
                  {f}
                </div>
              ))}
            </div>
          </div>

          <div style={{ background:"rgba(201,150,59,0.12)", borderRadius:12, padding:"14px 18px",
            border:`1px solid ${C.gold}33` }}>
            <div style={{ color:"rgba(251,245,236,0.6)", fontSize:12, marginBottom:4 }}>MONTHLY INVESTMENT</div>
            <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:32, color:"#FBF5EC" }}>
              ${plan?.price}<span style={{ fontSize:15, color:"rgba(251,245,236,0.5)" }}>/month</span>
            </div>
            <div style={{ color:"rgba(251,245,236,0.4)", fontSize:12, marginTop:4 }}>
              Billed monthly · Cancel anytime
            </div>
          </div>
        </div>

        <p style={{ color:"rgba(251,245,236,0.6)", fontSize:14, lineHeight:1.8, marginBottom:32, maxWidth:380, margin:"0 auto 32px" }}>
          A confirmation receipt has been sent to {user?.email}. You are officially part of the ThriveRoot family. 🌸
        </p>

        <button onClick={() => setView("dashboard")} className="btn-hover"
          style={{ ...S.btnPrimary, padding:"16px 48px", fontSize:17, borderRadius:12,
            background:`linear-gradient(135deg, ${C.gold}, #8A6018)` }}>
          Enter My Sanctuary →
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  MAIN APP ENTRY
// ══════════════════════════════════════════════════════════════════

export default function GlowFlowApp() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState("login");
  const [activePillar, setActivePillar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkIns, setCheckIns] = useState({});
  const [journalEntries, setJournalEntries] = useState([]);
  const [notification, setNotification] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const bp = useBreakpoint();
  const isMobile = bp === "mobile";
  const isTablet = bp === "tablet";
  const isDesktop = bp === "desktop";

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const styleEl = document.createElement("style");
    styleEl.textContent = GLOBAL_CSS;
    document.head.appendChild(styleEl);
    return () => { document.head.removeChild(link); document.head.removeChild(styleEl); };
  }, []);

  const [stripeSuccess, setStripeSuccess] = useState(null); // plan name if returning from Stripe

  useEffect(() => {
    (async () => {
      // Check if returning from Stripe checkout
      // Supports both ?payment=success and ?stripe=success param formats
      const urlParams = new URLSearchParams(window.location.search);
      const stripeStatus = urlParams.get("payment") || urlParams.get("stripe");

      const token = localStorage.getItem("gf_tok");
      if (token) {
        const authUser = await supaGetUser(token);
        if (authUser && !authUser.error) {
          _saveSession({ access_token: token, user: authUser });
          const meta = authUser.user_metadata || {};
          // Her plan always comes from the server (checked against Stripe).
          const { plan, trialEnded } = await fetchMyPlan(authUser);
          // New signup = came from the signup flow, not returning after a trial ended
          const isNewSignup = !!localStorage.getItem("gf_pending_email") && localStorage.getItem("gf_trial_ended") !== "1";
          if (stripeStatus) window.history.replaceState({}, "", window.location.pathname);

          // Just finished signing up through Stripe
          if (stripeStatus === "success" && plan !== "none" && isNewSignup) {
            // Read pending name BEFORE removing from localStorage
            const pendingName = localStorage.getItem("gf_pending_name");
            const userName = meta.name || pendingName || "Member";
            // Clean up localStorage
            localStorage.removeItem("gf_pending_name");
            localStorage.removeItem("gf_pending_email");
            localStorage.removeItem("gf_pending_plan");
            localStorage.removeItem("gf_trial_ended");
            const user = { id: authUser.id, email: authUser.email, name: userName, plan, subscribedAt: Date.now() };
            setUser(user);
            // Send welcome email (the server sends it only to her own address)
            try {
              const emailRes = await apiFetch("/api/send-welcome", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: userName })
              });
              const emailData = await emailRes.json();
              if (!emailRes.ok) console.error("Welcome email failed:", emailData);
            } catch(e) { console.error("Welcome email exception:", e); }
            setStripeSuccess(plan);
          } else if (plan === "none" && isNewSignup) {
            // Signed up but hasn't picked a plan yet: show the plan picker
            // (which also offers the free Seedling plan).
            setView("subscribe");
          } else {
            // No active subscription means the free Seedling plan.
            if (plan === "none") setCloudSync(false);
            const user = { id: authUser.id, email: authUser.email, name: meta.name || "Member", plan: plan === "none" ? "free" : plan, subscribedAt: meta.subscribedAt, hadTrial: !!trialEnded };
            if (plan === "none" && trialEnded && !localStorage.getItem(`gf_trial_notice_${authUser.email}`)) {
              localStorage.setItem(`gf_trial_notice_${authUser.email}`, "1");
              setTimeout(() => notify("Your free trial has ended. You're now on Seedling, our free plan. Upgrade anytime."), 300);
            }
            setUser(user);
            setView("dashboard");
            if (stripeStatus === "success") {
              ["gf_pending_name","gf_pending_email","gf_pending_plan","gf_trial_ended"].forEach(k => localStorage.removeItem(k));
              setTimeout(() => notify("Your plan is active. Welcome back!"), 300);
            }
            const ci = await safeRetrieve(`gf_checkins_${user.email}`) || {};
            const je = await safeRetrieve(`gf_journal_${user.email}`) || [];
            setCheckIns(ci); setJournalEntries(je);
            // Pre-warm tracker caches on page reload too
            const trackerKeys2 = [
              `gf_hydration_${user.email}`,
              `gf_rest_${user.email}`,
              `gf_goals_${user.email}`,
              `gf_appointments_${user.email}`,
              `gf_weekly_${user.email}`,
              `gf_nourishment_journal_${user.email}`,
              `gf_nourishment_favorites_${user.email}`,
            ];
            Promise.all(trackerKeys2.map(k => _forceRefreshFromCloud(k))).catch(() => {});
          }
        }
      } else if (stripeStatus === "cancel" || stripeStatus === "cancelled") {
        window.history.replaceState({}, "", window.location.pathname);
      }
      setLoading(false);
    })();
  }, []);

  // Free members: device only. Paid members: cloud backup. When a free
  // member upgrades, copy what's on this device up to the cloud once.
  useEffect(() => {
    _currentPlan = user?.plan || null;
    if (!user) return;
    const flag = `gf_device_only_${user.email}`;
    if (user.plan === "free") {
      setCloudSync(false);
      localStorage.setItem(flag, "1");
    } else {
      setCloudSync(true);
      if (localStorage.getItem(flag)) {
        localStorage.removeItem(flag);
        pushLocalDataToCloud(user.email);
      }
    }
  }, [user?.plan, user?.email]);

  const notify = (msg, type = "success") => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };

  const logout = async () => {
    await supaSignOut();
    setUser(null); setView("login");
    setCheckIns({}); setJournalEntries([]);
  };

  const saveCheckIn = async (pillarId, score) => {
    const today = new Date().toISOString().split("T")[0];
    const updated = { ...checkIns, [`${pillarId}_${today}`]: score };
    setCheckIns(updated);
    await store(`gf_checkins_${user.email}`, updated);
    notify("Check-in saved!");
  };

  const saveJournalEntry = async (entry) => {
    const updated = [entry, ...journalEntries].slice(0, 200);
    setJournalEntries(updated);
    await store(`gf_journal_${user.email}`, updated);
    notify("Journal entry saved");
  };

  if (loading) return <LoadingScreen />;

  // Show success screen after Stripe payment
  if (stripeSuccess && user) {
    return <SuccessScreen plan={stripeSuccess} onContinue={async () => {
      setStripeSuccess(null);
      const ci = await retrieve(`gf_checkins_${user.email}`) || {};
      const je = await retrieve(`gf_journal_${user.email}`) || [];
      setCheckIns(ci); setJournalEntries(je);
      setView("dashboard");
    }} />;
  }

  if (!user) return (
    <>
      {view === "login" && <LoginScreen setView={setView} setUser={setUser} setCheckIns={setCheckIns} setJournalEntries={setJournalEntries} notify={notify} />}
      {view === "register" && <RegisterScreen setView={setView} notify={notify} />}
      {view === "subscribe" && <SubscribeScreen setView={setView} setUser={setUser} notify={notify} pendingEmail={localStorage.getItem("gf_pending_email") || ""} pendingName={localStorage.getItem("gf_pending_name") || ""} trialEnded={localStorage.getItem("gf_trial_ended") === "1"} />}
      {notification && <Notification {...notification} />}
    </>
  );

  const navigateTo = (v, pillar = null) => {
    if (pillar) setActivePillar(pillar);
    setView(v);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const mobileNavItems = [
    { id:"dashboard", Icon:Home, label:"Home" },
    { id:"journal", Icon:BookOpen, label:"Journal" },
    { id:"guidance", Icon:Sparkles, label:"Guidance" },
    { id:"progress", Icon:BarChart2, label:"Progress" },
    { id:"more", Icon:null, label:"More" },
  ];

  const mainContent = (
    <>
      {view === "payment-success" && <PaymentSuccessScreen user={user} setView={setView} />}
      {view === "dashboard" && <Dashboard key={`dashboard_${Date.now()}`} user={user} checkIns={checkIns} journalEntries={journalEntries} setView={navigateTo} setActivePillar={setActivePillar} saveCheckIn={saveCheckIn} />}
      {view === "pillar" && activePillar && <PillarView pillar={activePillar} checkIns={checkIns} journalEntries={journalEntries} saveCheckIn={saveCheckIn} saveJournalEntry={saveJournalEntry} user={user} setView={navigateTo} />}
      {view === "journal" && <JournalView entries={journalEntries} saveJournalEntry={saveJournalEntry} />}
      {view === "progress" && <ProgressView checkIns={checkIns} journalEntries={journalEntries} isFree={user?.plan === "free"} />}
      {view === "guidance" && <GuidanceView user={user} />}
      {view === "wellness-hub" && <WellnessHubView />}
      {view === "goals" && <PillarGoalsView user={user} />}
      {view === "budget" && <BudgetTrackerView user={user} />}
      {view === "calming" && <CalmingExercisesView user={user} />}
      {view === "hydration" && <HydrationTrackerView user={user} />}
      {view === "rest" && <RestRelaxationView user={user} />}
      {view === "nourishment" && <NourishmentGuideView user={user} />}
      {view === "appointments" && <WellnessAppointmentsView user={user} />}
      {view === "weekly" && <WeeklyIntentionView user={user} />}
      {view === "roadmap" && <RoadmapView user={user} />}
      {view === "support" && <PrioritySupportView user={user} />}
      {view === "settings" && <SettingsView user={user} setUser={setUser} logout={logout} notify={notify} />}
      {user && !["login","register","subscribe"].includes(view) && <WellnessDisclaimer variant="footer" />}
    </>
  );

  if (isMobile) {
    return (
      <>
        {/* Mobile header */}
        <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:200,
          background: C.warm, padding:"12px 16px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          borderBottom:"1px solid rgba(255,255,255,0.08)",
          paddingTop: "max(12px, env(safe-area-inset-top))" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <BrandButterfly size={20} color="currentColor" />
            <div>
              <div style={{ fontFamily:"Cormorant Garamond, serif", color:"#FBF5EC", fontSize:15, fontWeight:400, lineHeight:1.1 }}>ThriveRoot</div>
              <div style={{ color: C.gold, fontSize:10, letterSpacing:"0.5px" }}>{user?.name?.split(" ")[0]} · {planInfo(user?.plan)?.name}</div>
            </div>
          </div>
          <button onClick={() => setMobileMenuOpen(o => !o)}
            style={{ background:"none", border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, cursor:"pointer",
              color:"rgba(251,245,236,0.8)", fontSize:18, padding:"4px 10px", lineHeight:1 }}>
            {mobileMenuOpen ? <X size={20} /> : <Filter size={20} />}
          </button>
        </div>

        {/* Mobile full-screen menu */}
        {mobileMenuOpen && (
          <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:300,
            background: C.warm, overflowY:"auto", padding:"80px 20px 40px" }}>
            <MobileSidebar user={user} view={view} navigateTo={navigateTo} logout={logout} setActivePillar={setActivePillar} onClose={() => setMobileMenuOpen(false)} />
          </div>
        )}

        {/* Mobile main content */}
        <div style={{ ...S.mainMobile, paddingTop: "max(72px, calc(60px + env(safe-area-inset-top)))" }}>
          {mainContent}
        </div>

        {/* Mobile bottom nav */}
        {!mobileMenuOpen && (
          <nav className="mobile-nav">
            {mobileNavItems.map(item => (
              <button key={item.id}
                className={`mobile-nav-btn${view === item.id ? " active" : ""}`}
                onClick={() => item.id === "more" ? setMobileMenuOpen(true) : navigateTo(item.id)}
                style={{ position:"relative" }}>
{item.Icon && <item.Icon size={22} />}
                <span style={{ fontSize:10, marginTop:1 }}>{item.label}</span>
                {view === item.id && (
                  <div style={{ position:"absolute", top:0, left:"50%", transform:"translateX(-50%)",
                    width:24, height:2, background: C.gold, borderRadius:2 }} />
                )}
              </button>
            ))}
          </nav>
        )}

        {notification && <Notification {...notification} />}
        <InstallPrompt />
      </>
    );
  }

  return (
    <>
      <div style={S.appShell}>
        <Sidebar user={user} view={view} setView={setView} setActivePillar={setActivePillar} logout={logout} isTablet={isTablet} />
        <div style={isTablet ? S.mainTablet : S.main}>
          {mainContent}
        </div>
      </div>
      {notification && <Notification {...notification} />}
      <InstallPrompt />
    </>
  );
}

// ══════════════════════════════════════════════════════════════════
//  HYDRATION TRACKER
// ══════════════════════════════════════════════════════════════════
function HydrationTrackerView({ user }) {
  const isThrive = user?.plan === "thrive";
  const today = new Date().toISOString().split("T")[0];

  const AFFIRMATIONS = [
    "Every sip is a small way of choosing myself.",
    "I care for my body before it has to beg.",
    "Water is part of how I rest and restore.",
    "My body has carried so much. I'm tending to it today.",
    "I deserve to feel good, not just get through the day.",
    "Small steps are better than no steps. This glass counts.",
    "I'm filling my own cup before I pour into everyone else's.",
  ];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [affirmation, setAffirmation] = useState(AFFIRMATIONS[0]);

  useEffect(() => {
    (async () => {
      const saved = await safeRetrieve(`gf_hydration_${user?.email}`) || {};
      const todayData = saved[today] || { glasses: 0, goal: 8, unit: "glasses" };
      setData({ ...saved, [today]: todayData });
      setAffirmation(AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)]);
      setLoading(false);
    })();
  }, []);

  const todayData = data?.[today] || { glasses: 0, goal: 8, unit: "glasses" };
  const goal = todayData.goal || 8;
  const glasses = todayData.glasses || 0;
  const pct = Math.min(100, Math.round((glasses / goal) * 100));

  const save = async (updated) => {
    const newData = { ...(data || {}), [today]: updated };
    setData(newData);
    setSaving(true);
    await safeSave(`gf_hydration_${user?.email}`, newData);
    setSaving(false);
  };

  const addGlass = async () => {
    if (glasses >= goal) return;
    await save({ ...todayData, glasses: glasses + 1 });
    if (glasses + 1 === goal) setAffirmation("You hit your goal today. Your body is so grateful.");
    else setAffirmation(AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)]);
  };

  const removeGlass = async () => {
    if (glasses <= 0) return;
    await save({ ...todayData, glasses: glasses - 1 });
  };

  const updateGoal = async (newGoal) => {
    await save({ ...todayData, goal: newGoal });
  };

  // Calculate streak
  const getStreak = () => {
    if (!data) return 0;
    let streak = 0;
    const d = new Date();
    while (true) {
      const key = d.toISOString().split("T")[0];
      const dayData = data[key];
      if (dayData && dayData.glasses >= (dayData.goal || 8)) {
        streak++;
        d.setDate(d.getDate() - 1);
      } else break;
    }
    return streak;
  };

  // Last 7 days for mini chart
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const key = d.toISOString().split("T")[0];
    const dayData = data?.[key] || { glasses: 0, goal: 8 };
    return {
      label: d.toLocaleDateString("en", { weekday: "short" }),
      glasses: dayData.glasses || 0,
      goal: dayData.goal || 8,
      met: (dayData.glasses || 0) >= (dayData.goal || 8)
    };
  });

  if (!isThrive) return (
    <div style={S.page}>
      <h1 className="rt-sm" style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Hydration Tracker</h1>
      <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
        <div style={{ marginBottom:16 }}><Droplets size={56} color="#5B9BD5" /></div>
        <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Thrive Members Only</h3>
        <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:24 }}>
          Track your daily hydration, set goals, and build healthy water habits as part of your healing journey.
        </p>
        <button onClick={() => startUpgradeCheckout("thrive", user?.email, user?.name)} className="btn-hover"
          style={{ ...S.btnPrimary, fontSize:14, padding:"10px 24px" }}>
          Upgrade to Thrive ($67/mo) to unlock →
        </button>
      </div>
    </div>
  );

  if (loading) return <LoadingScreen />;

  const streak = getStreak();

  return (
    <div style={S.page}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 className="rt-sm" style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>Hydration Tracker</h1>
          <p style={{ color: C.muted, fontSize:14 }}>Nourish your body, honor your healing 💧</p>
        </div>
        <div style={{ fontSize:12, color: C.muted }}>
          {saving ? "Saving..." : ""}
        </div>
      </div>

      {/* Affirmation banner */}
      <div style={{ background:`linear-gradient(135deg, #E8F4FD, #F0F8FF)`, border:`1px solid #B8D8F0`,
        borderRadius:14, padding:"16px 22px", marginBottom:24, display:"flex", alignItems:"center", gap:12 }}>
        <Droplets size={24} color="#5B9BD5" style={{ flexShrink:0 }} />
        <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:17, color:"#2A5A8B", fontStyle:"italic", margin:0 }}>
          {affirmation}
        </p>
      </div>

      <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20 }}>
        {/* Main tracker */}
        <div style={{ ...S.card }}>
          <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:20, color: C.warm }}>Today's Intake</h3>

          {/* Visual glass display */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:20, justifyContent:"center" }}>
            {Array.from({ length: goal }, (_, i) => (
              <div key={i} onClick={() => i < glasses ? removeGlass() : addGlass()}
                style={{ width:40, height:52, borderRadius:"4px 4px 8px 8px",
                  border:`2px solid ${i < glasses ? "#4A9FD4" : C.border}`,
                  background: i < glasses ? "linear-gradient(180deg, #7BC5E8, #4A9FD4)" : "transparent",
                  cursor:"pointer", transition:"all 0.2s", position:"relative", overflow:"hidden" }}>
                {i < glasses && <div style={{ position:"absolute", bottom:0, left:0, right:0, height:"60%",
                  background:"rgba(255,255,255,0.3)", borderRadius:"0 0 6px 6px" }} />}
              </div>
            ))}
          </div>

          {/* Count display */}
          <div style={{ textAlign:"center", marginBottom:20 }}>
            <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:52, color: glasses >= goal ? "#4A9FD4" : C.warm, lineHeight:1 }}>
              {glasses}
            </div>
            <div style={{ color: C.muted, fontSize:13 }}>of {goal} glasses · {pct}% of goal</div>
          </div>

          {/* Progress bar */}
          <div style={{ background: C.border, borderRadius:8, height:10, marginBottom:20, overflow:"hidden" }}>
            <div style={{ width:`${pct}%`, height:"100%", borderRadius:8, transition:"width 0.4s ease",
              background: pct >= 100 ? "linear-gradient(90deg, #4A9FD4, #27AE60)" : "linear-gradient(90deg, #7BC5E8, #4A9FD4)" }} />
          </div>

          {/* Buttons */}
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={addGlass} disabled={glasses >= goal} className="btn-hover"
              style={{ ...S.btnPrimary, flex:1, background:"linear-gradient(135deg, #4A9FD4, #2A7AB0)",
                opacity: glasses >= goal ? 0.5 : 1 }}>
              + Add Glass 💧
            </button>
            <button onClick={removeGlass} disabled={glasses <= 0} className="btn-hover"
              style={{ ...S.btnSecondary, padding:"10px 16px", opacity: glasses <= 0 ? 0.4 : 1 }}>
              −
            </button>
          </div>
        </div>

        {/* Stats + Goal */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          {/* Streak card */}
          <div style={{ ...S.card, background:`linear-gradient(135deg, #E8F4FD, #F8FCFF)`,
            border:`1px solid #B8D8F0`, textAlign:"center" }}>
            <div style={{ marginBottom:6 }}>{streak > 0 ? <Flame size={36} color="#E25822" /> : <Droplets size={36} color="#5B9BD5" />}</div>
            <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:38, color:"#2A5A8B", lineHeight:1 }}>{streak}</div>
            <div style={{ color:"#4A7FA0", fontSize:13, marginTop:4 }}>
              {streak === 1 ? "day streak" : "day streak"} {streak >= 3 ? "· Amazing!" : streak > 0 ? "· Keep going!" : "· Start today!"}
            </div>
          </div>

          {/* Daily goal setting */}
          <div style={{ ...S.card }}>
            <h4 style={{ fontSize:14, letterSpacing:"0.8px", color: C.muted, marginBottom:14 }}>DAILY GOAL</h4>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
              {[6, 8, 10, 12].map(g => (
                <button key={g} onClick={() => updateGoal(g)} className="btn-hover"
                  style={{ padding:"10px 4px", borderRadius:10, border:`2px solid ${goal === g ? "#4A9FD4" : C.border}`,
                    background: goal === g ? "#E8F4FD" : "transparent",
                    color: goal === g ? "#2A5A8B" : C.muted,
                    fontSize:14, fontWeight: goal === g ? 600 : 400, cursor:"pointer" }}>
                  {g}
                </button>
              ))}
            </div>
            <div style={{ color: C.muted, fontSize:12, marginTop:10, textAlign:"center" }}>glasses per day</div>
          </div>

          {/* Today summary */}
          <div style={{ ...S.card, background: pct >= 100 ? `linear-gradient(135deg, #E8F8F0, #F0FBF5)` : S.card.background,
            border: pct >= 100 ? `1px solid #A8DFC0` : S.card.border }}>
            <div style={{ fontSize:13, color: C.muted, marginBottom:8 }}>TODAY'S STATUS</div>
            {pct >= 100 ? (
              <div style={{ color:"#27AE60", fontWeight:600, fontSize:15 }}>✅ Goal complete! Beautiful work.</div>
            ) : (
              <div style={{ color: C.warm, fontSize:14 }}>
                <strong>{goal - glasses}</strong> more glass{goal - glasses !== 1 ? "es" : ""} to reach your goal
              </div>
            )}
            <div style={{ color: C.muted, fontSize:12, marginTop:6 }}>
              {glasses * 8} oz consumed · goal: {goal * 8} oz
            </div>
          </div>
        </div>
      </div>

      {/* 7-day chart */}
      <div style={{ ...S.card }}>
        <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:20, color: C.warm }}>7-Day Overview</h3>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:8, alignItems:"end" }}>
          {last7.map((day, i) => (
            <div key={i} style={{ textAlign:"center" }}>
              <div style={{ fontSize:12, color: C.muted, marginBottom:6 }}>{day.glasses}/{day.goal}</div>
              <div style={{ height:80, display:"flex", alignItems:"flex-end", justifyContent:"center", marginBottom:6 }}>
                <div style={{ width:"70%", borderRadius:"4px 4px 0 0",
                  height:`${Math.max(4, Math.min(80, (day.glasses / day.goal) * 80))}px`,
                  background: day.met ? "linear-gradient(180deg, #7BC5E8, #4A9FD4)" : `${C.border}`,
                  transition:"height 0.4s ease" }} />
              </div>
              <div style={{ fontSize:11, color: day.met ? "#4A9FD4" : C.muted, fontWeight: day.met ? 600 : 400 }}>
                {day.label}
              </div>
              {day.met && <div style={{ fontSize:10, color:"#4A9FD4" }}>✓</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  REST & RELAXATION MONITOR
// ══════════════════════════════════════════════════════════════════
function RestRelaxationView({ user }) {
  const isThrive = user?.plan === "thrive";
  const today = new Date().toISOString().split("T")[0];

  const RELAX_ACTIVITIES = [
    { id:"bath", label:"Bath / Shower Ritual", Icon:Droplets },
    { id:"reading", label:"Reading", Icon:BookOpen },
    { id:"meditation", label:"Meditation", Icon:Wind },
    { id:"nature", label:"Time in Nature", Icon:Leaf },
    { id:"music", label:"Music / Playlist", Icon:Music },
    { id:"journaling", label:"Journaling", Icon:Pencil },
    { id:"stretching", label:"Gentle Stretching", Icon:Activity },
    { id:"nap", label:"Nap", Icon:Moon },
    { id:"tea", label:"Tea / Mindful Drink", Icon:Coffee },
    { id:"creative", label:"Creative Activity", Icon:Sparkles },
    { id:"breathwork", label:"Breathwork", Icon:Wind },
    { id:"screen_free", label:"Screen-Free Time", Icon:EyeOff },
  ];

  const SLEEP_QUALITY = [
    { value:1, label:"Poor",  color:"#C4614B" },
    { value:2, label:"Fair",  color:"#C9963B" },
    { value:3, label:"Okay",  color:"#9A8B7C" },
    { value:4, label:"Good",  color:"#4A6741" },
    { value:5, label:"Great", color:"#27AE60" },
  ];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSavedFlag] = useState(false);
  const [tab, setTab] = useState("today");
  const [form, setForm] = useState({ sleepHours: "", sleepQuality: 0, napMinutes: "", activities: [], note: "" });

  const emptyDay = { sleepHours: "", sleepQuality: 0, napMinutes: "", activities: [], note: "" };

  useEffect(() => {
    (async () => {
      const cloudData = await safeRetrieve(`gf_rest_${user?.email}`) || {};
      setData(cloudData);
      const todayStored = cloudData[today] || emptyDay;
      setForm(todayStored);
      setLoading(false);
    })();
  }, []);

  // todayData reads from local form state for display
  const todayData = form;

  // Auto-save activities and quality immediately (button clicks feel instant)
  const saveImmediately = async (updatedForm) => {
    setForm(updatedForm);
    const newData = { ...(data || {}), [today]: updatedForm };
    setData(newData);
    setSaving(true);
    await safeSave(`gf_rest_${user?.email}`, newData);
    setSaving(false);
  };

  // Manual save for text fields
  const handleManualSave = async () => {
    setSaving(true);
    const newData = { ...(data || {}), [today]: form };
    setData(newData);
    await safeSave(`gf_rest_${user?.email}`, newData);
    setSaving(false);
    setSavedFlag(true);
    setTimeout(() => setSavedFlag(false), 2500);
  };

  const save = saveImmediately;

  const toggleActivity = async (actId) => {
    const acts = form.activities || [];
    const updatedActs = acts.includes(actId) ? acts.filter(a => a !== actId) : [...acts, actId];
    await saveImmediately({ ...form, activities: updatedActs });
  };

  // Calculate 7-day streak of logging sleep
  const getRestStreak = () => {
    if (!data) return 0;
    let streak = 0;
    const d = new Date();
    while (true) {
      const key = d.toISOString().split("T")[0];
      if (data[key]?.sleepHours) { streak++; d.setDate(d.getDate() - 1); }
      else break;
    }
    return streak;
  };

  // Last 7 days
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    const key = d.toISOString().split("T")[0];
    const dayData = data?.[key] || emptyDay;
    return {
      label: d.toLocaleDateString("en", { weekday: "short" }),
      key,
      sleepHours: parseFloat(dayData.sleepHours) || 0,
      sleepQuality: dayData.sleepQuality || 0,
      activities: dayData.activities?.length || 0,
      isToday: key === today
    };
  });

  const avgSleep = last7.filter(d => d.sleepHours > 0).reduce((sum, d, _, arr) => sum + d.sleepHours / arr.length, 0);
  const avgQuality = last7.filter(d => d.sleepQuality > 0).reduce((sum, d, _, arr) => sum + d.sleepQuality / arr.length, 0);
  const totalActivities = last7.reduce((sum, d) => sum + d.activities, 0);

  if (!isThrive) return (
    <div style={S.page}>
      <h1 className="rt-sm" style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Rest & Relaxation</h1>
      <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
        <div style={{ marginBottom:16 }}><Moon size={56} color="#7A6BA0" /></div>
        <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Thrive Members Only</h3>
        <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:24 }}>
          Track your sleep, rest rituals, and relaxation activities to honor the healing power of rest.
        </p>
        <button onClick={() => startUpgradeCheckout("thrive", user?.email, user?.name)} className="btn-hover"
          style={{ ...S.btnPrimary, fontSize:14, padding:"10px 24px" }}>
          Upgrade to Thrive ($67/mo) to unlock →
        </button>
      </div>
    </div>
  );

  if (loading) return <LoadingScreen />;

  const streak = getRestStreak();

  return (
    <div style={S.page}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 className="rt-sm" style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>Rest & Relaxation</h1>
          <p style={{ color: C.muted, fontSize:14 }}>Rest is not laziness. It is sacred healing. 🌙</p>
        </div>
        {saving && <div style={{ color: C.muted, fontSize:12 }}>Saving...</div>}
      </div>

      {/* Tabs */}
      <div className="tab-scroll" style={{ display:"flex", gap:6, marginBottom:24, background: C.white,
        borderRadius:14, padding:6, border:`1px solid ${C.border}`, width:"fit-content" }}>
        {[["today","Today's Log",null], ["weekly","Weekly Summary",null], ["reflections","My Reflections",null]].map(([id, label, icon]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:"9px 20px", borderRadius:10, border:"none", cursor:"pointer", fontSize:14,
              background: tab === id ? C.warm : "transparent",
              color: tab === id ? "#FBF5EC" : C.muted, fontWeight: tab === id ? 500 : 400,
              fontFamily:"DM Sans, sans-serif" }}>
            {icon} {label}
          </button>
        ))}
      </div>

      {tab === "today" && (
        <div style={{ display:"grid", gap:20 }}>
          {/* Sleep section */}
          <div style={{ ...S.card }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:20, color: C.warm }}>
              Sleep Log
            </h3>
            <div className="rg-2col" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>HOURS SLEPT</label>
                <input type="number" min="0" max="24" step="0.5" placeholder="e.g. 7.5"
                  value={form.sleepHours || ""}
                  onChange={e => setForm(f => ({...f, sleepHours: e.target.value}))}
                  onBlur={e => saveImmediately({ ...form, sleepHours: e.target.value })}
                  style={{ ...S.input }} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>NAP TIME (MINUTES)</label>
                <input type="number" min="0" max="240" step="5" placeholder="e.g. 20"
                  value={form.napMinutes || ""}
                  onChange={e => setForm(f => ({...f, napMinutes: e.target.value}))}
                  onBlur={e => saveImmediately({ ...form, napMinutes: e.target.value })}
                  style={{ ...S.input }} />
              </div>
            </div>

            {/* Sleep quality */}
            <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:12 }}>SLEEP QUALITY</label>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {SLEEP_QUALITY.map(q => (
                <button key={q.value} onClick={() => saveImmediately({ ...form, sleepQuality: q.value })}
                  style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, padding:"10px 14px",
                    borderRadius:12, border:`2px solid ${todayData.sleepQuality === q.value ? C.warm : C.border}`,
                    background: todayData.sleepQuality === q.value ? `${C.warm}15` : "transparent",
                    cursor:"pointer", minWidth:64, transition:"all 0.2s" }}>
                  
                  <span style={{ fontSize:11, color: todayData.sleepQuality === q.value ? C.warm : C.muted, fontWeight: todayData.sleepQuality === q.value ? 600 : 400 }}>
                    {q.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Relaxation activities */}
          <div style={{ ...S.card }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:6, color: C.warm }}>
              Relaxation Activities
            </h3>
            <p style={{ color: C.muted, fontSize:13, marginBottom:18 }}>What did you do for yourself today? Select all that apply.</p>
            <div className="rg-3col" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
              {RELAX_ACTIVITIES.map(act => {
                const selected = todayData.activities?.includes(act.id);
                return (
                  <button key={act.id} onClick={() => toggleActivity(act.id)}
                    style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 12px",
                      borderRadius:12, border:`2px solid ${selected ? C.gold : C.border}`,
                      background: selected ? `${C.gold}15` : "transparent",
                      cursor:"pointer", textAlign:"left", transition:"all 0.2s" }}>
                    <span style={{ flexShrink:0 }}><ItemIcon item={act} size={18} /></span>
                    <span style={{ fontSize:12, color: selected ? C.warm : C.muted, fontWeight: selected ? 500 : 400, lineHeight:1.3 }}>
                      {act.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {(todayData.activities?.length || 0) > 0 && (
              <div style={{ marginTop:14, padding:"10px 16px", background:`${C.gold}10`, borderRadius:10,
                color: C.warm, fontSize:13 }}>
                {todayData.activities.length} rest activit{todayData.activities.length === 1 ? "y" : "ies"} logged today. You are taking such good care of yourself.
              </div>
            )}
          </div>

          {/* Rest note */}
          <div style={{ ...S.card }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:22, marginBottom:8, color: C.warm }}>
              Rest Reflection
            </h3>
            <p style={{ color: C.muted, fontSize:13, marginBottom:12 }}>How did rest feel today? Any patterns you're noticing?</p>
            <textarea rows={3} placeholder="Today my body felt... I noticed that rest helped me..."
              value={form.note || ""}
              onChange={e => setForm(f => ({...f, note: e.target.value}))}
              style={{ ...S.textarea }} />
            <button onClick={handleManualSave} disabled={saving} className="btn-hover"
              style={{ ...S.btnPrimary, marginTop:14, opacity: saving ? 0.6 : 1 }}>
              {saved ? "Saved" : saving ? "Saving..." : "Save Today's Log"}
            </button>
          </div>

        </div>
      )}

      {tab === "reflections" && (
        <div style={{ maxWidth:680 }}>
          <div style={{ marginBottom:20 }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, marginBottom:6, color: C.warm }}>
              My Rest Reflections
            </h3>
            <p style={{ color: C.muted, fontSize:14, lineHeight:1.7 }}>
              Every entry you've saved lives here, in your personal rest journal. 🌙
            </p>
          </div>

          {/* Today's entry if it exists */}
          {form.note && (
            <div style={{ ...S.card, marginBottom:16, border:`1.5px solid ${C.gold}44`,
              background:`linear-gradient(135deg, #FDF8F2, #FFFEF9)` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <span style={{ fontSize:12, color: C.gold, fontWeight:500, letterSpacing:"0.8px" }}>TODAY</span>
                <span style={{ fontSize:11, color: C.muted }}>
                  {new Date().toLocaleDateString("en", { weekday:"long", month:"long", day:"numeric" })}
                </span>
              </div>
              <p style={{ fontSize:15, color: C.warm, lineHeight:1.8, fontStyle:"italic", marginBottom:10 }}>
                "{form.note}"
              </p>
              <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                {form.sleepHours && <span style={{ fontSize:13, color: C.muted }}>{form.sleepHours}h slept</span>}
                {form.sleepQuality > 0 && <span style={{ fontSize:13, color: C.muted }}>
                  {["","Poor","Fair","Okay","Good","Great"][form.sleepQuality]}
                </span>}
                {form.activities?.length > 0 && (
                  <span style={{ fontSize:13, color: C.muted }}>
                    {form.activities.length} rest activit{form.activities.length === 1 ? "y" : "ies"}
                  </span>
                )}
              </div>
              {form.activities?.length > 0 && (
                <div style={{ marginTop:8, display:"flex", gap:6, flexWrap:"wrap" }}>
                  {form.activities.map(actId => {
                    const act = RELAX_ACTIVITIES.find(a => a.id === actId);
                    return act ? (
                      <span key={actId} style={{ fontSize:12, padding:"3px 10px", borderRadius:20,
                        background: C.cream, border:`1px solid ${C.border}`, color: C.warm }}>
                        <span style={{display:"inline-flex",alignItems:"center",gap:4}}><ItemIcon item={act} size={13} />{act.label}</span>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          )}

          {/* All past entries */}
          {data && Object.keys(data).filter(k => data[k]?.note || data[k]?.sleepHours).length > 0 ? (
            <div style={{ display:"grid", gap:12 }}>
              {Object.keys(data)
                .filter(k => data[k]?.note || data[k]?.sleepHours)
                .sort((a, b) => b.localeCompare(a))
                .map(dateKey => {
                  const entry = data[dateKey];
                  const isToday2 = dateKey === today;
                  if (isToday2 && form.note) return null; // already shown above
                  const d = new Date(dateKey + "T12:00:00");
                  return (
                    <div key={dateKey} style={{ ...S.card, padding:"18px 20px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                        <span style={{ fontSize:12, color: isToday2 ? C.gold : C.muted, fontWeight: isToday2 ? 500 : 400, letterSpacing:"0.5px" }}>
                          {isToday2 ? "TODAY" : d.toLocaleDateString("en", { weekday:"long", month:"long", day:"numeric" })}
                        </span>
                        <div style={{ display:"flex", gap:12 }}>
                          {entry.sleepHours && <span style={{ fontSize:12, color: C.muted }}>{entry.sleepHours}h</span>}
                          {entry.sleepQuality > 0 && <span style={{ fontSize:13 }}>
                            {["","Poor","Fair","Okay","Good","Great"][entry.sleepQuality]}
                          </span>}
                        </div>
                      </div>
                      {entry.note && (
                        <p style={{ fontSize:14, color: C.warm, lineHeight:1.8, fontStyle:"italic", marginBottom:entry.activities?.length > 0 ? 10 : 0 }}>
                          "{entry.note}"
                        </p>
                      )}
                      {!entry.note && entry.sleepHours && (
                        <p style={{ fontSize:13, color: C.muted, fontStyle:"italic" }}>
                          No reflection written · {entry.sleepHours}h sleep logged
                        </p>
                      )}
                      {entry.activities?.length > 0 && (
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                          {entry.activities.map(actId => {
                            const act = RELAX_ACTIVITIES.find(a => a.id === actId);
                            return act ? (
                              <span key={actId} style={{ fontSize:11, padding:"3px 10px", borderRadius:20,
                                background: C.cream, border:`1px solid ${C.border}`, color: C.muted }}>
                                <span style={{display:"inline-flex",alignItems:"center",gap:4}}><ItemIcon item={act} size={13} />{act.label}</span>
                              </span>
                            ) : null;
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px",
              background:`linear-gradient(135deg, #EDE9F6, #F5F3FA)`, border:`1px solid #C8C0E0` }}>
              <div style={{ marginBottom:14 }}><Moon size={48} color="#7A6BA0" /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, color:"#4A3D70",
                fontStyle:"italic", lineHeight:1.8, marginBottom:8 }}>
                Your rest story begins here.
              </p>
              <p style={{ fontSize:13, color:"#7A6BA0", lineHeight:1.7 }}>
                Log your sleep and write a reflection in Today's Log,<br />
                then tap "Save Today's Log" to keep it here.
              </p>
              <button onClick={() => setTab("today")}
                style={{ marginTop:20, ...S.btnPrimary, fontSize:13, padding:"10px 24px" }}>
                Go to Today's Log →
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "weekly" && (
        <div style={{ display:"grid", gap:20 }}>
          {/* Summary stats */}
          <div className="rg-3col" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
            <div style={{ ...S.card, textAlign:"center", background:"linear-gradient(135deg, #EDE9F6, #F5F3FA)", border:`1px solid #C8C0E0` }}>
              <div style={{ marginBottom:6 }}><Moon size={32} color="#7A6BA0" /></div>
              <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:36, color:"#4A3D70" }}>
                {avgSleep > 0 ? avgSleep.toFixed(1) : "None yet"}
              </div>
              <div style={{ color:"#7A6BA0", fontSize:12, marginTop:4 }}>avg hours/night</div>
              {avgSleep > 0 && <div style={{ fontSize:11, color: avgSleep >= 7 ? "#27AE60" : "#C4614B", marginTop:4 }}>
                {avgSleep >= 8 ? "Excellent 🌟" : avgSleep >= 7 ? "Good ✓" : avgSleep >= 6 ? "Room to grow" : "Prioritize rest 💙"}
              </div>}
            </div>
            <div style={{ ...S.card, textAlign:"center", background:`linear-gradient(135deg, #FDF3E0, #FBF5EC)`, border:`1px solid ${C.gold}44` }}>
              <div style={{ fontSize:32, marginBottom:6 }}>⭐</div>
              <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:36, color: C.warm }}>
                {avgQuality > 0 ? avgQuality.toFixed(1) : "None yet"}
              </div>
              <div style={{ color: C.muted, fontSize:12, marginTop:4 }}>avg sleep quality</div>
              {avgQuality > 0 && <div style={{ fontSize:11, color: avgQuality >= 4 ? "#27AE60" : C.muted, marginTop:4 }}>
                {SLEEP_QUALITY.find(q => q.value === Math.round(avgQuality))?.label || ""}
              </div>}
            </div>
            <div style={{ ...S.card, textAlign:"center", background:"linear-gradient(135deg, #E3EEE2, #F0F5EF)", border:`1px solid #A8C8A0` }}>
              <div style={{ marginBottom:6 }}><Heart size={32} color="#C4614B" /></div>
              <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:36, color:"#2D4228" }}>{totalActivities}</div>
              <div style={{ color:"#4A6741", fontSize:12, marginTop:4 }}>rest activities this week</div>
              <div style={{ fontSize:11, color: streak > 0 ? "#27AE60" : C.muted, marginTop:4 }}>
                {streak > 0 ? `${streak} day logging streak` : "Start your streak today"}
              </div>
            </div>
          </div>

          {/* 7-day sleep chart */}
          <div style={{ ...S.card }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:20, color: C.warm }}>Sleep Hours: Last 7 Days</h3>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:8, alignItems:"end" }}>
              {last7.map((day, i) => (
                <div key={i} style={{ textAlign:"center" }}>
                  <div style={{ fontSize:11, color: C.muted, marginBottom:4 }}>{day.sleepHours > 0 ? `${day.sleepHours}h` : "·"}</div>
                  <div style={{ height:80, display:"flex", alignItems:"flex-end", justifyContent:"center", marginBottom:6 }}>
                    <div style={{ width:"70%", borderRadius:"4px 4px 0 0",
                      height:`${Math.max(4, Math.min(80, (day.sleepHours / 10) * 80))}px`,
                      background: day.sleepHours >= 7 ? "linear-gradient(180deg, #A8D4A0, #4A8741)" :
                        day.sleepHours > 0 ? "linear-gradient(180deg, #D4B8E0, #7A6BA0)" : C.border,
                      transition:"height 0.4s ease", opacity: day.sleepHours > 0 ? 1 : 0.3 }} />
                  </div>
                  <div style={{ fontSize:11, color: day.isToday ? C.gold : C.muted, fontWeight: day.isToday ? 600 : 400 }}>
                    {day.label}
                  </div>
                  {day.sleepQuality > 0 && (
                    <div style={{ fontSize:12, marginTop:2 }}>
                      {SLEEP_QUALITY.find(q => q.value === day.sleepQuality)?.icon}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:16, marginTop:16, fontSize:11, color: C.muted }}>
              <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                <div style={{ width:10, height:10, borderRadius:2, background:"#4A8741" }} /> 7+ hours (goal)
              </span>
              <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                <div style={{ width:10, height:10, borderRadius:2, background:"#7A6BA0" }} /> Under 7 hours
              </span>
            </div>
          </div>

          {/* Weekly activity log */}
          <div style={{ ...S.card }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:16, color: C.warm }}>Rest Activities by Day</h3>
            <div style={{ display:"grid", gap:10 }}>
              {last7.slice().reverse().map((day, i) => {
                const dayData = data?.[day.key] || emptyDay;
                const acts = dayData.activities || [];
                return (
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px",
                    background: day.isToday ? `${C.gold}10` : C.cream, borderRadius:12,
                    border:`1px solid ${day.isToday ? `${C.gold}44` : C.border}` }}>
                    <div style={{ fontSize:12, color: C.muted, minWidth:40, fontWeight: day.isToday ? 600 : 400 }}>
                      {day.isToday ? "Today" : day.label}
                    </div>
                    <div style={{ flex:1, display:"flex", gap:6, flexWrap:"wrap" }}>
                      {acts.length > 0 ? acts.map(actId => {
                        const act = RELAX_ACTIVITIES.find(a => a.id === actId);
                        return act ? (
                          <span key={actId} style={{ fontSize:16 }} title={act.label}>{act.icon}</span>
                        ) : null;
                      }) : (
                        <span style={{ fontSize:12, color: C.muted, fontStyle:"italic" }}>No activities logged</span>
                      )}
                    </div>
                    {dayData.sleepHours && (
                      <div style={{ fontSize:12, color: C.muted, flexShrink:0 }}>{dayData.sleepHours}h sleep</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  AI NOURISHMENT GUIDE
// ══════════════════════════════════════════════════════════════════
function NourishmentGuideView({ user }) {
  const isThrive = user?.plan === "thrive";

  const MOODS = [
    { id:"comfort", label:"I need comfort", Icon:Heart },
    { id:"energy", label:"I need energy", Icon:Zap },
    { id:"calm", label:"I need calm", Icon:Leaf },
    { id:"joy", label:"I need joy", Icon:Sun },
    { id:"grounded", label:"I need grounding", Icon:Leaf },
    { id:"clarity", label:"I need clarity", Icon:Sparkles },
  ];

  const [tab, setTab] = useState("explore");
  const [comfortFood, setComfortFood] = useState("");
  const [comfortResponse, setComfortResponse] = useState(null);
  const [comfortLoading, setComfortLoading] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [recipeResponse, setRecipeResponse] = useState(null);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalForm, setJournalForm] = useState({ before:"", food:"", after:"", insight:"" });
  const [savingJournal, setSavingJournal] = useState(false);
  const [journalSaved, setJournalSaved] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [savingFav, setSavingFav] = useState(false);
  const [favSaved, setFavSaved] = useState(false);

  useEffect(() => {
    (async () => {
      const saved = await safeRetrieve(`gf_nourishment_journal_${user?.email}`) || [];
      const savedFavs = await safeRetrieve(`gf_nourishment_favorites_${user?.email}`) || [];
      setJournalEntries(saved);
      setFavorites(savedFavs);
      setLoadingData(false);
    })();
  }, []);

  const saveFavorite = async (type, content, label) => {
    setSavingFav(true);
    const fav = { id: Date.now(), type, content, label, savedAt: new Date().toISOString() };
    const updated = [fav, ...favorites].slice(0, 50);
    setFavorites(updated);
    await safeSave(`gf_nourishment_favorites_${user?.email}`, updated);
    setSavingFav(false);
    setFavSaved(true);
    setTimeout(() => setFavSaved(false), 2500);
  };

  const removeFavorite = async (id) => {
    const updated = favorites.filter(f => f.id !== id);
    setFavorites(updated);
    await safeSave(`gf_nourishment_favorites_${user?.email}`, updated);
  };

  const isAlreadySaved = (content) => favorites.some(f => f.content === content);

  const exploreComfortFood = async () => {
    if (!comfortFood.trim()) return;
    setComfortLoading(true);
    setComfortResponse(null);
    try {
      const res = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: `You are a warm, trauma-informed wellness guide for ThriveRoot by Glow & Flow who specializes in the emotional relationship with food, speaking with Black women. You understand that our food carries history: survival, celebration, Sunday dinners, and love passed down through generations, across Southern, Caribbean, African, and many other traditions. You never ask anyone to give up the food of her culture. You help her notice when she's reaching for a plate because she's hungry, and when she's reaching for it because a feeling needs tending. No calorie counting, no restriction, no shame, and no weight talk. Never use words like "guilty," "cheat," "indulge," "clean," "bad food," or "unhealthy." Do not make medical claims; if she mentions a health condition, encourage her to talk with her doctor. Never use em dashes.`,
          messages: [{
            role: "user",
            content: `A woman shared that she is drawn to this comfort food: "${comfortFood}". 

Please respond with:
1. A compassionate, validating paragraph about the emotional need or memory this food likely holds (2-3 sentences). Honor it; never shame it
2. What the body might actually be craving nutritionally or emotionally (2-3 sentences)
3. Two or three options that honor the SAME emotional need and flavors, including at least one way to enjoy the original food with more presence or a gentle twist (like a family recipe reimagined), with a brief, loving note on each
4. A gentle affirmation to close (1-2 sentences)

Keep the tone warm, poetic, and deeply compassionate. Format with clear sections but no harsh headers; use gentle labels like "What your body is telling you:" etc.`
          }]
        })
      });
      const data = await res.json();
      const text = data?.reply || "Something went wrong. Please try again.";
      setComfortResponse(text);
    } catch(e) {
      setComfortResponse("I'm having trouble connecting right now. Please try again in a moment.");
    }
    setComfortLoading(false);
  };

  const generateRecipe = async () => {
    if (!selectedMood) return;
    setRecipeLoading(true);
    setRecipeResponse(null);
    const mood = MOODS.find(m => m.id === selectedMood);
    try {
      const res = await apiFetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: `You are a nourishment guide and home cook for ThriveRoot by Glow & Flow, creating recipes for Black women. You draw lovingly on soul food, Southern, Caribbean, and African diaspora kitchens, and you often reimagine family favorites in ways that keep their flavor and their meaning. Your approach is anti-diet and body-affirming: recipes are about pleasure, nourishment, and joy, never restriction or weight. Keep ingredients affordable and easy to find at an everyday grocery store. Describe how ingredients support the body warmly but accurately, without exaggerated health claims. Speak warmly and never use em dashes.`,
          messages: [{
            role: "user",
            content: `Create a nourishing recipe for a woman who says: "${mood.label}".

Please include:
1. A beautiful recipe name that reflects the emotional intention
2. A short intro (2 sentences) about why this recipe supports this emotional state
3. Ingredients list (keep it accessible, no exotic hard-to-find items)
4. Simple step-by-step instructions
5. For each KEY ingredient (pick 3-4), include a short "Nourishment Note" on how it supports the body, written warmly and accurately (for example, "collard greens bring fiber and folate" or "sweet potatoes offer steady, slow-burning energy")
6. A closing affirmation about nourishing oneself

Format clearly with sections. Keep it warm, poetic, and empowering.`
          }]
        })
      });
      const data = await res.json();
      const text = data?.reply || "Something went wrong. Please try again.";
      setRecipeResponse(text);
    } catch(e) {
      setRecipeResponse("I'm having trouble connecting right now. Please try again in a moment.");
    }
    setRecipeLoading(false);
  };

  const saveJournalEntry = async () => {
    if (!journalForm.food.trim()) return;
    setSavingJournal(true);
    const entry = {
      ...journalForm,
      date: new Date().toISOString(),
      id: Date.now()
    };
    const updated = [entry, ...journalEntries].slice(0, 100);
    setJournalEntries(updated);
    await safeSave(`gf_nourishment_journal_${user?.email}`, updated);
    setJournalForm({ before:"", food:"", after:"", insight:"" });
    setSavingJournal(false);
    setJournalSaved(true);
    setTimeout(() => setJournalSaved(false), 3000);
  };

  if (!isThrive) return (
    <div style={S.page}>
      <h1 className="rt-sm" style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:8 }}>Nourishment Guide</h1>
      <div style={{ ...S.card, textAlign:"center", padding:"56px 32px", maxWidth:480, margin:"40px auto" }}>
        <div style={{ marginBottom:16 }}><Apple size={56} color={C.gold} /></div>
        <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:28, marginBottom:10 }}>Thrive Members Only</h3>
        <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:24 }}>
          Explore the emotional roots of your food cravings, discover healing recipes, and build a nourishing relationship with food, with zero diet culture.
        </p>
        <button onClick={() => startUpgradeCheckout("thrive", user?.email, user?.name)} className="btn-hover"
          style={{ ...S.btnPrimary, fontSize:14, padding:"10px 24px" }}>
          Upgrade to Thrive ($67/mo) to unlock →
        </button>
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <h1 className="rt-sm" style={{ fontFamily:"Cormorant Garamond, serif", fontSize:42, fontWeight:400, marginBottom:4 }}>
          Nourishment Guide
        </h1>
        <p style={{ color: C.muted, fontSize:14, lineHeight:1.7 }}>
          Food is medicine, memory, and love. Let's explore your relationship with it, gently and without judgment 🍓
        </p>
      </div>

      {/* Tabs */}
      <div className="tab-scroll" style={{ display:"flex", gap:6, marginBottom:28, background: C.white,
        borderRadius:14, padding:6, border:`1px solid ${C.border}`, width:"fit-content", maxWidth:"100%" }}>
        {[
          ["explore", "Comfort Food Explorer", null],
          ["recipes", "Healing Recipes", null],
          ["journal", "Food & Feelings", null],
          ["favorites", `Saved (${favorites.length})`, null]
        ].map(([id, label, icon]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:"9px 18px", borderRadius:10, border:"none", cursor:"pointer", fontSize:13,
              background: tab === id ? C.warm : "transparent",
              color: tab === id ? "#FBF5EC" : C.muted,
              fontWeight: tab === id ? 500 : 400, fontFamily:"DM Sans, sans-serif",
              whiteSpace:"nowrap" }}>
            {icon} {label}
          </button>
        ))}
      </div>

      {/* ── COMFORT FOOD EXPLORER ── */}
      {tab === "explore" && (
        <div style={{ maxWidth:720 }}>
          <div style={{ ...S.card, marginBottom:20 }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, marginBottom:8, color: C.warm }}>
              What comfort food are you drawn to?
            </h3>
            <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:20 }}>
              There is no wrong answer here. Every craving is a message. Share what you're drawn to and let's explore the deeper need together, with complete compassion and zero judgment.
            </p>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <input
                value={comfortFood}
                onChange={e => setComfortFood(e.target.value)}
                onKeyDown={e => e.key === "Enter" && exploreComfortFood()}
                placeholder="e.g. mac and cheese, chocolate, chips, ice cream..."
                style={{ ...S.input, flex:1, minWidth:200 }}
              />
              <button onClick={exploreComfortFood} disabled={comfortLoading || !comfortFood.trim()}
                className="btn-hover"
                style={{ ...S.btnPrimary, padding:"12px 24px", opacity: comfortLoading || !comfortFood.trim() ? 0.6 : 1, whiteSpace:"nowrap" }}>
                {comfortLoading ? "Exploring..." : "Explore"}
              </button>
            </div>
          </div>

          {comfortLoading && (
            <div style={{ ...S.card, textAlign:"center", padding:"40px 24px" }}>
              <div style={{ marginBottom:12, animation:"float 2s ease-in-out infinite" }}><Heart size={36} color={C.gold} /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:18, color: C.warm, fontStyle:"italic" }}>
                Exploring the wisdom in your craving...
              </p>
            </div>
          )}

          {comfortResponse && !comfortLoading && (
            <div style={{ ...S.card, background:`linear-gradient(135deg, #FDF8F2, #FFFEF9)`,
              border:`1px solid ${C.gold}44` }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20,
                paddingBottom:16, borderBottom:`1px solid ${C.border}` }}>
                <Leaf size={28} color="#4A6741" />
                <div>
                  <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, color: C.warm }}>
                    Your craving for {comfortFood}
                  </div>
                  <div style={{ color: C.muted, fontSize:12 }}>Explored with compassion</div>
                </div>
              </div>
              <div style={{ color: C.warm, fontSize:15, lineHeight:1.9, whiteSpace:"pre-wrap" }}>
                {comfortResponse}
              </div>
              <div style={{ display:"flex", gap:10, marginTop:20, flexWrap:"wrap" }}>
                <button onClick={() => { setComfortFood(""); setComfortResponse(null); }}
                  style={{ background:"none", border:`1px solid ${C.border}`,
                    borderRadius:8, padding:"8px 16px", cursor:"pointer", color: C.muted, fontSize:13 }}>
                  Explore another food
                </button>
                {!isAlreadySaved(comfortResponse) ? (
                  <button onClick={() => saveFavorite("swap", comfortResponse, `Comfort swap: ${comfortFood}`)}
                    className="btn-hover"
                    style={{ background:`${C.gold}15`, border:`1px solid ${C.gold}66`,
                      borderRadius:8, padding:"8px 16px", cursor:"pointer", color: C.gold, fontSize:13 }}>
                    {savingFav ? "Saving..." : favSaved ? "Saved" : "Save this insight"}
                  </button>
                ) : (
                  <span style={{ fontSize:13, color: C.gold, padding:"8px 4px" }}>🩷 Saved to favorites</span>
                )}
              </div>
            </div>
          )}

          {!comfortResponse && !comfortLoading && (
            <div style={{ ...S.card, background:`linear-gradient(135deg, #FDF3E0, #FBF5EC)`,
              border:`1px solid ${C.gold}33`, padding:"28px 24px" }}>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:18, color: C.warm,
                fontStyle:"italic", lineHeight:1.8, textAlign:"center" }}>
                "Your cravings are not your enemy. They are your body speaking in the only language it knows.
                Every comfort food holds a story worth honoring."
              </p>
              <p style={{ textAlign:"center", color: C.gold, fontSize:13, marginTop:10 }}>
                Glow & Flow Nourishment Philosophy
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── HEALING RECIPES ── */}
      {tab === "recipes" && (
        <div style={{ maxWidth:720 }}>
          <div style={{ ...S.card, marginBottom:20 }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, marginBottom:8, color: C.warm }}>
              What does your body need right now?
            </h3>
            <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:20 }}>
              Choose the feeling you're seeking and receive a recipe crafted to nourish that specific emotional and physical need, with ingredient explanations rooted in holistic healing.
            </p>
            <div className="rg-3col" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:20 }}>
              {MOODS.map(mood => (
                <button key={mood.id} onClick={() => setSelectedMood(mood.id)} className="btn-hover"
                  style={{ padding:"14px 10px", borderRadius:14, border:`2px solid ${selectedMood === mood.id ? C.gold : C.border}`,
                    background: selectedMood === mood.id ? `${C.gold}15` : "transparent",
                    cursor:"pointer", textAlign:"center", transition:"all 0.2s" }}>
                  <div style={{ marginBottom:6 }}><ItemIcon item={mood} size={24} /></div>
                  <div style={{ fontSize:12, color: selectedMood === mood.id ? C.warm : C.muted,
                    fontWeight: selectedMood === mood.id ? 500 : 400 }}>
                    {mood.label}
                  </div>
                </button>
              ))}
            </div>
            <button onClick={generateRecipe} disabled={recipeLoading || !selectedMood}
              className="btn-hover"
              style={{ ...S.btnPrimary, width:"100%", opacity: recipeLoading || !selectedMood ? 0.6 : 1 }}>
              {recipeLoading ? "Creating your recipe..." : "Generate Recipe"}
            </button>
          </div>

          {recipeLoading && (
            <div style={{ ...S.card, textAlign:"center", padding:"40px 24px" }}>
              <div style={{ marginBottom:12, animation:"float 2s ease-in-out infinite" }}><Leaf size={36} color="#4A6741" /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:18, color: C.warm, fontStyle:"italic" }}>
                Crafting something nourishing just for you...
              </p>
            </div>
          )}

          {recipeResponse && !recipeLoading && (
            <div style={{ ...S.card, background:`linear-gradient(135deg, #F0F8F0, #FFFEF9)`,
              border:`1px solid #A8D4A044` }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20,
                paddingBottom:16, borderBottom:`1px solid ${C.border}` }}>
                <span>{(() => { const m = MOODS.find(m => m.id === selectedMood); return m ? <ItemIcon item={m} size={28} /> : null; })()}</span>
                <div>
                  <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, color: C.warm }}>
                    Your Healing Recipe
                  </div>
                  <div style={{ color: C.muted, fontSize:12 }}>
                    For when {MOODS.find(m => m.id === selectedMood)?.label.toLowerCase()}
                  </div>
                </div>
              </div>
              <div style={{ color: C.warm, fontSize:15, lineHeight:1.9, whiteSpace:"pre-wrap" }}>
                {recipeResponse}
              </div>
              <div style={{ display:"flex", gap:10, marginTop:20, flexWrap:"wrap" }}>
                <button onClick={() => { setSelectedMood(null); setRecipeResponse(null); }}
                  style={{ background:"none", border:`1px solid ${C.border}`,
                    borderRadius:8, padding:"8px 16px", cursor:"pointer", color: C.muted, fontSize:13 }}>
                  Generate another recipe
                </button>
                {!isAlreadySaved(recipeResponse) ? (
                  <button onClick={() => saveFavorite("recipe", recipeResponse, `Recipe: ${MOODS.find(m=>m.id===selectedMood)?.label}`)}
                    className="btn-hover"
                    style={{ background:`${C.gold}15`, border:`1px solid ${C.gold}66`,
                      borderRadius:8, padding:"8px 16px", cursor:"pointer", color: C.gold, fontSize:13 }}>
                    {savingFav ? "Saving..." : favSaved ? "Saved" : "Save this recipe"}
                  </button>
                ) : (
                  <span style={{ fontSize:13, color: C.gold, padding:"8px 4px" }}>🩷 Saved to favorites</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── FOOD & FEELINGS JOURNAL ── */}
      {tab === "journal" && (
        <div style={{ maxWidth:720 }}>
          <div style={{ ...S.card, marginBottom:20 }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, marginBottom:8, color: C.warm }}>
              Food & Feelings Journal
            </h3>
            <p style={{ color: C.muted, fontSize:14, lineHeight:1.8, marginBottom:20 }}>
              This is not a food diary. It is a space to notice patterns with curiosity and compassion. No tracking. No judgment. Just you, getting to know yourself better.
            </p>

            <div style={{ display:"grid", gap:14 }}>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>
                  HOW WERE YOU FEELING BEFORE YOU ATE?
                </label>
                <textarea rows={2} placeholder="Stressed, tired, lonely, bored, celebratory, anxious..."
                  value={journalForm.before}
                  onChange={e => setJournalForm(f => ({...f, before: e.target.value}))}
                  style={{ ...S.textarea }} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>
                  WHAT DID YOU EAT OR CRAVE?
                </label>
                <input placeholder="Be honest. This is a safe space..."
                  value={journalForm.food}
                  onChange={e => setJournalForm(f => ({...f, food: e.target.value}))}
                  style={{ ...S.input }} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>
                  HOW DID YOU FEEL AFTER?
                </label>
                <textarea rows={2} placeholder="Nourished, satisfied, heavy, comforted, energized, disconnected..."
                  value={journalForm.after}
                  onChange={e => setJournalForm(f => ({...f, after: e.target.value}))}
                  style={{ ...S.textarea }} />
              </div>
              <div>
                <label style={{ display:"block", fontSize:12, letterSpacing:"0.8px", color: C.muted, marginBottom:8 }}>
                  ANY INSIGHT OR PATTERN YOU NOTICED? (OPTIONAL)
                </label>
                <textarea rows={2} placeholder="I notice I reach for this when... I wonder if my body was really asking for..."
                  value={journalForm.insight}
                  onChange={e => setJournalForm(f => ({...f, insight: e.target.value}))}
                  style={{ ...S.textarea }} />
              </div>

              <button onClick={saveJournalEntry} disabled={savingJournal || !journalForm.food.trim()}
                className="btn-hover"
                style={{ ...S.btnPrimary, opacity: savingJournal || !journalForm.food.trim() ? 0.6 : 1 }}>
                {journalSaved ? "Saved" : savingJournal ? "Saving..." : "Save Reflection"}
              </button>
            </div>
          </div>

          {/* Past entries */}
          {journalEntries.length > 0 && (
            <div style={{ ...S.card }}>
              <h4 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, marginBottom:16, color: C.warm }}>
                Your Reflections
              </h4>
              <div style={{ display:"grid", gap:14 }}>
                {journalEntries.slice(0, 10).map(entry => (
                  <div key={entry.id} style={{ padding:"16px 18px", background: C.cream,
                    borderRadius:12, border:`1px solid ${C.border}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                      <div style={{ fontWeight:500, color: C.warm, fontSize:14 }}>{entry.food}</div>
                      <div style={{ fontSize:11, color: C.muted }}>
                        {new Date(entry.date).toLocaleDateString("en", { month:"short", day:"numeric", year:"numeric" })}
                      </div>
                    </div>
                    {entry.before && (
                      <div style={{ fontSize:13, color: C.muted, marginBottom:6 }}>
                        <span style={{ color: C.warm, fontWeight:500 }}>Before:</span> {entry.before}
                      </div>
                    )}
                    {entry.after && (
                      <div style={{ fontSize:13, color: C.muted, marginBottom:6 }}>
                        <span style={{ color: C.warm, fontWeight:500 }}>After:</span> {entry.after}
                      </div>
                    )}
                    {entry.insight && (
                      <div style={{ fontSize:13, color: C.gold, fontStyle:"italic", marginTop:8,
                        paddingTop:8, borderTop:`1px solid ${C.border}` }}>
                        {entry.insight}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {journalEntries.length === 0 && !loadingData && (
            <div style={{ ...S.card, textAlign:"center", padding:"36px 24px",
              background:`linear-gradient(135deg, #FDF3E0, #FBF5EC)`, border:`1px solid ${C.gold}33` }}>
              <div style={{ marginBottom:12 }}><BookOpen size={36} color={C.gold} /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:18, color: C.warm,
                fontStyle:"italic", lineHeight:1.8 }}>
                Your food story starts here. Every entry is an act of self-knowing.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── FAVORITES ── */}
      {tab === "favorites" && (
        <div style={{ maxWidth:720 }}>
          <div style={{ marginBottom:20 }}>
            <h3 style={{ fontFamily:"Cormorant Garamond, serif", fontSize:26, marginBottom:6, color: C.warm }}>
              Your Saved Recipes & Insights 🩷
            </h3>
            <p style={{ color: C.muted, fontSize:14, lineHeight:1.8 }}>
              Everything you've loved and saved, in your personal nourishment library.
            </p>
          </div>

          {favorites.length === 0 && (
            <div style={{ ...S.card, textAlign:"center", padding:"48px 32px",
              background:`linear-gradient(135deg, #FDF3E0, #FBF5EC)`, border:`1px solid ${C.gold}33` }}>
              <div style={{ marginBottom:14 }}><Heart size={48} color={C.gold} /></div>
              <p style={{ fontFamily:"Cormorant Garamond, serif", fontSize:20, color: C.warm,
                fontStyle:"italic", lineHeight:1.8, marginBottom:8 }}>
                Your favorites will live here.
              </p>
              <p style={{ color: C.muted, fontSize:14 }}>
                When you find a recipe or insight you love, tap "Save" to keep it here forever.
              </p>
            </div>
          )}

          {favorites.map(fav => (
            <div key={fav.id} style={{ ...S.card, marginBottom:16,
              border:`1px solid ${fav.type === "recipe" ? "#A8D4A044" : `${C.gold}44`}`,
              background: fav.type === "recipe" ? "linear-gradient(135deg, #F0F8F0, #FFFEF9)" : "linear-gradient(135deg, #FDF8F2, #FFFEF9)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14,
                paddingBottom:12, borderBottom:`1px solid ${C.border}` }}>
                <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                  {fav.type === "recipe" ? <Leaf size={20} color="#4A6741" /> : <Heart size={20} color={C.gold} />}
                  <div>
                    <div style={{ fontFamily:"Cormorant Garamond, serif", fontSize:18, color: C.warm }}>{fav.label}</div>
                    <div style={{ fontSize:11, color: C.muted }}>
                      Saved {new Date(fav.savedAt).toLocaleDateString("en", { month:"short", day:"numeric", year:"numeric" })}
                    </div>
                  </div>
                </div>
                <button onClick={() => removeFavorite(fav.id)}
                  style={{ background:"none", border:"none", cursor:"pointer", color: C.muted,
                    fontSize:18, padding:"4px", lineHeight:1 }} title="Remove from favorites">
                  ×
                </button>
              </div>
              <div style={{ color: C.warm, fontSize:14, lineHeight:1.9, whiteSpace:"pre-wrap",
                maxHeight:300, overflowY:"auto" }}>
                {fav.content}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
